import { CommonModule } from '@angular/common';
import { SsSelectClearDirective } from '../shared/ss-select-clear.directive';
import { RelationHopsComponent } from './relation-hops.component';
import { copyRelationHops, RelationHop, RelationHopsConfig } from './relation-hops.types';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import type * as echarts from 'echarts';

import { DrillDownComponent } from '../../chart-components/drill-down/drill-down.component';
import { UnifiedTableMatrixChartComponent } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.component';
import { DvmFileUploadComponent } from '../../chart-components/dvm-file-upload/dvm-file-upload.component';
import { DvmPopupComponent, DvmPopupBlock, DvmPopupButton } from '../../chart-components/dvm-popup/dvm-popup.component';
import type { TableMatrixCapabilities, TableMatrixColumn, TableMatrixNotes, TableMatrixSaveEvent, TableMatrixPayloadTemplate, TableMatrixPayloadEndpoint } from '../../chart-components/unified-table-matrix-chart/unified-table-matrix-chart.types';
import { applyDrillDownSaveToWidgetData } from '../../chart-components/unified-table-matrix-chart/drilldown-save.utils';
import {
  buildDrillDownTreeFromRows,
  drillTotalsAtBottom,
  drillTotalsAtRight,
  drillTotalsBlockPosition,
  drillTreeToResponse,
  normalizeDrillTotalsPlacement,
  resolveDrillColumnOrder,
  type DrillClientConfig,
  type DrillTotalsPlacement,
} from '../../chart-components/unified-table-matrix-chart/drilldown-client-tree.utils';
import { HorizontalBarChartComponent } from '../../chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { UnifiedBarColumnChartComponent } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.component';
import type { BarColumnCapabilities, BarColumnVariant, BarColumnOrientation } from '../../chart-components/unified-bar-column-chart/unified-bar-column-chart.types';
import { UnifiedScatterBubbleChartComponent } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.component';
import type { ScatterBubbleCapabilities, ScatterBubbleVariant } from '../../chart-components/unified-scatter-bubble-chart/unified-scatter-bubble-chart.types';
import { UnifiedLineAreaChartComponent } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.component';
import type { LineAreaCapabilities, LineAreaMeasure, LineAreaVariant } from '../../chart-components/unified-line-area-chart/unified-line-area-chart.types';
import { WaterfallChartComponent } from '../../chart-components/waterfall-chart/waterfall-chart.component';
import { MatSliderComponent } from '../../global-components/mat-slider/mat-slider.component';
import { InputBoxComponent } from '../../global-components/input-box/input-box.component';
import { GlobalDropdownComponent } from '../../global-components/global-dropdown/global-dropdown.component';
import { NumberStatsComponent } from '../../global-components/number-stats/number-stats.component';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceDatasetService } from '../shared/self-service-dataset.service';
import { resolveSelfServiceApiUrl, buildSelfServiceApiUrl } from '../shared/self-service-api.utils';
import { buildBarComputeWidgetData, buildDrillDownWidgetData, buildLineAreaComputeWidgetData, buildWaterfallOption, extractBarComputeRows, extractLineAreaComputeRows, extractLineAreaFlatRows, lineAreaRowsFitCapabilities, parseDropdownOptions, recomputeEntryHasData, resolveRecomputeChartType } from '../shared/self-service-recompute.utils';
import { toSerializable } from '../shared/self-service-json.utils';
import { SelfServiceDashboardService } from '../shared/self-service-dashboard.service';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import { ConfirmationDialogService } from '../../shared-services/confirmation-dialog.service';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { BarVariantOption, BuilderLayoutConfig, CapabilityRegistryEntry, ChartCategory, FormatControl, FormatControlGroup, FormulaBuilderConfig, LabelValueOption, LayoutStepSection, LayoutStepsDefinition, ScatterBubbleModeOption, ScatterBubbleVariantOption, LineAreaVariantOption } from '../shared/self-service-config.types';
import { SavedSelfServiceDashboard, ComputedColumn, FormulaTemplate } from '../shared/self-service-types';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import { markDashboardJustPublished, readDynamicDashboardCache, readJson, writeDynamicDashboardCache } from '../shared/self-service-storage.utils';

export type BridgeStepType = 'START' | 'CHANGE' | 'TOTAL';
export type BridgeSign = 'AUTO' | 'POSITIVE' | 'NEGATIVE';

/** One entry in the Table & Matrix "Table type" dropdown (from config
 *  `options.tableTypes`). `group` places it under a heading; `normal`/`editable`/
 *  `drilldown` are the render-dispatch flags read instead of comparing `value`. */
export interface TableTypeOption extends LabelValueOption {
  group?: string;
  hint?: string;
  normal?: boolean;
  editable?: boolean;
  drilldown?: boolean;
  [flag: string]: unknown;
}
type BuilderPane = 'preview' | 'steps' | 'data';
const PANE_DRAG_TYPE = 'application/x-dvm-pane';
const FIELD_DRAG_TYPE = 'application/x-dvm-field';
const TILE_DRAG_TYPE = 'application/x-dvm-tile';

/** One row in the sequential bridge builder. Every field is always present on
 *  the working object (simplifies template binding); `sanitizeStepForPayload`
 *  strips the fields that don't apply to the step's current type when the
 *  final configuration is generated. */
export interface BridgeStep {
  id: string;
  stepType: BridgeStepType;
  label: string;
  dataset: string;
  field: string;
  aggregation: string;
  sign: BridgeSign;
  formula: string;
  formulaMode?: boolean;
}

/** A typed-in reference for one bracket token used in a target's formula —
 *  tells the backend that token's live value comes from ANOTHER input's
 *  output (`inputTitle`) landing on a specific target/row (`targetLabel` /
 *  `rowLabel`), rather than a plain dataset column. Fully user-typed. */
export interface TargetFormulaReferenceEntry {
  token: string;
  inputTitle: string;
  targetLabel: string;
  rowLabel: string;
  /** Which WATERFALL bridge step this reference belongs to — undefined for a
   *  non-waterfall target's single formula (which has no steps at all). */
  step?: string;
}

/** One row in the "Custom request body" popup's static key/value editors
 *  (staticValues / extraFields) — values are entered as text in the UI and
 *  parsed as JSON when possible, so arrays/numbers/booleans are configurable. */
export interface PayloadTemplateKvRow {
  key: string;
  value: string;
}
/** One row in the popup's `endpoints` editor. */
export interface PayloadTemplateEndpointRow {
  functionName: string;
  version: string;
}
/** Draft state backing the "Custom request body" popup for ONE endpoint.
 *  Row-editable mirror of TableMatrixPayloadTemplate. */
export interface PayloadTemplateDraft {
  dataKey: string;
  /** Row fields to send. Empty = every column of the table. */
  includeColumns: string[];
  staticValues: PayloadTemplateKvRow[];
  endpoints: PayloadTemplateEndpointRow[];
  extraFields: PayloadTemplateKvRow[];
}

/** Pre-calculated bar the analytics engine returns per bridge step. */
export interface WaterfallBar {
  label: string;
  delta: number;
  bottom: number;
  top: number;
  type: string;
  /** The step's configured "Direction" — a display-only color hint. Never
   *  used to derive delta/bottom/top, only which bar color to paint. */
  colorSign?: string;
}

/** One hierarchy level in the Drill-Down Table's dimension list — e.g. Level 1
 *  = "Type Grouping", Level 2 = "Option Code". Unlike a bridge step, a
 *  dimension carries no aggregation of its own; it only groups. All
 *  dimensions (and measures) share the ONE dataset picked for the table. */
export interface DrillDownDimension {
  id: string;
  field: string;
  label: string;
  /** OPTIONAL fixed display order for this level's values, as the user typed it
   *  (comma or newline separated). Emitted to the payload as `valueOrder: []`
   *  only when filled in — omitted entirely otherwise, so the backend keeps its
   *  default ordering. Use when a level is categorical and its natural order
   *  isn't alphabetical, e.g. "Base Options, Additional Options, Routing Cost". */
  valueOrder?: string;
  /** Fallback order for values `valueOrder` doesn't (fully) pin.
   *  'alpha-asc'/'alpha-desc' = A→Z / Z→A (e.g. K-series before Q-series is
   *  just A→Z on the model name); 'custom' = no automatic fallback — rely
   *  entirely on the "Value order" list, same as leaving this unset. Listed
   *  `valueOrder` entries always win first regardless. Ignored for
   *  all-numeric values (years), which always sort ascending. */
  sortBy?: 'custom' | 'alpha-asc' | 'alpha-desc';
  /** When true, this level's node LABELS render as text inputs in the
   *  drill-down's tree column (a rename — mirrors the measures' Editable flag);
   *  Save flows through the standard table-matrix save pipeline with the
   *  level's dataset field + old/new name. */
  editable?: boolean;
  /** How an editable label is edited: absent = free-text input; 'dropdown' =
   *  a select over `dropdownOptions` (CUSTOM values typed comma-separated in
   *  the builder — they need not exist in the data) and/or, with
   *  `dropdownFromData`, the labels already present at that level. */
  editor?: 'dropdown';
  dropdownOptions?: string;
  dropdownFromData?: boolean;
  /** Merged drill-down + pivot: the dataset this level reads from when it is
   *  NOT the table's primary one (blank = primary). Joined by the flat compute
   *  contract in client-side mode. */
  dataset?: string;
}

/** One column of a Normal/Editable table. Unlike the drill-down levels (one
 *  shared dataset), EACH column carries its own dataset so a table can mix
 *  columns from different datasets — the compute payload sends every entry as
 *  `{dataset, field, displayName}` and the backend joins/returns flat rows.
 *  A blank `dataset` falls back to the table's primary dataset; a blank
 *  `displayName` falls back to a title-cased version of the field name.
 *
 *  `filterField`/`filterOperator`/`filterValue` are an OPTIONAL default filter
 *  attached to THIS column's row in the builder, applied on the table's very
 *  first data request, before any global filter is picked. `filterField`
 *  picks WHICH column the filter runs against — it defaults to this column's
 *  own `field` but can be pointed at any OTHER column (e.g. attach a
 *  "rule_categories = Certifications" filter to the Rule Name row even though
 *  `rule_categories` isn't itself a displayed column). A blank `filterValue`
 *  means no filter for this row — it's skipped entirely. */
/** A Drill-Down formula column: name + expression + optional display anchor
 *  (`after` = the measure label/field or earlier formula it follows). */
/** One row of the Drill-Down "Column order" list (a BASE column before pivot
 *  fan-out). `orderKey` is what gets persisted: the measure FIELD or the
 *  formula name. */
export interface DrillColumnOrderItem {
  key: string;
  orderKey: string;
  kind: 'measure' | 'formula';
  split: boolean;
}

export interface DrillComputedColumn extends ComputedColumn {
  after?: string;
}

export interface TmColumnDef {
  id: string;
  dataset: string;
  field: string;
  displayName: string;
  /** Per-column editability. Editable tables: absent/`true` = editable,
   *  `false` = locked read-only. Normal tables: `true` = cell-editable (turns
   *  on caps.cellEditing), absent/`false` = plain read-only cell. */
  editable?: boolean;
  /** Input control for an editable column's cells (text/number/dropdown/date/…,
   *  from config `tmColumnControls`). Blank = plain text box. */
  control?: string;
  /** Show/hide in the rendered table. `true` = not displayed, but the field is
   *  still sent in the save payload (hiding is presentation-only). */
  hidden?: boolean;
  /** Show the table-level Save all button only when this column was edited. */
  saveAllTrigger?: boolean;
  /** Mark the column required — its header shows a `*` so users can see which
   *  values must be filled in. Presentation only; no save-time enforcement. */
  mandatory?: boolean;
  /** ACTION column: renders a button in every row instead of a value. Needs no
   *  dataset field — `field` is just its identity/header key. */
  isAction?: boolean;
  /** Material icon name ("visibility") or any short glyph/emoji. */
  buttonIcon?: string;
  /** Button text (optional — icon-only is fine). */
  buttonLabel?: string;
  /** What the click does: call an API, open a row-details popup, call an API and
   *  show the response in a popup, or just emit for the host. */
  actionKind?: 'api' | 'rowPopup' | 'apiPopup' | 'popupComponent' | 'emit';
  actionUrl?: string;
  actionMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** JSON body template; `{field}` is replaced with that row's value. */
  actionBody?: string;
  /** popupComponent: the POPUP tile this column opens (componentId). */
  popupComponentId?: string;
  /** apiPopup: dot-path to the rows array in the response. */
  actionRowsPath?: string;
  /** apiPopup: sort the response rows by this field. */
  actionRowsSortBy?: string;
  actionRowsSortDir?: 'asc' | 'desc';
  /** apiPopup: cap the rows shown AFTER sorting (1 = latest only). */
  actionMaxRows?: number;
  /** apiPopup: which response columns to show, in order (comma-separated;
   *  `name:Label` renames the header). Blank = every column. */
  actionRowsColumns?: string;
  /** Modal heading (defaults to the column label). */
  actionTitle?: string;
  /** Non-empty = confirm prompt shown before firing. */
  actionConfirm?: string;
  /** Re-fetch the table after a successful call. */
  actionRefresh?: boolean;
  align: '' | 'start' | 'center' | 'end';
  filterField: string;
  filterOperator: string;
  filterValue: string;
}

/** One value column shown (and rolled up) at every level of the drill-down
 *  tree — e.g. "DN Price" summed as the tree rolls up from leaf to root. */
export interface DrillDownMeasure {
  id: string;
  field: string;
  label: string;
  aggregation: string;
  rollUpStrategy: string;
  /** When true, the backend adds a dataset-wide grand-total row for this
   *  measure (ignoring all dimension grouping); when false, that measure is
   *  simply left out of the grand-total row (see DrillDownExecutionService /
   *  the drill-down table's sticky total row). */
  total: boolean;
  /** When true, this measure's cells render as editable inputs in the
   *  drill-down table (mirrors the Normal table's per-column cell editing);
   *  parents + totals re-sum client-side and Save flows through the standard
   *  table-matrix save pipeline. */
  editable?: boolean;
  /** Which 1-based tree levels the editable cells appear on (Level 1 = the
   *  outermost dimension, the last level = the leaf rows). Empty / absent =
   *  the default: a Text measure edits on every level, a Number measure on
   *  leaf rows only. Lets a table offer e.g. the dropdown on level 1 only. */
  editableLevels?: number[];
  /** How an editable cell is edited: absent = number / text input; 'dropdown'
   *  = a select over `dropdownOptions` (CUSTOM values typed comma-separated in
   *  the builder — they need not exist in the data) and/or, with
   *  `dropdownFromData`, the values the column already holds in the table. */
  editor?: 'dropdown';
  dropdownOptions?: string;
  dropdownFromData?: boolean;
  /** Merged drill-down + pivot: the dataset this measure reads from when it is
   *  NOT the table's primary one (blank = primary). */
  dataset?: string;
  /** With pivot column dimensions: `false` = keep ONE column for this measure
   *  (an attribute like "Cost increase %" / "AOP Target") instead of a column
   *  per pivot value. Absent = split (default). */
  pivot?: boolean;
  /** 'text' = a per-row attribute column (e.g. "Voice of Business"): shown as
   *  text, never summed, editable as free text when flagged Editable. */
  kind?: 'number' | 'text';
  /** Number-only display format. 'percent' adds a trailing "%" to the raw
   *  value as-is (e.g. 4.6 → "4.6%") — for columns like Cost Increase that are
   *  already stored in percentage POINTS. Absent = plain number. */
  format?: 'percent';
  /** Only meaningful with `format: 'percent'`. 'fraction' means the raw value
   *  is a 0-1 ratio (e.g. 0.05 for 5%) and must be ×100'd before the "%" is
   *  added — for columns like SSA % / Margin % that are stored as fractions,
   *  as opposed to Cost Increase which is already stored in percentage points
   *  and needs no scaling. Absent = no scaling (raw value as-is + "%"). */
  percentScale?: 'fraction';
  /** Blanks this measure's cell entirely when its (scaled) value is negative
   *  — e.g. SSA % showing nothing instead of "-8.6%". The Total/Grand Total
   *  cells are unaffected (they still sum/average every row). */
  hideNegative?: boolean;
  /** Round-off override (0-6) for THIS measure's cells + its Total/Grand Total
   *  cells — e.g. 0 for a whole-number Net Quantity column while the rest of
   *  the table keeps the shared "Decimal places" setting. Absent = shared. */
  decimals?: number;
  /** Computed but NOT shown — a formula input only (e.g. Total Cost behind
   *  SSA % / Margin %). */
  hidden?: boolean;
  /** Header text of this measure's column inside the right-hand Total block
   *  only ("Revenue" over the Sales summed across the verticals). Blank =
   *  the measure label. Only written to the payload when set. */
  rowTotalHeader?: string;
  /** Client mode: 'latest' = this measure aggregates only the rows of the
   *  newest year in the data (Sales = 2026 sales, not every year summed).
   *  Absent = all rows. */
  yearScope?: 'latest';
  /** The dataset column holding the year (e.g. fiscal_year) for `yearScope`. */
  yearField?: string;
  /** With `yearScope`: also show the year column itself in the table. Off
   *  (default) = the year column is plumbing only and never renders, even
   *  when a response / saved tree still carries it. */
  showYearColumn?: boolean;
  /** Aggregation AVG_BY ("Average per group"): the dataset column to group by
   *  (e.g. engine_model) — the node shows the mean of one average per group,
   *  so every engine model counts once regardless of its row count. */
  groupField?: string;
}

/** The year column a "Latest year only" measure reads, carried as a measure so
 *  the flat rows include it (see drillYearHelperMeasures). Hidden plumbing by
 *  default; a visible text column when the measure ticked "Show year column". */
interface DrillYearHelperMeasure {
  field: string;
  label: string;
  aggregation: string;
  rollUpStrategy: string;
  total: boolean;
  hidden: boolean;
  yearHelper: true;
  dataset?: string;
  pivot?: false;
  kind?: 'text';
  yearScope?: 'latest';
  yearField?: string;
}

/** One value column mapped in the Bar & Column builder. Multiple measures give
 *  "Multiple Measures"; `renderAs`/`axis` drive Line/Area combos + dual axis. A
 *  `series` split (on the layout, not per-measure) uses the FIRST measure. */
export interface BarBuilderMeasure {
  id: string;
  field: string;
  label: string;
  renderAs: 'bar' | 'line' | 'area';
  axis: 'y1' | 'y2';
}

/** One value column mapped in the Line & Area builder. Multiple measures give
 *  "Multi-Line"; `renderAs`/`axis` drive Line/Area combos + dual axis. A `series`
 *  split (on the layout, not per-measure) uses the FIRST measure. */
export interface LineAreaBuilderMeasure {
  id: string;
  field: string;
  dataset?: string;
  label: string;
  renderAs: 'line' | 'area';
  axis: 'y1' | 'y2';
  /** Explicit line colour for this measure; '' = use the palette slot. */
  color?: string;
  /** Data-point marker shape; 'none' hides markers for this measure. */
  symbol?: string;
  /** Data-point marker size in pixels; 0 hides markers for this measure. */
  symbolSize?: number;
}

/**
 * CLIENT-SIDE cross-filter registry. Declares, per layoutType, HOW a tile's
 * render data + its unfiltered rows are stored on the DashboardTile, so the
 * cross-filter engine can publish/react WITHOUT knowing about any specific chart
 * type. A tile whose layoutType is listed here participates in cross-filtering.
 *
 * Adding a new client-side chart (Pie, Donut, Line, …) to cross-filter is then a
 * ONE-LINE registry entry — no changes to the cross-filter logic:
 *   PIE: { dataField: 'pieData', rowsField: 'pieRows' },
 *
 * `dataField`  — the DashboardTile key holding the widget `[data]` object (whose
 *                `staticData` is the rendered rows).
 * `rowsField`  — the DashboardTile key holding the ORIGINAL unfiltered rows
 *                (so a filter can be reversed). Defaults to `${dataField}Rows`.
 */
export interface ClientChartCrossFilterSpec {
  dataField: keyof DashboardTile;
  rowsField: keyof DashboardTile;
}

export const CLIENT_CHART_CROSS_FILTER: Record<string, ClientChartCrossFilterSpec> = {
  BAR: { dataField: 'barData', rowsField: 'barRows' },
  SCATTER_BUBBLE: { dataField: 'scatterBubbleData', rowsField: 'scatterBubbleRows' },
  LINE_AREA: { dataField: 'lineAreaData', rowsField: 'lineAreaRows' },
  // Future client-side charts drop in here — e.g.
  // PIE:  { dataField: 'pieData',  rowsField: 'pieRows'  },
  // LINE: { dataField: 'lineData', rowsField: 'lineRows' },
};

/** One chart/input placed on the dashboard preview canvas. Tiles flow into a
 *  fixed 12-column grid (order = array order) and never overlap; each tile spans
 *  `colSpan` columns and `rowSpan` row-units. Exactly one of the render payloads
 *  (previewOption / drillData / hbData / kpiData / inputControl) is populated per
 *  tile, chosen by `layoutType`. */
export interface DashboardTile {
  id: string;
  componentId: string;
  layoutType: string;
  title: string;
  /** Width in grid columns (1..DASH_COLS) and height in row-units. */
  colSpan: number;
  rowSpan: number;
  /** Optional published-dashboard free placement, preserved when reopening from published view. */
  x?: number;
  y?: number;
  customSize?: boolean;
  /** The compute payload that produced this tile (echoed for save/debug). */
  payload: Record<string, unknown>;
  // ── Exactly one of the following, per layoutType ──
  previewOption?: echarts.EChartsOption | null;
  waterfallData?: Record<string, unknown> | null;
  drillData?: Record<string, unknown> | null;
  hbData?: Record<string, unknown> | null;
  kpiData?: Record<string, unknown> | null;
  /** `[data]` input for <app-unified-bar-column-chart> (BAR layout). */
  barData?: Record<string, unknown> | null;
  /** Unfiltered rows for a BAR tile, kept so a cross-filter (which replaces the
   *  rendered staticData with a filtered subset) can be reversed. */
  barRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-scatter-bubble-chart> (SCATTER_BUBBLE). */
  scatterBubbleData?: Record<string, unknown> | null;
  /** Unfiltered rows for a SCATTER_BUBBLE tile (mirrors barRows). */
  scatterBubbleRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-line-area-chart> (LINE_AREA layout). */
  lineAreaData?: Record<string, unknown> | null;
  /** Unfiltered rows for a LINE_AREA tile (mirrors barRows). */
  lineAreaRows?: Array<Record<string, unknown>> | null;
  /** `[data]` input for <app-unified-table-matrix-chart> (TABLE_MATRIX). */
  tableMatrixData?: Record<string, unknown> | null;
  inputControl?: DashboardTileInput | null;
  dropdownControl?: DashboardTileDropdown | null;
  fileUploadControl?: DashboardTileFileUpload | null;
  popupControl?: DashboardTilePopup | null;
  /** Per-tile formatting chosen in the tile's Format popup (keyed by FormatControl.key). */
  format?: Record<string, unknown>;
  /** Raw waterfall bars kept so formatting can re-derive the ECharts option. */
  waterfallBars?: WaterfallBar[];
  /** The UNFILTERED bars, kept only while a cross-filter recompute is showing
   *  filtered bars — clearing the filter restores these instantly (no API call). */
  waterfallBarsAll?: WaterfallBar[];
  /** Cross-filter: the column this tile PUBLISHES when a data point is clicked
   *  (mirrors the dashboard-builder's filterKey). Empty/undefined = doesn't
   *  publish. Defaults to the tile's category column when a chart click fires. */
  filterKey?: string;
  /** Cross-filter: the ids of the OTHER tiles that REACT to this tile's filter.
   *  undefined = not yet configured (defaults to every other chart tile); an
   *  explicit (possibly empty) array = exactly those tiles react. */
  listenerTileIds?: string[];
  /** GROUP tiles only: user-editable card title + the member tiles nested
   *  inside (stacked in the card, own colSpan/rowSpan, independent of the
   *  outer dashboard grid). Grouping just moves existing tiles in here —
   *  their own render data (waterfallData/kpiData/etc.) is untouched. */
  groupTitle?: string;
  groupMembers?: DashboardTile[];
  /** How many side-by-side columns the group's members lay out in (1 or 2).
   *  Defaults to 1 (stacked) when absent. */
  groupColumns?: number;
  /** Free-text notes drawn UNDER the card, OUTSIDE it (builder-form "Notes"
   *  accordion) — every chart/table type. The tile keeps its grid footprint;
   *  with notes the card chrome moves onto the header + body so the notes sit
   *  on the page background. Published as the widget's `tileNotes`. */
  tileNotes?: TableMatrixNotes;
}

interface PublishedLayoutSnapshotItem {
  id: string;
  componentId: string;
  colSpan: number;
  rowSpan: number;
  x: number;
  y: number;
  order: number;
  customSize: boolean;
}

/** Per-control "API Call" configuration: when `enabled`, a value change on
 *  this input fires THIS request instead of the built-in generate/recompute
 *  call, and the response is applied to the configured `targets` exactly like
 *  a normal recompute result (`{ results: [{ componentId, result }] }`) — no
 *  separate response-mapping config needed. `bodyTemplate` is a JSON string
 *  supporting `{value}` `{componentId}` `{field}` tokens (a whole-string
 *  token like `"{value}"` keeps the native type; a token embedded in a larger
 *  string is stringified), applied for every method except GET. */
export interface InputApiCallConfig {
  enabled: boolean;
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  bodyTemplate: string;
  /** How the custom call relates to the built-in compute call.
   *  - `replace` (the default, and what every config saved before this existed
   *    means): ONLY the custom call fires, so it must answer for every target.
   *  - `parallel`: the custom call and the built-in compute call fire at the
   *    SAME time — the custom endpoint answers for `targets` below, the compute
   *    API answers for the rest, and the two `results` arrays are merged. Lets
   *    ONE target be served by a microservice without moving the others off the
   *    standard compute pipeline. */
  mode: 'replace' | 'parallel';
  /** Component ids the CUSTOM call owns in `parallel` mode. Ignored in
   *  `replace` mode (there it owns everything). Empty means the custom call is
   *  a pure side effect and every target still goes to the compute API.
   *
   *  `mode` and `targets` are REQUIRED, not optional: the build form binds both
   *  with [(ngModel)], and an optional property would let `undefined` reach the
   *  mode select / target picker. Configs saved before these keys existed are
   *  filled in by restoreApiCallConfig + defaultApiCallConfig instead. */
  targets: string[];
}

/** Render state for an INPUT_BOX / SLIDER tile. The tile reuses the existing
 *  shared widgets (app-mat-slider / app-input-box) rather than bespoke markup —
 *  `widgetData` is the `[data]` input those components consume. `formula` /
 *  `value` / `targets` are retained for the compute payload we push. */
export interface DashboardTileInput {
  kind: 'INPUT_BOX' | 'SLIDER';
  formula: string;
  value: number | string | null;
  targets: string[];
  /** `[data]` input for the reused app-mat-slider / app-input-box component. */
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

export interface DashboardTileDropdown {
  field: string;
  options: string[];
  value: string[];
  targets: string[];
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

/** Render state for a standalone FILE_UPLOAD tile — an uploader that POSTs the
 *  picked file to the compute API and re-renders the selected `targets`. Mirrors
 *  the dropdown/slider input-control pattern (componentType:'INPUT'). */
export interface DashboardTileFileUpload {
  targets: string[];
  buttonLabel: string;
  accept: string;
  /** `[data]` input the rendered uploader widget consumes. */
  widgetData: Record<string, unknown>;
  apiConfig?: InputApiCallConfig;
}

/** Render state for a POPUP tile — a trigger (icon/button/tile) that opens a
 *  configurable popup dialog. Everything the popup needs (trigger + behaviour
 *  keys and the content `blocks`, incl. table rows snapshotted client-side at
 *  Generate time) rides `widgetData`, so the published view renders it with no
 *  backend call. */
export interface DashboardTilePopup {
  /** `[data]` input for <app-dvm-popup>. */
  widgetData: Record<string, unknown>;
}

/** One content-block ROW in the popup build form. A discriminated bag: only
 *  the fields for its `type` are shown/used (heading text+level, key-value
 *  pairs, table dataset+columns, apiTable url+columns, buttons list). */
export interface PopupBlockForm {
  id: number;
  type: 'heading' | 'text' | 'keyValue' | 'table' | 'apiTable' | 'buttons' | 'divider';
  text: string;
  level: string;
  align: string;
  pairs: Array<{ label: string; value: string }>;
  dataset: string;
  columns: string[];
  maxRows: number | null;
  /** apiTable: endpoint + response mapping (see DvmPopupBlock). */
  url: string;
  method: string;
  body: string;
  rowsPath: string;
  groupsPath: string;
  groupRowsField: string;
  metaFields: Array<{ field: string; label: string }>;
  apiColumns: Array<{ field: string; label: string; color: string }>;
  refreshOnOpen: boolean;
  buttons: Array<{ label: string; action: string; url: string; targetDashboard: string; style: string }>;
}

/**
 * DVM Layout Builder — a Power BI-style split-pane workbench for modelling a
 * DVM chart layout's metadata configuration against one or more entities.
 * Generic by design (a "Chart layout" picker selects the target DVM layout
 * type), with WATERFALL the only implemented layout today — the step-builder
 * concept (START/CHANGE/TOTAL bridge steps) is specific to that layout; future
 * layout types would bring their own step shape alongside this shell.
 *
 * Left pane: layout-type + dataset selection, and a re-orderable list of bridge
 * steps (START anchor / CHANGE floating segment / TOTAL subtotal). Right pane:
 * an ECharts preview built from the analytics engine's response once the user
 * generates a configuration.
 *
 * Config-driven like the other builders (see SelfServiceConfigService): the
 * step-type/aggregation/sign option lists and the analytics-engine endpoint
 * come from assets/self-service-config/dvm-layout-builder.json, falling back
 * to DEFAULT_LAYOUT when the file is absent or invalid.
 */
@Component({
  selector: 'app-dvm-layout-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    DrillDownComponent,
    UnifiedTableMatrixChartComponent,
    DvmFileUploadComponent,
    DvmPopupComponent,
    HorizontalBarChartComponent,
    UnifiedBarColumnChartComponent,
    UnifiedScatterBubbleChartComponent,
    UnifiedLineAreaChartComponent,
    WaterfallChartComponent,
    MatSliderComponent,
    InputBoxComponent,
    GlobalDropdownComponent,
    NumberStatsComponent,
    CapitalCasePipe,
    RelationHopsComponent,
    // Makes the ng-select clear (X) actually clear — ng-select's overlay input
    // steals the click target, so the library's own handler never fires. Auto-
    // applies to every `ng-select.ss-select` in this template.
    SsSelectClearDirective,
  ],
  templateUrl: './dvm-layout-builder.component.html',
  styleUrl: './dvm-layout-builder.component.scss',
})
export class DvmLayoutBuilderComponent implements OnInit {
  relationHops: RelationHop[] = [];

  get activeRelationHops(): RelationHopsConfig | null {
    const config = this.layout.relationHops;
    return config?.enabled && config.dropdowns?.length &&
      config.categoryIds.includes(this.categoryOfType(this.selectedLayoutType)) ? config : null;
  }

  private readonly http = inject(HttpClient);
  private readonly ui = inject(SelfServiceUiService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly dashboardService = inject(SelfServiceDashboardService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly keycloak = inject(KeycloakService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>(['dvmLayoutWorkbench']);

  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'dvm-layout',
    steps: [
      { id: 'workbench', label: 'DVM Layout Workbench', sections: [{ id: 'workbench', fields: [{ field: 'dvmLayoutWorkbench' }] }] },
    ],
    options: {
      layoutTypes: [
        { label: 'Waterfall', value: 'WATERFALL' },
        { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
        { label: 'Horizontal Bar Chart', value: 'HORIZONTAL_BAR_CHART' },
        { label: 'Input Box', value: 'INPUT_BOX' },
        { label: 'Slider', value: 'SLIDER' },
      ],
      stepTypes: [
        { label: 'Start (anchor pillar)', value: 'START' },
        { label: 'Change (floating segment)', value: 'CHANGE' },
        { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
      ],
      layoutAggregations: [
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
      ],
      signOptions: [
        { label: 'Auto (let the query decide)', value: 'AUTO' },
        { label: 'Positive (adds)', value: 'POSITIVE' },
        { label: 'Negative (subtracts)', value: 'NEGATIVE' },
      ],
      measureAggregations: [
        { label: 'None (raw value)', value: 'NONE' },
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
        { label: 'Average per group (one value per e.g. engine model)', value: 'AVG_BY' },
        { label: 'Count', value: 'COUNT' },
      ],
      rollUpStrategies: [
        { label: 'Sum', value: 'SUM' },
        { label: 'Average', value: 'AVG' },
        { label: 'Minimum', value: 'MIN' },
        { label: 'Maximum', value: 'MAX' },
      ],
      entityGroup: 'NontimeSeriesEntities',
      hiddenColumns: ['uuid', '__rowKey', 'source_row_hash', 'sourced_row_hash', 'spriced_row_hash'],
    },
    defaults: {
      componentType: 'CHART',
      dashboardId: 101,
    },
  };

  layout: BuilderLayoutConfig = DvmLayoutBuilderComponent.DEFAULT_LAYOUT;

  get layoutTypes(): LabelValueOption[] {
    return this.layout.options?.layoutTypes?.length
      ? this.layout.options.layoutTypes
      : [
          { label: 'Waterfall', value: 'WATERFALL' },
          { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
        ];
  }
  // ── LAYER 1: Chart Catalogue (Category → Type) ─────────────────────────────

  /** All catalogue categories from config (empty when the block is absent). */
  get chartCategories(): ChartCategory[] {
    return this.layout.options?.chartCatalogue ?? [];
  }

  /** True when a catalogue is configured — the UI shows the Category picker only
   *  then, otherwise it falls back to the flat layout-type dropdown. */
  get hasChartCatalogue(): boolean {
    return this.chartCategories.length > 0;
  }

  /** The chart TYPES (as {label,value}) belonging to the selected category, using
   *  the labels from `layoutTypes`. MEMOIZED — a fresh [items] array each CD
   *  cycle breaks ng-select appendTo:'body' (the panel rebuilds mid-click and
   *  swallows the selection); see [[project_ngselect_appendtobody_memoize]]. */
  private typesForCategoryCache: LabelValueOption[] = [];
  private typesForCategoryKey = '';
  get typesForSelectedCategory(): LabelValueOption[] {
    const category = this.chartCategories.find((c) => c.id === this.selectedChartCategory);
    if (!category) return this.layoutTypes;
    const key = `${category.id}::${category.types.join('|')}::${this.layoutTypes.length}`;
    if (key !== this.typesForCategoryKey) {
      const byValue = new Map(this.layoutTypes.map((t) => [t.value, t]));
      this.typesForCategoryCache = category.types
        .map((type) => byValue.get(type) || { label: type, value: type })
        .filter(Boolean);
      this.typesForCategoryKey = key;
    }
    return this.typesForCategoryCache;
  }

  /** The category id that owns a given layout type (first match), or ''. */
  private categoryOfType(type: string): string {
    return this.chartCategories.find((c) => c.types.includes(type))?.id || '';
  }

  /** Switch category → select that category's first type (keeps type + category
   *  consistent). No-op if the category has no types. */
  onChartCategoryChange(): void {
    const types = this.typesForSelectedCategory;
    if (types.length && !types.some((t) => t.value === this.selectedLayoutType)) {
      this.selectedLayoutType = types[0].value;
      this.onLayoutTypeChange();
    }
  }

  // ── LAYER 2: Capability Registry ───────────────────────────────────────────

  /** The capability entry for a chart type (empty object when unregistered). */
  capabilitiesForType(type: string): CapabilityRegistryEntry {
    return this.layout.options?.capabilityRegistry?.[type] ?? {};
  }

  /** The active chart's capabilities. */
  get activeCapabilities(): CapabilityRegistryEntry {
    return this.capabilitiesForType(this.selectedLayoutType);
  }

  /** Whether the active chart supports a capability flag (registry-driven). When
   *  the chart has no registry entry at all, default to `true` so an unconfigured
   *  chart isn't accidentally stripped of controls. */
  chartSupports(capability: string): boolean {
    const entry = this.activeCapabilities;
    if (!Object.keys(entry).length) return true;
    return entry[capability] === true;
  }

  /** Whether the active chart exposes a given Configuration section (accordion).
   *  Registry `sections` is the allow-list; absent → allow (defaults preserved). */
  chartExposesSection(section: string): boolean {
    const sections = this.activeCapabilities.sections;
    return Array.isArray(sections) ? sections.includes(section) : true;
  }

  get isWaterfallLayout(): boolean {
    return this.selectedLayoutType === 'WATERFALL';
  }
  get isDrillDownLayout(): boolean {
    return this.selectedLayoutType === 'DRILL_DOWN_TABLE';
  }
  get isHorizontalBarLayout(): boolean {
    return this.selectedLayoutType === 'HORIZONTAL_BAR_CHART';
  }
  /** Unified Bar & Column layout — one builder → many bar/column variants. */
  get isBarLayout(): boolean {
    return this.selectedLayoutType === 'BAR';
  }
  /** Unified Scatter & Bubble layout — one builder → Scatter/Multi/Bubble/Quadrant. */
  get isScatterBubbleLayout(): boolean {
    return this.selectedLayoutType === 'SCATTER_BUBBLE';
  }
  /** Unified Line & Area layout — one builder → line/step/spline/area variants. */
  get isLineAreaLayout(): boolean {
    return this.selectedLayoutType === 'LINE_AREA';
  }

  /** Heading for the middle "Build" panel — per active layout. */
  get buildPanelTitle(): string {
    if (this.isDrillDownLayout) return 'Drill-down table';
    if (this.isBarLayout) return 'Bar & column';
    if (this.isScatterBubbleLayout) return 'Scatter & bubble';
    if (this.isHorizontalBarLayout) return 'Horizontal bar';
    if (this.isKpiLayout) return 'KPI card';
    if (this.isInputControlLayout) return this.isSliderLayout ? 'Slider input' : 'Input box';
    return 'Bridge steps';
  }

  /** Count badge for the build panel; null hides it (layouts with no list). */
  get buildPanelCount(): number | null {
    if (this.isDrillDownLayout) return this.drillDownDimensions.length;
    if (this.isBarLayout) return this.barMeasures.length;
    if (this.isWaterfallLayout) return this.steps.length;
    return null;
  }
  get isKpiLayout(): boolean {
    return this.selectedLayoutType === 'KPI';
  }

  get isDropdownLayout(): boolean {
    return this.selectedLayoutType === 'DROPDOWN';
  }

  get isFileUploadLayout(): boolean {
    return this.selectedLayoutType === 'FILE_UPLOAD';
  }

  get isPopupLayout(): boolean {
    return this.selectedLayoutType === 'POPUP';
  }

  get isTableMatrixLayout(): boolean {
    return this.selectedLayoutType === 'TABLE_MATRIX';
  }

  /** CHART layouts (everything except the input controls / dropdown) can also
   *  declare interaction targets — besides filterKey/filterListeners, a chart
   *  can name the component(s) it interacts with; sent as `payload.targets`
   *  (same shape the input controls use, minus per-target formulas). */
  get isChartTargetLayout(): boolean {
    // FILE_UPLOAD already has its OWN target picker in the Upload settings
    // section, so the generic "Chart interactions" accordion would be a
    // duplicate (both bind the same `selectedTargetCharts`) — exclude it.
    // POPUP is a pure display component — it drives nothing and reacts to
    // nothing, so it takes no interaction targets at all.
    return !this.isInputControlLayout && !this.isDropdownLayout && !this.isFileUploadLayout && !this.isPopupLayout;
  }

  // ── Table & Matrix: config-driven table-type picker + flag getters ──────────
  /** Stable fallback (NOT an inline literal — this feeds an appendTo="body"
   *  ng-select, which swallows clicks when [items] changes identity every CD). */
  private readonly tableTypesFallback: TableTypeOption[] = [
    { label: 'Normal Table', value: 'normal', group: 'Standard', normal: true },
    { label: 'Editable Table', value: 'editable', group: 'Interactive', editable: true },
    { label: 'Drill-Down Table', value: 'drilldown', group: 'Interactive', drilldown: true },
  ];
  /** Table-type values a saved tile may still carry even though config no
   *  longer lists them (e.g. 'pivot' was retired from the picker when the pivot
   *  merged into the Drill-Down Table; the Phase-1 shape types are also
   *  config-trimmed). Maps each to its display label + the render/builder flag
   *  it dispatches on, so a reopened tile of that type shows its real type in
   *  the picker and gets its own per-type pickers instead of the Normal
   *  "Columns" card (which a re-Generate would then silently convert it to). */
  private static readonly LEGACY_TABLE_TYPES: Record<string, { label: string; flag: string }> = {
    normal: { label: 'Normal Table', flag: 'normal' },
    editable: { label: 'Editable Table', flag: 'editable' },
    drilldown: { label: 'Drill-Down Table', flag: 'drilldown' },
    matrix: { label: 'Matrix Table', flag: 'matrix' },
    pivot: { label: 'Pivot Table', flag: 'pivot' },
    grouped: { label: 'Grouped Table', flag: 'grouped' },
    treegrid: { label: 'Tree Grid', flag: 'treegrid' },
    financial: { label: 'Financial Table', flag: 'financial' },
  };
  /** Memo for the "config list + synthesized legacy entry" array so the
   *  ng-select keeps a stable [items] reference across change detection. */
  private legacyTableTypesCache: { base: TableTypeOption[]; type: string; items: TableTypeOption[] } | null = null;

  /** The "Table type" options from config (Normal / Editable / Drill-Down …).
   *  When the ACTIVE type (a restored tile's saved `subType`) is missing from
   *  config, a "Legacy" entry is appended for it: ng-select with `bindValue`
   *  renders a value it can't find as an EMPTY pill, which is exactly how
   *  "Back to Builder" showed a blank Table type for old pivot tiles. */
  get tableTypes(): TableTypeOption[] {
    const configured = ((this.layout.options as Record<string, unknown> | undefined)?.['tableTypes'] as TableTypeOption[] | undefined)
      ?? this.tableTypesFallback;
    const type = this.tmType;
    if (!type || configured.some((entry) => entry.value === type)) return configured;
    const cache = this.legacyTableTypesCache;
    if (cache && cache.base === configured && cache.type === type) return cache.items;
    const legacy = DvmLayoutBuilderComponent.LEGACY_TABLE_TYPES[type];
    const entry: TableTypeOption = {
      label: `${legacy?.label ?? type} (legacy)`,
      value: type,
      group: 'Legacy',
      hint: 'This table type is no longer offered for new tables (restored from a saved tile). It stays editable here; the Drill-Down Table now covers pivot layouts.',
      [legacy?.flag ?? type]: true,
    };
    const items = [...configured, entry];
    this.legacyTableTypesCache = { base: configured, type, items };
    return items;
  }
  /** The currently-selected table-type config entry. */
  get tmTypeEntry(): TableTypeOption | undefined {
    return this.tableTypes.find((entry) => entry.value === this.tmType);
  }
  /** True flags on the active table-type entry (drives builder fields + render). */
  tmFlagsFor(type: string): Record<string, boolean> {
    const entry = this.tableTypes.find((e) => e.value === type);
    const flags: Record<string, boolean> = {};
    if (entry) {
      Object.keys(entry).forEach((key) => {
        if (entry[key] === true) flags[key] = true;
      });
    }
    // Fallback: derive from the value name ONLY when the config entry carries
    // no flag at all (unregistered/legacy type names). Previously this keyed
    // off normal/editable/drilldown alone, so matrix/pivot/grouped/… entries —
    // whose OWN flag is set — were ALSO stamped `normal: true`, which rendered
    // the Normal columns UI on top of their real per-type pickers.
    if (Object.keys(flags).length === 0) {
      // Retired-but-known names (pivot/matrix/grouped/…) keep their OWN flag so
      // a saved tile of that type isn't re-generated as a Normal table.
      const legacy = DvmLayoutBuilderComponent.LEGACY_TABLE_TYPES[type];
      if (legacy) flags[legacy.flag] = true;
      else if (type === 'editable') flags['editable'] = true;
      else if (type === 'drilldown') flags['drilldown'] = true;
      else flags['normal'] = true;
    }
    return flags;
  }
  /** Converts a tile saved with the RETIRED 'pivot' table type into the merged
   *  Drill-Down Table's client-mode config: row fields → hierarchy levels,
   *  column fields → pivot column dimensions, value fields → measures (the
   *  pivot's single aggregation applied to each). Returns null when the tile
   *  isn't a legacy pivot, when config still offers 'pivot' (nothing to
   *  migrate), or when no Drill-Down type exists to migrate into. */
  private legacyPivotToDrilldown(
    payload: Record<string, unknown>,
    caps: Record<string, unknown>,
  ): { dimensions: DrillDownDimension[]; columnDimensions: DrillDownDimension[]; measures: DrillDownMeasure[] } | null {
    const type = String(payload['subType'] ?? caps['tableType'] ?? '');
    if (type !== 'pivot') return null;
    const configured = ((this.layout.options as Record<string, unknown> | undefined)?.['tableTypes'] as TableTypeOption[] | undefined)
      ?? this.tableTypesFallback;
    if (configured.some((entry) => entry.value === 'pivot')) return null;
    if (!configured.some((entry) => entry.value === 'drilldown' || entry['drilldown'] === true)) return null;

    const list = (value: unknown, single: unknown): string[] => {
      const many = Array.isArray(value) ? (value as unknown[]).map(String).filter(Boolean) : [];
      if (many.length) return many;
      const one = String(single ?? '');
      return one ? [one] : [];
    };
    const rowFields = list(caps['rowFields'], caps['rowField']);
    const columnFields = list(caps['columnFields'], caps['columnField']);
    const valueFields = list(caps['valueFields'], caps['valueField']);
    if (!rowFields.length && !valueFields.length) return null;

    // Header display names the pivot's flat compute payload carried per column.
    const displayNames = new Map<string, string>();
    (Array.isArray(payload['columns']) ? payload['columns'] as unknown[] : []).forEach((column) => {
      if (column && typeof column === 'object') {
        const field = String((column as Record<string, unknown>)['field'] ?? '');
        const displayName = String((column as Record<string, unknown>)['displayName'] ?? '');
        if (field && displayName) displayNames.set(field, displayName);
      }
    });
    const labelFor = (field: string): string => displayNames.get(field) ?? field;
    const toDimension = (field: string): DrillDownDimension => ({
      id: this.nextDrillDimensionId(), field, label: labelFor(field), valueOrder: '', editable: false, dataset: '',
    });
    // Pivot aggregation vocabulary (sum/avg/count/distinct/min/max/none) → the
    // drill-down measure vocabulary (SUM/AVG/COUNT/DISTINCT/MIN/MAX/NONE).
    const rawAggregation = String(caps['aggregation'] ?? 'sum').toLowerCase();
    const aggregation = rawAggregation === 'none' ? 'SUM' : rawAggregation.toUpperCase();
    const rollUpStrategy = ['SUM', 'AVG', 'MIN', 'MAX'].includes(aggregation) ? aggregation : 'SUM';
    const total = caps['showGrandTotal'] === true;
    return {
      dimensions: rowFields.map(toDimension),
      columnDimensions: columnFields.map(toDimension),
      measures: valueFields.map((field) => ({
        id: this.nextDrillMeasureId(), field, label: labelFor(field),
        aggregation, rollUpStrategy, total, editable: false, dataset: '',
      })),
    };
  }
  get isTmNormal(): boolean {
    return !!this.tmFlagsFor(this.tmType)['normal'];
  }
  get isTmEditable(): boolean {
    return !!this.tmFlagsFor(this.tmType)['editable'];
  }
  get isTmDrilldown(): boolean {
    return !!this.tmFlagsFor(this.tmType)['drilldown'];
  }
  get isTmMatrix(): boolean { return !!this.tmFlagsFor(this.tmType)['matrix']; }
  get isTmPivot(): boolean { return !!this.tmFlagsFor(this.tmType)['pivot']; }
  get isTmGrouped(): boolean { return !!this.tmFlagsFor(this.tmType)['grouped']; }
  get isTmTreegrid(): boolean { return !!this.tmFlagsFor(this.tmType)['treegrid']; }
  get isTmFinancial(): boolean { return !!this.tmFlagsFor(this.tmType)['financial']; }
  /** True for the shape types that render from flat rows via getEntityRows (all
   *  except drill-down, which uses the compute tree). */
  get isTmFlatShape(): boolean {
    return this.isTableMatrixLayout && !this.isTmDrilldown;
  }
  /** Hint text for the active table type (shown under the picker). */
  get tmTypeHint(): string {
    return String(this.tmTypeEntry?.['hint'] ?? '');
  }
  /** Row/group aggregation options for Normal tables (from config). */
  get tmAggregations(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmAggregations'] as LabelValueOption[] | undefined) ?? [
      { label: 'None (raw rows)', value: 'none' },
      { label: 'Sum', value: 'sum' },
      { label: 'Average', value: 'avg' },
      { label: 'Count', value: 'count' },
    ];
  }
  /** Per-column input-control options for Editable tables (from config).
   *  Fallback is a stable field, NOT an inline literal — this getter feeds an
   *  appendTo="body" ng-select, which swallows clicks when [items] returns a
   *  fresh array every change-detection cycle. */
  private readonly tmColumnControlsFallback: LabelValueOption[] = [
    { label: 'Text box', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Dropdown', value: 'dropdown' },
    { label: 'Checkbox', value: 'checkbox' },
    { label: 'Date', value: 'date' },
    { label: 'Text area', value: 'textarea' },
  ];
  get tmColumnControls(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmColumnControls'] as LabelValueOption[] | undefined)
      ?? this.tmColumnControlsFallback;
  }
  get tmColumnAlignments(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmColumnAlignments'] as LabelValueOption[] | undefined) ?? [
      { label: 'Left', value: 'start' },
      { label: 'Center', value: 'center' },
      { label: 'Right', value: 'end' },
    ];
  }
  /** What an action column's per-row button does (config `tmColumnActionKinds`). */
  get tmColumnActionKinds(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmColumnActionKinds'] as LabelValueOption[] | undefined) ?? [
      { label: 'Call API', value: 'api' },
      { label: 'Row details popup', value: 'rowPopup' },
      { label: 'API popup (show response)', value: 'apiPopup' },
      { label: 'Popup component (blocks)', value: 'popupComponent' },
      { label: 'Emit event only', value: 'emit' },
    ];
  }
  get tmColumnActionMethods(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmColumnActionMethods'] as LabelValueOption[] | undefined) ?? [
      { label: 'POST', value: 'POST' },
      { label: 'GET', value: 'GET' },
      { label: 'PUT', value: 'PUT' },
      { label: 'DELETE', value: 'DELETE' },
    ];
  }
  /** Sort directions for an apiPopup's response rows. Default desc: a history
   *  popup wants newest first. */
  get tmActionSortDirections(): LabelValueOption[] {
    return [
      { label: 'Newest first (desc)', value: 'desc' },
      { label: 'Oldest first (asc)', value: 'asc' },
    ];
  }

  /** True when this action kind needs the URL / method / body fields. */
  tmActionNeedsUrl(column: TmColumnDef): boolean {
    const kind = column.actionKind || 'emit';
    return kind === 'api' || kind === 'apiPopup';
  }

  /** POPUP tiles on THIS dashboard, for an action column's "Use popup" picker.
   *  Only popups can be referenced — they are the tiles that carry content
   *  blocks (heading / text / table / apiTable / buttons). */
  get tmPopupComponentOptions(): LabelValueOption[] {
    return this.dashboardTiles
      .filter((tile) => tile.layoutType === 'POPUP' && !!tile.componentId)
      .map((tile) => ({
        label: tile.title?.trim() || String(tile.componentId),
        value: String(tile.componentId),
      }));
  }

  /** The referenced popup's widgetData — the snapshot an action column bakes so
   *  the published table can render its blocks without a sibling-tile lookup. */
  private tmPopupDataFor(componentId: string): Record<string, unknown> | null {
    const id = String(componentId ?? '').trim();
    if (!id) return null;
    const tile = this.dashboardTiles.find(
      (item) => item.layoutType === 'POPUP' && String(item.componentId ?? '') === id,
    );
    const data = tile?.popupControl?.widgetData;
    return data && typeof data === 'object' ? { ...(data as Record<string, unknown>) } : null;
  }

  /** The picked popup no longer exists on the dashboard (deleted or renamed
   *  away) — surfaced in the builder so the column isn't silently inert. */
  tmPopupComponentMissing(column: TmColumnDef): boolean {
    const id = String(column.popupComponentId ?? '').trim();
    if (!id || column.actionKind !== 'popupComponent') return false;
    return !this.tmPopupComponentOptions.some((option) => option.value === id);
  }

  /** Whether an action column's icon value is a Material icon NAME (rendered by
   *  <mat-icon>) rather than an emoji / literal glyph (rendered as text).
   *  Mirrors UnifiedTableMatrixChartComponent.isMaterialIcon so the builder
   *  preview and the table agree. */
  isTmMaterialIcon(icon?: string): boolean {
    return !!icon && /^[a-z][a-z0-9_]*$/i.test(icon.trim());
  }
  /** Operator options for a column's default filter (from config). */
  get tmInitialFilterOperators(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmInitialFilterOperators'] as LabelValueOption[] | undefined) ?? [
      { label: 'Equals', value: 'eq' },
      { label: 'Not equals', value: 'neq' },
      { label: 'Contains', value: 'contains' },
      { label: 'In (comma-separated list)', value: 'in' },
    ];
  }

  // ── Navigation: saved-dashboard + target-component option sources ───────────
  private navDashboardsMemo: { key: string; value: LabelValueOption[] } | null = null;
  /** The user's saved/published dashboards as {label:name, value:slug}. Reads the
   *  same localStorage cache the builder already consolidates. Memoized so the
   *  ng-select [items] reference stays stable across change-detection cycles
   *  (appendTo:body panels churn if the array identity changes mid-interaction). */
  get tmNavTargetDashboards(): LabelValueOption[] {
    // Source 1 — the API-published list (authoritative, logout-proof).
    const options: LabelValueOption[] = this.apiTargetDashboards.map((d) => ({ label: d.label, value: d.value }));
    const seen = new Set(options.map((o) => o.value));
    // Source 2 — localStorage saved dashboards (may include unpublished drafts
    // not yet on the API); union in, de-duped by slug.
    const saved = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    saved.forEach((dashboard) => {
      const slug = String(dashboard.config?.['dashboardId'] ?? '');
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        options.push({ label: dashboard.name || slug, value: slug });
      }
    });
    // Source 3 — a pinned target restored on edit that the async API/local lists
    // don't (yet) contain. Without this, the ng-select can't render the saved
    // slug as selected and shows BLANK, and a re-Generate then drops it.
    if (this.navPinnedTarget && !seen.has(this.navPinnedTarget.value)) {
      options.push(this.navPinnedTarget);
      seen.add(this.navPinnedTarget.value);
    }
    const key = options.map((o) => o.value).join('|');
    if (!this.navDashboardsMemo || this.navDashboardsMemo.key !== key) {
      this.navDashboardsMemo = { key, value: options };
    }
    return this.navDashboardsMemo.value;
  }

  /** A restored target dashboard that may not be in the async-loaded lists yet —
   *  pinned into the dropdown so the ng-select renders it as selected on edit. */
  private navPinnedTarget: LabelValueOption | null = null;
  private ensureNavTargetInList(slug: string): void {
    const existing = [...this.apiTargetDashboards.map((d) => d.value)];
    if (existing.includes(slug)) { this.navPinnedTarget = null; return; }
    // Label it from the API/local list if findable, else prettify the slug.
    const apiHit = this.apiTargetDashboards.find((d) => d.value === slug);
    const label = apiHit?.label
      || slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    this.navPinnedTarget = { label, value: slug };
    this.navDashboardsMemo = null; // force the getter to include the pin
  }

  private navComponentsMemo: { key: string; value: LabelValueOption[] } | null = null;
  /** Components (charts) on the chosen target dashboard as {label:title, value:componentId}.
   *  Walks the saved dashboard's pageContent (+ tabbed tabs[].pageContent). */
  get tmNavTargetComponentOptions(): LabelValueOption[] {
    const slug = this.tmNavTargetDashboard;
    if (!slug) { this.navComponentsMemo = { key: '', value: [] }; return []; }
    // Prefer the API dashboard's layout_json; fall back to localStorage config.
    const apiEntry = this.apiTargetDashboards.find((d) => d.value === slug);
    let config: Record<string, unknown> = apiEntry?.layoutJson || {};
    if (!config['pageContent'] && !config['tabs']) {
      const saved = this.persistence.consolidateSavedDashboards(
        readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
      );
      const dashboard = saved.find((d) => String(d.config?.['dashboardId'] ?? '') === slug);
      config = (dashboard?.config as Record<string, unknown>) || {};
    }
    const options: LabelValueOption[] = [];
    const seen = new Set<string>();
    const collectFromPage = (pageContent: unknown) => {
      if (!Array.isArray(pageContent)) return;
      pageContent.forEach((row) => {
        const cols = (row as Record<string, unknown>)?.['columns'];
        if (!Array.isArray(cols)) return;
        cols.forEach((widget) => {
          const w = widget as Record<string, unknown>;
          const id = String(w?.['componentId'] ?? '').trim();
          const type = String(w?.['type'] ?? '');
          // Only chartable components can be filter targets (skip pure inputs).
          const isInput = type === 'slider' || type === 'input-box' || type === 'dropdown';
          if (id && !isInput && !seen.has(id)) { seen.add(id); options.push({ label: String(w?.['title'] ?? id), value: id }); }
        });
      });
    };
    collectFromPage(config['pageContent']);
    const tabs = config['tabs'];
    if (Array.isArray(tabs)) tabs.forEach((tab) => collectFromPage((tab as Record<string, unknown>)?.['pageContent']));
    const key = slug + '::' + options.map((o) => o.value).join('|');
    if (!this.navComponentsMemo || this.navComponentsMemo.key !== key) {
      this.navComponentsMemo = { key, value: options };
    }
    return this.navComponentsMemo.value;
  }

  /** Reset the dependent target-components selection when the target dashboard changes. */
  onTmNavTargetDashboardChange(): void {
    this.tmNavTargetComponents = [];
    this.navComponentsMemo = null;
    this.onTableMatrixTypeChange();
  }
  toggleTmNavAccordion(): void { this.tmNavAccordionOpen = !this.tmNavAccordionOpen; }

  // ── Notes under any chart/table (builder-form "Notes" accordion) ──────────
  toggleTmNotesAccordion(): void { this.tmNotesAccordionOpen = !this.tmNotesAccordionOpen; }
  /** Fallback is a stable field, NOT an inline literal — it feeds an
   *  appendTo="body" ng-select (fresh [items] arrays swallow clicks). */
  private readonly tmNotesListStylesFallback: LabelValueOption[] = [
    { label: 'Bullet (•)', value: 'bullet' },
    { label: 'Dash (-)', value: 'dash' },
    { label: 'Numbered (1. 2. 3.)', value: 'number' },
    { label: 'None (plain lines)', value: 'none' },
  ];
  private readonly tmNotesPositionsFallback: LabelValueOption[] = [
    { label: 'Below the card', value: 'below' },
    { label: 'Above the card', value: 'above' },
  ];
  /** Placement options for the notes block (config `tmNotesPositions`). */
  get tmNotesPositions(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmNotesPositions'] as LabelValueOption[] | undefined) ?? this.tmNotesPositionsFallback;
  }
  /** Marker options for the note lines (config `tmNotesListStyles`). */
  get tmNotesListStyles(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmNotesListStyles'] as LabelValueOption[] | undefined) ?? this.tmNotesListStylesFallback;
  }
  /** Config defaults a NEW chart starts from (`tmNotesDefaults`: heading /
   *  listStyle). */
  private get tmNotesDefaults(): Record<string, unknown> {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['tmNotesDefaults'] as Record<string, unknown> | undefined) ?? {};
  }
  /** The textarea split into rendered note lines (blank lines dropped). */
  private tmNotesItems(): string[] {
    return this.tmNotesText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  }
  /** Layout types that can carry notes — every chart/table; input controls
   *  (slider / input box / dropdown / upload / popup) and GROUP cards can't. */
  private static readonly NOTES_LAYOUT_TYPES = new Set([
    'WATERFALL', 'DRILL_DOWN_TABLE', 'TABLE_MATRIX', 'HORIZONTAL_BAR_CHART', 'KPI', 'BAR', 'LINE_AREA', 'SCATTER_BUBBLE',
  ]);
  /** Whether the build form shows the Notes accordion for the active layout. */
  get supportsTileNotes(): boolean {
    return DvmLayoutBuilderComponent.NOTES_LAYOUT_TYPES.has(this.selectedLayoutType);
  }
  /** The form's notes as the saved shape — undefined when switched off, so an
   *  un-ticked box CLEARS a previously-saved note. */
  private formNotes(): TableMatrixNotes | undefined {
    if (!this.tmNotesEnabled) return undefined;
    return {
      enabled: true,
      heading: this.tmNotesHeading.trim() || undefined,
      items: this.tmNotesItems(),
      listStyle: this.tmNotesListStyle as TableMatrixNotes['listStyle'],
      position: this.tmNotesPosition === 'above' ? 'above' : 'below',
    };
  }
  /** A Notes-accordion control changed — push it into the tile LIVE. */
  onTileNotesChange(): void {
    this.syncFormNotesToTile();
  }
  /** Stamp the form's notes onto the tile being edited (or the live draft
   *  preview). The notes sit OUTSIDE the card, under it, inside the tile's own
   *  grid footprint — so the chart's box changes height: nudge ECharts to
   *  re-measure. */
  private syncFormNotesToTile(): void {
    const id = this.editingTileId || this.previewTileId;
    const tile = id ? this.findTileAnywhere(id) : undefined;
    if (!tile || !DvmLayoutBuilderComponent.NOTES_LAYOUT_TYPES.has(tile.layoutType)) return;
    this.updateTileInPlace({ ...tile, tileNotes: this.formNotes() });
    this.reflowCharts();
  }
  /** Restore the Notes accordion from a tile (notes never ride the compute
   *  payload — they live on the tile / published widget as `tileNotes`). */
  private restoreNotesFromTile(tile: DashboardTile): void {
    const notes = (this.savedTileNotes(tile) as Record<string, unknown> | undefined) || {};
    this.resetTmNotes();
    if (notes['enabled'] !== true) return;
    this.tmNotesEnabled = true;
    this.tmNotesHeading = String(notes['heading'] ?? '');
    this.tmNotesText = Array.isArray(notes['items']) ? (notes['items'] as unknown[]).map(String).join('\n') : '';
    this.tmNotesListStyle = String(notes['listStyle'] ?? 'bullet');
    this.tmNotesPosition = notes['position'] === 'above' ? 'above' : 'below';
    this.tmNotesAccordionOpen = true;
  }
  /** A tile's saved notes. LEGACY: the first cut stored a table's notes in its
   *  `capabilities.notes` (drawn inside the card) — still read so those tiles
   *  keep their text; the next Update moves it onto `tileNotes`. */
  private savedTileNotes(tile: DashboardTile): TableMatrixNotes | undefined {
    if (tile.tileNotes || 'tileNotes' in tile) return tile.tileNotes;
    const caps = tile.tableMatrixData?.['capabilities'] as Record<string, unknown> | undefined;
    return caps?.['notes'] as TableMatrixNotes | undefined;
  }
  /** Template helper: the tile's notes when they have something to draw. */
  tileNotesOf(tile: DashboardTile): TableMatrixNotes | null {
    const notes = this.savedTileNotes(tile);
    if (!notes || notes.enabled !== true) return null;
    return String(notes.heading ?? '').trim() || (notes.items?.length ?? 0) > 0 ? notes : null;
  }
  private resetTmNotes(): void {
    const defaults = this.tmNotesDefaults;
    this.tmNotesEnabled = false;
    this.tmNotesHeading = String(defaults['heading'] ?? '');
    this.tmNotesText = '';
    this.tmNotesListStyle = String(defaults['listStyle'] ?? 'bullet');
    this.tmNotesPosition = defaults['position'] === 'above' ? 'above' : 'below';
    this.tmNotesAccordionOpen = false;
  }

  /** Config-driven show/hide for the "Reference" UI under a formula field,
   *  scoped to ONE chart/component layout type (KPI, HORIZONTAL_BAR_CHART,
   *  WATERFALL, INPUT_BOX, SLIDER, ...). A plain boolean config applies to
   *  every layout type; a per-type map is checked by key; either way, a
   *  missing/absent config defaults to shown. */
  formulaReferencesEnabledFor(layoutType: string): boolean {
    const config = this.layout.options?.enableFormulaReferences;
    if (config === undefined) return true;
    if (typeof config === 'boolean') return config;
    return config[layoutType] !== false;
  }

  /** The layout type of a target/reference id — resolves a dashboard tile's
   *  own componentId to its chart type, so the Reference toggle can be scoped
   *  per chart type even when the id refers to ANOTHER tile (an input
   *  control's target), not the chart currently being built. */
  targetLayoutTypeForId(id: string): string {
    return this.allTilesFlat().find((tile) => tile.componentId === id)?.layoutType || '';
  }

  /** True when another reference row (for the same id/step) already uses this
   *  row's token — such a collision means only ONE of them actually reaches
   *  the backend (`referencesForFormula` keys the sent map by token), so the
   *  UI surfaces it instead of silently dropping the earlier row. */
  isDuplicateReferenceToken(id: string, ref: TargetFormulaReferenceEntry, step?: string): boolean {
    const token = ref.token.trim();
    if (!token) return false;
    return this.targetReferences(id, step).some((other) => other !== ref && other.token.trim() === token);

  }
  get isInputBoxLayout(): boolean {
    return this.selectedLayoutType === 'INPUT_BOX';
  }
  get isSliderLayout(): boolean {
    return this.selectedLayoutType === 'SLIDER';
  }
  /** INPUT_BOX and SLIDER share the same builder shape — a titled control that
   *  feeds a formula-driven value into one or more target charts. */
  get isInputControlLayout(): boolean {
    return this.isInputBoxLayout || this.isSliderLayout;
  }
  private targetChartsCache: LabelValueOption[] = [];
  private targetChartsKey = '';

  /** Tiles an input control can drive — built DYNAMICALLY from every OTHER tile
   *  currently on the dashboard: charts (KPI included) AND other input controls
   *  (a slider/input box can feed its value into another slider/input box, which
   *  can in turn drive its own targets). Value = the tile's componentId, which
   *  the recompute response resolves back to. The tile currently being edited is
   *  excluded so an input can never target itself.
   *
   *  MEMOIZED: a getter returns on every change-detection cycle, so returning a
   *  fresh array each time made ng-select rebuild its option list mid-click and
   *  swallow the selection. Return a STABLE reference, recomputed only when the
   *  tiles (id/title/layoutType) actually change. */
  /** Targets assignable to the custom API in `parallel` mode — the SAME list as
   *  the main target picker (every chart/KPI/input on the dashboard bar this
   *  tile and POPUPs), with the control's own picks listed first.
   *
   *  This deliberately does NOT filter down to `selectedTargetCharts`: a
   *  DROPDOWN rebuilds its targets to every chart on the dashboard at change
   *  time (see the published view's onInputWidgetChange), so a target the
   *  author never ticked explicitly is still driven and is still a legitimate
   *  thing to hand to a microservice. Filtering hid KPIs and every other
   *  un-ticked tile, leaving only whatever happened to be selected. */
  get apiCallTargetOptions(): LabelValueOption[] {
    const options = this.targetCharts;
    // MEMOIZED, exactly like `targetCharts` itself. The template reads this
    // every change-detection cycle; returning a freshly-built array each time
    // made ng-select tear down and rebuild its option list on every tick, so
    // the panel rendered but a click never landed on a stable node — the
    // "dropdown opens but nothing is selectable" bug. Same cached-by-key shape
    // as the other pickers in this form.
    const key = `${this.targetChartsKey}~${this.selectedTargetCharts.join('|')}`;
    if (key !== this.apiCallTargetOptionsKey) {
      this.apiCallTargetOptionsKey = key;
      const selected = new Set(this.selectedTargetCharts.map(String));
      this.apiCallTargetOptionsCache = [
        ...options.filter((option) => selected.has(String(option.value))),
        ...options.filter((option) => !selected.has(String(option.value))),
      ];
    }
    return this.apiCallTargetOptionsCache;
  }
  private apiCallTargetOptionsCache: LabelValueOption[] = [];
  private apiCallTargetOptionsKey = '';

  get targetCharts(): LabelValueOption[] {
    const editingComponentId = this.editingTileId
      ? this.findTileAnywhere(this.editingTileId)?.componentId
      : undefined;
    // POPUP tiles are display-only — they can't be recomputed/filtered, so
    // they're never a valid target for an input/dropdown/upload control.
    const chartTiles = this.allTilesFlat().filter((tile) => tile.componentId !== editingComponentId && tile.layoutType !== 'POPUP');
    const key = chartTiles.map((tile) => `${tile.componentId}|${tile.title}|${tile.layoutType}`).join('~');
    if (key !== this.targetChartsKey) {
      this.targetChartsKey = key;
      const seen = new Set<string>();
      const options: LabelValueOption[] = [];
      chartTiles.forEach((tile) => {
        const value = String(tile.componentId ?? '');
        if (!value || seen.has(value)) return;
        seen.add(value);
        const isInputTile = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
        options.push({ label: (tile.title || tile.layoutType) + (isInputTile ? ' (Input)' : ''), value });
      });
      this.targetChartsCache = options;
    }
    return this.targetChartsCache;
  }

  /** True when the given target componentId is itself a WATERFALL tile — such a
   *  target gets one formula PER BRIDGE STEP instead of a single chart-wide
   *  formula, since each bar (Start / Change / Total) is its own independent
   *  calculation. */
  isWaterfallTarget(targetId: string): boolean {
    return this.dashboardTiles.some((tile) => tile.componentId === targetId && tile.layoutType === 'WATERFALL');
  }

  private targetStepsCache = new Map<string, { key: unknown; result: Array<{ label: string; stepType: string }> }>();

  /** The bridge steps (label + stepType) of a WATERFALL target, read back from
   *  that tile's saved payload — used to render one formula row per bar.
   *
   *  MEMOIZED per target id: this is called from the template's `*ngFor`, so a
   *  fresh array/objects on every change-detection pass made Angular tear down
   *  and rebuild the formula `<input>` rows on every keystroke (default
   *  `*ngFor` tracking is by object identity) — the input lost focus/cursor
   *  position constantly. Returning the SAME array reference while the
   *  underlying steps haven't changed keeps those rows (and their focus) stable. */
  targetSteps(targetId: string): Array<{ label: string; stepType: string }> {
    const tile = this.allTilesFlat().find((item) => item.componentId === targetId && item.layoutType === 'WATERFALL');
    const rawSteps = Array.isArray(tile?.payload?.['steps']) ? tile!.payload['steps'] as Array<Record<string, unknown>> : [];
    const cached = this.targetStepsCache.get(targetId);
    if (cached && cached.key === rawSteps) return cached.result;
    const result = rawSteps.map((step) => ({ label: String(step['label'] ?? ''), stepType: String(step['stepType'] ?? '') }));
    this.targetStepsCache.set(targetId, { key: rawSteps, result });
    return result;
  }

  trackByStepLabel(index: number, step: { label: string; stepType: string }): string {
    return step.label || `step-${index}`;
  }

  targetStepFormula(targetId: string, stepLabel: string): string {
    return this.targetStepFormulas[targetId]?.[stepLabel] || '';
  }

  setTargetStepFormula(targetId: string, stepLabel: string, value: string): void {
    const forTarget = { ...(this.targetStepFormulas[targetId] || {}) };
    forTarget[stepLabel] = value;
    this.targetStepFormulas = { ...this.targetStepFormulas, [targetId]: forTarget };
  }

  /** Display/back-compat summary for a target's formula(s) — one joined string
   *  whether the target takes a single chart-wide formula or a per-step map. */
  private targetFormulaSummary(targetId: string): string {
    if (this.isWaterfallTarget(targetId)) {
      const perStep: Record<string, string> = this.targetStepFormulas[targetId] || {};
      return Object.keys(perStep)
        .map((label) => (perStep[label] || '').trim() ? `${label}: ${perStep[label].trim()}` : '')
        .filter(Boolean)
        .join(', ');
    }
    return (this.targetFormulas[targetId] || '').trim();
  }

  /** The `formulas` array sent on the wire for one target — ONE consistent
   *  shape for every target type. A non-waterfall target gets a single entry
   *  with no `step`; a WATERFALL target gets one entry PER bar that actually has
   *  a formula filled in (bars left blank are simply omitted, not sent empty).
   *  Each entry also carries `references` when the formula uses a bracket
   *  token the user has typed a reference for (see `targetFormulaReferences`). */
  private targetFormulasForPayload(
    targetId: string,
  ): Array<{ step?: string; formula: string; references?: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> }> {
    if (this.isWaterfallTarget(targetId)) {
      return this.targetSteps(targetId)
        .map((step) => {
          const formula = this.targetStepFormula(targetId, step.label).trim();
          const references = this.referencesForFormula(targetId, formula, step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry.formula);
    }
    const formula = (this.targetFormulas[targetId] || '').trim();
    // An INPUT-control target (slider/input box) with no formula still expects
    // the driving value — default to a plain `value` pass-through so "input
    // drives input" works without typing a formula. Other targets stay filtered
    // out: with no formula the backend has nothing to compute, its response
    // entry comes back empty and recomputeEntryHasData skips it (the tile shows
    // a loader but never updates).
    if (!formula) return this.isInputControlTargetId(targetId) ? [{ formula: 'value' }] : [];
    const references = this.referencesForFormula(targetId, formula);
    return [{ formula, ...(references ? { references } : {}) }];
  }

  /** True when the target componentId belongs to a SLIDER / INPUT_BOX tile.
   *  Public: the templates use it to hint the `value` pass-through default. */
  isInputControlTargetId(targetId: string): boolean {
    const layoutType = this.targetLayoutTypeForId(targetId);
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX';
  }

  /** Builds the `references` object for one formula string — only tokens
   *  that (a) the user has typed a reference row for THIS step (or, for a
   *  non-waterfall target, any row at all — there are no steps), AND (b)
   *  actually appear as a `[Token]` in the formula, are included. */
  private referencesForFormula(
    targetId: string,
    formula: string,
    step?: string,
  ): Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> | undefined {
    const entries = (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
    if (!entries.length || !formula) return undefined;
    const tokensInFormula = new Set(Array.from(formula.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1].trim()));
    const references: Record<string, { inputTitle: string; targetLabel: string; rowLabel: string }> = {};
    entries.forEach((entry) => {
      const token = entry.token.trim();
      if (!token || !tokensInFormula.has(token)) return;
      references[token] = {
        inputTitle: entry.inputTitle.trim(),
        targetLabel: entry.targetLabel.trim(),
        rowLabel: entry.rowLabel.trim(),
      };
    });
    return Object.keys(references).length ? references : undefined;
  }

  /** Reverse of `referencesForFormula` — rebuilds typed-in reference rows from
   *  a saved `references` map (one row per token), for a chart's OWN formula
   *  (KPI / horizontal bar), keyed by its own componentId. Same shape as the
   *  per-target rehydration in `editTile`'s INPUT_BOX/SLIDER branch. */
  private rehydrateOwnFormulaReferences(componentId: string, references: unknown): void {
    const rows: TargetFormulaReferenceEntry[] = [];
    if (references && typeof references === 'object') {
      Object.keys(references as Record<string, Record<string, unknown>>).forEach((token) => {
        const ref = (references as Record<string, Record<string, unknown>>)[token] || {};
        rows.push({
          token,
          inputTitle: String(ref['inputTitle'] ?? ''),
          targetLabel: String(ref['targetLabel'] ?? ''),
          rowLabel: String(ref['rowLabel'] ?? ''),
        });
      });
    }
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [componentId]: rows };
  }

  /** References typed in for one target, for the template's *ngFor. Pass
   *  `step` to scope to one WATERFALL bar; omit it for a non-waterfall
   *  target's single formula. */
  targetReferences(targetId: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.targetFormulaReferences[targetId] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }

  /** Adds a blank, fully-typed-by-hand reference row for a target's formula
   *  (or one specific WATERFALL bar, when `step` is given). */
  addTargetReference(targetId: string, step?: string): void {
    const forTarget = [...(this.targetFormulaReferences[targetId] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  /** Removes a reference row (matched by identity, since it may live in a
   *  step-filtered subset of the target's full reference list). */
  removeTargetReference(targetId: string, entry: TargetFormulaReferenceEntry): void {
    const forTarget = (this.targetFormulaReferences[targetId] || []).filter((item) => item !== entry);
    this.targetFormulaReferences = { ...this.targetFormulaReferences, [targetId]: forTarget };
  }

  /** Resolves the current layout type's builder-step definition from the config's
   *  `layoutSteps` map, following a string alias (shared builders — INPUT_BOX /
   *  SLIDER both point at "INPUT_CONTROL"). Null when the config omits it, in
   *  which case the template falls back to its built-in headings/hints. */
  get activeLayoutSteps(): LayoutStepsDefinition | null {
    const map = this.layout.layoutSteps;
    if (!map) return null;
    let entry = map[this.selectedLayoutType];
    // One hop of alias resolution ("SLIDER" -> "INPUT_CONTROL" -> {...}).
    if (typeof entry === 'string') entry = map[entry];
    return entry && typeof entry !== 'string' ? entry : null;
  }

  private layoutSection(sectionId: string): LayoutStepSection | undefined {
    return this.activeLayoutSteps?.sections.find((section) => section.id === sectionId);
  }

  /** Config-driven section heading with a hardcoded fallback so a missing/partial
   *  `layoutSteps` block never blanks out a title. */
  layoutSectionTitle(sectionId: string, fallback: string): string {
    return this.layoutSection(sectionId)?.title || fallback;
  }

  /** Config-driven section helper text; empty string hides the hint. */
  layoutSectionHint(sectionId: string, fallback = ''): string {
    return this.layoutSection(sectionId)?.hint ?? fallback;
  }

  /** Whether a control should render, per the active layout's config. Returns
   *  true when the field is listed in the section's `fields`; false when the
   *  section defines `fields` but omits it (so removing a field from the JSON
   *  hides its control). Falls back to true when the layout/section has no
   *  `fields` config at all, so an absent/partial config never blanks controls. */
  fieldEnabled(sectionId: string, fieldKey: string): boolean {
    const section = this.layoutSection(sectionId);
    if (!section || !section.fields) return true;
    return section.fields.some((field) => field.field === fieldKey);
  }

  /** Config-driven label for a specific field, with a hardcoded fallback. */
  fieldLabel(sectionId: string, fieldKey: string, fallback: string): string {
    const section = this.layoutSection(sectionId);
    return section?.fields?.find((field) => field.field === fieldKey)?.label || fallback;
  }
  get stepTypes(): LabelValueOption[] {
    return this.layout.options?.stepTypes?.length
      ? this.layout.options.stepTypes
      : [
          { label: 'Start (anchor pillar)', value: 'START' },
          { label: 'Change (floating segment)', value: 'CHANGE' },
          { label: 'Total (subtotal / milestone)', value: 'TOTAL' },
        ];
  }
  get layoutAggregations(): LabelValueOption[] {
    return this.layout.options?.layoutAggregations?.length
      ? this.layout.options.layoutAggregations
      : [{ label: 'Sum', value: 'SUM' }, { label: 'Average', value: 'AVG' }];
  }
  get signOptions(): LabelValueOption[] {
    return this.layout.options?.signOptions?.length
      ? this.layout.options.signOptions
      : [
          { label: 'Auto (let the query decide)', value: 'AUTO' },
          { label: 'Positive (adds)', value: 'POSITIVE' },
          { label: 'Negative (subtracts)', value: 'NEGATIVE' },
        ];
  }
  /** Aggregation picker for a Drill-Down measure. "Average per group" (AVG_BY)
   *  is computed by the CLIENT tree builder only — a server-mode tile sends
   *  the aggregation to the backend, which does not know it — so the option
   *  is offered in client mode alone (a saved AVG_BY still shows while set). */
  get drillMeasureAggregationOptions(): LabelValueOption[] {
    const all = this.measureAggregations;
    if (this.isDrillClientMode) return all;
    // Memoized: an appendTo="body" ng-select fed a fresh array each change
    // detection swallows its clicks (see the ng-select memoize note).
    if (this.drillMeasureAggregationMemo?.source !== all) {
      this.drillMeasureAggregationMemo = { source: all, result: all.filter((o) => String(o.value).toUpperCase() !== 'AVG_BY') };
    }
    return this.drillMeasureAggregationMemo.result;
  }
  private drillMeasureAggregationMemo: { source: LabelValueOption[]; result: LabelValueOption[] } | null = null;
  get measureAggregations(): LabelValueOption[] {
    return this.layout.options?.measureAggregations?.length
      ? this.layout.options.measureAggregations
      : [
          { label: 'None (raw value)', value: 'NONE' },
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
        ];
  }
  get rollUpStrategies(): LabelValueOption[] {
    return this.layout.options?.rollUpStrategies?.length
      ? this.layout.options.rollUpStrategies
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }
  get kpiAggregations(): LabelValueOption[] {
    return this.layout.options?.kpiAggregations?.length
      ? this.layout.options.kpiAggregations
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
          { label: 'Distinct Count', value: 'COUNT_DISTINCT' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }
  get barVariants(): BarVariantOption[] {
    return this.layout.options?.barVariants?.length
      ? this.layout.options.barVariants
      : [
          { label: 'Simple Bar', value: 'simple', usesSeries: false },
          { label: 'Grouped Bar', value: 'grouped', usesSeries: true },
          { label: 'Stacked Bar', value: 'stacked', usesSeries: true },
          { label: '100% Stacked Bar', value: 'stacked-100', usesSeries: true },
          { label: 'Clustered Bar Chart', value: 'clustered', usesSeries: true },
          { label: 'Comparison Bar (A vs B)', value: 'comparison', needsTwoMeasures: true },
        ];
  }

  /** The config entry for the currently-selected variant (behaviour flags + hint). */
  private get barVariantEntry(): BarVariantOption | undefined {
    return this.barVariants.find((v) => v.value === this.barVariant);
  }
  get barRenderTypes(): LabelValueOption[] {
    return this.layout.options?.barRenderTypes?.length
      ? this.layout.options.barRenderTypes
      : [
          { label: 'Bar', value: 'bar' },
          { label: 'Line', value: 'line' },
          { label: 'Area', value: 'area' },
        ];
  }
  get barAxisOptions(): LabelValueOption[] {
    return this.layout.options?.barAxisOptions?.length
      ? this.layout.options.barAxisOptions
      : [
          { label: 'Primary axis', value: 'y1' },
          { label: 'Secondary axis (dual)', value: 'y2' },
        ];
  }
  /** Whether the selected variant splits each category into sub-bars by a series
   *  column — read from the variant's `usesSeries` config flag (no hardcoded list). */
  get barVariantUsesSeries(): boolean {
    return this.barVariantEntry?.usesSeries === true;
  }

  get barVariantSeriesFromMeasures(): boolean {
    return this.barVariantEntry?.seriesFromMeasures === true;
  }

  /** Series is coming from the measure list (variant allows it, no Split picked). */
  get barSeriesComesFromMeasures(): boolean {
    return this.barVariantSeriesFromMeasures && !this.barSeries;
  }

  /** A Split column is mandatory. `seriesFromMeasures` makes it optional, since
   *  a computed column is a measure and can never be a categorical split. */
  get barSeriesRequired(): boolean {
    return this.barVariantUsesSeries && !this.barVariantSeriesFromMeasures;
  }

  /** Per-variant guidance shown under the Variant picker — from config `hint`. */
  get barVariantHint(): string {
    return this.barVariantEntry?.hint || '';
  }

  // ── Line & Area variant getters (config-driven, from line-area.json) ──
  private readonly lineAreaVariantsFallback: LineAreaVariantOption[] = [
    { label: 'Line', value: 'line', group: 'Line', usesSeries: false, allowMultipleMeasures: true },
    { label: 'Multi-Line', value: 'multi-line', group: 'Line', usesSeries: false, allowMultipleMeasures: true },
    { label: 'Step Line', value: 'step', group: 'Line', usesSeries: false, allowMultipleMeasures: true, step: true },
    { label: 'Spline Line', value: 'spline', group: 'Line', usesSeries: false, allowMultipleMeasures: true, smooth: true },
    { label: 'Area', value: 'area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true },
    { label: 'Stacked Area', value: 'stacked-area', group: 'Area', usesSeries: true, area: true, stack: true },
    { label: '100% Stacked Area', value: 'area-100', group: 'Area', usesSeries: true, area: true, stack: true, normalize100: true },
    { label: 'Spline Area', value: 'spline-area', group: 'Area', usesSeries: false, allowMultipleMeasures: true, area: true, smooth: true },
  ];
  get lineAreaVariants(): LineAreaVariantOption[] {
    return this.layout.options?.lineAreaVariants?.length
      ? (this.layout.options.lineAreaVariants as LineAreaVariantOption[])
      : this.lineAreaVariantsFallback;
  }
  private get laVariantEntry(): LineAreaVariantOption | undefined {
    return this.lineAreaVariants.find((v) => v.value === this.laVariant);
  }
  private readonly lineAreaRenderTypesFallback: LabelValueOption[] = [
    { label: 'Line', value: 'line' }, { label: 'Area', value: 'area' },
  ];
  get lineAreaRenderTypes(): LabelValueOption[] {
    return this.layout.options?.lineAreaRenderTypes?.length ? this.layout.options.lineAreaRenderTypes : this.lineAreaRenderTypesFallback;
  }
  private readonly lineAreaAxisOptionsFallback: LabelValueOption[] = [
    { label: 'Primary axis', value: 'y1' }, { label: 'Secondary axis (dual)', value: 'y2' },
  ];
  get lineAreaAxisOptions(): LabelValueOption[] {
    return this.layout.options?.lineAreaAxisOptions?.length ? this.layout.options.lineAreaAxisOptions : this.lineAreaAxisOptionsFallback;
  }
  private readonly lineAreaSymbolOptionsFallback: LabelValueOption[] = [
    { label: 'None', value: 'none' },
    { label: 'Circle', value: 'circle' },
    { label: 'Square', value: 'rect' },
    { label: 'Diamond', value: 'diamond' },
    { label: 'Triangle', value: 'triangle' },
    { label: 'Pin', value: 'pin' },
    { label: 'Arrow', value: 'arrow' },
  ];
  get lineAreaSymbolOptions(): LabelValueOption[] {
    return this.layout.options?.lineAreaSymbolOptions?.length ? this.layout.options.lineAreaSymbolOptions : this.lineAreaSymbolOptionsFallback;
  }
  /** Weights for the per-axis "label weight" selects (shared shell `fontWeights`
   *  list — same one the Format-popup "Axis label weight" control uses). Stable
   *  reference (config array or this constant) so appendTo="body" panels work. */
  private readonly fontWeightOptionsFallback: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Normal', value: 'normal' },
    { label: 'Medium (500)', value: '500' },
    { label: 'Semi-bold (600)', value: '600' },
    { label: 'Bold', value: 'bold' },
  ];
  get fontWeightOptions(): LabelValueOption[] {
    return this.layout.options?.fontWeights?.length ? this.layout.options.fontWeights : this.fontWeightOptionsFallback;
  }
  /** Whether the selected variant splits into one line/area per series value. */
  get laVariantUsesSeries(): boolean {
    return this.laVariantEntry?.usesSeries === true;
  }
  get laSeriesRequired(): boolean {
    return this.laVariantUsesSeries && this.laMeasures.filter((measure) => !!measure.field).length < 2;
  }
  get laVariantHint(): string {
    return this.laVariantEntry?.hint || '';
  }
  /** Whether adding MORE than one measure is meaningful for the selected variant. */
  get laAllowsMultipleMeasures(): boolean {
    if (this.laVariantEntry?.allowMultipleMeasures === true) return true;
    return !this.laVariantUsesSeries;
  }
  /** A secondary (right) value axis is in play — via the Secondary-Y picker or
   *  any measure mapped to y2 — so its label input is worth showing. */
  get laHasSecondaryY(): boolean {
    return !!this.laSecondaryYField.trim() || this.laMeasures.some((m) => !!m.field && m.axis === 'y2');
  }

  // ── Scatter & Bubble variant getters (config-driven, from scatter-bubble.json) ──
  /** Stable fallback instance — the getter must not mint a fresh array per CD
   *  cycle (ng-select appendTo:'body' identity-churn hazard). */
  private readonly scatterBubbleVariantsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Scatter', value: 'scatter', mode: 'scatter', group: 'Scatter' },
    { label: 'Multi Scatter', value: 'multi-scatter', mode: 'scatter', group: 'Scatter', usesSeries: true, multiSeries: true },
    { label: 'Bubble', value: 'bubble', mode: 'bubble', group: 'Bubble', usesSize: true, bubble: true },
    { label: 'Quadrant', value: 'quadrant', mode: 'scatter', group: 'Advanced', quadrant: true },
  ];
  get scatterBubbleVariants(): ScatterBubbleVariantOption[] {
    return this.layout.options?.scatterBubbleVariants?.length
      ? (this.layout.options.scatterBubbleVariants as ScatterBubbleVariantOption[])
      : this.scatterBubbleVariantsFallback;
  }
  /** The Orientation toggle's sides — FULLY config-driven (label + icon +
   *  value) from `scatterBubbleModes`; a JSON edit can add/remove/rename a
   *  side. Stable fallback instance (no fresh array per CD cycle). */
  private readonly sbModesFallback: ScatterBubbleModeOption[] = [
    { label: 'Scatter', value: 'scatter', icon: 'scatter_plot' },
    { label: 'Bubble', value: 'bubble', icon: 'bubble_chart' },
  ];
  get sbModes(): ScatterBubbleModeOption[] {
    return this.layout.options?.scatterBubbleModes?.length
      ? (this.layout.options.scatterBubbleModes as ScatterBubbleModeOption[])
      : this.sbModesFallback;
  }
  /** The config-declared default side = the FIRST mode entry. */
  private get sbDefaultMode(): string {
    return this.sbModes[0]?.value ?? 'scatter';
  }
  /** Which toggle side a variant belongs to — config `mode`, else its render flag. */
  private sbVariantModeOf(v: ScatterBubbleVariantOption): string {
    return v.mode ?? (v.bubble ? 'bubble' : this.sbDefaultMode);
  }
  /** Legacy marker: pre-overlay configs listed advanced entries INSIDE
   *  scatterBubbleVariants under a `group: 'Advanced'` heading — keep them out
   *  of the Variant dropdown if such a config is still deployed. */
  private sbVariantIsAdvanced(v: ScatterBubbleVariantOption): boolean {
    return v.group === 'Advanced';
  }
  /** Variant dropdown items — the active toggle side's BASE variants. MEMOIZED
   *  (fresh array each CD cycle breaks ng-select appendTo:'body'). */
  private sbVariantItemsCache: ScatterBubbleVariantOption[] = [];
  private sbVariantItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.scatterBubbleVariants;
    if (this.sbVariantItemsCacheKey?.source !== source || this.sbVariantItemsCacheKey.mode !== this.sbMode) {
      this.sbVariantItemsCache = source.filter((v) => this.sbVariantModeOf(v) === this.sbMode && !this.sbVariantIsAdvanced(v));
      this.sbVariantItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbVariantItemsCache;
  }
  /** Advanced analytic OVERLAYS (Quadrant / Regression / Correlation / Cluster)
   *  — from config `scatterBubbleAdvancedOptions`. NOT variants: one can be
   *  layered onto whichever base variant is active (Quadrant + Bubble, …) by
   *  ORing its flags into the base flags. Stable fallback instance (no fresh
   *  array per CD cycle). */
  private readonly sbAdvancedOptionsFallback: ScatterBubbleVariantOption[] = [
    { label: 'Simple', value: '', hint: 'The plain base chart — no analytic overlay applied.' },
    { label: 'Quadrant', value: 'quadrant', quadrant: true },
    { label: 'Regression', value: 'regression', regression: true },
    { label: 'Correlation', value: 'correlation', correlation: true },
    { label: 'Cluster Scatter', value: 'cluster', cluster: true },
  ];
  /** Accordion rows — overlays available on the active toggle side (an entry
   *  with no `mode` shows on both sides). MEMOIZED. */
  private sbAdvancedItemsCache: ScatterBubbleVariantOption[] = [];
  private sbAdvancedItemsCacheKey: { source: ScatterBubbleVariantOption[]; mode: string } | null = null;
  get sbAdvancedVariantItems(): ScatterBubbleVariantOption[] {
    const source = this.layout.options?.scatterBubbleAdvancedOptions?.length
      ? (this.layout.options.scatterBubbleAdvancedOptions as ScatterBubbleVariantOption[])
      : this.sbAdvancedOptionsFallback;
    if (this.sbAdvancedItemsCacheKey?.source !== source || this.sbAdvancedItemsCacheKey.mode !== this.sbMode) {
      this.sbAdvancedItemsCache = source.filter((v) => !v.mode || v.mode === this.sbMode);
      this.sbAdvancedItemsCacheKey = { source, mode: this.sbMode };
    }
    return this.sbAdvancedItemsCache;
  }
  /** Aggregation picker choices — config `sbAggregations`, stable fallback. */
  private readonly sbAggregationOptionsFallback: LabelValueOption[] = [
    { label: 'None (raw points)', value: 'none' },
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Count', value: 'count' },
    { label: 'Min', value: 'min' },
    { label: 'Max', value: 'max' },
  ];
  get sbAggregationOptions(): LabelValueOption[] {
    return this.layout.options?.sbAggregations?.length
      ? this.layout.options.sbAggregations
      : this.sbAggregationOptionsFallback;
  }
  /** The active overlay's config entry (undefined = no overlay). */
  private get sbAdvancedEntry(): ScatterBubbleVariantOption | undefined {
    if (!this.sbAdvanced) return undefined;
    return this.sbAdvancedVariantItems.find((v) => v.value === this.sbAdvanced);
  }
  /** Scatter/Bubble segmented-toggle click. Keeps the variant valid for the new
   *  side (falls back to the side's first variant); the advanced overlay
   *  carries across sides (Quadrant + Scatter ↔ Quadrant + Bubble). */
  setSbMode(mode: string): void {
    if (this.sbMode === mode) return;
    this.sbMode = mode;
    if (!this.sbVariantItems.some((v) => v.value === this.sbVariant)) {
      this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    }
    // Drop an overlay the new side doesn't offer (only possible with mode-scoped entries).
    if (this.sbAdvanced && !this.sbAdvancedVariantItems.some((v) => v.value === this.sbAdvanced)) {
      this.sbAdvanced = '';
    }
    this.onScatterBubbleChange();
  }
  /** Advanced-accordion row click — single-select, click again to turn off. */
  toggleSbAdvanced(value: string): void {
    this.sbAdvanced = this.sbAdvanced === value ? '' : value;
    this.onScatterBubbleChange();
  }
  /** Toggle side owning a saved variant — for rehydrating the segmented toggle. */
  private sbModeForVariant(variant: string): string {
    const entry = this.scatterBubbleVariants.find((v) => v.value === variant);
    return entry ? this.sbVariantModeOf(entry) : this.sbDefaultMode;
  }
  private get sbVariantEntry(): ScatterBubbleVariantOption | undefined {
    return this.scatterBubbleVariants.find((v) => v.value === this.sbVariant);
  }
  /** Whether the Series column is consumed — by the base variant (Multi
   *  Scatter) OR the active advanced overlay (Cluster Scatter). */
  get sbVariantUsesSeries(): boolean {
    return this.sbVariantEntry?.usesSeries === true || this.sbAdvancedEntry?.usesSeries === true;
  }
  /** Whether the Size column is consumed — base variant (Bubble) or overlay. */
  get sbVariantUsesSize(): boolean {
    return this.sbVariantEntry?.usesSize === true || this.sbAdvancedEntry?.usesSize === true;
  }
  /** Base-variant hint (the overlay's own hint shows inside the accordion). */
  get sbVariantHint(): string {
    return this.sbVariantEntry?.hint || '';
  }
  /** Active overlay's hint — shown under the Advanced accordion rows. */
  get sbAdvancedHint(): string {
    return this.sbAdvancedEntry?.hint || '';
  }

  // ── Scatter Build-form format accordions (reuse the generic groups computation
  //    keyed off selectedLayoutType; own cache + open-set since sbFormat is a
  //    separate state map from barFormat). ──
  private sbFormatGroupsCache: FormatControlGroup[] | null = null;
  private sbFormatGroupsCacheType = '';
  get scatterBubbleFormatGroups(): FormatControlGroup[] {
    if (this.sbFormatGroupsCache && this.sbFormatGroupsCacheType === this.selectedLayoutType) {
      return this.sbFormatGroupsCache;
    }
    const applicable = this.applicableFormatControls(this.selectedLayoutType);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) { byGroup.set(group, []); seen.push(group); }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    const exposed = seen.filter((name) => this.chartExposesSection(name));
    this.sbFormatGroupsCacheType = this.selectedLayoutType;
    this.sbFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.sbFormatGroupsCache;
  }
  isSbFormatGroupOpen(group: string): boolean {
    return this.openSbFormatGroups.has(group);
  }
  toggleSbFormatGroup(group: string): void {
    const next = new Set(this.openSbFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openSbFormatGroups = next;
  }
  private seedScatterBubbleFormatDefaults(): void {
    this.applicableFormatControls('SCATTER_BUBBLE').forEach((control) => {
      if (control.default !== undefined && this.sbFormat[control.key] === undefined) {
        this.sbFormat[control.key] = control.default;
      }
    });
  }

  /** Column list for the Scatter/Bubble field pickers — MEMOIZED (fresh array each
   *  CD cycle breaks ng-select appendTo:'body'; see [[project_ngselect_appendtobody_memoize]]). */
  private sbColumnsCache: string[] = [];
  private sbColumnsKey = '';
  get scatterBubbleColumns(): string[] {
    const base = this.drillDatasetColumns;
    const key = base.join('|');
    if (key !== this.sbColumnsKey) {
      this.sbColumnsKey = key;
      this.sbColumnsCache = [...base];
    }
    return this.sbColumnsCache;
  }

  /** Cap on the Category-colors swatch list — a high-cardinality Color column
   *  (ids, names) would otherwise flood the form; the renderer still colors
   *  every value from the palette. */
  private static readonly SB_MAX_CATEGORY_COLORS = 40;
  private sbCatRows: Array<Record<string, unknown>> | undefined;
  private sbCatField = '';
  private sbCatCache: string[] = [];
  /** Distinct Color-by values of the active dataset (first-seen order, capped).
   *  Memoized on rows identity + column so the *ngFor gets a stable array. */
  get sbColorCategories(): string[] {
    const rows = this.entityRowsCache.get(this.drillDataset);
    const field = this.sbColorField;
    if (rows === this.sbCatRows && field === this.sbCatField) return this.sbCatCache;
    this.sbCatRows = rows;
    this.sbCatField = field;
    const out: string[] = [];
    if (field && rows) {
      const seen = new Set<string>();
      for (const row of rows) {
        const value = String(row[field] ?? '');
        if (seen.has(value)) continue;
        seen.add(value);
        out.push(value);
        if (out.length >= DvmLayoutBuilderComponent.SB_MAX_CATEGORY_COLORS) break;
      }
    }
    this.sbCatCache = out;
    return out;
  }
  /** Swatch shown for a Color-by value: the explicit pick, else the palette
   *  slot at its index — the SAME fallback the renderer uses (Format-popup
   *  palette first, then the config chartDefaults palette). */
  sbCategoryColor(category: string, index: number): string {
    const explicit = this.sbCategoryColors[category];
    if (explicit) return explicit;
    const defaults = this.layout.options?.scatterBubbleChartDefaults as { palette?: string[] } | undefined;
    const palette = this.resolvePaletteColors(this.sbFormat['palette'] ? String(this.sbFormat['palette']) : undefined)
      || defaults?.palette
      || [];
    return palette.length ? palette[index % palette.length] : '#1f6feb';
  }
  get hasSbCategoryColors(): boolean {
    return Object.keys(this.sbCategoryColors).length > 0;
  }
  onSbCategoryColorChange(category: string, color: string): void {
    const next = { ...this.sbCategoryColors };
    if (color) next[category] = color;
    else delete next[category];
    this.sbCategoryColors = next;
    this.onScatterBubbleChange();
  }
  resetSbCategoryColors(): void {
    this.sbCategoryColors = {};
    this.onScatterBubbleChange();
  }

  /** Comparison-style variant needs two measures — from config `needsTwoMeasures`. */
  get isBarComparison(): boolean {
    return this.barVariantEntry?.needsTwoMeasures === true;
  }

  /** Whether adding MORE than one measure is meaningful for the selected variant,
   *  so the "Add measure" button + per-measure reorder/remove UI only show where
   *  extra measures actually render. Config-driven:
   *   - variant `allowMultipleMeasures: true`  → allowed (e.g. Simple Bar),
   *   - variant `needsTwoMeasures: true`       → allowed (Comparison: A vs B),
   *   - series-split variants (grouped/stacked/…) → NOT allowed: the split column
   *     supplies the series and the option builder uses only the FIRST measure for
   *     bar values, so extra bar measures are silently ignored. (A Line/Area combo
   *     overlay is a separate, advanced case handled via each measure's Draw-as.)
   *  Falls back to "allowed when the variant doesn't use a series split". */
  get barAllowsMultipleMeasures(): boolean {
    if (this.barVariantEntry?.allowMultipleMeasures === true) return true;
    if (this.isBarComparison) return true;
    // Measures ARE the series here; picking a Split reverts to one measure.
    if (this.barSeriesComesFromMeasures) return true;
    return !this.barVariantUsesSeries;
  }

  /** Config-driven orientation-aware axis labels (X/Y ↔ category/value). */
  private get axisLabelsConfig(): Record<string, string> {
    return (this.layout.options?.axisLabels ?? {}) as Record<string, string>;
  }
  get barCategoryAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['categoryHorizontal'] || 'Y-axis (category)')
      : (c['categoryVertical'] || 'X-axis (category)');
  }
  get barValueAxisLabel(): string {
    const c = this.axisLabelsConfig;
    return this.barOrientation === 'horizontal'
      ? (c['valueHorizontal'] || 'X-axis (values)')
      : (c['valueVertical'] || 'Y-axis (values)');
  }
  /** Which secondary picker is the REAL dual value-axis for the current
   *  orientation: Secondary Y when Vertical, Secondary X when Horizontal. The
   *  other is the category side and can't carry a second data column on a bar. */
  get barSecondaryValueAxis(): 'x' | 'y' {
    return this.barOrientation === 'horizontal' ? 'x' : 'y';
  }
  /** True for the secondary picker that actually drives a dual axis (the other is
   *  shown but inert, with a hint, per the "both pickers" request). */
  isBarSecondaryAxisActive(which: 'x' | 'y'): boolean {
    return this.barSecondaryValueAxis === which;
  }
  /** The column chosen on whichever secondary picker is active (or ''). */
  get barActiveSecondaryField(): string {
    return this.barSecondaryValueAxis === 'x' ? this.barSecondaryXField : this.barSecondaryYField;
  }

  /** Whether the bar chart currently has a secondary (dual) value axis mapped —
   *  gates the build form's "Secondary axis label weight" select. */
  get barHasSecondaryAxis(): boolean {
    return !!this.barActiveSecondaryField.trim() || this.barMeasures.some((m) => !!m.field && m.axis === 'y2');
  }

  /** Memoized accordion groups — see barFormatGroups. Built once from the config
   *  (which is stable after load); a plain getter here would return a NEW array +
   *  new objects every change-detection cycle, so *ngFor (no trackBy) would tear
   *  down & rebuild the whole accordion on every CD — which made a header click
   *  never appear to open its section. Cached ⇒ stable reference ⇒ clicks stick. */
  private barFormatGroupsCache: FormatControlGroup[] | null = null;
  /** Layout type the cache was built for — a chart-type switch rebuilds it. */
  private barFormatGroupsCacheType = '';

  /** Format controls applicable to the ACTIVE layout type, grouped into ordered
   *  accordion sections for the Build form (Bar & Waterfall both render these).
   *  Title lives in its OWN accordion (the top-form Title field was removed), so
   *  every configured group is shown as an accordion. */
  get barFormatGroups(): FormatControlGroup[] {
    // Table & Matrix gates controls per sub-type (visibleWhenType), so its
    // accordion must rebuild when the Table type changes — key on it.
    const cacheKey = this.isTableMatrixLayout ? `${this.selectedLayoutType}|${this.tmType}` : this.selectedLayoutType;
    if (this.barFormatGroupsCache && this.barFormatGroupsCacheType === cacheKey) {
      return this.barFormatGroupsCache;
    }
    const applicable = this.applicableFormatControls(this.selectedLayoutType)
      .filter((control) => this.controlVisibleForSubType(control, this.isTableMatrixLayout ? this.tmType : ''));
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    applicable.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        seen.push(group);
      }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    // LAYER 2 — the Capability Registry's `sections` allow-list gates which
    // accordions appear, so unsupported sections are never shown for this chart.
    const exposed = seen.filter((name) => this.chartExposesSection(name));
    this.barFormatGroupsCacheType = cacheKey;
    this.barFormatGroupsCache = exposed.map((name) => ({ name, controls: byGroup.get(name)! }));
    return this.barFormatGroupsCache;
  }

  /** Whether a format control applies to the given family sub-type (Table &
   *  Matrix normal/editable/drilldown). Empty subType or no `visibleWhenType`
   *  on the control → always visible (non-family charts are unaffected). */
  private controlVisibleForSubType(control: FormatControl, subType: string): boolean {
    if (!subType) return true;
    const allowed = control.visibleWhenType;
    return !Array.isArray(allowed) || !allowed.length || allowed.includes(subType);
  }

  isBarFormatGroupOpen(group: string): boolean {
    return this.openBarFormatGroups.has(group);
  }

  toggleBarFormatGroup(group: string): void {
    // Reassign the Set (new reference) so change detection is unambiguous.
    const next = new Set(this.openBarFormatGroups);
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openBarFormatGroups = next;
  }

  /** Seed barFormat with every control default applicable to the ACTIVE layout
   *  type (so a value exists before the user touches anything). Idempotent —
   *  only fills missing keys. */
  private seedBarFormatDefaults(): void {
    this.applicableFormatControls(this.selectedLayoutType).forEach((control) => {
      if (control.default !== undefined && this.barFormat[control.key] === undefined) {
        this.barFormat[control.key] = control.default;
      }
    });
  }

  get numberFormats(): LabelValueOption[] {
    return this.layout.options?.numberFormats?.length
      ? this.layout.options.numberFormats
      : [
          { label: 'Default', value: 'NONE' },
          { label: 'Commas (1,234)', value: 'THOUSANDS' },
          { label: 'Auto scale (K/M/B)', value: 'COMPACT' },
          { label: 'Thousands (K)', value: 'SCALE_THOUSANDS' },
          { label: 'Millions (M)', value: 'MILLIONS' },
          { label: 'Billions (B)', value: 'BILLIONS' },
          { label: 'Percent (12%)', value: 'PERCENT' },
        ];
  }
  /** All configured per-tile format controls (unfiltered). */
  get formatControls(): FormatControl[] {
    return this.layout.options?.formatControls ?? [];
  }

  /** Enabled controls applicable to a layout type, DEDUPED by key: when a
   *  chart's own control (explicit `appliesTo`) and a shared shell control (no
   *  `appliesTo`) share a key, the chart-specific one wins — e.g. Scatter's
   *  "Show point labels" replaces the shell's "Show data labels" instead of
   *  both toggles rendering (and fighting over the same format key). */
  private applicableFormatControls(layoutType: string): FormatControl[] {
    // A GROUP card is a container, not a chart, so it takes ONLY controls that
    // opt into it explicitly (its own border / background design set). Shared
    // shell controls carry no `appliesTo` and would otherwise all land in the
    // group's popup — "Show data labels" on a card that draws no series.
    // DROPDOWN is explicit-only for the same reason: it draws no chart, so the
    // shared Title/alignment/etc. controls are meaningless — it takes just its
    // own Appearance set (pill colors / radius / font size).
    const explicitOnly = layoutType === 'GROUP' || layoutType === 'DROPDOWN';
    const applicable = this.formatControls.filter(
      (control) =>
        control.enabled !== false &&
        (explicitOnly
          ? !!control.appliesTo?.includes(layoutType)
          : !control.appliesTo || !control.appliesTo.length || control.appliesTo.includes(layoutType)),
    );
    const winners = new Map<string, FormatControl>();
    applicable.forEach((control) => {
      const prev = winners.get(control.key);
      const specific = !!(control.appliesTo && control.appliesTo.length);
      const prevSpecific = !!(prev && prev.appliesTo && prev.appliesTo.length);
      if (!prev || (specific && !prevSpecific)) winners.set(control.key, control);
    });
    return applicable.filter((control) => winners.get(control.key) === control);
  }
  private readonly emptyColumnOptions: LabelValueOption[] = [];
  private formatColumnOptionsMemo = new Map<string, { key: string; result: LabelValueOption[] }>();

  /** Options list for a select-type format control (resolved via optionsKey or
   *  dynamically from the active dataset/tile columns). */
  formatControlOptions(control: FormatControl, tile?: DashboardTile): LabelValueOption[] {
    if (control.control === 'column-select' || control.optionsSource === 'datasetColumns') {
      const columns = tile ? this.tileFilterColumns(tile) : this.barColumns;
      if (!columns.length) return this.emptyColumnOptions;
      const cacheKey = tile ? `tile:${tile.id}` : `builder:${this.drillDataset}`;
      const key = columns.join('|');
      const cached = this.formatColumnOptionsMemo.get(cacheKey);
      if (cached && cached.key === key) return cached.result;
      const result = columns.map((column) => ({ label: column, value: column }));
      this.formatColumnOptionsMemo.set(cacheKey, { key, result });
      return result;
    }
    if (control.optionsSource === 'categoryValues') return this.categoryValueOptions(control, tile);
    const key = control.optionsKey;
    const list = key ? ((this.layout.options as Record<string, unknown> | undefined)?.[key] as LabelValueOption[] | undefined) : undefined;
    return Array.isArray(list) ? list : [];
  }

  // ── `categoryValues` option source — the distinct values of the tile's X-axis
  //    column, read from the rows the builder already holds, so a picker (e.g.
  //    "Highlight categories") lists real engine models instead of free text.
  //    Memoized per control/tile: ng-select with appendTo="body" swallows clicks
  //    when [items] returns a fresh array every change-detection pass. ──
  private categoryValueOptionsMemo = new Map<string, { key: string; rows: unknown; result: LabelValueOption[] }>();

  private categoryValueOptions(control: FormatControl, tile?: DashboardTile): LabelValueOption[] {
    let field = '';
    let rows: Array<Record<string, unknown>> = [];
    let format: Record<string, unknown> | undefined;
    if (tile) {
      const data = (tile.lineAreaData ?? tile.barData ?? null) as Record<string, unknown> | null;
      const caps = data?.['capabilities'] as Record<string, unknown> | undefined;
      field = String(caps?.['category'] ?? '');
      rows = (tile.lineAreaRows ?? tile.barRows ?? (data?.['staticData'] as Array<Record<string, unknown>> | undefined)) || [];
      format = tile.format;
    } else {
      field = this.isLineAreaLayout ? this.laCategory : this.isBarLayout ? this.barCategory : '';
      rows = this.entityRowsCache.get(this.drillDataset) || [];
      format = this.barFormat;
    }
    // Values picked earlier (possibly typed, or from rows an alternate API
    // returned) stay listed even when the current rows don't carry them.
    const selected = this.multiSelectModel(format, control.key);
    const cacheKey = tile ? `tile:${tile.id}:${control.key}` : `builder:${control.key}`;
    const key = `${field}|${rows.length}|${selected.join('\u0001')}`;
    const cached = this.categoryValueOptionsMemo.get(cacheKey);
    if (cached && cached.key === key && cached.rows === rows) return cached.result;
    const seen = new Set<string>();
    const values: string[] = [];
    if (field) {
      rows.forEach((row) => {
        const v = String(row?.[field] ?? '').trim();
        if (v && !seen.has(v)) { seen.add(v); values.push(v); }
      });
    }
    values.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    selected.forEach((v) => { if (!seen.has(v)) { seen.add(v); values.push(v); } });
    const result = values.map((value) => ({ label: value, value }));
    this.categoryValueOptionsMemo.set(cacheKey, { key, rows, result });
    return result;
  }

  /** Stable ARRAY model for a `multi-select` control. Older tiles saved these
   *  keys as comma-separated text (the control used to be a text box), so a
   *  string is split; the result is memoized per format bag + key so ng-select
   *  isn't handed a new array reference on every change-detection pass. */
  private multiSelectModelMemo = new WeakMap<object, Map<string, { raw: unknown; arr: string[] }>>();

  multiSelectModel(format: Record<string, unknown> | undefined, key: string): string[] {
    if (!format) return [];
    const raw = format[key];
    let perBag = this.multiSelectModelMemo.get(format);
    if (!perBag) { perBag = new Map(); this.multiSelectModelMemo.set(format, perBag); }
    const hit = perBag.get(key);
    if (hit && hit.raw === raw) return hit.arr;
    const arr = this.parseValueOrder(raw);
    perBag.set(key, { raw, arr });
    return arr;
  }

  /** ng-select `addTag` factory: lets a value that isn't in the loaded rows
   *  (e.g. one only an alternate API returns) still be typed and kept. */
  readonly addTypedValueTag = (term: string): LabelValueOption => ({ label: term, value: term });

  formatControlVisible(control: FormatControl, format: Record<string, unknown> | undefined): boolean {
    const rule = control.visibleWhen;
    if (!rule) return true;
    const value = format?.[rule.key];
    return rule.values.some((candidate) => candidate === value);
  }

  // ── "Custom request body" popup — ONE PER CRUD ENDPOINT ───────────────────
  // Each endpoint (createUrl/updateUrl/deleteUrl/saveUrl/bulkSaveUrl) gets its
  // own body, opened from the "Body" button beside that endpoint's label and
  // stored under format.payloadTemplates[<endpoint key>] — a different URL is
  // exactly why the contract would differ. Row edits (whatever columns the
  // table has) are the DYNAMIC part and need no config here: the chart
  // component's toSaveRow/buildCustomPayload already send them. This popup only
  // edits the STATIC scaffolding around them.
  showPayloadTemplateModal = false;
  /** Which control this popup is editing: the Build-form `barFormat`, or one
   *  already-placed tile's `format` (opened from its own Format popup). */
  private payloadTemplateTarget: 'bar' | 'tile' = 'bar';
  private payloadTemplateTile: DashboardTile | null = null;
  /** The endpoint config key this popup is editing the body for. */
  ptEndpointKey = '';
  /** That endpoint's label, echoed in the popup header. */
  ptEndpointLabel = '';
  ptDraft: PayloadTemplateDraft = this.emptyPayloadTemplateDraft();

  /** Column names offered as payload fields, seeded when the popup opens. */
  ptAvailableColumns: string[] = [];

  private emptyPayloadTemplateDraft(): PayloadTemplateDraft {
    return { dataKey: 'requestData', includeColumns: [], staticValues: [], endpoints: [], extraFields: [] };
  }
  private ptKvRowsFrom(record?: Record<string, unknown>): PayloadTemplateKvRow[] {
    return record ? Object.keys(record).map((key) => ({ key, value: this.ptValueToText(record[key]) })) : [];
  }
  /** Inverse of `parsePtValue` — a saved array/object/number/boolean round-trips
   *  back to its JSON text (so re-opening the popup shows `["engine_model"]`,
   *  not a lossy `Array.toString()` join); a plain string stays literal text. */
  private ptValueToText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  private ptEndpointRowsFrom(list?: TableMatrixPayloadEndpoint[]): PayloadTemplateEndpointRow[] {
    return (list || []).map((e) => ({ functionName: e.functionName || '', version: e.version || '' }));
  }
  /** Every endpoint's saved body, off whichever format object is in play. */
  private ptTemplatesOn(format: Record<string, unknown> | undefined): Record<string, TableMatrixPayloadTemplate> {
    return (format?.['payloadTemplates'] as Record<string, TableMatrixPayloadTemplate> | undefined) || {};
  }
  /** Drives the button's "Body" vs "Body ✓" label so a configured endpoint reads
   *  as configured at a glance. */
  hasPayloadTemplate(format: Record<string, unknown> | undefined, endpointKey: string): boolean {
    return !!this.ptTemplatesOn(format)[endpointKey];
  }
  private seedPayloadTemplateDraft(tpl: TableMatrixPayloadTemplate | undefined): void {
    const t = tpl || {};
    this.ptDraft = {
      dataKey: t.dataKey || 'requestData',
      includeColumns: [...(t.includeColumns || [])],
      staticValues: this.ptKvRowsFrom(t.staticValues),
      endpoints: this.ptEndpointRowsFrom(t.endpoints),
      extraFields: this.ptKvRowsFrom(t.extraFields),
    };
  }
  /** Field list for the payload-template column picker while still on the
   *  Build form (not yet a placed tile). `barColumns` is only ONE dataset
   *  (`drillDataset`) — fine for Bar/Waterfall/etc, but Table & Matrix (and
   *  Drill-Down) can mix fields from a SECOND dataset per column/measure
   *  (`tmColumns[].dataset` / `drillDownMeasures[].dataset` / dimensions), and
   *  those were dropped entirely because this always read `barColumns`. Falls
   *  back to `barColumns` when nothing has been picked yet (fresh form). */
  private get ptBuildFormColumns(): string[] {
    if (this.isTableMatrixLayout && this.isTmDrilldown) {
      const fields = [
        ...this.drillDownDimensions.map((d) => d.field),
        ...this.drillColumnDimensions.map((d) => d.field),
        ...this.drillDownMeasures.map((m) => m.field),
      ].filter(Boolean);
      return fields.length ? fields : this.barColumns;
    }
    if (this.isTableMatrixLayout) {
      return this.tmColumnFields.length ? this.tmColumnFields : this.barColumns;
    }
    if (this.isDrillDownLayout) {
      const fields = [
        ...this.drillDownDimensions.map((d) => d.field),
        ...this.drillColumnDimensions.map((d) => d.field),
        ...this.drillDownMeasures.map((m) => m.field),
      ].filter(Boolean);
      return fields.length ? fields : this.barColumns;
    }
    return this.barColumns;
  }
  /** The dataset's columns (same source the format popup's column pickers use),
   *  unioned with anything already selected so a saved field never vanishes from
   *  the list just because the dataset cache hasn't loaded it. */
  private seedPtAvailableColumns(tile: DashboardTile | null): void {
    const source = tile ? this.tileFilterColumns(tile) : this.ptBuildFormColumns;
    const seen = new Set<string>();
    this.ptAvailableColumns = [...source, ...this.ptDraft.includeColumns]
      .map((column) => String(column ?? '').trim())
      .filter((column) => column && !seen.has(column) && (seen.add(column), true));
  }
  isPtColumnSelected(column: string): boolean {
    return this.ptDraft.includeColumns.includes(column);
  }
  togglePtColumn(column: string): void {
    const list = this.ptDraft.includeColumns;
    const at = list.indexOf(column);
    if (at >= 0) list.splice(at, 1);
    else list.push(column);
  }
  /** "Send every column" — clearing the list restores the default behaviour. */
  clearPtColumns(): void { this.ptDraft.includeColumns = []; }

  /** Open for one endpoint from the Build form (`barFormat` is the target). */
  openPayloadTemplateModalForBar(control: FormatControl): void {
    this.seedPayloadTemplateDraft(this.ptTemplatesOn(this.barFormat)[control.key]);
    this.seedPtAvailableColumns(null);
    this.payloadTemplateTarget = 'bar';
    this.payloadTemplateTile = null;
    this.ptEndpointKey = control.key;
    this.ptEndpointLabel = control.label;
    this.showPayloadTemplateModal = true;
  }
  /** Open for one endpoint from an already-placed tile's Format popup. */
  openPayloadTemplateModalForTile(tile: DashboardTile, control: FormatControl, event?: MouseEvent): void {
    event?.stopPropagation();
    this.seedPayloadTemplateDraft(this.ptTemplatesOn(tile.format)[control.key]);
    this.seedPtAvailableColumns(tile);
    this.payloadTemplateTarget = 'tile';
    this.payloadTemplateTile = tile;
    this.ptEndpointKey = control.key;
    this.ptEndpointLabel = control.label;
    this.showPayloadTemplateModal = true;
  }
  closePayloadTemplateModal(): void {
    this.showPayloadTemplateModal = false;
    this.payloadTemplateTile = null;
    this.ptEndpointKey = '';
    this.ptEndpointLabel = '';
  }

  addPtKvRow(rows: PayloadTemplateKvRow[]): void { rows.push({ key: '', value: '' }); }
  removePtKvRow(rows: PayloadTemplateKvRow[], index: number): void { rows.splice(index, 1); }
  addPtEndpointRow(rows: PayloadTemplateEndpointRow[]): void { rows.push({ functionName: '', version: '' }); }
  removePtEndpointRow(rows: PayloadTemplateEndpointRow[], index: number): void { rows.splice(index, 1); }

  private ptRecordFrom(rows: PayloadTemplateKvRow[]): Record<string, unknown> | undefined {
    const out: Record<string, unknown> = {};
    rows.forEach((r) => { const k = r.key.trim(); if (k) out[k] = this.parsePtValue(r.value); });
    return Object.keys(out).length ? out : undefined;
  }
  /** A Static values / Extra fields row is typed as plain text, but the target
   *  contract often needs a REAL array/object/number/boolean — e.g.
   *  `businessKey: ["engine_model"]`, not the string "[\"engine_model\"]". A
   *  value that PARSES as JSON keeps its real type; anything else (an ordinary
   *  string, or a `{token}` — not valid JSON on its own) stays literal text so
   *  token substitution still applies at save time. */
  private parsePtValue(value: string): unknown {
    const trimmed = value.trim();
    if (!trimmed) return value;
    const looksLikeJson = /^[[{]/.test(trimmed) || /^"/.test(trimmed) || /^-?\d/.test(trimmed) || trimmed === 'true' || trimmed === 'false' || trimmed === 'null';
    if (!looksLikeJson) return value;
    try { return JSON.parse(trimmed); } catch { return value; }
  }
  private ptEndpointsFrom(rows: PayloadTemplateEndpointRow[]): TableMatrixPayloadEndpoint[] | undefined {
    const out = rows.filter((r) => r.functionName.trim()).map((r) => ({ functionName: r.functionName.trim(), version: r.version.trim() || undefined }));
    return out.length ? out : undefined;
  }
  /** Undefined when nothing meaningful was entered — that CLEARS the endpoint's
   *  body so it falls back to the default contract. */
  private buildPayloadTemplateFromDraft(): TableMatrixPayloadTemplate | undefined {
    const d = this.ptDraft;
    const staticValues = this.ptRecordFrom(d.staticValues);
    const endpoints = this.ptEndpointsFrom(d.endpoints);
    const extraFields = this.ptRecordFrom(d.extraFields);
    const includeColumns = d.includeColumns.length ? [...d.includeColumns] : undefined;
    if (!staticValues && !endpoints && !extraFields && !includeColumns) return undefined;
    return { dataKey: d.dataKey.trim() || undefined, includeColumns, staticValues, endpoints, extraFields };
  }

  /** Persist this endpoint's body onto whichever target opened the popup,
   *  leaving every OTHER endpoint's body untouched. */
  savePayloadTemplateModal(): void {
    const key = this.ptEndpointKey;
    if (!key) { this.closePayloadTemplateModal(); return; }
    const tpl = this.buildPayloadTemplateFromDraft();
    const isTile = this.payloadTemplateTarget === 'tile' && !!this.payloadTemplateTile;
    const format = isTile ? this.payloadTemplateTile!.format : this.barFormat;
    const templates = { ...this.ptTemplatesOn(format) };
    if (tpl) templates[key] = tpl;
    else delete templates[key];
    const next = Object.keys(templates).length ? templates : undefined;
    if (isTile) {
      const tile = this.payloadTemplateTile!;
      tile.format = { ...(tile.format || {}), payloadTemplates: next };
      this.onTileFormatChange(tile);
      this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? tile : t));
    } else {
      this.barFormat['payloadTemplates'] = next;
      this.onBarFormatChange();
    }
    this.closePayloadTemplateModal();
  }

  // ── Phase A: workspace & target context ───────────────────────────────────
  selectedLayoutType = 'WATERFALL';
  /** LAYER 1 — the Chart Catalogue category currently selected (drives the Type
   *  picker). Defaults to whichever category owns the initial layout type. */
  selectedChartCategory = '';
  private lastSelectedLayoutType = this.selectedLayoutType;
  entityOptions: string[] = [];
  entitiesLoading = false;
  selectedDatasets: string[] = [];

  title = '';
  componentId = this.createComponentId();
  dashboardId: number | null = null;
  componentType = 'CHART';

  // ── Phase B: sequential step builder (WATERFALL) ──────────────────────────
  steps: BridgeStep[] = [];

  // ── Drill-down builder (DRILL_DOWN_TABLE) — single dataset, N dimensions, N measures.
  drillDownDimensions: DrillDownDimension[] = [];
  drillDownMeasures: DrillDownMeasure[] = [];
  /** `data` input for the shared <lib-drill-down> widget — null until the
   *  user clicks Generate; the widget self-fetches via ConfigService.getConfig
   *  using apiUrl/customPayload/skipDefaultPayload once mounted. */
  drillDownWidgetData: Record<string, unknown> | null = null;

  // ── Table & Matrix (TABLE_MATRIX) — one unified family, config-driven type.
  //    `tmType` selects Normal / Editable / Drill-Down (flags from config).
  //    Normal/Editable pick columns (empty = all); Drill-Down REUSES the
  //    drillDownDimensions/drillDownMeasures state above.
  tmType = 'normal';
  /** Shown under the Table type picker after a restored legacy-pivot tile was
   *  auto-converted to a Drill-Down Table (see legacyPivotToDrilldown). */
  tmMigrationNotice = '';
  /** Columns chosen for Normal/Editable tables (empty = show every column of
   *  the primary dataset). Each row picks its OWN dataset (defaults to the
   *  primary one) + field + display name — the payload sends them as
   *  `{dataset, field, displayName}` objects so columns can mix datasets. */
  tmColumns: TmColumnDef[] = [];
  /** Field-name view of `tmColumns` — kept for every consumer that only needs
   *  the flat field list (tree payload fallback, reorder sync, drop dedup). */
  get tmColumnFields(): string[] {
    return this.tmColumns.map((column) => column.field).filter(Boolean);
  }
  // ── Header view toggle (two pre-configured views in one table) ──────────────
  /** Show a two-option toggle in the table header. Option 1 renders the table
   *  exactly as configured above; option 2 renders `tmToggleColumns` from
   *  `tmToggleDataset`, fetched with its OWN compute payload on switch. */
  tmToggleEnabled = false;
  tmToggleLabel1 = '';
  tmToggleLabel2 = '';
  /** Dataset for the second view (blank = the table's primary dataset). */
  tmToggleDataset = '';
  /** Columns for the second view — the SAME per-column config as `tmColumns`
   *  (dataset, display name, editable + control, alignment, hidden, action
   *  columns, default filters), edited through the same column-card template. */
  tmToggleColumns: TmColumnDef[] = [];
  /** True when `column` belongs to the second view's list. Per-column handlers
   *  are shared between both lists, so they resolve the owning list from the
   *  column itself rather than taking a flag from every call site. */
  private isTmToggleColumn(column: TmColumnDef): boolean {
    return this.tmToggleColumns.some((item) => item.id === column.id);
  }
  /** Re-seat the array a column belongs to so Angular re-renders it (the column
   *  objects themselves are mutated in place by the per-column handlers). */
  private touchTmColumns(column: TmColumnDef): void {
    if (this.isTmToggleColumn(column)) this.tmToggleColumns = [...this.tmToggleColumns];
    else this.tmColumns = [...this.tmColumns];
  }
  /** The dataset a column defaults to: the second view's own, else the primary. */
  private tmDatasetFor(column: TmColumnDef): string {
    return column.dataset || (this.isTmToggleColumn(column) ? this.tmToggleDatasetEffective : this.drillDataset);
  }
  /** Column to group Normal-table rows by (subtotals per group). */
  tmGroupBy = '';
  /** Row/group roll-up aggregation for Normal tables. */
  tmAggregation = 'none';
  // ── New shape types (Matrix/Pivot/Grouped/Tree Grid/Financial) ──────────────
  tmRowField = '';           // Matrix row dimension
  tmColumnField = '';        // Matrix column dimension
  tmValueField = '';         // Matrix/Pivot measure
  tmValueFields: string[] = [];  // Pivot MULTI-measure (each fans out per column value)
  tmRowFields: string[] = [];    // Pivot row fields
  tmColumnFields2: string[] = []; // Pivot column fields
  // Pivot column-VALUE order (comma/newline-separated band names, e.g.
  // "Mining, Marine, Total") — otherwise the bands render in first-seen data
  // order, which is why a pre-aggregated "Total" value often lands first.
  tmPivotColumnOrderText = '';
  /** Alphabetical fallback for whatever `tmPivotColumnOrderText` doesn't pin —
   *  same "Sort" control as the Drill-Down row/column dimensions. */
  tmPivotColumnOrderSort: '' | 'custom' | 'alpha-asc' | 'alpha-desc' = '';
  tmGroupByFields: string[] = []; // Grouped: ordered group levels
  tmGroupsExpanded = true;
  tmTreeIdField = '';        // Tree Grid: row id column
  tmTreeParentField = '';    // Tree Grid: parent id column
  tmRoleField = '';          // Financial: row role column
  tmIndentField = '';        // Financial: indent column
  /** `data` input for <app-unified-table-matrix-chart> — null until Generate. */
  tableMatrixWidgetData: Record<string, unknown> | null = null;

  // ── Merged Drill-Down + Pivot (Table & Matrix drill-down type) ─────────────
  /** How the drill-down tree is computed: 'client' = flat rows (cross-dataset
   *  flat contract) → hierarchy / pivot columns / formulas built in the browser;
   *  'server' = the legacy backend dimensions+measures tree. New tiles default
   *  to client; tiles saved before the merge restore as server so they keep
   *  behaving exactly as published. */
  drillDataMode: 'client' | 'server' = 'client';
  /** Drill-Down "Totals" (build form): bottom grand-total row and/or the
   *  right-hand Total block across the pivot column values. Default both. */
  drillTotalsPlacement: DrillTotalsPlacement = 'both';
  /** Pivot column dimension(s): every measure fans out per distinct value
   *  ("2024 Net Sales | 2025 Net Sales | …"). Same card model as the levels. */
  drillColumnDimensions: DrillDownDimension[] = [];
  /** Formula columns evaluated per tree node on its aggregated measures. */
  drillComputedColumns: DrillComputedColumn[] = [];
  /** Drill-Down "Column order" (client-side tree): explicit order of the BASE
   *  output columns — a measure's FIELD (rename-proof) or a formula's name.
   *  Empty = default (measure order + formula "placed after" anchors). */
  drillColumnOrder: string[] = [];
  drillColumnOrderAccordionOpen = false;
  private drillColumnOrderMemo?: { sig: string; items: DrillColumnOrderItem[] };
  newDrillColumnName = '';
  newDrillColumnExpression = '';
  /** "Place after" anchor for the formula being added (measure label / field
   *  or an earlier formula name; blank = after the last measure). */
  newDrillColumnAfter = '';
  newDrillColumnError = '';
  drillFormulaAccordionOpen = false;
  /** Builder-only: the flat rows a client-mode drill-down tile was generated
   *  from, keyed by tile id, so label / value-type / split / formula edits
   *  re-shape the table LIVE without another compute call. Never published
   *  (the tile keeps only the built tree). */
  private readonly drillRowsCache = new Map<string, Array<Record<string, unknown>>>();
  /** Client-mode drill-down tiles whose flat source rows are being fetched for
   *  the live reshape (see fetchRowsForEditingDrillTile). One request per tile. */
  private readonly drillRowsFetching = new Set<string>();

  // ── Table & Matrix navigation (cell → target dashboard, filtered) ──────────
  tmNavEnabled = false;
  /** Column whose clicked value is sent as the filter (defaults to first column). */
  tmNavFilterKey = '';
  /** Columns whose cells are clickable (empty = the filterKey column only). */
  tmNavColumns: string[] = [];
  /** Target published dashboard slug. */
  tmNavTargetDashboard = '';
  /** Target component ids on that dashboard (empty = all charts). */
  tmNavTargetComponents: string[] = [];
  /** Navigate button label. */
  tmNavButtonLabel = 'Navigate';
  /** Whether the Navigation accordion is open in the form. */
  tmNavAccordionOpen = false;

  // ── Notes under any chart/table (free text OUTSIDE the card, below it) ─────
  tmNotesEnabled = false;
  /** Bold lead line, e.g. "Note: Price increase modelled at:". */
  tmNotesHeading = '';
  /** Textarea content — ONE rendered note line per text line. */
  tmNotesText = '';
  tmNotesListStyle = 'bullet';
  /** 'below' (default) or 'above' the card — outside it either way. */
  tmNotesPosition = 'below';
  /** Whether the Notes accordion is open in the form. */
  tmNotesAccordionOpen = false;
  /** Published dashboards fetched from the API (same list the sidebar shows) —
   *  the authoritative target-dashboard source (localStorage may be cold). Each:
   *  {label:name, value:slug, layoutJson} so we can also list its components. */
  private apiTargetDashboards: Array<{ label: string; value: string; layoutJson: Record<string, unknown> }> = [];

  // ── Horizontal Bar builder (HORIZONTAL_BAR_CHART) — single dataset, one
  //    group-by field on the Y-axis, one formula-driven value on the X-axis.
  hbGroupByField = '';
  hbGroupByLabel = '';
  hbValueLabel = 'Value';
  hbValueFormula = '';
  /** Per-group formula overrides (backend: `value.groupFormulas`) - when two
   *  groups genuinely compute their value differently (e.g. "Base Options"
   *  folds in SSA $ while "Additional Options" folds in Surcharge instead),
   *  rather than one formula applying uniformly to every group. A group whose
   *  value isn't listed here falls back to `hbValueFormula`. */
  hbGroupFormulas: Array<{ group: string; formula: string }> = [];
  /** `data` input for the shared <app-horizontal-bar-chart> — null until the
   *  user clicks Generate; set with the POST response's rows as staticData. */
  hbWidgetData: Record<string, unknown> | null = null;

  addHbGroupFormula(): void {
    this.hbGroupFormulas = [...this.hbGroupFormulas, { group: '', formula: '' }];
  }

  removeHbGroupFormula(entry: { group: string; formula: string }): void {
    this.hbGroupFormulas = this.hbGroupFormulas.filter((item) => item !== entry);
  }

  // ── KPI card (KPI) — single dataset, one column aggregated to a single value.
  kpiField = '';
  kpiAggregation = 'SUM';

  // ── Bar & Column (BAR) — one dataset, one category, N measures, optional
  //    series split. Orientation toggle + variant picker decide how it renders.
  barOrientation: BarColumnOrientation = 'vertical';
  barVariant: BarColumnVariant = 'simple';
  barCategory = '';
  /** Optional outer grouping column → Hierarchical Category Axis (parent → child). */
  barCategoryParent = '';
  barCategoryLabel = '';
  /** Per-axis label font weights picked in the build form ('' = default —
   *  falls back to the Format-popup tile-wide "Axis label weight"). */
  barXAxisFontWeight = '';
  barYAxisFontWeight = '';
  barSecondaryAxisFontWeight = '';
  barSeries = '';
  barSeriesOrder = '';

  // ── Bar value formula (BACKEND-computed, unlike Formula Builder columns —
  //    these can reference a live input via [Token] + references). ───────────
  /** Blank → falls back to `SUM(<first measure>)`. */
  barValueFormula = '';
  barValueLabel = '';
  /** Per-category overrides; unlisted categories use `barValueFormula`. */
  barGroupFormulas: Array<{ group: string; formula: string }> = [];

  addBarGroupFormula(): void {
    this.barGroupFormulas = [...this.barGroupFormulas, { group: '', formula: '' }];
  }

  removeBarGroupFormula(entry: { group: string; formula: string }): void {
    this.barGroupFormulas = this.barGroupFormulas.filter((item) => item !== entry);
  }

  get barUsesValueFormula(): boolean {
    return !!this.barValueFormula.trim()
      || this.barGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
  }

  /** Reference / target lines drawn across the value axis (value + label + color). */
  barReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private barRefLineIdCounter = 0;
  barMeasures: BarBuilderMeasure[] = [];
  /** Optional column mapped onto the SECONDARY value axis. A bar chart has one
   *  category axis + up to two value axes, so only the value-side secondary is
   *  real: Secondary Y is live when Vertical, Secondary X when Horizontal. The
   *  active one is folded into capabilities as an extra measure on the y2/x2 axis. */
  barSecondaryXField = '';
  barSecondaryYField = '';
  private barMeasureIdCounter = 0;

  // ── Line & Area (LINE_AREA) — one dataset, one category (X), N measures, optional
  //    series split. Variant picker decides line/step/spline/area/stacked rendering.
  //    Reuses `barFormat` (the shared Format-accordion bag) like KPI/HBAR/Table do.
  laVariant: LineAreaVariant = 'line';
  laCategory = '';
  laCategoryDataset = '';
  /** Kept optional for a future Hierarchical Category Axis (phase 1: unused). */
  laCategoryParent = '';
  laCategoryLabel = '';
  /** Axis TITLE overrides typed in the Chart Data accordion. Empty = auto
   *  (measure name / secondary-X column name). The Format-accordion Axis
   *  titles (barFormat.xAxisLabel/…) still win when set there. */
  laYAxisLabel = '';
  laSecondaryYLabel = '';
  laShowSecondaryYAxis = true;
  laSecondaryYDataset = '';
  laSecondaryXLabel = '';
  /** Per-axis label font weights picked in the build form ('' = default —
   *  falls back to the Format-popup tile-wide "Axis label weight"). */
  laXAxisFontWeight = '';
  laYAxisFontWeight = '';
  laSecondaryXFontWeight = '';
  laSecondaryYFontWeight = '';
  laSeries = '';
  laSeriesOrder = '';
  laSeriesDataset = '';
  /** Reference / target lines drawn across the value axis (value + label + color). */
  laReferenceLines: Array<{ id: string; value: number | null; label: string; color: string }> = [];
  private laRefLineIdCounter = 0;
  laMeasures: LineAreaBuilderMeasure[] = [];
  /** Optional column mapped onto the SECONDARY (y2) value axis. Folded into
   *  capabilities as an extra measure {renderAs:'line', axis:'y2'}. */
  laSecondaryYField = '';
  /** Optional column mapped onto a SECONDARY X axis — its own value series
   *  plotted against a second x-axis (capabilities.secondaryXField/…YForX). */
  laSecondaryXField = '';
  laSecondaryXDataset = '';
  /** The value column plotted against the secondary X axis (defaults to the
   *  first measure when a secondary X column is chosen but no value picked). */
  laSecondaryXValueField = '';
  laSecondaryXValueDataset = '';
  /** Where the secondary X axis renders: stacked below the primary axis
   *  (default) or across the top of the chart. */
  laSecondaryXPosition: 'top' | 'bottom' = 'bottom';
  /** Optional line-chart API override. Blank URL keeps the existing dynamic
   * dataset source. The payload editor accepts the complete request envelope. */
  laApiUrl = '';
  laApiPayload = '';
  laApiResponsePath = '';
  /** Stable options array for the secondary-X position ng-select (a fresh array
   *  per CD cycle breaks appendTo="body" panels — see barColumns memoization). */
  readonly laSecondaryXPositions: Array<{ label: string; value: 'top' | 'bottom' }> = [
    { label: 'Below primary X-axis', value: 'bottom' },
    { label: 'Top of chart', value: 'top' },
  ];
  private laMeasureIdCounter = 0;

  onLaAxisDatasetChange(axis: 'category' | 'series' | 'secondaryX' | 'secondaryXValue' | 'secondaryY', value: string | null): void {
    const dataset = String(value || '');
    if (axis === 'category') this.laCategoryDataset = dataset;
    if (axis === 'series') this.laSeriesDataset = dataset;
    if (axis === 'secondaryX') this.laSecondaryXDataset = dataset;
    if (axis === 'secondaryXValue') this.laSecondaryXValueDataset = dataset;
    if (axis === 'secondaryY') this.laSecondaryYDataset = dataset;
    if (dataset) this.loadColumnsForDataset(dataset);
    this.onLineAreaChange();
  }

  onLaMeasureDatasetChange(measure: LineAreaBuilderMeasure, value: string | null): void {
    measure.dataset = String(value || '');
    if (measure.dataset) this.loadColumnsForDataset(measure.dataset);
    this.onLineAreaChange();
  }

  laColumnsForDataset(dataset: string): string[] {
    return this.columnsForRow(`line-area-${dataset}`, dataset || this.drillDataset);
  }

  /** Bar chart's Title/Axis/Tooltip/Formatting/Advanced options, chosen in the
   *  Build form (keyed by FormatControl.key) — folded into the capabilities on
   *  Generate. Same control config the tile Format popup uses, rendered inline. */
  barFormat: Record<string, unknown> = {};
  /** Which Build-form format accordions are expanded (by group name). */
  openBarFormatGroups = new Set<string>();
  /** The bespoke "Chart Data" accordion (axis/measure mapping) — open by default. */
  barDataAccordionOpen = true;
  /** dataset name → full rows, cached so the BAR chart aggregates client-side
   *  without re-fetching (getEntityRows already returns full row objects). */
  private entityRowsCache = new Map<string, Array<Record<string, unknown>>>();

  // ── Scatter & Bubble (SCATTER_BUBBLE) — one dataset; X + Y measures, optional
  //    Size (bubble), Color-by, and Series (multi-scatter). Variant picker chooses
  //    Scatter / Multi Scatter / Bubble / Quadrant. Renders client-side.
  /** Active side of the Orientation segmented toggle (a `scatterBubbleModes`
   *  value) — filters the Variant dropdown. Not persisted: derived from the
   *  saved variant on rehydrate. */
  sbMode = 'scatter';
  /** Advanced analytic overlay applied on top of the base variant ('' = none).
   *  Persisted in capabilities as `advanced`; flags OR into variantFlags. */
  sbAdvanced = '';
  /** Advanced-overlays accordion (Quadrant / Regression / …) expanded? */
  sbAdvancedAccordionOpen = false;
  sbVariant: ScatterBubbleVariant = 'scatter';
  sbXField = '';
  sbYField = '';
  sbSizeField = '';
  /** Per-field query aggregation (SUM/AVG/COUNT/…) sent to the backend for
   *  the X / Y / Size measure columns — independent of `sbAggregation`
   *  below, which only controls client-side rollup of points sharing X. */
  sbXAggregation = 'NONE';
  sbYAggregation = 'NONE';
  sbSizeAggregation = 'NONE';
  sbColorField = '';
  /** Column drawn as each point's text label (entity name); '' = none. */
  sbLabelField = '';
  /** Explicit color per Color-by value (value → hex). Unpicked values keep
   *  their palette slot — the renderer applies the same fallback. */
  sbCategoryColors: Record<string, string> = {};
  sbSeries = '';
  /** Row rollup for points sharing X + group ('none' = plot every raw row). */
  sbAggregation = 'none';
  /** Extra dataset columns appended to the hover tooltip. */
  sbTooltipColumns: string[] = [];
  sbXLabel = '';
  sbYLabel = '';
  /** Per-axis label font weights picked in the build form ('' = default —
   *  falls back to the Format-popup tile-wide "Axis label weight"). */
  sbXAxisFontWeight = '';
  sbYAxisFontWeight = '';
  /** Scatter/Bubble Title/Axis/…/Advanced options chosen in the Build form. */
  sbFormat: Record<string, unknown> = {};
  /** Which Build-form format accordions are expanded (by group name). */
  openSbFormatGroups = new Set<string>();
  /** The bespoke "Chart Data" accordion (X/Y/Size/Color/Series mapping). */
  sbDataAccordionOpen = true;

  // ── Formula Builder (BAR) — row-level COMPUTED columns derived from the
  //    dataset's columns (e.g. "Margin = (profit / net_sales) * 100"). Reuses the
  //    generate-visualization formula engine (SelfServiceFormulaService). Each
  //    computed column is applied to the dataset rows before the chart aggregates,
  //    and its name appears in the Category/Measure/Split pickers like any column.
  barComputedColumns: ComputedColumn[] = [];
  /** New-formula draft (the "Add column" inputs). */
  newBarColumnName = '';
  newBarColumnExpression = '';
  newBarColumnError = '';
  /** The Formula Builder sub-accordion (inside Chart Data) — collapsed by default. */
  barFormulaAccordionOpen = false;
  /** Optional formula-driven value (e.g. "[price] * [qty]") — when set, the
   *  compute API uses it instead of the plain Column + Aggregation pair, same
   *  contract as the horizontal bar chart's `hbValueFormula`. */
  kpiFormula = '';

  // ── Dropdown control (DROPDOWN) — distinct values from one dataset column
  //    are exposed as options and the selected value filters the chosen targets.
  dropdownField = '';
  dropdownDisplayLabel = '';
  dropdownIsMultiSelect = false;
  dropdownShowAllOption = false;
  dropdownAutoSelectFirst = false;
  dropdownDefaultValue = '';
  dropdownOptionOrderMode: 'response' | 'asc' | 'desc' | 'custom' = 'response';
  dropdownCustomOptionOrder = '';
  /** The Advanced (option ordering) accordion — collapsed by default; opened
   *  by its toggle, or automatically when a reopened tile uses a non-default
   *  order (so the saved setting isn't hidden). */
  dropdownAdvancedAccordionOpen = false;
  /** How the control renders: 'dropdown' (classic select) | 'buttons' | 'tiles'
   *  (every option an always-visible filter button). Rides the payload +
   *  widgetData so the published view needs no extra wiring. */
  dropdownDisplayMode = 'dropdown';
  /** "Display as" items (config `dropdownDisplayModes`, built-in fallback).
   *  MEMOIZED — a fresh [items] array each CD cycle breaks ng-select
   *  appendTo:'body' (panel rebuilds mid-click and swallows the selection). */
  private dropdownDisplayModesCache: LabelValueOption[] = [];
  private dropdownDisplayModesKey: unknown = undefined;
  get dropdownDisplayModes(): LabelValueOption[] {
    const fromConfig = (this.layout.options as Record<string, unknown> | undefined)?.['dropdownDisplayModes'] as LabelValueOption[] | undefined;
    const key = Array.isArray(fromConfig) && fromConfig.length ? fromConfig : 'fallback';
    if (key !== this.dropdownDisplayModesKey) {
      this.dropdownDisplayModesCache = key === 'fallback'
        ? [
            { label: 'Dropdown', value: 'dropdown' },
            { label: 'Buttons', value: 'buttons' },
            { label: 'Tiles', value: 'tiles' },
          ]
        : (fromConfig as LabelValueOption[]);
      this.dropdownDisplayModesKey = key;
    }
    return this.dropdownDisplayModesCache;
  }

  /** Submenu groups (buttons/tiles mode): options nested under a parent pill
   *  (e.g. MARI + MXDR inside Mining). Rides the payload + widgetData as
   *  `optionGroups`; app-global-dropdown validates labels/children against
   *  the LIVE backend options at render time, so stale values just drop.
   *  `displayLabel` is DISPLAY-ONLY pill text (falls back to `label`) —
   *  matching/anchoring always keys off `label`, the actual option value. */
  dropdownGroups: Array<{ label: string; displayLabel: string; children: string[]; showAll: boolean }> = [];
  /** addTag resolver for the group pickers — authors can type values that
   *  aren't in the cached dataset rows yet (options load from the backend at
   *  render time, so the cache can lag). Bound once: a fresh function ref per
   *  CD cycle would rebuild the appendTo:'body' panel mid-click. */
  dropdownGroupTag = (term: string) => term.trim();

  /** Config default for the "All <group>" tray entry (input-control.json
   *  `options.dropdownGroupShowAllDefault`, true when absent): seeds each NEW
   *  group's checkbox, and is ENFORCED for every group when the config hides
   *  the checkbox (no `groupShowAll` field in the dropdown section). */
  get dropdownGroupShowAllDefault(): boolean {
    return (this.layout.options as Record<string, unknown> | undefined)?.['dropdownGroupShowAllDefault'] !== false;
  }

  addDropdownGroup(): void {
    this.dropdownGroups = [...this.dropdownGroups, { label: '', displayLabel: '', children: [], showAll: this.dropdownGroupShowAllDefault }];
  }

  removeDropdownGroup(index: number): void {
    this.dropdownGroups = this.dropdownGroups.filter((_, i) => i !== index);
  }

  /** Candidate values for the group pickers: distinct values of the chosen
   *  option field from the cached dataset rows. MEMOIZED for ng-select
   *  appendTo:'body' (same reason as dropdownDisplayModes). Empty until the
   *  dataset rows are cached — typing via addTag still works then. */
  private dropdownFieldOptionsCache: string[] = [];
  private dropdownFieldOptionsKey = '';
  get dropdownFieldOptions(): string[] {
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    const key = `${this.drillDataset}|${this.dropdownField}|${rows.length}`;
    if (key !== this.dropdownFieldOptionsKey) {
      const seen = new Set<string>();
      if (this.dropdownField) {
        rows.forEach((row) => {
          const value = row?.[this.dropdownField];
          if (value !== null && value !== undefined && String(value).trim() !== '') seen.add(String(value));
        });
      }
      this.dropdownFieldOptionsCache = [...seen];
      this.dropdownFieldOptionsKey = key;
    }
    return this.dropdownFieldOptionsCache;
  }

  /** Groups as persisted: trimmed, empty rows dropped. `showAll` controls the
   *  "All <group>" entry inside the tray — the per-group checkbox value when
   *  the config exposes the checkbox, else the config default (three-tier
   *  chain: JSON default → per-group override → render-time validation). */
  private sanitizedDropdownGroups(): Array<{ label: string; displayLabel: string; children: string[]; showAll: boolean }> {
    const checkboxExposed = this.fieldEnabled('dropdown', 'groupShowAll');
    return this.dropdownGroups
      .map((group) => ({
        label: String(group?.label ?? '').trim(),
        displayLabel: String(group?.displayLabel ?? '').trim(),
        children: Array.isArray(group?.children)
          ? group.children.map((child) => String(child).trim()).filter(Boolean)
          : [],
        showAll: checkboxExposed ? group?.showAll !== false : this.dropdownGroupShowAllDefault,
      }))
      .filter((group) => group.label && group.children.length);
  }

  get dropdownOptionOrderModes(): LabelValueOption[] {
    const fromConfig = (this.layout.options as Record<string, unknown> | undefined)?.['dropdownOptionOrderModes'] as LabelValueOption[] | undefined;
    return Array.isArray(fromConfig) && fromConfig.length
      ? fromConfig
      : [
          { label: 'Backend response order', value: 'response' },
          { label: 'Ascending', value: 'asc' },
          { label: 'Descending', value: 'desc' },
          { label: 'Custom', value: 'custom' },
        ];
  }

  // ── File Upload control (FILE_UPLOAD) — a standalone uploader that POSTs the
  //    picked file to the compute API and re-renders the chosen target charts/
  //    tables. Targets are captured in `selectedTargetCharts` (shared).
  fileUploadButtonLabel = 'Upload file';
  fileUploadAccept = '.xlsx,.xls,.csv';

  // ── Popup component (POPUP) — a trigger (icon/button/tile) that opens a
  //    configurable dialog of content blocks. Static blocks live in the tile's
  //    widgetData; table blocks snapshot rows CLIENT-SIDE from entityRowsCache
  //    at Generate time (no compute call — the popup is display-only).
  popupTriggerMode = 'button';
  popupTriggerIcon = 'info';
  popupTriggerLabel = '';
  popupTriggerHint = '';
  popupSize = 'medium';
  popupPosition = 'center';
  popupCloseLabel = 'Close';
  popupDismissOnBackdrop = true;
  popupTriggerBg = '';
  popupTriggerText = '';
  popupTriggerRadius: number | null = null;
  /** Popup-wide font overrides (trigger + dialog). 0/empty = theme default. */
  popupFontSize: number | null = null;
  popupFontFamily = '';
  /** Dialog title-bar colors (the OPENED popup's own header strip). */
  popupHeaderBg = '';
  popupHeaderText = '';
  /** The TILE CARD's header colors — same cardHeaderBackground/cardHeaderColor
   *  keys every other chart tile uses (stored on tile.format). */
  popupCardHeaderBg = '';
  popupCardHeaderText = '';
  /** Dialog body + action-button styling (CSS vars on the opened dialog). */
  popupBodyBg = '';
  popupBodyText = '';
  popupButtonBg = '';
  popupButtonText = '';
  popupButtonRadius: number | null = null;
  popupRadius: number | null = null;
  popupFormatAccordionOpen = false;
  popupBlocks: PopupBlockForm[] = [];
  private popupBlockIdCounter = 0;

  private static readonly POPUP_FONT_FAMILIES_FALLBACK: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Segoe UI', value: "'Segoe UI', Tahoma, sans-serif" },
    { label: 'Roboto', value: "Roboto, 'Segoe UI', sans-serif" },
    { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
    { label: 'Courier New', value: "'Courier New', Courier, monospace" },
  ];
  /** The SHARED fontFamilies option list (same one the Format popup uses). */
  get popupFontFamilies(): LabelValueOption[] {
    return this.popupOptionsList('fontFamilies', DvmLayoutBuilderComponent.POPUP_FONT_FAMILIES_FALLBACK);
  }

  private static readonly POPUP_TRIGGER_MODES_FALLBACK: LabelValueOption[] = [
    { label: 'Icon click', value: 'icon' },
    { label: 'Button click', value: 'button' },
    { label: 'Tile click', value: 'tile' },
  ];
  private static readonly POPUP_BLOCK_TYPES_FALLBACK: LabelValueOption[] = [
    { label: 'Heading', value: 'heading' },
    { label: 'Text', value: 'text' },
    { label: 'Key-value rows', value: 'keyValue' },
    { label: 'Data table', value: 'table' },
    { label: 'Buttons', value: 'buttons' },
    { label: 'Divider', value: 'divider' },
  ];
  private static readonly POPUP_SIZES_FALLBACK: LabelValueOption[] = [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Fullscreen', value: 'fullscreen' },
  ];
  private static readonly POPUP_POSITIONS_FALLBACK: LabelValueOption[] = [
    { label: 'Center', value: 'center' },
    { label: 'Right drawer', value: 'drawer' },
  ];
  private static readonly POPUP_HEADING_LEVELS_FALLBACK: LabelValueOption[] = [
    { label: 'Large (H1)', value: 'h1' },
    { label: 'Medium (H2)', value: 'h2' },
    { label: 'Small (H3)', value: 'h3' },
  ];
  private static readonly POPUP_BUTTON_ACTIONS_FALLBACK: LabelValueOption[] = [
    { label: 'Close popup', value: 'close' },
    { label: 'Open link', value: 'link' },
    { label: 'Go to dashboard', value: 'navigate' },
  ];
  private static readonly POPUP_BUTTON_STYLES_FALLBACK: LabelValueOption[] = [
    { label: 'Primary', value: 'primary' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Link', value: 'link' },
  ];
  private static readonly POPUP_ICONS_FALLBACK: LabelValueOption[] = [
    { label: 'Info', value: 'info' },
    { label: 'Help', value: 'help' },
    { label: 'History / audit', value: 'history' },
    { label: 'Description', value: 'description' },
    { label: 'Table', value: 'table_chart' },
    { label: 'Warning', value: 'warning' },
    { label: 'None', value: '' },
  ];

  /** Config-driven option lists for the popup form. Each getter returns the
   *  SAME array reference on every call (the config object's own array, or a
   *  static fallback) so ng-select's [items] never churns mid-click — see the
   *  memoized `targetCharts` getter for why that matters. */
  private popupOptionsList(key: string, fallback: LabelValueOption[]): LabelValueOption[] {
    const fromConfig = (this.layout.options as Record<string, unknown> | undefined)?.[key] as LabelValueOption[] | undefined;
    return Array.isArray(fromConfig) && fromConfig.length ? fromConfig : fallback;
  }
  get popupTriggerModes(): LabelValueOption[] {
    return this.popupOptionsList('popupTriggerModes', DvmLayoutBuilderComponent.POPUP_TRIGGER_MODES_FALLBACK);
  }
  get popupBlockTypes(): LabelValueOption[] {
    return this.popupOptionsList('popupBlockTypes', DvmLayoutBuilderComponent.POPUP_BLOCK_TYPES_FALLBACK);
  }
  get popupSizes(): LabelValueOption[] {
    return this.popupOptionsList('popupSizes', DvmLayoutBuilderComponent.POPUP_SIZES_FALLBACK);
  }
  get popupPositions(): LabelValueOption[] {
    return this.popupOptionsList('popupPositions', DvmLayoutBuilderComponent.POPUP_POSITIONS_FALLBACK);
  }
  get popupHeadingLevels(): LabelValueOption[] {
    return this.popupOptionsList('popupHeadingLevels', DvmLayoutBuilderComponent.POPUP_HEADING_LEVELS_FALLBACK);
  }
  get popupButtonActions(): LabelValueOption[] {
    return this.popupOptionsList('popupButtonActions', DvmLayoutBuilderComponent.POPUP_BUTTON_ACTIONS_FALLBACK);
  }
  get popupButtonStyles(): LabelValueOption[] {
    return this.popupOptionsList('popupButtonStyles', DvmLayoutBuilderComponent.POPUP_BUTTON_STYLES_FALLBACK);
  }
  get popupIcons(): LabelValueOption[] {
    return this.popupOptionsList('popupIcons', DvmLayoutBuilderComponent.POPUP_ICONS_FALLBACK);
  }
  private static readonly POPUP_API_METHODS_FALLBACK: LabelValueOption[] = [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
  ];
  get popupApiMethods(): LabelValueOption[] {
    return this.popupOptionsList('popupApiMethods', DvmLayoutBuilderComponent.POPUP_API_METHODS_FALLBACK);
  }
  private static readonly POPUP_VALUE_COLORS_FALLBACK: LabelValueOption[] = [
    { label: 'Default', value: '' },
    { label: 'Red', value: 'red' },
    { label: 'Green', value: 'green' },
    { label: 'Blue', value: 'blue' },
    { label: 'Orange', value: 'orange' },
  ];
  get popupValueColors(): LabelValueOption[] {
    return this.popupOptionsList('popupValueColors', DvmLayoutBuilderComponent.POPUP_VALUE_COLORS_FALLBACK);
  }

  private newPopupBlock(type: PopupBlockForm['type'] = 'text'): PopupBlockForm {
    this.popupBlockIdCounter += 1;
    return {
      id: this.popupBlockIdCounter,
      type,
      text: '',
      level: 'h2',
      align: 'left',
      pairs: [{ label: '', value: '' }],
      dataset: '',
      columns: [],
      maxRows: null,
      url: '',
      method: 'GET',
      body: '',
      rowsPath: '',
      groupsPath: '',
      groupRowsField: '',
      metaFields: [{ field: '', label: '' }],
      apiColumns: [{ field: '', label: '', color: '' }],
      refreshOnOpen: false,
      buttons: [{ label: '', action: 'close', url: '', targetDashboard: '', style: 'primary' }],
    };
  }
  addPopupBlock(): void {
    this.popupBlocks = [...this.popupBlocks, this.newPopupBlock()];
    this.onPopupFormChange();
  }
  removePopupBlock(index: number): void {
    this.popupBlocks = this.popupBlocks.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  movePopupBlock(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= this.popupBlocks.length) return;
    const next = [...this.popupBlocks];
    [next[index], next[target]] = [next[target], next[index]];
    this.popupBlocks = next;
    this.onPopupFormChange();
  }
  addPopupPair(block: PopupBlockForm): void {
    block.pairs = [...block.pairs, { label: '', value: '' }];
    this.onPopupFormChange();
  }
  removePopupPair(block: PopupBlockForm, index: number): void {
    block.pairs = block.pairs.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  addPopupButton(block: PopupBlockForm): void {
    block.buttons = [...block.buttons, { label: '', action: 'close', url: '', targetDashboard: '', style: 'primary' }];
    this.onPopupFormChange();
  }
  removePopupButton(block: PopupBlockForm, index: number): void {
    block.buttons = block.buttons.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  /** A table block picked its dataset — fetch its columns + rows (client-side
   *  snapshot source), same cache every other layout uses. */
  onPopupBlockDatasetChange(block: PopupBlockForm): void {
    block.columns = [];
    if (block.dataset) this.loadColumnsForDataset(block.dataset);
    this.onPopupFormChange();
  }

  /** LIVE refresh — every popup form control calls this on change, so the
   *  canvas reflects edits immediately (no Generate click needed). Mirrors
   *  refreshEditingBarTile: the tile being edited updates in place; while
   *  BUILDING a new popup, a draft preview tile is created/updated (and
   *  dropped again if the form loses its content), which Generate finalizes. */
  onPopupFormChange(): void {
    if (!this.isPopupLayout) return;
    // Editing a real (already-generated) tile — refresh it live.
    if (this.editingTileId) {
      const tile = this.findTileAnywhere(this.editingTileId);
      if (tile && tile.layoutType === 'POPUP') {
        this.updateTileInPlace({
          ...tile,
          title: this.title.trim() || tile.title,
          popupControl: { widgetData: this.buildPopupWidgetData() },
          format: this.popupCardFormat(),
        });
      }
      return;
    }
    // Not enough config yet → drop any existing draft preview tile.
    if (!this.title.trim() || !this.popupBlocks.some((block) => this.popupBlockHasContent(block))) {
      this.removePreviewTile();
      return;
    }
    // Create the draft preview tile on first eligibility, else update it.
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const draft = this.buildPopupTile(this.buildPayload());
      this.previewTileId = draft.id;
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const existing = this.dashboardTiles[existingIndex];
      const updated = [...this.dashboardTiles];
      updated[existingIndex] = {
        ...existing,
        title: this.title.trim() || 'Popup',
        payload: this.buildPayload(),
        popupControl: { widgetData: this.buildPopupWidgetData() },
        format: this.popupCardFormat(),
      };
      this.dashboardTiles = updated;
    }
  }

  /** The popup's Formatting-accordion state as the flat top-level `format`
   *  payload key — the same wire shape headerFormatPayload gives the charts.
   *  Empty/unset values are omitted; {} when nothing is set at all. */
  private popupFormatPayload(): Record<string, unknown> {
    const format = Object.fromEntries(
      Object.entries({
        triggerBg: this.popupTriggerBg,
        triggerText: this.popupTriggerText,
        triggerRadius: this.popupTriggerRadius,
        cardHeaderBackground: this.popupCardHeaderBg,
        cardHeaderColor: this.popupCardHeaderText,
        headerBg: this.popupHeaderBg,
        headerText: this.popupHeaderText,
        bodyBg: this.popupBodyBg,
        bodyText: this.popupBodyText,
        buttonBg: this.popupButtonBg,
        buttonText: this.popupButtonText,
        buttonRadius: this.popupButtonRadius,
        popupRadius: this.popupRadius,
        fontSize: this.popupFontSize,
        fontFamily: this.popupFontFamily,
      }).filter(([, value]) => value !== '' && value != null),
    );
    return Object.keys(format).length ? { format } : {};
  }

  /** The popup tile's card-header format (same keys as the chart tiles), or
   *  undefined when no card-header color is set. */
  private popupCardFormat(): Record<string, unknown> | undefined {
    if (!this.popupCardHeaderBg && !this.popupCardHeaderText) return undefined;
    return {
      ...(this.popupCardHeaderBg ? { cardHeaderBackground: this.popupCardHeaderBg } : {}),
      ...(this.popupCardHeaderText ? { cardHeaderColor: this.popupCardHeaderText } : {}),
    };
  }
  /** Column options for a table block's picker — the cached array reference. */
  popupBlockColumnOptions(block: PopupBlockForm): string[] {
    return block.dataset ? this.entityColumnsCache.get(block.dataset) || [] : [];
  }
  /** True when a block has enough content to be worth rendering. */
  private popupBlockHasContent(block: PopupBlockForm): boolean {
    if (block.type === 'heading' || block.type === 'text') return !!block.text.trim();
    if (block.type === 'keyValue') return block.pairs.some((pair) => pair.label.trim() || pair.value.trim());
    if (block.type === 'table') return !!block.dataset;
    if (block.type === 'apiTable') return !!block.url.trim();
    if (block.type === 'buttons') return block.buttons.some((button) => button.label.trim());
    return true; // divider
  }

  addPopupMetaField(block: PopupBlockForm): void {
    block.metaFields = [...block.metaFields, { field: '', label: '' }];
    this.onPopupFormChange();
  }
  removePopupMetaField(block: PopupBlockForm, index: number): void {
    block.metaFields = block.metaFields.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }
  addPopupApiColumn(block: PopupBlockForm): void {
    block.apiColumns = [...block.apiColumns, { field: '', label: '', color: '' }];
    this.onPopupFormChange();
  }
  removePopupApiColumn(block: PopupBlockForm, index: number): void {
    block.apiColumns = block.apiColumns.filter((_, i) => i !== index);
    this.onPopupFormChange();
  }

  /** A popup `navigate` button was clicked in the builder preview — route to
   *  that published dashboard (same destination the published view uses). */
  onPopupNavigate(slug: string): void {
    if (slug) this.router.navigate(['/dvm-published', slug]);
  }

  // ── Input controls (INPUT_BOX / SLIDER) — a dashboard input whose value feeds
  //    a formula/measure into one or more target charts. Title comes from the
  //    shared `title` field above; the rest is captured here.
  inputFormula = '';
  /** Per-target formula, keyed by the target chart's componentId. The input's
   *  value feeds EACH target's own expression (e.g. a KPI can use `[price]*value`
   *  while a waterfall uses a different one). `value` in the expression is the
   *  control's live value. */
  targetFormulas: Record<string, string> = {};
  /** Per-step formulas for a WATERFALL target, keyed by [target componentId][step
   *  label] — used instead of `targetFormulas` when the target is a waterfall,
   *  since each bar (Start / Change / Total) needs its own independent formula. */
  targetStepFormulas: Record<string, Record<string, string>> = {};
  /** Typed-in references for a target's formula tokens, keyed by target
   *  componentId. Each entry says "this bracket token isn't a plain dataset
   *  column — it's another input's live/recomputed output", so the backend
   *  can resolve the CURRENT value instead of a stale one. User types every
   *  field by hand (no drag-and-drop for this). */
  targetFormulaReferences: Record<string, TargetFormulaReferenceEntry[]> = {};
  /** Default/seed value the control shows on load (slider handle position, or
   *  the input box's pre-filled number). Null = no default. */
  inputDefaultValue: number | null = null;
  /** Target charts (by component id) this input drives — see targetCharts. */
  selectedTargetCharts: string[] = [];
  /** The custom "API Call" config for the input control being built/edited in
   *  the right-side build form (Dropdown/Slider/Input Box/File Upload) — see
   *  InputApiCallConfig. Reset per new tile, restored by loadTileIntoForm when
   *  editing an existing one. */
  buildApiCallConfig: InputApiCallConfig = this.defaultApiCallConfig();

  /** The OPTIONAL Chart-interactions accordion in the build form — collapsed by
   *  default; opened by its toggle, or automatically when editing a tile that
   *  already has targets wired. (Steps/axes sections stay always visible —
   *  those are required.) */
  interactionsSectionOpen = false;
  // Slider-only bounds.
  sliderMin = 0;
  sliderMax = 100;
  sliderStep = 1;
  /** Live value bound to the rendered preview control once generated. */
  inputPreviewValue: number | string | null = null;
  /** True once Generate has run for an input control — drives the preview. */
  inputControlReady = false;

  /** Drill-Down Table AND Horizontal Bar bind to exactly one dataset (unlike
   *  WATERFALL, which can combine several) — the first of the shared multi-select. */
  get drillDataset(): string {
    return this.selectedDatasets[0] || '';
  }
  get drillDatasetColumns(): string[] {
    return this.drillDataset ? this.entityColumnsCache.get(this.drillDataset) || [] : [];
  }
  get isDrillDatasetColumnsLoading(): boolean {
    return this.isDatasetColumnsLoading(this.drillDataset);
  }

  // ── Dashboard preview canvas ───────────────────────────────────────────────
  // The right/left pane is a full dashboard (not a single chart): a named canvas
  // holding N tiles laid out in a fixed 12-column grid (they flow, never overlap,
  // and fit the width). Each "Generate Configuration" appends the built
  // chart/input as a new tile; "New chart" resets the builder form.
  static readonly DASH_COLS = 12;
  static readonly DASH_ROW_PX = 40;
  static readonly DASH_GAP = 12;

  dashboardName = '';
  dashboardTiles: DashboardTile[] = [];
  /** Active dashboard cross-filter published by a bar tile click (null = none).
   *  Listener tiles re-render from their rows filtered by column IN `values`
   *  (multi-select: each bar click toggles a value in/out). */
  activeCrossFilter: { column: string; values: string[]; sourceTileId: string } | null = null;
  /** Which tile's cross-filter "listener editor" panel is expanded (null = none).
   *  Mirrors the dashboard-builder's expandedListenerEditor. */
  expandedListenerTileId: string | null = null;
  /** Tile currently loaded into Bridge steps for editing. Generate replaces it. */
  editingTileId: string | null = null;
  /** Auto-created DRAFT bar tile shown live on the canvas while building, BEFORE
   *  Generate. Generate finalizes it (clears this id → becomes a normal tile). */
  private previewTileId: string | null = null;
  /** "Group" mode: click tiles to select (checkbox appears), then Group them
   *  into one card. Off by default so normal builder use is untouched. */
  groupSelectMode = false;
  selectedTileIds = new Set<string>();
  activeSavedDashboardId = '';
  /** The exact `dashboardId`/URL slug this dashboard was already published under,
   *  read back from the restored layout_json when "Back to Builder" reopens an
   *  existing dashboard. `activeSavedDashboardId` alone isn't enough to survive
   *  that round trip — it's only ever set by a save/publish performed in THIS
   *  component instance, and the route navigation destroys and recreates it, so
   *  without this saveDashboardInternal fell back to matching the local
   *  saved-dashboards cache by name, minted a brand-new slug when that lookup
   *  missed, and republished as an insert — a different row than the one being
   *  edited, discarding the prior layout edits. */
  private restoredDashboardId = '';
  private restoredPublishedLayout: Record<string, unknown> | null = null;
  isSavingDashboard = false;
  /** True once this dashboard is known to already have an owned `dashboard_layouts`
   *  row (found by name match against the locally cached saved dashboards, or by
   *  a resolved uuid from a prior publish this session) — drives the Publish
   *  button's label ("Update" vs "Publish") since a re-save now updates that row
   *  instead of inserting a duplicate. */
  isExistingPublishedDashboard = false;
  /** Full-screen read-only view of the finished dashboard. */
  isDashboardFullscreen = false;
  /** In Preview, toggles the tile edit chrome (drag/resize) back on so the user
   *  can rearrange the layout without leaving the published-style preview. */
  previewEditLayout = false;
  /** Id of the tile whose Format popup is open (null = none). */
  openFormatTileId: string | null = null;
  formatPopupAlign: 'left' | 'right' = 'right';
  /** Id of the tile whose Interactions popup is open (null = none). Lets a
   *  user wire targets/references AFTER other components exist, instead of only
   *  at create time (see toggleTileInteractions). */
  openInteractionTileId: string | null = null;
  private tileIdCounter = 0;
  /** Index of the tile being dragged to reorder (HTML5 DnD), or null. */
  tileDragIndex: number | null = null;
  @ViewChild('dashboardCanvas') private dashboardCanvasRef?: ElementRef<HTMLElement>;
  /** Active corner-resize of a tile (grid-snapped), null when idle. */
  private activeTileInteraction:
    | {
        tile: DashboardTile;
        startX: number;
        startY: number;
        startColSpan: number;
        startRowSpan: number;
      }
    | null = null;

  // ── Generate / preview ─────────────────────────────────────────────────────
  isGenerating = false;
  generateError = '';
  /** True while an input-control recompute POST is in flight — drives the
   *  "Recomputing…" overlay on the dashboard canvas. */
  isRecomputing = false;
  previewOption: echarts.EChartsOption | null = null;

  targetPanelCollapsed = false;
  stepsPanelCollapsed = false;
  previewPanelCollapsed = false;
  dataPanelCollapsed = false;
  paneOrder: BuilderPane[] = ['preview', 'steps', 'data'];
  draggingPane: BuilderPane | null = null;

  targetPaneWidth = 230;
  stepsPaneWidth = 320;
  dataPaneWidth = 320;
  dataPaneFilter = '';
  fieldPaneFilter = '';
  activeExplorerEntity = '';
  explorerColumns: string[] = [];
  explorerColumnsLoading = false;

  private activePaneResize: { pane: 'steps' | 'data'; startX: number; startWidth: number; direction: 1 | -1 } | null = null;

  private stepIdCounter = 0;
  private drillDimensionIdCounter = 0;
  private drillMeasureIdCounter = 0;
  /** dataset name -> column names for that dataset — shared across bridge
   *  steps AND drill-down dimensions/measures, since they all just need
   *  "columns for X dataset". */
  private entityColumnsCache = new Map<string, string[]>();
  private datasetColumnsLoading: Record<string, boolean> = {};
  private datasetColumnsError: Record<string, string> = {};
  // Same identity-churn hazard as the chart-builder's mapping selects: memoize
  // per row id (bridge-step id — the only row kind still needing per-row
  // column lists, since drill-down dimensions/measures all share
  // drillDatasetColumns directly) so ng-select's [items] array reference only
  // changes when the underlying dataset's columns actually change.
  private rowColumnsMemo = new Map<string, { dataset: string; source: string[] | undefined; result: string[] }>();

  get filteredExplorerEntities(): string[] {
    const query = this.dataPaneFilter.trim().toLowerCase();
    return query ? this.entityOptions.filter((entity) => entity.toLowerCase().includes(query)) : this.entityOptions;
  }

  get filteredExplorerColumns(): string[] {
    const query = this.fieldPaneFilter.trim().toLowerCase();
    return query ? this.explorerColumns.filter((column) => column.toLowerCase().includes(query)) : this.explorerColumns;
  }

  get paneGridTemplate(): string {
    return this.paneOrder.map((pane) => this.paneTrack(pane)).join(' 6px ');
  }

  paneGridColumn(pane: BuilderPane): number {
    return this.paneOrder.indexOf(pane) * 2 + 1;
  }

  /** Keeps the collapse affordance correct after panes are reordered. */
  paneCollapseIcon(pane: BuilderPane): 'chevron_left' | 'chevron_right' {
    const sitsOnRight = this.paneOrder.indexOf(pane) >= Math.ceil(this.paneOrder.length / 2);
    const collapsed = this.isPaneCollapsed(pane);
    return sitsOnRight === collapsed ? 'chevron_left' : 'chevron_right';
  }

  onPaneDragStart(pane: BuilderPane, event: DragEvent): void {
    this.draggingPane = pane;
    event.dataTransfer?.setData(PANE_DRAG_TYPE, pane);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onPaneDragOver(event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onPaneDrop(target: BuilderPane, event: DragEvent): void {
    if (!this.draggingPane && !Array.from(event.dataTransfer?.types || []).includes(PANE_DRAG_TYPE)) return;
    event.preventDefault();
    const source = this.draggingPane || event.dataTransfer?.getData(PANE_DRAG_TYPE) as BuilderPane;
    if (!source || source === target || !this.paneOrder.includes(source)) return;
    const reordered = this.paneOrder.filter((pane) => pane !== source);
    const targetElement = event.currentTarget as HTMLElement;
    const targetBounds = targetElement.getBoundingClientRect();
    const insertAfterTarget = event.clientX > targetBounds.left + targetBounds.width / 2;
    const targetIndex = reordered.indexOf(target);
    reordered.splice(targetIndex + (insertAfterTarget ? 1 : 0), 0, source);
    this.paneOrder = reordered;
    this.draggingPane = null;
  }

  onPaneDragEnd(): void {
    this.draggingPane = null;
  }

  startDividerResize(dividerIndex: 0 | 1, event: MouseEvent): void {
    event.preventDefault();
    const leftPane = this.paneOrder[dividerIndex];
    const rightPane = this.paneOrder[dividerIndex + 1];
    const pane = rightPane === 'preview' ? leftPane : rightPane;
    if (pane === 'preview' || this.isPaneCollapsed(pane)) return;
    const startWidth = pane === 'steps' ? this.stepsPaneWidth : this.dataPaneWidth;
    this.activePaneResize = { pane, startX: event.clientX, startWidth, direction: pane === leftPane ? 1 : -1 };
  }

  isDividerDisabled(dividerIndex: 0 | 1): boolean {
    const adjacent = [this.paneOrder[dividerIndex], this.paneOrder[dividerIndex + 1]];
    return adjacent.every((pane) => pane === 'preview' || this.isPaneCollapsed(pane));
  }

  @HostListener('window:mousemove', ['$event'])
  onPaneResize(event: MouseEvent): void {
    // A tile corner-resize takes priority over pane resizing. Convert the pixel
    // drag into whole grid-cell span changes (snapped) so tiles stay aligned and
    // can't overlap.
    if (this.activeTileInteraction) {
      const i = this.activeTileInteraction;
      const { colUnit, rowUnit } = this.gridUnitSizes();
      const deltaCols = colUnit ? Math.round((event.clientX - i.startX) / colUnit) : 0;
      const deltaRows = rowUnit ? Math.round((event.clientY - i.startY) / rowUnit) : 0;
      i.tile.colSpan = this.clamp(i.startColSpan + deltaCols, 2, DvmLayoutBuilderComponent.DASH_COLS);
      i.tile.rowSpan = this.clamp(i.startRowSpan + deltaRows, 3, 40);
      return;
    }
    if (!this.activePaneResize) return;
    const delta = event.clientX - this.activePaneResize.startX;
    const { pane, startWidth, direction } = this.activePaneResize;
    if (pane === 'steps') this.stepsPaneWidth = this.clamp(startWidth + delta * direction, 240, 560);
    if (pane === 'data') this.dataPaneWidth = this.clamp(startWidth + delta * direction, 300, 620);
  }

  @HostListener('window:mouseup')
  stopPaneResize(): void {
    this.activePaneResize = null;
    if (this.activeTileInteraction) {
      this.activeTileInteraction = null;
      // Tile changed size — nudge the embedded ECharts widgets to re-fit.
      this.reflowCharts();
    }
  }

  /** Pixel size of one grid column-step and one row-step (track + gap), measured
   *  from the live canvas so snapped resizing matches what the user sees. */
  private gridUnitSizes(): { colUnit: number; rowUnit: number } {
    const gap = DvmLayoutBuilderComponent.DASH_GAP;
    const cols = DvmLayoutBuilderComponent.DASH_COLS;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    // scrollWidth covers the case where the narrow builder pane made the canvas
    // scroll sideways (grid min column width) — the tracks span the CONTENT
    // width, not the visible clientWidth.
    const inner = canvas ? Math.max(canvas.clientWidth, canvas.scrollWidth) - 32 /* padding */ : cols * 80;
    const track = (inner - (cols - 1) * gap) / cols;
    return { colUnit: track + gap, rowUnit: DvmLayoutBuilderComponent.DASH_ROW_PX + gap };
  }

  /** ECharts-based widgets resize on window resize; dispatch one so they re-fit
   *  their tile after a layout change (resize / reorder / fullscreen toggle). */
  private reflowCharts(): void {
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  }

  private paneTrack(pane: BuilderPane): string {
    if (this.isPaneCollapsed(pane)) return '48px';
    if (pane === 'steps') return `${this.stepsPaneWidth}px`;
    if (pane === 'data') return `${this.dataPaneWidth}px`;
    return 'minmax(440px, 1fr)';
  }

  private isPaneCollapsed(pane: BuilderPane): boolean {
    return pane === 'preview' ? this.previewPanelCollapsed : pane === 'steps' ? this.stepsPanelCollapsed : this.dataPanelCollapsed;
  }

  selectExplorerEntity(entity: string): void {
    this.activeExplorerEntity = entity;
    this.fieldPaneFilter = '';
    const cached = this.entityColumnsCache.get(entity);
    if (cached) {
      this.explorerColumns = cached;
      return;
    }
    this.explorerColumns = [];
    this.explorerColumnsLoading = true;
    this.datasetService.getEntityAttributes(entity, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(entity, columns);
        // Cache the rows too — the Bar & Column chart aggregates them client-side.
        // Without this, expanding a table here (columns-only) then Generating a
        // bar chart left entityRowsCache empty → a blank chart.
        this.loadRowsForDataset(entity);
        if (this.activeExplorerEntity === entity) this.explorerColumns = columns;
        this.explorerColumnsLoading = false;
      },
      error: () => {
        this.explorerColumnsLoading = false;
        this.ui.showError(`Unable to load columns for "${entity}".`);
      },
    });
  }

  onExplorerFieldDragStart(event: DragEvent, dataset: string, field: string): void {
    event.stopPropagation();
    event.dataTransfer?.setData(FIELD_DRAG_TYPE, JSON.stringify({ dataset, field }));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  allowFieldDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onStepFieldDrop(event: DragEvent, step: BridgeStep): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping || step.stepType === 'TOTAL') return;
    if (step.stepType === 'CHANGE' && step.formulaMode) {
      step.formula = this.appendFormulaToken(step.formula, mapping.field);
      this.onStepFormulaOrLabelChange();
      return;
    }
    if (!this.selectedDatasets.includes(mapping.dataset)) this.selectedDatasets = [...this.selectedDatasets, mapping.dataset];
    step.dataset = mapping.dataset;
    step.field = mapping.field;
    this.loadColumnsForDataset(mapping.dataset);
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onDrillDimensionFieldDrop(event: DragEvent, dimension?: DrillDownDimension): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = dimension || { id: this.nextDrillDimensionId(), field: '', label: '' };
    target.field = mapping.field;
    if (!target.label || /^Level \d+$/.test(target.label)) target.label = mapping.field;
    if (!dimension) this.drillDownDimensions = [...this.drillDownDimensions, target];
    this.onDrillDimensionChange();
  }

  onDrillMeasureFieldDrop(event: DragEvent, measure?: DrillDownMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || {
      id: this.nextDrillMeasureId(), field: '', label: '', aggregation: 'NONE', rollUpStrategy: 'SUM', total: false,
    };
    target.field = mapping.field;
    if (!target.label || /^Measure \d+$/.test(target.label)) target.label = mapping.field;
    if (!measure) this.drillDownMeasures = [...this.drillDownMeasures, target];
    this.onDrillMeasureChange();
  }

  /** Drop a Data-pane field onto the Table & Matrix "Columns" list — appends a
   *  column row for it (in drop order, skipping duplicates), keeping the
   *  dropped field's OWN dataset so cross-dataset columns work. */
  onTmColumnFieldDrop(event: DragEvent, toggle = false): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    // The second view has its own dataset, so a drop there must not re-point
    // the table's primary dataset.
    if (!toggle) this.useDroppedSingleDataset(mapping.dataset);
    const fallback = toggle ? this.tmToggleDatasetEffective : this.drillDataset;
    const dataset = mapping.dataset || fallback;
    const list = toggle ? this.tmToggleColumns : this.tmColumns;
    const duplicate = list.some(
      (column) => column.field === mapping.field && (column.dataset || fallback) === dataset,
    );
    if (!duplicate) {
      const row: TmColumnDef = {
        id: this.nextTmColumnId(),
        dataset,
        field: mapping.field,
        displayName: this.defaultColumnDisplayName(mapping.field),
        align: '',
        filterField: '',
        filterOperator: 'eq',
        filterValue: '',
      };
      if (toggle) this.tmToggleColumns = [...this.tmToggleColumns, row];
      else this.tmColumns = [...this.tmColumns, row];
      if (dataset) this.loadColumnsForDataset(dataset);
    }
    this.onTableMatrixTypeChange();
  }

  /** Drop a field onto the Table & Matrix "Group rows by" picker (Normal). */
  onTmGroupByFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.tmGroupBy = mapping.field;
    this.onTableMatrixTypeChange();
  }

  onHorizontalBarGroupFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.hbGroupByField = mapping.field;
    if (!this.hbGroupByLabel) this.hbGroupByLabel = mapping.field;
    this.invalidatePreview();
  }

  onHorizontalBarValueFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    // Append (build up) rather than replace, so several columns can be dragged
    // in to compose an expression (e.g. "[price] + [cost]").
    this.hbValueFormula = this.appendFormulaToken(this.hbValueFormula, mapping.field);
    if (!this.hbValueLabel || this.hbValueLabel === 'Value') this.hbValueLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Appends the token so several columns can compose one expression. */
  onBarValueFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barValueFormula = this.appendFormulaToken(this.barValueFormula, mapping.field);
    this.onBarChange();
  }

  /** Drop a column onto a WATERFALL TOTAL step's formula — appends its token. */
  onWaterfallFormulaDrop(step: BridgeStep, event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    step.formula = this.appendFormulaToken(step.formula, mapping.field);
    this.onStepFormulaOrLabelChange();
  }

  /** Drop a column onto the KPI card's column picker. */
  onKpiFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.kpiField = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the KPI card's formula field — appends its token so
   *  several columns can be dragged in to compose an expression. */
  onKpiFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.kpiFormula = this.appendFormulaToken(this.kpiFormula, mapping.field);
    this.invalidatePreview();
  }

  /** Drop a dataset column onto the dropdown option-field picker. */
  onDropdownFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.dropdownField = mapping.field;
    if (!this.dropdownDisplayLabel) this.dropdownDisplayLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto a specific target's formula — appends its token. When
   *  `stepLabel` is given, the target is a WATERFALL and the token goes into
   *  that bridge step's own formula instead of a single chart-wide one. */
  onTargetFormulaDrop(event: DragEvent, targetId: string, stepLabel?: string): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (stepLabel) {
      this.setTargetStepFormula(targetId, stepLabel, this.appendFormulaToken(this.targetStepFormula(targetId, stepLabel), mapping.field));
      return;
    }
    this.targetFormulas[targetId] = this.appendFormulaToken(this.targetFormulas[targetId] || '', mapping.field);
  }

  /** Display label for a target chart id (falls back to the id). */
  targetLabelForId(id: string): string {
    return this.targetCharts.find((chart) => chart.value === id)?.label || id;
  }

  /** Append a dropped column to a formula as a bracketed token, space-separated
   *  from whatever's already there — lets users build expressions by dragging. */
  private appendFormulaToken(current: string, field: string): string {
    const token = `[${field}]`;
    const trimmed = (current || '').trimEnd();
    return trimmed ? `${trimmed} ${token}` : token;
  }

  private readFieldDrop(event: DragEvent): { dataset: string; field: string } | null {
    event.preventDefault();
    event.stopPropagation();
    const raw = event.dataTransfer?.getData(FIELD_DRAG_TYPE);
    if (!raw) return null;
    try {
      const mapping = JSON.parse(raw) as { dataset?: string; field?: string };
      return mapping.dataset && mapping.field ? { dataset: mapping.dataset, field: mapping.field } : null;
    } catch {
      return null;
    }
  }

  private useDroppedSingleDataset(dataset: string): void {
    this.onSelectedDatasetsChange([dataset, ...this.selectedDatasets.filter((item) => item !== dataset)]);
    this.loadColumnsForDataset(dataset);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  ngOnInit(): void {
    this.configService.loadBuilderLayout('dvm-layout', DvmLayoutBuilderComponent.DEFAULT_LAYOUT).subscribe((layout) => {
      const validation = this.configService.validateLayout(layout, DvmLayoutBuilderComponent.STEP_FIELD_IDS);
      if (validation.unknownFields.length) {
        console.warn('[dvm-layout-builder] config references unknown field ids; they will be skipped:', validation.unknownFields);
      }
      this.layout = layout;
      // Config just loaded — drop the memoized accordion groups so they rebuild
      // from the (now real) formatControls instead of the DEFAULT_LAYOUT's.
      this.barFormatGroupsCache = null;
      this.selectedLayoutType = this.layoutTypes[0]?.value || 'WATERFALL';
      // Seed the catalogue category that owns the initial type (Layer 1).
      this.selectedChartCategory = this.categoryOfType(this.selectedLayoutType) || this.chartCategories[0]?.id || '';
      this.componentType = layout.defaults?.componentType || 'CHART';
      this.dashboardId = typeof layout.defaults?.dashboardId === 'number' ? layout.defaults.dashboardId : null;
      this.loadEntityOptions();
      this.restoreDashboardFromRoute();
      this.loadApiTargetDashboards();
    });
  }

  /** Fetch the user's published dashboards from the API (the SAME email-filtered
   *  `dashboard_layouts/dynamic_filter` list the sidebar shows) so the Table
   *  navigation "Target dashboard" picker works even when localStorage is cold
   *  (fresh session / different user). Merged with localStorage in the getter. */
  private async loadApiTargetDashboards(): Promise<void> {
    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
      // Not logged in — the localStorage list is the only source.
    }
    if (!email) return;
    this.dashboardService.fetchOwnedDashboardsFromApi(email).subscribe({
      next: (owned) => {
        this.apiTargetDashboards = (owned || []).map((d) => ({
          label: d.name || d.slug,
          value: d.slug,
          layoutJson: (d.layoutJson || {}) as Record<string, unknown>,
        }));
        // Invalidate the memoized option lists so the picker re-renders.
        this.navDashboardsMemo = null;
        this.navComponentsMemo = null;
      },
      error: () => { /* API unavailable — fall back to localStorage list */ },
    });
  }

  /** When the published view's "Back to Builder" returns here (view=layout), it
   *  passes the dashboard slug as ?dashboard. Fetch that published layout and
   *  rebuild the canvas tiles so the user can keep adding/editing charts. */
  private async restoreDashboardFromRoute(): Promise<void> {
    const slug = this.route.snapshot.queryParamMap.get('dashboard');
    if (!slug) return;

    const stateLayout = this.cloneRecord(history.state?.['layoutJson']);
    if (stateLayout && this.hasRenderableContent(stateLayout)) {
      this.applyRestoredDashboardLayout(stateLayout, slug);
      return;
    }

    const cached = readDynamicDashboardCache<Record<string, unknown> | null>(slug, null);
    if (cached && this.hasRenderableContent(cached)) {
      this.applyRestoredDashboardLayout(cached, slug);
      return;
    }

    let email = '';
    try {
      if (await this.keycloak.isLoggedIn()) email = (await this.keycloak.loadUserProfile()).email || '';
    } catch {
      // no keycloak - getPublishedLayoutBySlug falls back to the email-independent select
    }
    this.dashboardService.getPublishedLayoutBySlug(slug, email).subscribe({
      next: (layoutJson) => {
        if (!layoutJson) return;
        this.applyRestoredDashboardLayout(layoutJson, slug);
      },
      error: (error) => console.warn('[dvm-layout-builder] could not restore published dashboard:', error),
    });
  }

  private applyRestoredDashboardLayout(layoutJson: Record<string, unknown>, slug: string): void {
    // Keep the exact dashboard id/slug this published dashboard already uses so
    // the next Save/Publish updates the same API row instead of deriving a fresh id.
    this.restoredDashboardId = String(layoutJson['dashboardId'] ?? slug);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const matched = savedDashboards.find((dashboard) => dashboard.config?.['dashboardId'] === this.restoredDashboardId);
    if (matched) this.activeSavedDashboardId = matched.id;
    this.restoreTilesFromLayout(layoutJson);
  }

  private hasRenderableContent(layoutJson: Record<string, unknown> | null | undefined): boolean {
    return Array.isArray(layoutJson?.['pageContent']);
  }


  /** Reverse of buildDashboardLayoutJson: rebuild the canvas tiles from a stored
   *  layout_json's pageContent so a published DVM dashboard can be re-opened and
   *  extended in the builder. */
  private restoreTilesFromLayout(layout: Record<string, unknown>): void {
    this.dashboardName = String(layout['dashboard-name'] ?? layout['name'] ?? '') || '';
    this.restoredPublishedLayout = this.cloneRecord(layout['layout']);
    const rows = (layout['pageContent'] as Array<Record<string, unknown>>) || [];
    // ngOnInit stamps `this.dashboardId` from the STATIC config default
    // (DEFAULT_LAYOUT.defaults.dashboardId, e.g. 101) and nothing ever updates
    // it afterwards - so reopening a dashboard whose components actually live
    // under a DIFFERENT numeric dashboardId (e.g. 9101) left every subsequent
    // Save/compute call stamping the WRONG dashboardId onto each tile's payload.
    // The backend then upserts by (dashboardId, title), so a save silently
    // merged this tile with an unrelated same-titled component under the
    // default dashboard. Every tile's own saved payload already carries the
    // CORRECT dashboardId it was built with - reconcile from the first one found.
    for (const row of rows) {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      const found = cols
        .map((widget) => (widget['payload'] as Record<string, unknown> | undefined)?.['dashboardId'])
        .find((id) => typeof id === 'number');
      if (typeof found === 'number') {
        this.dashboardId = found;
        break;
      }
    }
    const tiles: DashboardTile[] = [];
    rows.forEach((row) => {
      const cols = (row['columns'] as Array<Record<string, unknown>>) || [];
      cols.forEach((widget) => tiles.push(this.tileFromWidget(widget)));
    });
    if (tiles.length) {
      this.dashboardTiles = this.applyPublishedLayoutToTiles(tiles, this.restoredPublishedLayout);
      // Autofill the build form from the restored chart so "Back to Builder"
      // lands with the values pre-filled (no manual pencil click needed). Prefer
      // the first BAR tile (its whole capability config round-trips); else the
      // first editable tile of any type.
      const firstBar = tiles.find((t) => t.layoutType === 'BAR');
      const firstEditable = firstBar
        ?? tiles.find((t) => ['WATERFALL', 'DRILL_DOWN_TABLE', 'TABLE_MATRIX', 'SCATTER_BUBBLE', 'LINE_AREA', 'HORIZONTAL_BAR_CHART', 'KPI', 'INPUT_BOX', 'SLIDER', 'DROPDOWN', 'FILE_UPLOAD', 'POPUP'].includes(t.layoutType));
      if (firstEditable) {
        // Defer so entity options / columns can begin loading first.
        setTimeout(() => this.loadTileIntoForm(firstEditable), 0);
      }
      // The ECharts widgets init while the grid is still settling — nudge them
      // to re-measure their tile, otherwise restored charts can stay blank/0-height.
      this.reflowCharts();
    }
  }

  private static readonly WIDGET_TYPE_TO_LAYOUT: Record<string, string> = {
    'waterfall-chart': 'WATERFALL',
    'drill-down': 'DRILL_DOWN_TABLE',
    'horizontal-bar-chart': 'HORIZONTAL_BAR_CHART',
    'kpi-card': 'KPI',
    'bar-column': 'BAR',
    'scatter-bubble': 'SCATTER_BUBBLE',
    'line-area': 'LINE_AREA',
    'slider': 'SLIDER',
    'input-box': 'INPUT_BOX',
    'dropdown': 'DROPDOWN',
    'table-matrix': 'TABLE_MATRIX',
    'file-upload': 'FILE_UPLOAD',
    'popup': 'POPUP',
    // Legacy alias: older publishes emitted Bar tiles as 'bar-chart' (the
    // typeMap fallback). Without this entry they restored as WATERFALL and a
    // re-publish then stored a data-less waterfall widget titled after the
    // bar chart (the "double Bar header / No data" tiles).
    'bar-chart': 'BAR',
  };

  /**
   * A published table-matrix node carries its rows TWICE: `staticData` (the
   * builder's snapshot at publish time) and `data.data` (which the backend
   * re-hydrates from the live dataset every time it SERVES layout_json — the
   * published view promotes it over `staticData` at load, see
   * withFreshTableSnapshot). Reopening the dashboard here used to keep that
   * served copy on the tile as-is: `staticData` then moved on (cell edits saved
   * + refreshed, Update Configuration, file upload…) while `data.data` stayed
   * frozen at whatever the backend served when Back to Builder ran. Publish
   * spread the whole tile back out, so the published view's just-published
   * render promoted the FROZEN `data.data` over the fresh `staticData` — an
   * edit saved in the builder (101) showed the pre-edit value (10) right after
   * publishing, and only came right on a reload (API path → backend re-hydrates).
   *
   * Restore: treat the served rows as the current truth (promote them into
   * `staticData`, same as the published view does) and DROP `data` — the
   * builder only ever reads/updates `staticData`, and the backend's copy must
   * never travel back out of the builder.
   */
  private restoredTableMatrixData(renderData: Record<string, unknown>): Record<string, unknown> {
    const { data, ...rest } = renderData;
    const served = (data as { data?: unknown } | undefined)?.data;
    const caps = (rest['capabilities'] as Record<string, unknown> | undefined) ?? {};
    const isDrill = caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true;
    // Drill-downs render their own `drilldownData` tree, not flat staticData —
    // keep the tree, just drop the served flat copy.
    if (isDrill || !Array.isArray(served) || !served.length) return rest;
    return { ...rest, staticData: served };
  }

  /** Publish-side guard for the same problem: whatever path put a `data` key on
   *  a table-matrix tile (an older restore, a re-normalised node), the builder's
   *  `staticData` is the snapshot to publish — never a stale served copy. */
  private publishableTableMatrixData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
    if (!data || !('data' in data)) return data;
    const { data: _served, ...rest } = data;
    void _served;
    return rest;
  }

  /** Rebuild one DashboardTile from its published widget object — extracted
   *  from restoreTilesFromLayout so a 'group-card' widget (below) can rebuild
   *  its children the exact same way, recursively. */
  private tileFromWidget(widget: Record<string, unknown>): DashboardTile {
    if (String(widget['type']) === 'group-card') return this.groupTileFromWidget(widget);

    const layoutType = DvmLayoutBuilderComponent.WIDGET_TYPE_TO_LAYOUT[String(widget['type'])] || 'WATERFALL';
    const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
    const payload = (widget['payload'] as Record<string, unknown>) || {};
    // Everything except the meta keys is the render data (spread at publish).
    const { type, title, widgetId, componentId, layout: _l, payload: _p, format: savedFormat, height: _h, class: _c, recomputeUrl: _r, apiConfig: widgetApiConfig, filterKey: savedFilterKey, listenerTileIds: savedListeners, tileNotes: savedTileNotes, ...renderData } =
      widget as Record<string, unknown>;
    void type; void widgetId; void _l; void _p; void _h; void _c; void _r;
    // The per-control custom "API Call". Prefer the compute payload's copy (the
    // backend-persisted one) and fall back to the published widget's, so a
    // dashboard saved before the payload carried it still round-trips. Without
    // this, reopening a dashboard reset every control to the default recompute.
    const savedApiConfig = this.restoreApiCallConfig(payload['apiConfig'] ?? widgetApiConfig);
    // Reuse the persisted widgetId as the tile id so cross-filter listener
    // references (which point at these ids) still resolve after restore.
    const tile: DashboardTile = {
      id: this.restoreTileId(widgetId),
      componentId: String(componentId ?? this.createComponentId()),
      layoutType,
      title: String(title ?? 'Untitled'),
      // Clamp to the same minimums as the interactive corner-resize so a
      // restored tile is never too small to show its content.
      colSpan: this.clamp(Math.round(wLayout.colSpan ?? 6), 2, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
      payload,
      // Restore the cross-filter config (published widget carries these).
      ...(typeof savedFilterKey === 'string' && savedFilterKey ? { filterKey: savedFilterKey } : {}),
      ...(Array.isArray(savedListeners) ? { listenerTileIds: (savedListeners as unknown[]).map(String) } : {}),
      // Notes under the chart (published as the widget's `tileNotes`).
      ...(savedTileNotes && typeof savedTileNotes === 'object' ? { tileNotes: savedTileNotes as TableMatrixNotes } : {}),
    };
    // The Format popup (isFormattableTile: WATERFALL/HORIZONTAL_BAR_CHART/KPI)
    // only renders when `tile.format` is truthy — but `format` is a builder-only
    // field never written into the published widget (buildDashboardLayoutJson's
    // renderData carries only drillData/hbData/kpiData/option), so a tile
    // rebuilt here from a saved/published layout always came back with
    // `format` undefined and the popup silently never opened. Reseed it with
    // the same defaults a freshly-generated tile gets.
    if (this.isFormattableTile(tile)) {
      tile.format = (savedFormat as Record<string, unknown>) ?? this.defaultTileFormat(layoutType);
    }
    if (layoutType === 'DRILL_DOWN_TABLE') tile.drillData = renderData;
    else if (layoutType === 'TABLE_MATRIX') tile.tableMatrixData = this.restoredTableMatrixData(renderData);
    else if (layoutType === 'HORIZONTAL_BAR_CHART') tile.hbData = renderData;
    else if (layoutType === 'KPI') tile.kpiData = renderData;
    else if (layoutType === 'BAR') {
      tile.barData = renderData;
      // Re-seed the ORIGINAL unfiltered rows from the published staticData.
      // Without this, `isCrossFilterTile` (which requires BOTH barData AND an
      // array barRows) returned false on a reopened dashboard, so the
      // cross-filter icon disappeared and cross-filtering stopped working
      // after "Back to Builder". staticData IS the tile's full row set.
      const staticRows = renderData['staticData'];
      tile.barRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'LINE_AREA') {
      tile.lineAreaData = renderData;
      const staticRows = renderData['staticData'];
      tile.lineAreaRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'SCATTER_BUBBLE') {
      tile.scatterBubbleData = renderData;
      // Reseed unfiltered rows so isCrossFilterTile stays true after restore.
      const staticRows = renderData['staticData'];
      tile.scatterBubbleRows = Array.isArray(staticRows) ? (staticRows as Array<Record<string, unknown>>) : [];
    }
    else if (layoutType === 'WATERFALL') {
      // Restore BOTH — buildDashboardLayoutJson's renderData carries
      // `{ waterfallBars, option }`. Without `waterfallBars`, onTileFormatChange's
      // WATERFALL branch falls back to withWaterfallXAxisLabelRotation (rotation
      // only), so every OTHER format control (color, number format, show
      // values, wrap) silently did nothing when editing a reopened dashboard.
      tile.waterfallBars = (renderData['waterfallBars'] as WaterfallBar[] | undefined) ?? undefined;
      tile.previewOption = (renderData['option'] as echarts.EChartsOption) ?? null;
      tile.waterfallData = this.buildWaterfallTileData(tile);
    }
    else if (layoutType === 'SLIDER' || layoutType === 'INPUT_BOX') {
      tile.inputControl = {
        kind: layoutType,
        formula: String(payload['formula'] ?? ''),
        value: (payload['value'] as number | string | null) ?? null,
        targets: Array.isArray(payload['targets'])
          ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
          : [],
        widgetData: renderData,
        ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
      };
    } else if (layoutType === 'DROPDOWN') {
      const optionOrderMode = this.normalizeDropdownOptionOrderMode(payload['optionOrderMode'] ?? renderData['optionOrderMode']);
      const customOptionOrder = String(payload['customOptionOrder'] ?? renderData['customOptionOrder'] ?? '');
      const rawOptions = Array.isArray(renderData['optionsList']) ? (renderData['optionsList'] as unknown[]).map(String) : [];
      const options = this.orderDropdownOptions(rawOptions, optionOrderMode, customOptionOrder);
      const rawValue = payload['value'];
      tile.dropdownControl = {
        field: String(payload['field'] ?? renderData['key'] ?? ''),
        options,
        value: Array.isArray(rawValue) ? rawValue.map(String) : rawValue == null || rawValue === '' ? [] : [String(rawValue)],
        targets: Array.isArray(payload['targets'])
          ? (payload['targets'] as Array<{ componentId?: string }>).map((target) => target.componentId || '').filter(Boolean)
          : [],
        // The payload's display settings are authoritative — overlay them so a
        // reopened dashboard renders even if the stored widgetData predates
        // these keys.
        widgetData: {
          ...renderData,
          optionsList: options,
          optionOrderMode,
          customOptionOrder,
          displayMode: payload['displayMode'] ?? renderData['displayMode'] ?? 'dropdown',
          optionGroups: Array.isArray(payload['optionGroups'])
            ? payload['optionGroups']
            : (renderData['optionGroups'] ?? []),
          showAllOption: payload['showAllOption'] ?? renderData['showAllOption'] ?? false,
          autoSelectFirst: payload['autoSelectFirst'] ?? renderData['autoSelectFirst'] ?? false,
          // Widget label: stored widgetData from before the `name` key (or a
          // publish that predates it) would render label-less — rebuild it
          // from the payload the same way buildDropdownTile does.
          name: renderData['name'] ?? payload['displayLabel'] ?? payload['title'] ?? payload['field'] ?? '',
        },
        ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
      };
    } else if (layoutType === 'FILE_UPLOAD') {
      const targets = Array.isArray(payload['targets'])
        ? (payload['targets'] as Array<{ componentId?: string }>).map((t) => t.componentId || '').filter(Boolean)
        : [];
      tile.fileUploadControl = {
        targets,
        buttonLabel: String(payload['buttonLabel'] ?? renderData['buttonLabel'] ?? 'Upload file'),
        accept: String(payload['accept'] ?? renderData['accept'] ?? '.xlsx,.xls,.csv'),
        widgetData: renderData,
        ...(savedApiConfig ? { apiConfig: savedApiConfig } : {}),
      };
    } else if (layoutType === 'POPUP') {
      // renderData IS the popup's widgetData (trigger/behaviour keys + blocks
      // with their snapshotted table rows) — published widgets spread it at the
      // top level, so it round-trips back whole. `title` is one of the meta
      // keys destructured OUT of renderData above, but the popup dialog header
      // reads it from its widgetData — put it back.
      tile.popupControl = { widgetData: { ...renderData, title: String(title ?? '') } };
      // Card-header colors ride tile.format (POPUP isn't isFormattableTile, so
      // the generic reseed above skipped it) — restore them for republish.
      if (savedFormat) tile.format = savedFormat as Record<string, unknown>;
    }
    return tile;
  }

  private groupTileFromWidget(widget: Record<string, unknown>): DashboardTile {
    const wLayout = (widget['layout'] as { colSpan?: number; rowSpan?: number }) || {};
    const children = Array.isArray(widget['children']) ? widget['children'] as Array<Record<string, unknown>> : [];
    return {
      id: this.restoreTileId(widget['widgetId']),
      componentId: String(widget['componentId'] ?? this.createComponentId('GRP')),
      layoutType: 'GROUP',
      title: String(widget['title'] ?? 'Grouped card'),
      groupTitle: String(widget['title'] ?? 'Grouped card'),
      colSpan: this.clamp(Math.round(wLayout.colSpan ?? 6), 2, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(3, Math.round(wLayout.rowSpan ?? 8)),
      payload: {},
      // Card design travels with the published widget; fall back to the control
      // defaults for cards published before those controls existed.
      format: (widget['format'] as Record<string, unknown> | undefined) ?? this.defaultTileFormat('GROUP'),
      groupColumns: this.clamp(Math.round(Number(widget['groupColumns']) || 1), 1, 2),
      groupMembers: children.map((child) => this.tileFromWidget(child)),
    };
  }

  private loadEntityOptions(): void {
    const entityGroup = this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
    this.entitiesLoading = true;
    this.entitiesLoadFailed = false;
    this.datasetService.getEntities(entityGroup).subscribe({
      next: (entities) => {
        this.entityOptions = entities;
        this.entitiesLoading = false;
      },
      error: () => {
        this.entityOptions = [];
        this.entitiesLoading = false;
        this.entitiesLoadFailed = true;
        this.ui.showError('Unable to load datasets/entities.');
      },
    });
  }

  entitiesLoadFailed = false;

  reloadDatasets(): void {
    this.loadEntityOptions();
  }

  /** Dataset status shown by every chart builder's picker. */
  get datasetStatus(): { kind: 'error' | 'hint' | 'none'; text: string; canRetry: boolean } {
    if (this.entitiesLoading) {
      return { kind: 'hint', text: 'Loading datasets…', canRetry: false };
    }
    if (this.entitiesLoadFailed) {
      return { kind: 'error', text: 'Could not load the dataset list. Check your connection and try again.', canRetry: true };
    }
    if (!this.entityOptions.length) {
      return { kind: 'error', text: 'No datasets found. Create one in the Data Builder first, then reload.', canRetry: true };
    }
    if (!this.drillDataset) {
      return { kind: 'hint', text: 'Pick a dataset to enable the field pickers and Generate.', canRetry: false };
    }
    if (this.isDrillDatasetColumnsLoading) {
      return { kind: 'hint', text: `Loading columns from "${this.drillDataset}"…`, canRetry: false };
    }
    if (this.drillColumnsErrorMessage) {
      return { kind: 'error', text: `${this.drillColumnsErrorMessage} (see console for details)`, canRetry: false };
    }
    return { kind: 'none', text: '', canRetry: false };
  }

  onSelectedDatasetsChange(datasets: string[] | null): void {
    const previousPrimary = this.drillDataset;
    this.selectedDatasets = datasets || [];
    // Both single-dataset layouts (drill-down, horizontal bar) need the chosen
    // dataset's columns for their field pickers.
    if ((this.isDrillDownLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) && this.drillDataset) {
      this.loadColumnsForDataset(this.drillDataset);
    }
    // Drill-down levels/measures keep their field picks across a dataset switch,
    // which the compute API then rejects ("Dataset 'x' has no field 'y'") — drop
    // any pick the new dataset doesn't have (deferred until its columns load).
    if (this.drillDataset && this.drillDataset !== previousPrimary) {
      this.pruneDrillFieldsForDataset(this.drillDataset);
    }
    // WATERFALL bridges across the selected datasets, and each START/CHANGE step
    // pins its OWN `step.dataset`. Changing the chart's dataset(s) never
    // re-pointed those, so buildPayload's `sanitizeStepForPayload` kept emitting
    // the OLD per-step dataset — which then got published and recomputed against
    // the stale dataset. Re-point any step whose dataset is no longer among the
    // selected ones onto the new primary dataset (and clear its field, since the
    // column set changes) so the dataset change actually flows through.
    if (this.isWaterfallLayout && this.steps.length) {
      const valid = new Set(this.selectedDatasets);
      const primary = this.drillDataset;
      let changed = false;
      this.steps.forEach((step) => {
        if (step.dataset && !valid.has(step.dataset)) {
          step.dataset = primary;
          step.field = '';
          changed = true;
        }
      });
      if (changed) {
        if (primary) this.loadColumnsForDataset(primary);
        this.steps = [...this.steps];
        this.invalidatePreview();
      }
    }
  }

  /** Switching layout type swaps the whole left-pane builder + the preview it
   *  drives — clear the stale preview so a leftover waterfall chart doesn't
   *  linger while in Drill-Down mode (or vice versa). Working steps/levels are
   *  kept so flipping back and forth doesn't lose entered data. */
  onLayoutTypeChange(): void {
    const previousLayoutType = this.lastSelectedLayoutType;
    this.lastSelectedLayoutType = this.selectedLayoutType;
    // Switching chart type — drop any bar draft preview tile.
    this.removePreviewTile();
    this.previewOption = null;
    this.drillDownWidgetData = null;
    this.hbWidgetData = null;
    this.tableMatrixWidgetData = null;
    this.inputControlReady = false;
    this.generateError = '';
    // Keep the catalogue category in sync when the type changes directly (Layer 1).
    const owningCategory = this.categoryOfType(this.selectedLayoutType);
    if (owningCategory) this.selectedChartCategory = owningCategory;
    // BAR seeds an empty first measure so the picker isn't blank on first open.
    if (this.isBarLayout && !this.barMeasures.length) this.addBarMeasure();
    if (this.isLineAreaLayout && !this.laMeasures.length) this.addLaMeasure();
    // Every layout has a Build-form format accordion now (charts + inputs) —
    // seed its options with the configured defaults for the active type.
    this.seedBarFormatDefaults();
    if (this.isScatterBubbleLayout) this.seedScatterBubbleFormatDefaults();
    if (!this.editingTileId && previousLayoutType !== this.selectedLayoutType) {
      this.clearInputTargetState();
    }
    if ((this.isDrillDownLayout || this.isTableMatrixLayout || this.isHorizontalBarLayout || this.isKpiLayout || this.isBarLayout || this.isLineAreaLayout || this.isScatterBubbleLayout || this.isDropdownLayout) && this.drillDataset) {
      this.loadColumnsForDataset(this.drillDataset);
    }
    // Popup: seed a first block so the form isn't empty, and (re)load the
    // datasets its table blocks point at (columns for the picker + rows for
    // the client-side snapshot).
    if (this.isPopupLayout) {
      if (!this.popupBlocks.length) this.addPopupBlock();
      this.popupBlocks.forEach((block) => { if (block.dataset) this.loadColumnsForDataset(block.dataset); });
    }
  }

  /** Table-type change (Normal/Editable/Drill-Down) — clears the stale preview
   *  so the form re-renders the fields for the newly-selected type. */
  onTableMatrixTypeChange(): void {
    this.tableMatrixWidgetData = null;
    this.generateError = '';
    // If we're editing a table tile that's already on the canvas, push the
    // updated capabilities (incl. navigation) into it LIVE — so toggling
    // navigation / changing its config reflects immediately without needing to
    // click Generate again. Only capabilities change here (not the rows).
    this.refreshEditingTableMatrixTile();
  }

  /** Rebuild the capabilities of the currently-edited table tile from the live
   *  form state, preserving its already-fetched rows / drill data. No-op unless
   *  a TABLE_MATRIX tile is being edited and already has render data. */
  private refreshEditingTableMatrixTile(): void {
    if (!this.editingTileId) return;
    const tile = this.findTileAnywhere(this.editingTileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const capabilities = this.buildTableMatrixCapabilities(tile.format);
    tile.tableMatrixData = { ...tile.tableMatrixData, capabilities };
    this.updateTileInPlace({ ...tile });
  }

  private clearInputTargetState(): void {
    this.relationHops = [];
    this.selectedTargetCharts = [];
    this.targetFormulas = {};
    this.targetStepFormulas = {};
    this.targetFormulaReferences = {};
    this.buildApiCallConfig = this.defaultApiCallConfig();
  }

  // ── Shared dataset/column loading (bridge steps AND drill-down levels) ────

  private loadColumnsForDataset(dataset: string): void {
    if (!dataset) return;
    // Columns already cached — but the ROWS may not be (e.g. cached column-only by
    // selectExplorerEntity on an older build). Backfill rows for the bar chart,
    // then skip the re-fetch.
    if (this.entityColumnsCache.has(dataset)) {
      if (!this.entityRowsCache.has(dataset)) {
        this.datasetService.getEntityRows(dataset).subscribe({
          next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
          error: () => this.entityRowsCache.set(dataset, []),
        });
      }
      return;
    }

    this.datasetColumnsLoading[dataset] = true;
    delete this.datasetColumnsError[dataset];
    this.datasetService.getEntityAttributes(dataset, this.entityGroupName).subscribe({
      next: (attributes) => {
        const columns = this.filterVisibleColumns(attributes);
        this.entityColumnsCache.set(dataset, columns);
        if (this.pendingDrillFieldPrune === dataset) this.pruneDrillFieldsForDataset(dataset);
        // Cache the raw rows too — the Bar & Column chart aggregates them
        // client-side (no separate compute call for the preview).
        this.loadRowsForDataset(dataset);
        this.datasetColumnsLoading[dataset] = false;
        if (!columns.length) {
          // The call succeeded but yielded zero usable columns — the
          // getEntityAttributes response didn't match any of the shapes it
          // normalizes, or the entity genuinely exposes none. Log the raw
          // response so it's diagnosable instead of a silent empty picker.
          console.warn(`[dvm-layout-builder] "${dataset}" returned 0 attributes — raw response:`, attributes);
          this.datasetColumnsError[dataset] = `"${dataset}" returned no columns — check the entity name or its metadata.`;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.datasetColumnsLoading[dataset] = false;
        const message = error.error?.message || error.message || `Unable to load columns for "${dataset}".`;
        this.datasetColumnsError[dataset] = message;
        console.error(`[dvm-layout-builder] getEntityAttributes("${dataset}") failed:`, error);
        this.ui.showError(message);
      },
    });
  }

  /** Dataset whose columns, once loaded, should trigger a stale-field prune of
   *  the drill-down levels/measures (set when the dataset switches before its
   *  column list has arrived). */
  private pendingDrillFieldPrune = '';

  /** After a dataset switch, clear drill-down dimension/measure FIELD picks the
   *  new dataset doesn't have — left in place they ride into the compute payload
   *  and the API rejects the whole Generate ("Dataset 'x' has no field 'y'").
   *  Rows and display labels stay; only the stale field is emptied so the user
   *  re-picks from the new dataset's column list. */
  private pruneDrillFieldsForDataset(dataset: string): void {
    if (!dataset || (!this.drillDownDimensions.length && !this.drillDownMeasures.length && !this.tmColumns.length)) return;
    const columns = this.entityColumnsCache.get(dataset);
    if (!columns) {
      this.pendingDrillFieldPrune = dataset;
      return;
    }
    this.pendingDrillFieldPrune = '';
    const known = new Set(columns);
    let changed = false;
    this.drillDownDimensions.forEach((dimension) => {
      if (dimension.field && !known.has(dimension.field)) {
        dimension.field = '';
        changed = true;
      }
    });
    this.drillDownMeasures.forEach((measure) => {
      if (measure.field && !known.has(measure.field)) {
        measure.field = '';
        changed = true;
      }
    });
    // Table column rows that FOLLOW the primary dataset (blank `dataset`) point
    // at the new primary now — clear picks it doesn't have. Rows pinned to
    // their own explicit dataset are untouched (cross-dataset is the point).
    let tmChanged = false;
    this.tmColumns.forEach((column) => {
      if (!column.dataset && column.field && !known.has(column.field)) {
        column.field = '';
        tmChanged = true;
      }
    });
    if (tmChanged) this.tmColumns = [...this.tmColumns];
    // Second-view rows follow the primary only while that view has no dataset
    // of its own; otherwise they're pinned to theirs and stay untouched.
    if (!this.tmToggleDataset) {
      let toggleChanged = false;
      this.tmToggleColumns.forEach((column) => {
        if (!column.dataset && column.field && !known.has(column.field)) {
          column.field = '';
          toggleChanged = true;
        }
      });
      if (toggleChanged) { this.tmToggleColumns = [...this.tmToggleColumns]; tmChanged = true; }
    }
    if (changed || tmChanged) {
      this.drillDownDimensions = [...this.drillDownDimensions];
      this.drillDownMeasures = [...this.drillDownMeasures];
      this.invalidatePreview();
      this.ui.showError(`Some fields pointed at columns "${dataset}" doesn't have — re-pick them for the new dataset.`);
    }
  }

  private loadRowsForDataset(dataset: string): void {
    if (!dataset || this.entityRowsCache.has(dataset)) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => { this.entityRowsCache.set(dataset, Array.isArray(rows) ? rows : []); this.onDatasetRowsLoaded(dataset); },
      error: () => this.entityRowsCache.set(dataset, []),
    });
  }

  /** Rows for `dataset` just landed. The client-side chart families (bar /
   *  line-area / scatter) build their tiles SYNCHRONOUSLY from the rows cache,
   *  so a tile edited or previewed while the fetch was still in flight rendered
   *  with ZERO rows — a permanently blank chart (nothing re-triggered a rebuild
   *  when the rows arrived). Re-run the active layout's live refresh so the
   *  editing/preview tile picks the rows up the moment they exist. */
  private onDatasetRowsLoaded(dataset: string): void {
    // Popup table blocks snapshot rows client-side from this cache and pick
    // their OWN dataset (independent of drillDataset) — re-snapshot any popup
    // tile whose table block was still waiting on these rows.
    this.refreshPopupTilesForDataset(dataset);
    if (this.drillDataset !== dataset) return;
    if (this.isLineAreaLayout) this.refreshEditingLineAreaTile();
    else if (this.isBarLayout) this.refreshEditingBarTile();
    else if (this.isScatterBubbleLayout) this.refreshEditingScatterBubbleTile();
  }
  isDatasetColumnsLoading(dataset: string): boolean {
    return !!dataset && !!this.datasetColumnsLoading[dataset];
  }

  /** Memoized per row id (bridge-step or drill-level) so ng-select's [items]
   *  array reference only changes when the underlying dataset's columns
   *  actually change — see rowColumnsMemo's declaration comment. */
  private columnsForRow(rowId: string, dataset: string): string[] {
    const source = dataset ? this.entityColumnsCache.get(dataset) : undefined;
    const cached = this.rowColumnsMemo.get(rowId);
    if (cached && cached.dataset === dataset && cached.source === source) {
      return cached.result;
    }
    const result = source || [];
    this.rowColumnsMemo.set(rowId, { dataset, source, result });
    return result;
  }

  // ── Phase B: bridge-step CRUD + reordering (WATERFALL) ────────────────────

  addStep(): void {
    const step: BridgeStep = {
      id: this.nextStepId(),
      stepType: this.steps.length === 0 ? 'START' : 'CHANGE',
      label: this.steps.length === 0 ? 'Starting value' : `Step ${this.steps.length + 1}`,
      dataset: this.selectedDatasets[0] || '',
      field: '',
      aggregation: 'SUM',
      sign: 'AUTO',
      formula: '',
      formulaMode: false,
    };
    this.steps = [...this.steps, step];
    if (step.dataset) this.loadColumnsForDataset(step.dataset);
  }

  removeStep(step: BridgeStep): void {
    this.steps = this.steps.filter((item) => item.id !== step.id);
    this.rowColumnsMemo.delete(step.id);
    this.invalidatePreview();
  }

  moveStepUp(index: number): void {
    if (index <= 0) return;
    this.reorderStep(index, index - 1);
  }

  moveStepDown(index: number): void {
    if (index >= this.steps.length - 1) return;
    this.reorderStep(index, index + 1);
  }

  private reorderStep(fromIndex: number, toIndex: number): void {
    const next = [...this.steps];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.steps = next;
    this.invalidatePreview();
  }

  // ── Phase C: state sanitization on step-type change ───────────────────────

  onStepTypeChange(step: BridgeStep): void {
    if (step.stepType === 'TOTAL') {
      step.formulaMode = true;
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
      step.sign = 'AUTO';
      step.formula = step.formula || '';
    } else {
      // START and CHANGE both support the dataset-vs-formula toggle, so each
      // can carry its own independent formula (an anchor with its own
      // computed baseline, or a floating segment computed from other bars).
      step.formulaMode = (step.stepType === 'CHANGE' || step.stepType === 'START') ? !!step.formulaMode : false;
      if (!step.formulaMode) step.formula = '';
      if (!step.dataset) {
        step.dataset = this.selectedDatasets[0] || '';
      }
      if (!step.aggregation) {
        step.aggregation = 'SUM';
      }
      // START is an anchor pillar — it never carries a sign modifier.
      step.sign = step.stepType === 'START' ? 'AUTO' : (step.sign || 'AUTO');
      if (step.dataset) this.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  /** Toggles a START or CHANGE step between a dataset-driven aggregation and a
   *  free-form formula — each step gets its OWN independent formula this way
   *  (TOTAL is always formula-driven, so it never needs this toggle). */
  toggleStepFormulaMode(step: BridgeStep): void {
    if (step.stepType !== 'CHANGE' && step.stepType !== 'START') return;
    step.formulaMode = !step.formulaMode;
    if (step.formulaMode) {
      step.dataset = '';
      step.field = '';
      step.aggregation = '';
    } else {
      step.formula = '';
      step.dataset = this.selectedDatasets[0] || '';
      step.aggregation = 'SUM';
      if (step.dataset) this.loadColumnsForDataset(step.dataset);
    }
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onStepDatasetChange(step: BridgeStep, dataset: string | null): void {
    step.dataset = dataset || '';
    step.field = '';
    this.steps = [...this.steps];
    if (step.dataset) this.loadColumnsForDataset(step.dataset);
    this.invalidatePreview();
  }

  onStepFieldChange(step: BridgeStep, field: string | null): void {
    step.field = field || '';
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  onStepFormulaOrLabelChange(): void {
    this.steps = [...this.steps];
    this.invalidatePreview();
  }

  isStepColumnsLoading(step: BridgeStep): boolean {
    return this.isDatasetColumnsLoading(step.dataset);
  }

  stepFieldPlaceholder(step: BridgeStep): string {
    if (!step.dataset) return 'Select a dataset first';
    return this.isStepColumnsLoading(step) ? 'Loading columns...' : 'Select field';
  }

  columnsForStep(step: BridgeStep): string[] {
    return this.columnsForRow(step.id, step.dataset);
  }

  // ── Table & Matrix column rows (Normal/Editable) — per-column dataset ──────
  // Same per-row dataset/field pattern as bridge steps: each column row loads
  // (and memoizes) its OWN dataset's column list, so a table can mix columns
  // from several datasets. The payload sends {dataset, field, displayName}.

  /** `toggle` = add to the header view toggle's second-view list instead. */
  addTmColumn(toggle = false): void {
    const row: TmColumnDef = {
      id: this.nextTmColumnId(),
      dataset: toggle ? this.tmToggleDatasetEffective : this.drillDataset,
      field: '',
      displayName: '',
      align: '',
      filterField: '',
      filterOperator: 'eq',
      filterValue: '',
    };
    if (toggle) this.tmToggleColumns = [...this.tmToggleColumns, row];
    else this.tmColumns = [...this.tmColumns, row];
  }

  removeTmColumn(column: TmColumnDef): void {
    if (this.isTmToggleColumn(column)) this.tmToggleColumns = this.tmToggleColumns.filter((item) => item.id !== column.id);
    else this.tmColumns = this.tmColumns.filter((item) => item.id !== column.id);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDatasetChange(column: TmColumnDef, dataset: string | null): void {
    column.dataset = dataset || '';
    column.field = '';
    this.touchTmColumns(column);
    if (column.dataset) this.loadColumnsForDataset(column.dataset);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFieldChange(column: TmColumnDef, field: string | null): void {
    const previousDefault = this.defaultColumnDisplayName(column.field);
    column.field = field || '';
    // Follow the field with a title-cased default display name unless the user
    // typed their own (i.e. it no longer matches the previous field's default).
    if (!column.displayName || column.displayName === previousDefault) {
      column.displayName = this.defaultColumnDisplayName(column.field);
    }
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnDisplayNameChange(column: TmColumnDef, displayName: string): void {
    column.displayName = displayName;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** (Editable tables) toggle a single column between editable and read-only. */
  onTmColumnEditableChange(column: TmColumnDef, editable: boolean): void {
    column.editable = editable;
    if (editable && !column.control) column.control = 'text';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** (Editable tables) pick the input control for an editable column's cells. */
  onTmColumnControlChange(column: TmColumnDef, control: string | null): void {
    column.control = control || 'text';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** Show/hide a single column in the rendered table. Presentation-only: the
   *  column still reaches `model.columns`, so its value is still sent on save. */
  onTmColumnHiddenChange(column: TmColumnDef, hidden: boolean): void {
    column.hidden = hidden;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnSaveAllTriggerChange(column: TmColumnDef, saveAllTrigger: boolean): void {
    column.saveAllTrigger = saveAllTrigger;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnMandatoryChange(column: TmColumnDef, mandatory: boolean): void {
    column.mandatory = mandatory;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** Toggle a column into/out of ACTION mode. Turning it on seeds a sensible
   *  default kind + icon so the column renders something immediately instead of
   *  a blank button, and clears the data-column settings it can no longer use. */
  onTmColumnIsActionChange(column: TmColumnDef, isAction: boolean): void {
    column.isAction = isAction;
    if (isAction) {
      column.actionKind = column.actionKind || 'rowPopup';
      column.buttonIcon = column.buttonIcon || 'visibility';
      column.actionMethod = column.actionMethod || 'POST';
      // An action column has no value to edit, hide, or require.
      column.editable = undefined;
      column.control = '';
      column.hidden = false;
      column.mandatory = false;
      column.saveAllTrigger = false;
    }
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** Any action-column field changed (kind / url / icon / …). */
  onTmColumnActionChange(): void {
    this.tmColumns = [...this.tmColumns];
    this.tmToggleColumns = [...this.tmToggleColumns];
    this.onTableMatrixTypeChange();
  }

  /**
   * Validate an action column's JSON body template, returning a message or ''.
   *
   * Placeholders are STUBBED before parsing, because `{"id": "{rule_name}"}` is
   * valid as a template but `{premium}` in a value position is not valid JSON on
   * its own — checking the raw text would flag every correct template. Stubbing
   * mirrors what the chart substitutes at click time, so what validates here is
   * what actually gets sent.
   */
  tmActionBodyError(column: TmColumnDef): string {
    const raw = String(column.actionBody ?? '').trim();
    if (!raw) return '';   // blank = send the whole row; nothing to validate
    const stubbed = raw
      .replace(/"\{[^{}"]+\}"/g, '"x"')   // quoted token → a string
      .replace(/\{[^{}"]+\}/g, 'null');   // bare token   → a JSON value
    try {
      const parsed = JSON.parse(stubbed);
      if (parsed === null || typeof parsed !== 'object') {
        return 'Body should be a JSON object or array.';
      }
      return '';
    } catch (error) {
      return `Invalid JSON — ${(error as Error).message}`;
    }
  }

  /** The `{field}` tokens this table can substitute: every configured column
   *  that maps to a real dataset field (action columns have none). */
  tmActionTokens(): string[] {
    return this.tmColumns
      .filter((column) => !column.isAction && column.field)
      .map((column) => `{${column.field}}`);
  }

  /** Click a token chip → append it to the body template, seeding a minimal
   *  object when the template is still empty so the result stays valid JSON. */
  appendTmActionToken(column: TmColumnDef, token: string): void {
    const field = token.replace(/[{}]/g, '');
    const current = String(column.actionBody ?? '').trim();
    column.actionBody = current
      ? `${current.replace(/\}\s*$/, '').replace(/,\s*$/, '')},\n  "${field}": ${token}\n}`
      : `{\n  "${field}": ${token}\n}`;
    this.onTmColumnActionChange();
  }

  /** Whether a column row is editable. Type-aware default (matches runtime):
   *  Editable tables → absent = editable; Normal tables → absent = read-only. */
  isTmColumnEditable(column: TmColumnDef): boolean {
    return this.isTmEditable ? column.editable !== false : column.editable === true;
  }

  onTmColumnAlignChange(column: TmColumnDef, align: string | null): void {
    column.align = align === 'start' || align === 'center' || align === 'end' ? align : '';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** Per-column default filter — WHICH column it runs against (blank = this
   *  row's own field; can be pointed at any OTHER column of the same
   *  dataset). */
  onTmColumnFilterFieldChange(column: TmColumnDef, field: string | null): void {
    column.filterField = field || '';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  /** Operator (blank `filterValue` = no filter for this row, regardless of
   *  operator/filterField). */
  onTmColumnFilterOperatorChange(column: TmColumnDef, operator: string | null): void {
    column.filterOperator = operator || 'eq';
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  onTmColumnFilterValueChange(column: TmColumnDef, value: string): void {
    column.filterValue = value;
    this.touchTmColumns(column);
    this.onTableMatrixTypeChange();
  }

  columnsForTmColumn(column: TmColumnDef): string[] {
    return this.columnsForRow(column.id, this.tmDatasetFor(column));
  }

  isTmColumnLoading(column: TmColumnDef): boolean {
    return this.isDatasetColumnsLoading(this.tmDatasetFor(column));
  }

  tmColumnFieldPlaceholder(column: TmColumnDef): string {
    if (!this.tmDatasetFor(column)) return 'Select a dataset first';
    return this.isTmColumnLoading(column) ? 'Loading columns...' : 'Select column';
  }

  /** "technical_configuration" → "Technical Configuration" — the default
   *  `displayName` when the user hasn't typed one. */
  private defaultColumnDisplayName(field: string): string {
    return String(field || '')
      .replace(/[_\-.]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** The `columns` array for payloads/capabilities: one {dataset, field,
   *  displayName} per configured row (rows without a field yet are skipped;
   *  blanks fall back to the primary dataset / title-cased field). Editable
   *  tables additionally persist per-column `editable` (+ `control` for the
   *  editable ones) so restore and publish keep read-only columns locked. */
  /** Stable synthetic field key for an action column. Derived from the builder
   *  row's own id so it survives edits to the display name, and prefixed so it
   *  can never collide with a real dataset field. */
  private tmActionFieldKey(column: TmColumnDef): string {
    return `__action_${String(column.id || '').replace(/[^a-zA-Z0-9_]/g, '') || 'col'}`;
  }

  /** Payload columns for the SHAPE types that configure FIELDS instead of
   *  column cards (Matrix/Pivot): the deduped union of their row/column/value
   *  fields as {dataset, field, displayName} entries. The flat NORMAL compute
   *  contract then returns exactly the fields the client-side pivot needs —
   *  and the backend's "non-empty columns" component validation passes. */
  private tmShapeFieldColumns(): Array<{ dataset: string; field: string; displayName: string }> {
    const fields = this.isTmMatrix
      ? [this.tmRowField, this.tmColumnField, this.tmValueField]
      : this.isTmPivot
        ? [...this.tmRowFields, ...this.tmColumnFields2, ...(this.tmValueFields.length ? this.tmValueFields : [this.tmValueField])]
        : [];
    const seen = new Set<string>();
    return fields
      .map((field) => String(field || '').trim())
      .filter((field) => field && !seen.has(field) && (seen.add(field), true))
      .map((field) => ({
        dataset: this.drillDataset,
        field,
        displayName: this.defaultColumnDisplayName(field),
      }));
  }

  /** Map payload column entries onto the runtime `capabilities.columns` shape.
   *  Shared by the table's own columns and the header view toggle's second
   *  view, so both render with identical per-column behaviour. */
  private tmCapabilityColumns(cols: ReturnType<DvmLayoutBuilderComponent['tmColumnEntries']>): TableMatrixColumn[] {
    return cols.map((column) => ({
      field: column.field,
      label: column.displayName,
      ...(column.hidden ? { hidden: true } : {}),
      ...(column.saveAllTrigger ? { saveAllTrigger: true } : {}),
      ...(column.mandatory ? { mandatory: true } : {}),
      ...(column.isAction
        ? {
            isAction: true as const,
            ...(column.buttonIcon ? { buttonIcon: column.buttonIcon } : {}),
            ...(column.buttonLabel ? { buttonLabel: column.buttonLabel } : {}),
            actionKind: (column.actionKind || 'emit') as TableMatrixColumn['actionKind'],
            ...(column.popupComponentId ? { popupComponentId: column.popupComponentId } : {}),
            ...(column.popupData ? { popupData: column.popupData } : {}),
            ...(column.actionUrl ? { actionUrl: column.actionUrl } : {}),
            ...(column.actionMethod ? { actionMethod: column.actionMethod as TableMatrixColumn['actionMethod'] } : {}),
            ...(column.actionBody ? { actionBody: column.actionBody } : {}),
            ...(column.actionRowsPath ? { actionRowsPath: column.actionRowsPath } : {}),
            ...(column.actionRowsSortBy ? { actionRowsSortBy: column.actionRowsSortBy } : {}),
            ...(column.actionRowsSortDir ? { actionRowsSortDir: column.actionRowsSortDir } : {}),
            ...(Number(column.actionMaxRows) > 0 ? { actionMaxRows: Number(column.actionMaxRows) } : {}),
            ...(column.actionRowsColumns ? { actionRowsColumns: column.actionRowsColumns } : {}),
            ...(column.actionTitle ? { actionTitle: column.actionTitle } : {}),
            ...(column.actionConfirm ? { actionConfirm: column.actionConfirm } : {}),
            ...(column.actionRefresh ? { actionRefresh: true } : {}),
          }
        : {}),
      ...(column.align ? { align: column.align } : {}),
      ...(column.editable === false ? { editable: false as const } : {}),
      ...(column.editable !== false && column.control
        ? { control: column.control as TableMatrixColumn['control'] }
        : {}),
    }));
  }

  private tmColumnEntries(source?: TmColumnDef[]): Array<{ dataset: string; field: string; displayName: string; editable?: boolean; control?: string; hidden?: boolean; saveAllTrigger?: boolean; mandatory?: boolean; isAction?: boolean; buttonIcon?: string; buttonLabel?: string; actionKind?: string; popupComponentId?: string; popupData?: Record<string, unknown>; actionUrl?: string; actionMethod?: string; actionBody?: string; actionRowsPath?: string; actionRowsSortBy?: string; actionRowsSortDir?: 'asc' | 'desc'; actionMaxRows?: number; actionRowsColumns?: string; actionTitle?: string; actionConfirm?: string; actionRefresh?: boolean; align?: 'start' | 'center' | 'end'; filterField?: string; filterOperator?: string; filterValue?: string }> {
    // Defaults to the table's own column list; the header view toggle passes
    // its second view's list (+ that view's dataset) to build the same entries.
    const list = source ?? this.tmColumns;
    const fallbackDataset = source ? this.tmToggleDatasetEffective : this.drillDataset;
    return list
      // An ACTION column deliberately has no dataset field — it renders a
      // button, not a value — so it must survive the "field required" filter
      // that every data column has to pass.
      .filter((column) => column.field || column.isAction)
      .map((column) => ({
        dataset: column.isAction ? '' : (column.dataset || fallbackDataset),
        // Action columns get a synthetic key: the model needs a unique column
        // key, and it must never collide with a real dataset field (hence the
        // `__action_` prefix) because nothing is read or saved under it.
        field: column.isAction ? this.tmActionFieldKey(column) : column.field,
        displayName: column.isAction
          ? (column.displayName || '')
          : (column.displayName || this.defaultColumnDisplayName(column.field)),
        // Editable tables default a column to editable; every other type
        // defaults to read-only and only an explicit `editable: true` (the
        // per-column toggle on Normal tables) carries a control.
        ...(this.isTmEditable
          ? (column.editable === false
              ? { editable: false }
              : { editable: true, control: column.control || 'text' })
          : (column.editable === true
              ? { editable: true, control: column.control || 'text' }
              : {})),
        // Presentation-only: a hidden column is not rendered, but stays in the
        // model so the save payload still carries its value.
        ...(column.hidden ? { hidden: true } : {}),
        ...(column.saveAllTrigger ? { saveAllTrigger: true } : {}),
        ...(column.mandatory ? { mandatory: true } : {}),
        // ACTION column: carries its button + click behaviour instead of a
        // value. Emitted only when the flag is on, so data columns are
        // byte-identical to before this feature.
        ...(column.isAction
          ? {
              isAction: true,
              ...(column.buttonIcon?.trim() ? { buttonIcon: column.buttonIcon.trim() } : {}),
              ...(column.buttonLabel?.trim() ? { buttonLabel: column.buttonLabel.trim() } : {}),
              actionKind: column.actionKind || 'emit',
              // popupComponent: persist BOTH the reference (so the builder can
              // re-select it on edit) and a snapshot of the popup's blocks (so
              // the published table renders without a sibling-tile lookup).
              ...(column.actionKind === 'popupComponent' && column.popupComponentId?.trim()
                ? {
                    popupComponentId: column.popupComponentId.trim(),
                    ...(this.tmPopupDataFor(column.popupComponentId) ? { popupData: this.tmPopupDataFor(column.popupComponentId)! } : {}),
                  }
                : {}),
              ...(column.actionUrl?.trim() ? { actionUrl: column.actionUrl.trim() } : {}),
              ...(column.actionMethod ? { actionMethod: column.actionMethod } : {}),
              ...(column.actionBody?.trim() ? { actionBody: column.actionBody.trim() } : {}),
              ...(column.actionRowsPath?.trim() ? { actionRowsPath: column.actionRowsPath.trim() } : {}),
              ...(column.actionRowsSortBy?.trim() ? { actionRowsSortBy: column.actionRowsSortBy.trim() } : {}),
              ...(column.actionRowsSortDir ? { actionRowsSortDir: column.actionRowsSortDir } : {}),
              ...(Number(column.actionMaxRows) > 0 ? { actionMaxRows: Number(column.actionMaxRows) } : {}),
              ...(column.actionRowsColumns?.trim() ? { actionRowsColumns: column.actionRowsColumns.trim() } : {}),
              ...(column.actionTitle?.trim() ? { actionTitle: column.actionTitle.trim() } : {}),
              ...(column.actionConfirm?.trim() ? { actionConfirm: column.actionConfirm.trim() } : {}),
              ...(column.actionRefresh ? { actionRefresh: true } : {}),
            }
          : {}),
        ...(column.align ? { align: column.align } : {}),
        // Carried along (harmless extra keys) purely so `restoreTmColumns` can
        // rebuild this exact row's filter on edit — the actual filtering
        // instruction the backend acts on is the separate `initialFilters` key.
        ...(column.filterValue?.trim()
          ? { filterField: column.filterField || column.field, filterOperator: column.filterOperator || 'eq', filterValue: column.filterValue.trim() }
          : {}),
      }));
  }

  // ── Header view toggle ──────────────────────────────────────────────────────
  /** Dataset whose columns the second view offers (its own, else the primary). */
  get tmToggleDatasetEffective(): string {
    return this.tmToggleDataset || this.drillDataset;
  }
  onTmToggleDatasetChange(dataset: string | null): void {
    this.tmToggleDataset = dataset || '';
    // Columns of the previous dataset no longer exist on the new one.
    this.tmToggleColumns = [];
    if (this.tmToggleDataset) this.loadColumnsForDataset(this.tmToggleDataset);
    this.onTableMatrixTypeChange();
  }
  /** Drill-Down's simplified Option 2 picker: just field names (a plain
   *  read-only multi-select, unlike the Normal/Editable per-column card
   *  editor), read back out of `tmToggleColumns` for the ng-select binding.
   *  Memoized on the `tmToggleColumns` array reference — ng-select's
   *  `[multiple]` binding treats a fresh array on every read as an external
   *  value change and re-fires its writeValue, which re-triggers change
   *  detection and calls this getter again; without memoizing that becomes a
   *  tight synchronous loop that hangs the tab the moment the toggle (or this
   *  select) renders. */
  private _drillToggleColumnFieldsCache: { source: TmColumnDef[]; fields: string[] } | null = null;
  get drillToggleColumnFields(): string[] {
    if (this._drillToggleColumnFieldsCache?.source === this.tmToggleColumns) {
      return this._drillToggleColumnFieldsCache.fields;
    }
    const fields = this.tmToggleColumns.map((c) => c.field).filter(Boolean);
    this._drillToggleColumnFieldsCache = { source: this.tmToggleColumns, fields };
    return fields;
  }
  onDrillToggleColumnsChange(fields: string[] | null): void {
    const list = Array.isArray(fields) ? fields : [];
    const dataset = this.tmToggleDatasetEffective;
    this.tmToggleColumns = list.map((field) => ({
      id: this.nextTmColumnId(),
      dataset,
      field,
      displayName: this.defaultColumnDisplayName(field),
      align: '',
      filterField: '',
      filterOperator: 'eq',
      filterValue: '',
    }));
    this.onTableMatrixTypeChange();
  }
  /** The `toggleView` block baked onto the tile — each view's columns + the
   *  compute payload that fetches them. BOTH views carry their own request so
   *  every switch re-reads from the API (no rows are cached in the UI).
   *  Undefined when the toggle is off (nothing changes for tables not using it). */
  private buildTmToggleView(): Record<string, unknown> | undefined {
    if (!this.tmToggleEnabled) return undefined;
    const computeUrl = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
    const dataset = this.tmToggleDatasetEffective;
    // Second view's columns carry the SAME per-column config as the table's own.
    const columns = this.tmColumnEntries(this.tmToggleColumns);
    return {
      enabled: true,
      labels: [this.tmToggleLabel1 || 'View 1', this.tmToggleLabel2 || 'View 2'],
      default: 1,
      // View 1 = the table as configured above; re-requested on switch-back with
      // ITS dataset + columns (the same payload Generate sends).
      // For Drill-Down, "view 1" is the tree itself — it's already rendered
      // and `setToggleView(1)` switches back to it with no refetch, so there's
      // no flat-table payload to build (buildFlatTableComputePayload() assumes
      // the Normal/Editable column shape and isn't meaningful here).
      view1: this.isTmDrilldown
        ? { apiUrl: computeUrl }
        : {
            apiUrl: computeUrl,
            requestPayload: this.buildFlatTableComputePayload(),
          },
      view2: {
        // The second view's own columns drive its rendered header + behaviour
        // (editable/control, alignment, hidden, action columns) — same mapping
        // the table's primary columns get.
        // Only the keys that DIFFER from the table's own capabilities — the
        // runtime layers these over them, so all Format-popup styling (colours,
        // number format, density, freeze, fonts) applies to this view too.
        capabilities: {
          ...(columns.length ? { columns: this.tmCapabilityColumns(columns) } : {}),
          // Always explicit: inheriting the other view's cell-editing state
          // would silently make this view editable (or not) by accident.
          cellEditing: this.isTmNormal && columns.some((c) => c.editable === true && c.control),
          dataset,
        },
        apiUrl: computeUrl,
        // POSTed verbatim on switch — the SAME flat-table contract the builder
        // uses, carrying THIS view's dataset + columns.
        requestPayload: {
          componentId: this.componentId,
          componentType: 'TABLE',
          tableType: 'NORMAL',
          subType: this.tmType,
          dashboardId: this.dashboardId,
          title: this.title.trim(),
          dataset,
          columns,
          aggregation: this.tmAggregation,
        },
      },
    };
  }
  /** Restore the toggle config from a saved payload/capabilities block. */
  private restoreTmToggleView(raw: unknown): void {
    const block = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    this.tmToggleEnabled = block['enabled'] === true;
    const labels = Array.isArray(block['labels']) ? block['labels'] as unknown[] : [];
    this.tmToggleLabel1 = String(labels[0] ?? '');
    this.tmToggleLabel2 = String(labels[1] ?? '');
    const view2 = (block['view2'] && typeof block['view2'] === 'object' ? block['view2'] : {}) as Record<string, unknown>;
    const request = (view2['requestPayload'] && typeof view2['requestPayload'] === 'object' ? view2['requestPayload'] : {}) as Record<string, unknown>;
    this.tmToggleDataset = String(request['dataset'] ?? '');
    this.tmToggleColumns = this.tmColumnDefsFrom(request['columns']);
    // Warm each referenced dataset so the restored pickers show their values.
    new Set(this.tmToggleColumns.map((column) => column.dataset || this.tmToggleDatasetEffective).filter(Boolean))
      .forEach((dataset) => this.loadColumnsForDataset(dataset));
  }

  /** Rebuild `tmColumns` from a saved payload's `columns` — accepts BOTH the
   *  new object shape ({dataset, field, displayName, filterField,
   *  filterOperator, filterValue}) and the legacy plain string[] shape, so
   *  older saved dashboards restore cleanly. `legacyInitialFilters` backfills
   *  a row's filter from the OLD standalone `initialFilters` payload shape
   *  (matched onto the column whose OWN field equals that filter's key), for
   *  dashboards saved before columns carried their own filter state. */
  private restoreTmColumns(raw: unknown, legacyInitialFilters?: unknown): void {
    this.tmColumns = this.tmColumnDefsFrom(raw, legacyInitialFilters);
    // Warm every referenced dataset's column cache so the row pickers render
    // their selections (instead of empty dropdowns) straight away.
    const datasets = new Set(this.tmColumns.map((column) => column.dataset || this.drillDataset).filter(Boolean));
    datasets.forEach((dataset) => this.loadColumnsForDataset(dataset));
  }

  /** Saved column entries → editable `TmColumnDef` rows. Shared by the table's
   *  own columns and the header view toggle's second view. */
  private tmColumnDefsFrom(raw: unknown, legacyInitialFilters?: unknown): TmColumnDef[] {
    const list = Array.isArray(raw) ? raw : [];
    const legacyByField = new Map<string, { operator: string; value: string }>();
    if (Array.isArray(legacyInitialFilters)) {
      legacyInitialFilters.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;
        const record = entry as Record<string, unknown>;
        const field = String(record['filterKey'] ?? '');
        if (!field) return;
        legacyByField.set(field, {
          operator: String(record['filterOperator'] ?? 'eq'),
          value: String(record['filterValue'] ?? ''),
        });
      });
    }
    return list
      .map((entry): TmColumnDef => {
        if (entry && typeof entry === 'object') {
          const record = entry as Record<string, unknown>;
          const field = String(record['field'] ?? '');
          const legacy = legacyByField.get(field);
          return {
            id: this.nextTmColumnId(),
            dataset: String(record['dataset'] ?? ''),
            field,
            displayName: String(record['displayName'] ?? record['label'] ?? ''),
            // Preserve absence — the type-aware default applies at render time
            // (Editable: absent = editable; Normal: absent = read-only), so
            // pre-feature payloads restore with their original behaviour.
            editable: typeof record['editable'] === 'boolean' ? record['editable'] : undefined,
            control: typeof record['control'] === 'string' ? record['control'] : '',
            hidden: record['hidden'] === true,
            saveAllTrigger: record['saveAllTrigger'] === true,
            mandatory: record['mandatory'] === true,
            isAction: record['isAction'] === true,
            buttonIcon: String(record['buttonIcon'] ?? ''),
            buttonLabel: String(record['buttonLabel'] ?? ''),
            actionKind: (record['actionKind'] as TmColumnDef['actionKind']) ?? undefined,
            actionUrl: String(record['actionUrl'] ?? ''),
            actionMethod: (record['actionMethod'] as TmColumnDef['actionMethod']) ?? undefined,
            actionBody: String(record['actionBody'] ?? ''),
            popupComponentId: String(record['popupComponentId'] ?? ''),
            actionRowsPath: String(record['actionRowsPath'] ?? ''),
            actionRowsSortBy: String(record['actionRowsSortBy'] ?? ''),
            actionRowsSortDir: (record['actionRowsSortDir'] as TmColumnDef['actionRowsSortDir']) ?? undefined,
            actionMaxRows: Number(record['actionMaxRows']) > 0 ? Number(record['actionMaxRows']) : undefined,
            actionRowsColumns: String(record['actionRowsColumns'] ?? ''),
            actionTitle: String(record['actionTitle'] ?? ''),
            actionConfirm: String(record['actionConfirm'] ?? ''),
            actionRefresh: record['actionRefresh'] === true,
            align: this.asTmColumnAlign(record['align']),
            filterField: String(record['filterField'] ?? ''),
            filterOperator: String(record['filterOperator'] ?? legacy?.operator ?? 'eq'),
            filterValue: String(record['filterValue'] ?? legacy?.value ?? ''),
          };
        }
        const field = String(entry ?? '');
        const legacy = legacyByField.get(field);
        return {
          id: this.nextTmColumnId(), dataset: '', field, displayName: '', align: '', filterField: '',
          filterOperator: legacy?.operator ?? 'eq', filterValue: legacy?.value ?? '',
        };
      })
      .filter((column) => column.field || column.isAction);
  }

  private asTmColumnAlign(value: unknown): '' | 'start' | 'center' | 'end' {
    return value === 'start' || value === 'center' || value === 'end' ? value : '';
  }

  // ── Drill-down dimension CRUD + reordering (DRILL_DOWN_TABLE) ────────────────────
  // Dimensions are the hierarchy levels (Level 1 = outermost group, etc.) —
  // order in the array IS the level order, so no separate "level" field to
  // keep in sync.

  addDrillDimension(): void {
    const dimension: DrillDownDimension = {
      id: this.nextDrillDimensionId(),
      field: '',
      label: `Level ${this.drillDownDimensions.length + 1}`,
      editable: false,
    };
    this.drillDownDimensions = [...this.drillDownDimensions, dimension];
  }

  /** Split the typed value-order into a clean list. Accepts comma OR newline
   *  separators so pasting a column of values from Excel works as well as
   *  typing them inline. Blank entries are dropped, so trailing commas and
   *  stray blank lines are harmless. */
  private parseValueOrder(raw: unknown): string[] {
    // A `multi-select` control stores an array; text controls store a
    // comma / newline separated string — both normalize to a trimmed list.
    if (Array.isArray(raw)) return raw.map((value) => String(value ?? '').trim()).filter(Boolean);
    return String(raw ?? '')
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  /** Reverse of `parseValueOrder` — turn a saved `valueOrder` array back into
   *  the comma-separated text the input edits. Tolerates a plain string too, in
   *  case a hand-written config supplied one. */
  private valueOrderToText(raw: unknown): string {
    if (Array.isArray(raw)) return raw.map((value) => String(value).trim()).filter(Boolean).join(', ');
    return typeof raw === 'string' ? raw : '';
  }

  /** The `valueOrder` / `sortBy` fragment for one dimension's payload entry —
   *  keys are omitted when unset, so they never reach the backend unless
   *  actually configured. */
  private valueOrderPayload(dimension: DrillDownDimension): Record<string, string[] | string> {
    const order = this.parseValueOrder(dimension.valueOrder);
    return {
      ...(order.length ? { valueOrder: order } : {}),
      ...(dimension.sortBy ? { sortBy: dimension.sortBy } : {}),
    };
  }

  private restoreValueOrder(item: Record<string, unknown>): Pick<DrillDownDimension, 'valueOrder' | 'sortBy'> {
    const rawSortBy = item['sortBy'];
    const sortBy = rawSortBy === 'custom' || rawSortBy === 'alpha-asc' || rawSortBy === 'alpha-desc' ? rawSortBy : undefined;
    return {
      valueOrder: this.valueOrderToText(item['valueOrder']),
      ...(sortBy ? { sortBy } : {}),
    };
  }

  /** The dropdown-editor fragment for one level's / measure's payload,
   *  capabilities and widget-data entry: `editor: 'dropdown'` + the parsed
   *  custom option list (+ `dropdownFromData`). Empty unless the row is
   *  editable AND set to a dropdown, so every other tile's payload stays
   *  byte-identical. */
  private dropdownEditorPayload(row: { editable?: boolean; editor?: string; dropdownOptions?: string; dropdownFromData?: boolean }): Record<string, unknown> {
    if (!row.editable || row.editor !== 'dropdown') return {};
    return {
      editor: 'dropdown',
      dropdownOptions: parseDropdownOptions(row.dropdownOptions),
      ...(row.dropdownFromData ? { dropdownFromData: true } : {}),
    };
  }

  /** Reverse of dropdownEditorPayload — the form fields from a saved entry
   *  (options back to the comma-separated text the input edits). */
  private restoreDropdownEditor(item: Record<string, unknown>): { editor?: 'dropdown'; dropdownOptions?: string; dropdownFromData?: boolean } {
    if (item['editor'] !== 'dropdown') return {};
    return {
      editor: 'dropdown',
      dropdownOptions: parseDropdownOptions(item['dropdownOptions']).join(', '),
      ...(item['dropdownFromData'] === true ? { dropdownFromData: true } : {}),
    };
  }

  /** The "Editable on levels" fragment for one measure's payload /
   *  capabilities entry: `editableLevels: [1, 2, …]` (1-based, de-duplicated,
   *  clamped to the levels that currently exist). Empty unless the measure is
   *  editable AND the user picked levels, so the default (text: every level,
   *  number: leaf only) stays implicit and other tiles' payloads unchanged. */
  private editableLevelsPayload(measure: { editable?: boolean; editableLevels?: number[] }): Record<string, number[]> {
    if (!measure.editable || !Array.isArray(measure.editableLevels)) return {};
    const max = this.drillDownDimensions.length;
    const levels = Array.from(new Set(measure.editableLevels.map(Number)))
      .filter((level) => Number.isInteger(level) && level >= 1 && level <= max)
      .sort((a, b) => a - b);
    return levels.length ? { editableLevels: levels } : {};
  }

  /** `rowTotalHeader` for the payload / capabilities / tree config — only
   *  when the measure's "Header in Total block" is filled (existing tiles'
   *  payloads stay byte-identical). */
  private rowTotalHeaderPayload(measure: { rowTotalHeader?: string }): { rowTotalHeader?: string } {
    const header = String(measure.rowTotalHeader ?? '').trim();
    return header ? { rowTotalHeader: header } : {};
  }

  /** Reverse of editableLevelsPayload — a saved `editableLevels` back onto the
   *  measure form row (absent when the entry has none). */
  private restoreEditableLevels(item: Record<string, unknown>): { editableLevels?: number[] } {
    const raw = item['editableLevels'];
    if (!Array.isArray(raw)) return {};
    const levels = Array.from(new Set(raw.map(Number))).filter((level) => Number.isInteger(level) && level >= 1).sort((a, b) => a - b);
    return levels.length ? { editableLevels: levels } : {};
  }

  /** ng-select items for a measure's "Editable on levels" multi-select: one
   *  entry per dimension (Level 1 · <label> … last = leaf rows). Memoized on
   *  the dimension labels — an appendTo="body" ng-select whose [items] getter
   *  returns a fresh array every change-detection cycle swallows its clicks. */
  private editableLevelOptionsCache: { key: string; items: Array<{ label: string; value: number }> } | null = null;
  get drillEditableLevelOptions(): Array<{ label: string; value: number }> {
    const dimensions = this.drillDownDimensions;
    const key = dimensions.map((dimension) => dimension.label || dimension.field || '').join('');
    if (this.editableLevelOptionsCache?.key === key) return this.editableLevelOptionsCache.items;
    const items = dimensions.map((dimension, index) => {
      const name = dimension.label || dimension.field || '';
      const suffix = index === dimensions.length - 1 ? ' (leaf rows)' : '';
      return { label: `Level ${index + 1}${name ? ` · ${name}` : ''}${suffix}`, value: index + 1 };
    });
    this.editableLevelOptionsCache = { key, items };
    return items;
  }
  onDrillMeasureLevelsChange(measure: DrillDownMeasure, levels: unknown): void {
    const picked = Array.isArray(levels) ? levels.map(Number).filter((level) => Number.isInteger(level) && level >= 1) : [];
    measure.editableLevels = picked.length ? picked : undefined;
    this.onDrillMeasureChange();
  }

  removeDrillDimension(dimension: DrillDownDimension): void {
    this.drillDownDimensions = this.drillDownDimensions.filter((item) => item.id !== dimension.id);
    this.invalidatePreview();
  }

  moveDrillDimensionUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillDimension(index, index - 1);
  }

  moveDrillDimensionDown(index: number): void {
    if (index >= this.drillDownDimensions.length - 1) return;
    this.reorderDrillDimension(index, index + 1);
  }

  private reorderDrillDimension(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownDimensions];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownDimensions = next;
    this.invalidatePreview();
  }

  onDrillDimensionChange(): void {
    this.drillDownDimensions = [...this.drillDownDimensions];
    this.invalidatePreview();
  }

  // ── Drill-down measure CRUD + reordering (DRILL_DOWN_TABLE) ──────────────────────
  // Every measure applies at every level of the tree, rolled up per its own
  // rollUpStrategy — they're columns of the matrix, not per-level mappings.

  addDrillMeasure(): void {
    const measure: DrillDownMeasure = {
      id: this.nextDrillMeasureId(),
      field: '',
      label: `Measure ${this.drillDownMeasures.length + 1}`,
      aggregation: 'NONE',
      rollUpStrategy: 'SUM',
      total: false,
      editable: false,
    };
    this.drillDownMeasures = [...this.drillDownMeasures, measure];
  }

  removeDrillMeasure(measure: DrillDownMeasure): void {
    this.drillDownMeasures = this.drillDownMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
  }

  moveDrillMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderDrillMeasure(index, index - 1);
  }

  moveDrillMeasureDown(index: number): void {
    if (index >= this.drillDownMeasures.length - 1) return;
    this.reorderDrillMeasure(index, index + 1);
  }

  private reorderDrillMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.drillDownMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.drillDownMeasures = next;
    this.invalidatePreview();
  }

  onDrillMeasureChange(): void {
    this.drillDownMeasures = [...this.drillDownMeasures];
    this.invalidatePreview();
  }
  /** The "Decimal places (this column only)" number input — blank/invalid
   *  clears the override (falls back to the table's shared setting). */
  onDrillMeasureDecimalsChange(measure: DrillDownMeasure, value: unknown): void {
    const raw = Number(value);
    measure.decimals = (value === null || value === '' || !Number.isFinite(raw)) ? undefined : Math.max(0, Math.min(6, Math.floor(raw)));
    this.onDrillMeasureChange();
  }

  // ── Merged Drill-Down + Pivot (Table & Matrix drill-down type) ─────────────
  // Multiple datasets (per level / measure), pivot column dimension(s), formula
  // columns and a client-side tree build on top of the classic hierarchy — so
  // the separate Pivot Table type is no longer needed.

  private readonly drillDataModesFallback: LabelValueOption[] = [
    { label: 'Client-side (multiple datasets, pivot columns, formulas)', value: 'client' },
    { label: 'Backend tree (legacy dimensions + measures compute)', value: 'server' },
  ];
  /** "Data computation" options (config `drillDataModes`). Stable fallback
   *  field — this feeds an appendTo="body" ng-select. */
  get drillDataModes(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['drillDataModes'] as LabelValueOption[] | undefined)
      ?? this.drillDataModesFallback;
  }
  /** Measure value kinds (stable field — feeds an appendTo="body" ng-select). */
  readonly drillMeasureKinds: LabelValueOption[] = [
    { label: 'Number (aggregated)', value: 'number' },
    { label: 'Text (attribute / comment)', value: 'text' },
  ];
  /** Measure year scope (stable field — feeds an appendTo="body" ng-select):
   *  all rows (default) or only the newest year present in the data. */
  readonly drillMeasureYearScopes: LabelValueOption[] = [
    { label: 'All years (default)', value: 'all' },
    { label: 'Latest year only', value: 'latest' },
  ];
  /** Year scope picker: switching to "Latest year only" pre-fills the year
   *  column with the first column of the measure's dataset that looks like a
   *  year column (fiscal_year, year, …) so the common case needs no second
   *  click; the user can still pick another. */
  onDrillMeasureYearScopeChange(measure: DrillDownMeasure, value: string | null): void {
    if (value === 'latest') {
      measure.yearScope = 'latest';
      if (!String(measure.yearField || '').trim()) measure.yearField = this.defaultYearFieldFor(measure) || '';
    } else {
      measure.yearScope = undefined;
    }
    this.onDrillMeasureChange();
  }
  private defaultYearFieldFor(measure: { id?: string; dataset?: string }): string | undefined {
    return this.resolveYearColumn({ ...measure, yearScope: 'latest' })?.field;
  }
  /** The year column a "Latest year only" measure reads, WITH the dataset that
   *  owns it. The picked column when set, else the first year-looking column
   *  (fiscal_year / fy / year / *year*) of the measure's own dataset, else of
   *  the primary / any other dataset the table uses — rows arrive joined, so a
   *  year column from another dataset scopes this measure just as well. The
   *  owner is looked up the same way for a picked column, so the hidden helper
   *  measure and the compute column always request it from the dataset that
   *  really has it (asking the measure's dataset for a column it lacks is a
   *  backend error: "Dataset 'x' has no field 'fiscal_year'").
   *  A tile saved with the scope on but the column blank (older tiles; the
   *  auto-fill only runs when the picker is switched) used to be scoped to
   *  NOTHING — the tree builder needs both the flag and the column — so it
   *  silently averaged every year while the form said "Latest year only".
   *  null = no year column found anywhere. */
  private resolveYearColumn(m: { id?: string; yearScope?: string; yearField?: string; dataset?: string }): { field: string; dataset: string } | null {
    if (m.yearScope !== 'latest') return null;
    const primary = this.drillDataset || '';
    const candidates = Array.from(new Set([
      m.dataset || primary,
      primary,
      ...this.drillDownMeasures.map((x) => x.dataset || primary),
      ...this.drillDownDimensions.map((d) => d.dataset || primary),
      ...this.drillColumnDimensions.map((d) => d.dataset || primary),
    ].filter(Boolean)));
    const columnsOf = (ds: string): string[] => this.columnsForRow(`year-column|${ds}`, ds);
    const picked = String(m.yearField || '').trim();
    if (picked) {
      const owner = candidates.find((ds) => columnsOf(ds).includes(picked));
      return { field: picked, dataset: owner ?? (m.dataset || primary) };
    }
    for (const ds of candidates) {
      const cols = columnsOf(ds);
      const hit = cols.find((c) => /^(fiscal_?year|fy|year)$/i.test(c)) || cols.find((c) => /year/i.test(c));
      if (hit) return { field: hit, dataset: ds };
    }
    return null;
  }
  /** The year column a "Latest year only" measure actually reads (see
   *  resolveYearColumn). Empty = no year column found. */
  effectiveYearField(m: { id?: string; yearScope?: string; yearField?: string; dataset?: string }): string {
    return this.resolveYearColumn(m)?.field ?? '';
  }
  /** Form warning: "Latest year only" with no usable year column. */
  yearScopeUnresolved(m: DrillDownMeasure): boolean {
    return m.yearScope === 'latest' && !this.effectiveYearField(m) && this.drillRowHasDataset(m) && !this.isDrillRowLoading(m);
  }
  /** `yearScope` + `yearField` as a spreadable payload fragment — empty unless
   *  the scope is on, so existing tiles' payloads stay byte-identical. */
  private yearScopePayload(m: { id?: string; yearScope?: string; yearField?: string; showYearColumn?: boolean; dataset?: string }): { yearScope?: 'latest'; yearField?: string; showYearColumn?: true } {
    if (m.yearScope !== 'latest') return {};
    const field = this.effectiveYearField(m);
    // `showYearColumn` only when opted in — the default (hidden) stays implicit.
    return { yearScope: 'latest', ...(field ? { yearField: field } : {}), ...(m.showYearColumn === true ? { showYearColumn: true as const } : {}) };
  }
  /** Aggregation "Average per group" (AVG_BY): the group column as a payload
   *  fragment — only for that aggregation, so other measures stay untouched. */
  private groupFieldPayload(m: { aggregation?: string; groupField?: string }): { groupField?: string } {
    const field = String(m.groupField || '').trim();
    return String(m.aggregation || '').toUpperCase() === 'AVG_BY' && field ? { groupField: field } : {};
  }
  /** True for the "Average per group" aggregation (shows the group-column picker). */
  isAvgByMeasure(m: { aggregation?: string }): boolean {
    return String(m.aggregation || '').toUpperCase() === 'AVG_BY';
  }
  /** The year columns the scoped measures read, as HIDDEN helper measures for
   *  the saved payload / capabilities. The published view re-sends the saved
   *  payload to the compute API and rebuilds the tree from the flat rows it
   *  gets back — those rows only carry dimension / measure fields, so the year
   *  column has to ride along as a measure to reach the tree builder. Hidden
   *  (never a column), one per distinct dataset + column, skipped when the
   *  column is already a real measure; `yearHelper` marks them so the form
   *  restore drops them again. */
  private drillYearHelperMeasures(): DrillYearHelperMeasure[] {
    // Columns the flat rows ALREADY carry (blank dataset = primary, and an
    // explicit primary normalises to blank): every measure, level and pivot
    // column. A helper is only needed for a column outside this set.
    const primary = this.drillDataset || '';
    const norm = (ds: string | undefined): string => (!ds || ds === primary ? '' : ds);
    const real = new Set([
      ...this.drillDownMeasures.map((m) => `${norm(m.dataset)}|${m.field}`),
      ...this.drillDownDimensions.map((d) => `${norm(d.dataset)}|${d.field}`),
      ...this.drillColumnDimensions.map((d) => `${norm(d.dataset)}|${d.field}`),
    ]);
    const seen = new Set<string>();
    const helpers: DrillYearHelperMeasure[] = [];
    // "Average per group" measures: their group column (e.g. engine_model)
    // rides along the same way — hidden plumbing so the recompute's flat rows
    // carry it for the tree builder to group by. Skipped when the column is
    // already a level / pivot column / measure of the table.
    this.drillDownMeasures.forEach((m) => {
      const field = String(this.groupFieldPayload(m).groupField || '');
      if (!field) return;
      const dataset = norm(m.dataset);
      const key = `${dataset}|${field}`;
      if (real.has(key) || seen.has(key)) return;
      seen.add(key);
      helpers.push({ field, label: field, aggregation: 'MAX', rollUpStrategy: 'MAX', total: false, hidden: true, yearHelper: true as const, ...(dataset ? { dataset } : {}) });
    });
    this.drillDownMeasures.forEach((m) => {
      const yc = this.resolveYearColumn(m);
      if (!yc) return;
      const field = yc.field;
      // The helper asks the dataset that OWNS the year column (blank = primary,
      // the convention every other measure follows) — not the measure's.
      const dataset = norm(yc.dataset);
      const key = `${dataset}|${field}`;
      if (real.has(key) || seen.has(key)) return;
      seen.add(key);
      // "Show year column in table" on ANY scoped measure reading this column
      // turns the helper into a real column: one unsplit TEXT column (never
      // summed, no K/M formatting) holding the year that measure's values come
      // from — scoped to the newest year like the measure, so a row with no
      // rows in that year stays blank. Header = the field humanized
      // ("fiscal_year" → "Fiscal Year"); no "(2026)" suffix (see yearScopes).
      const show = this.drillDownMeasures.some((x) =>
        x.showYearColumn === true && this.effectiveYearField(x) === field);
      helpers.push({
        field,
        label: show ? this.humanizeFieldName(field) : field,
        aggregation: 'MAX', rollUpStrategy: 'MAX', total: false, hidden: !show, yearHelper: true as const,
        ...(show ? { pivot: false as const, kind: 'text' as const, yearScope: 'latest' as const, yearField: field } : {}),
        ...(dataset ? { dataset } : {}),
      });
    });
    return helpers;
  }
  /** "fiscal_year" → "Fiscal Year" for a helper column's header. */
  private humanizeFieldName(field: string): string {
    return String(field || '').replace(/[_\s]+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  /** Edit controls for an editable level label / measure cell (stable field —
   *  feeds an appendTo="body" ng-select). */
  readonly drillEditorKinds: LabelValueOption[] = [
    { label: 'Input (type a value)', value: 'input' },
    { label: 'Dropdown (pick from a list)', value: 'dropdown' },
  ];
  /** True when the Table & Matrix drill-down builds its tree client-side. */
  get isDrillClientMode(): boolean {
    return this.isTableMatrixLayout && this.isTmDrilldown && this.drillDataMode === 'client';
  }
  /** Columns of the dataset a level / pivot column / measure reads from (its
   *  own pick, else the primary). Memoized per row id (ng-select [items]). */
  columnsForDrillRow(row: { id: string; dataset?: string }): string[] {
    return this.columnsForRow(row.id, row.dataset || this.drillDataset);
  }
  isDrillRowLoading(row: { dataset?: string }): boolean {
    return this.isDatasetColumnsLoading(row.dataset || this.drillDataset);
  }
  drillRowHasDataset(row: { dataset?: string }): boolean {
    return !!(row.dataset || this.drillDataset);
  }
  /** A level / pivot column / measure switched dataset: its field belonged to
   *  the old dataset, so clear it, and warm the new dataset's columns. A pick
   *  equal to the primary is stored blank (= "follow the primary"). */
  onDrillRowDatasetChange(row: DrillDownDimension | DrillDownMeasure, dataset: string | null, kind: 'dimension' | 'columnDimension' | 'measure'): void {
    const next = dataset || '';
    row.dataset = next && next !== this.drillDataset ? next : '';
    row.field = '';
    if (next) this.loadColumnsForDataset(next);
    if (kind === 'dimension') this.onDrillDimensionChange();
    else if (kind === 'columnDimension') this.onDrillColumnDimensionChange();
    else this.onDrillMeasureChange();
  }
  onDrillDataModeChange(mode: string): void {
    this.drillDataMode = mode === 'server' ? 'server' : 'client';
    // "Average per group" is client-side only: a server-mode payload would hand
    // the backend an aggregation it does not know — degrade to plain Average.
    if (this.drillDataMode === 'server') {
      this.drillDownMeasures.forEach((m) => { if (this.isAvgByMeasure(m)) m.aggregation = 'AVG'; });
    }
    this.invalidatePreview();
  }
  private readonly drillTotalsPlacementsFallback: LabelValueOption[] = [
    { label: 'Bottom and right (total row + Total block)', value: 'both' },
    { label: 'Bottom and left (total row + Total block after the first column)', value: 'both-left' },
    { label: 'Right only (Total block across pivot columns)', value: 'right' },
    { label: 'Left only (Total block after the first column)', value: 'left' },
    { label: 'Bottom only (grand-total row)', value: 'bottom' },
    { label: 'None', value: 'none' },
  ];
  /** "Totals" options (config `drillTotalsPlacements`), stable fallback. */
  get drillTotalsPlacements(): LabelValueOption[] {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['drillTotalsPlacements'] as LabelValueOption[] | undefined)
      ?? this.drillTotalsPlacementsFallback;
  }
  onDrillTotalsPlacementChange(value: string): void {
    this.drillTotalsPlacement = normalizeDrillTotalsPlacement(value);
    this.invalidatePreview();
  }

  // Pivot column dimension(s) — same card model as the hierarchy levels.
  addDrillColumnDimension(): void {
    this.drillColumnDimensions = [...this.drillColumnDimensions, { id: this.nextDrillDimensionId(), field: '', label: '' }];
  }
  removeDrillColumnDimension(dimension: DrillDownDimension): void {
    this.drillColumnDimensions = this.drillColumnDimensions.filter((item) => item.id !== dimension.id);
    this.invalidatePreview();
  }
  onDrillColumnDimensionChange(): void {
    this.drillColumnDimensions = [...this.drillColumnDimensions];
    this.invalidatePreview();
  }
  onDrillColumnDimensionFieldDrop(event: DragEvent, dimension?: DrillDownDimension): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    if (!this.selectedDatasets.length) this.useDroppedSingleDataset(mapping.dataset);
    else this.loadColumnsForDataset(mapping.dataset);
    const target = dimension || { id: this.nextDrillDimensionId(), field: '', label: '' };
    target.dataset = mapping.dataset !== this.drillDataset ? mapping.dataset : '';
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!dimension) this.drillColumnDimensions = [...this.drillColumnDimensions, target];
    this.onDrillColumnDimensionChange();
  }

  // Formula columns — evaluated per tree node on its AGGREGATED measures (so a
  // ratio at a parent divides the sums) and re-evaluated live after an edit.
  /** Names a drill formula may reference: measure headers, raw measure fields
   *  and the formula columns defined before it. */
  get drillFormulaColumns(): string[] {
    const names = new Set<string>();
    this.drillDownMeasures.forEach((m) => { if (m.label) names.add(m.label); if (m.field) names.add(m.field); });
    this.drillComputedColumns.forEach((c) => names.add(c.name));
    return [...names];
  }
  /** "Place after" choices: every measure (by label) + the formulas defined so
   *  far. Memoized on a signature — this feeds an appendTo="body" ng-select,
   *  which swallows clicks when [items] is a fresh array each CD cycle. */
  private drillFormulaAnchorMemo: { sig: string; items: LabelValueOption[] } | null = null;
  get drillFormulaAnchorOptions(): LabelValueOption[] {
    const measures = this.drillDownMeasures.filter((m) => m.field);
    const sig = [...measures.map((m) => `${m.label}|${m.field}`), ...this.drillComputedColumns.map((c) => c.name)].join('\u0001');
    if (this.drillFormulaAnchorMemo?.sig === sig) return this.drillFormulaAnchorMemo.items;
    const items: LabelValueOption[] = [
      { label: 'End (after the last measure)', value: '' },
      ...measures.map((m) => ({ label: `After: ${m.label || m.field}`, value: m.label || m.field })),
      ...this.drillComputedColumns.map((c) => ({ label: `After formula: ${c.name}`, value: c.name })),
    ];
    this.drillFormulaAnchorMemo = { sig, items };
    return items;
  }
  /** Human label for a stored anchor (for the formula list). */
  drillFormulaAnchorLabel(after?: string): string {
    if (!after) return '';
    const measure = this.drillDownMeasures.find((m) => (m.label || m.field) === after || m.field === after);
    return measure ? (measure.label || measure.field) : after;
  }
  /** Config-driven DESIGN preset (`options.drillColumnOrderPreset`): the
   *  column names in the order the design shows them, each entry optionally
   *  "name|alias|…". Applied whenever the tile has no explicit Column order,
   *  to whichever of those columns the table actually has. */
  get drillColumnOrderPreset(): string[] {
    const raw = (this.layout.options as Record<string, unknown> | undefined)?.['drillColumnOrderPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  /** Config-driven DESIGN preset for the PIVOT COLUMN VALUE sequence
   *  (`options.drillPivotValueOrderPreset`): the band order the design shows
   *  (Mining | Marine | Oil & Gas | Rail), each entry optionally "name|alias|…".
   *  Applied whenever the pivot dimension's own "Column order" box is blank. */
  get drillPivotValueOrderPreset(): string[] {
    const raw = (this.layout.options as Record<string, unknown> | undefined)?.['drillPivotValueOrderPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  /** Config-driven DESIGN preset for the right-hand TOTAL block's columns
   *  (`options.drillRowTotalColumnsPreset`): only the listed totalled measures
   *  join the block (Revenue | Price %), each entry optionally "name|alias|…".
   *  Empty = every totalled measure joins. */
  get drillRowTotalColumnsPreset(): string[] {
    const raw = (this.layout.options as Record<string, unknown> | undefined)?.['drillRowTotalColumnsPreset'];
    return Array.isArray(raw) ? raw.map((entry) => String(entry ?? '').trim()).filter(Boolean) : [];
  }
  /** Preset names for the accordion hint (first alias of each entry). */
  get drillColumnOrderPresetLabel(): string {
    return this.drillColumnOrderPreset.map((entry) => entry.split('|')[0].trim()).filter(Boolean).join(' · ');
  }
  /** The Drill-Down "Column order" list: every BASE output column (measure
   *  block / formula, before pivot fan-out) in the order the table renders it —
   *  resolved by the SAME function the tree builder uses, so the list is the
   *  table. Hidden (formula-input-only) measures are left out. Memoized by
   *  signature so the getter returns a stable array across CD cycles. */
  get drillColumnOrderItems(): DrillColumnOrderItem[] {
    const measures = this.drillDownMeasures.filter((m) => m.field);
    const hasPivot = this.drillColumnDimensions.some((d) => !!d.field);
    const sig = [
      ...measures.map((m) => `${m.label}|${m.field}|${m.pivot === false ? 0 : 1}|${m.hidden ? 1 : 0}`), '#',
      ...this.drillComputedColumns.map((c) => `${c.name}|${c.after || ''}`), '#',
      ...this.drillColumnOrder, '#', ...this.drillColumnOrderPreset, String(hasPivot),
    ].join('\u0001');
    if (this.drillColumnOrderMemo?.sig === sig) return this.drillColumnOrderMemo.items;
    const items: DrillColumnOrderItem[] = resolveDrillColumnOrder(measures, this.drillComputedColumns, this.drillColumnOrder, this.drillColumnOrderPreset)
      .filter((item) => item.kind === 'formula' || !measures[item.index].hidden)
      .map((item) => ({
        key: item.key,
        orderKey: item.kind === 'measure' ? measures[item.index].field : item.key,
        kind: item.kind,
        split: hasPivot && (item.kind === 'formula' || measures[item.index].pivot !== false),
      }));
    this.drillColumnOrderMemo = { sig, items };
    return items;
  }
  /** Move one column of the Column-order list up (-1) / down (+1). The whole
   *  current order is written out so it round-trips as an explicit list. */
  moveDrillColumn(index: number, delta: number): void {
    const keys = this.drillColumnOrderItems.map((item) => item.orderKey);
    const to = index + delta;
    if (index < 0 || index >= keys.length || to < 0 || to >= keys.length) return;
    const [moved] = keys.splice(index, 1);
    keys.splice(to, 0, moved);
    this.drillColumnOrder = keys;
    this.invalidatePreview();
  }
  /** Back to the default order (measure order + formula anchors). */
  resetDrillColumnOrder(): void {
    if (!this.drillColumnOrder.length) return;
    this.drillColumnOrder = [];
    this.invalidatePreview();
  }
  applyDrillFormulaTemplate(tpl: FormulaTemplate): void {
    this.newDrillColumnName = tpl.defaultName;
    this.newDrillColumnExpression = tpl.expressionTemplate;
    this.newDrillColumnError = '';
  }
  /** Typographic characters pasted from Word / Teams / Excel that the parser
   *  rejects: en/em/minus dashes → "-", multiplication/division signs, curly
   *  quotes → straight. Applied before validation AND to the stored expression. */
  private normalizeDrillFormula(raw: string): string {
    return String(raw ?? '')
      .replace(/[‒–—−]/g, '-')
      .replace(/[×]/g, '*')
      .replace(/[÷]/g, '/')
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .trim();
  }
  /** Function / keyword names the formula engine understands — bare identifiers
   *  that are neither these nor a known column are reported as unknown. */
  private static readonly DRILL_FORMULA_FUNCS = new Set([
    'IF', 'IFERROR', 'SUM', 'AVERAGE', 'COUNT', 'COUNTIF', 'CONCAT', 'CONCATENATE', 'AND', 'OR', 'NOT', 'TRUE', 'FALSE',
    'ABS', 'ROUND', 'CEIL', 'FLOOR', 'SQRT', 'LOG', 'EXP', 'POW', 'MIN', 'MAX', 'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'SUBSTR',
    'REPLACE', 'CONTAINS', 'STARTSWITH', 'ENDSWITH', 'YEAR', 'MONTH', 'DAY', 'DAYOFWEEK', 'WEIGHTEDAVERAGE', 'VARP', 'VARS',
    'STDEVP', 'STDEVS', 'E', 'PI',
  ]);
  /** Resolve every column reference in a drill formula to the EXACT name this
   *  table exposes: `[Net Sales]` → `[net_sales]` (case / spacing / punctuation
   *  insensitive), bare `total_cost` kept as is. Anything that matches nothing
   *  is returned in `unknown` — an unknown reference would otherwise compile
   *  fine and silently evaluate to 0, leaving the whole column blank. */
  private resolveDrillFormulaRefs(expression: string): { expression: string; unknown: string[] } {
    const known = this.drillFormulaColumns;
    const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const byNorm = new Map<string, string>();
    known.forEach((n) => { if (!byNorm.has(norm(n))) byNorm.set(norm(n), n); });
    const unknown = new Set<string>();
    // 1. Bracketed references.
    let out = expression.replace(/\[([^\]]+)\]/g, (_match, raw: string) => {
      const name = raw.trim();
      if (known.includes(name)) return `[${name}]`;
      const hit = byNorm.get(norm(name));
      if (hit) return `[${hit}]`;
      unknown.add(name);
      return `[${name}]`;
    });
    // 2. Bare identifiers (outside brackets / string literals).
    const stripped = out.replace(/\[[^\]]+\]/g, ' ').replace(/"[^"]*"|'[^']*'/g, ' ');
    const tokens = Array.from(new Set(stripped.match(/[A-Za-z_][A-Za-z0-9_]*/g) || []));
    tokens.forEach((token) => {
      if (known.includes(token) || DvmLayoutBuilderComponent.DRILL_FORMULA_FUNCS.has(token.toUpperCase())) return;
      const hit = byNorm.get(norm(token));
      const pattern = new RegExp(`(?<![\\w\\]\\[])${token}(?![\\w\\[(])`, 'g');
      if (hit) out = out.replace(pattern, `[${hit}]`);
      else unknown.add(token);
    });
    return { expression: out, unknown: [...unknown] };
  }
  /** Click-to-insert helper under the expression box: appends `[Name]`. */
  insertDrillFormulaRef(name: string): void {
    const current = this.newDrillColumnExpression || '';
    this.newDrillColumnExpression = `${current}${current && !/s$/.test(current) ? ' ' : ''}[${name}]`;
    this.newDrillColumnError = '';
  }
  addDrillComputedColumn(): void {
    const name = this.newDrillColumnName.trim();
    const typed = this.normalizeDrillFormula(this.newDrillColumnExpression);
    this.newDrillColumnError = '';
    const resolved = typed ? this.resolveDrillFormulaRefs(typed) : { expression: typed, unknown: [] as string[] };
    const expression = resolved.expression;
    if (resolved.unknown.length) {
      const names = this.drillFormulaColumns.map((n) => `[${n}]`).join(', ');
      this.newDrillColumnError = `Unknown name${resolved.unknown.length > 1 ? 's' : ''} ${resolved.unknown.map((u) => `[${u}]`).join(', ')} — this table's measures are: ${names || '(add a measure first)'}. Use those names (or rename the measure labels first).`;
      return;
    }
    const cfg = this.formulaBuilderConfig;
    if (!name) { this.newDrillColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newDrillColumnError = 'Expression is required.'; return; }
    // Any text but square brackets (the reference delimiters) — "$ Impact",
    // "Price %", "Revenue ($)" are all valid, like measure labels.
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[^[\]]+$/;
    if (!pattern.test(name)) { this.newDrillColumnError = cfg.nameError || 'Invalid column name.'; return; }
    if (this.drillFormulaColumns.includes(name)) { this.newDrillColumnError = `A column named "${name}" already exists.`; return; }
    try {
      this.formulaService.buildRowEvaluator(expression, this.drillFormulaColumns);
    } catch (e) {
      const names = this.drillFormulaColumns.map((n) => `[${n}]`).join(', ');
      this.newDrillColumnError = `Invalid expression: ${e instanceof Error ? e.message : 'parse error'}. Wrap names in brackets — available here: ${names || '(add a measure first)'}.`;
      return;
    }
    const after = this.newDrillColumnAfter.trim();
    this.drillComputedColumns = [...this.drillComputedColumns, { name, expression, ...(after ? { after } : {}) }];
    this.newDrillColumnName = '';
    this.newDrillColumnExpression = '';
    this.newDrillColumnAfter = '';
    this.invalidatePreview();
  }
  removeDrillComputedColumn(name: string): void {
    this.drillComputedColumns = this.drillComputedColumns.filter((c) => c.name !== name);
    this.invalidatePreview();
  }

  updateDrillComputedExpression(column: DrillComputedColumn, input: HTMLInputElement): void {
    this.newDrillColumnError = '';
    const typed = this.normalizeDrillFormula(input.value);
    if (!typed) {
      this.removeDrillComputedColumn(column.name);
      return;
    }
    const resolved = this.resolveDrillFormulaRefs(typed);
    if (resolved.unknown.length) {
      this.newDrillColumnError = `Unknown column: ${resolved.unknown.map((name) => `[${name}]`).join(', ')}. Use the available names below.`;
      input.value = column.expression;
      return;
    }
    if (resolved.expression.includes(`[${column.name}]`)) {
      this.newDrillColumnError = 'A formula cannot reference itself.';
      input.value = column.expression;
      return;
    }
    try {
      this.formulaService.buildRowEvaluator(resolved.expression, this.drillFormulaColumns);
    } catch (error) {
      this.newDrillColumnError = `Invalid expression: ${error instanceof Error ? error.message : 'parse error'}`;
      input.value = column.expression;
      return;
    }
    this.drillComputedColumns = this.drillComputedColumns.map((item) => item === column
      ? { ...item, expression: resolved.expression }
      : item);
    input.value = resolved.expression;
    this.invalidatePreview();
  }

  /** A formula's name is also its output field/header. Rename references along
   *  with it so dependent formulas and column placement continue to work. */
  renameDrillComputedColumn(column: DrillComputedColumn, input: HTMLInputElement): void {
    const name = input.value.trim();
    const oldName = column.name;
    this.newDrillColumnError = '';
    if (name === oldName) { input.value = oldName; return; }
    const cfg = this.formulaBuilderConfig;
    // Any text but square brackets (the reference delimiters) — "$ Impact",
    // "Price %", "Revenue ($)" are all valid, like measure labels.
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[^[\]]+$/;
    if (!name || !pattern.test(name)) {
      this.newDrillColumnError = !name ? 'Column name is required.' : (cfg.nameError || 'Invalid column name.');
      input.value = oldName;
      return;
    }
    if (this.drillFormulaColumns.some((existing) => existing.toLowerCase() === name.toLowerCase() && existing !== oldName)) {
      this.newDrillColumnError = `A column named "${name}" already exists.`;
      input.value = oldName;
      return;
    }
    const renameReferences = (expression: string): string => expression.replace(
      /\[([^\]]+)\]/g,
      (match, ref: string) => ref.trim() === oldName ? `[${name}]` : match,
    );
    this.drillComputedColumns = this.drillComputedColumns.map((item) => ({
      ...item,
      name: item === column ? name : item.name,
      expression: renameReferences(item.expression),
      ...(item.after === oldName ? { after: name } : {}),
    }));
    this.drillColumnOrder = this.drillColumnOrder.map((key) => key === oldName ? name : key);
    if (this.newDrillColumnAfter === oldName) this.newDrillColumnAfter = name;
    this.newDrillColumnExpression = renameReferences(this.newDrillColumnExpression);
    this.invalidatePreview();
  }

  /** The client tree-builder config from the live form. */
  private drillClientConfig(): DrillClientConfig {
    const dim = (d: DrillDownDimension) => {
      const order = this.parseValueOrder(d.valueOrder);
      return {
        field: d.field, label: d.label || undefined, dataset: d.dataset || undefined,
        ...(order.length ? { valueOrder: order } : {}),
        ...(d.sortBy ? { sortBy: d.sortBy } : {}),
      };
    };
    return {
      dimensions: this.drillDownDimensions.map(dim),
      columnDimensions: this.drillColumnDimensions.map(dim),
      measures: [
        ...this.drillDownMeasures.map((m) => ({
          field: m.field, label: m.label || undefined, aggregation: m.aggregation, total: !!m.total, dataset: m.dataset || undefined,
          ...this.rowTotalHeaderPayload(m),
          ...(m.pivot === false ? { pivot: false } : {}),
          ...(m.kind === 'text' ? { kind: 'text' as const } : {}),
          ...(m.format === 'percent' ? { format: 'percent' as const } : {}),
          ...(m.percentScale === 'fraction' ? { percentScale: 'fraction' as const } : {}),
          ...(m.hideNegative ? { hideNegative: true } : {}),
          ...(typeof m.decimals === 'number' ? { decimals: m.decimals } : {}),
          ...(m.hidden ? { hidden: true } : {}),
          ...this.yearScopePayload(m),
          ...this.groupFieldPayload(m),
        })),
        // "Show year column in table": the year helper as a visible text
        // column. Hidden helpers are payload plumbing only — the preview's
        // compute payload already requests the year column.
        ...this.drillYearHelperMeasures().filter((helper) => !helper.hidden),
      ],
      computedColumns: this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) })),
      ...(this.drillColumnOrder.length ? { columnOrder: [...this.drillColumnOrder] } : {}),
      ...(this.drillColumnOrderPreset.length ? { columnOrderPreset: [...this.drillColumnOrderPreset] } : {}),
      ...(this.drillPivotValueOrderPreset.length ? { columnValueOrderPreset: [...this.drillPivotValueOrderPreset] } : {}),
      ...(this.drillRowTotalColumnsPreset.length ? { rowTotalColumns: [...this.drillRowTotalColumnsPreset] } : {}),
      // Format toggle: individual response rows at the leaf level (no combining).
      rawLeafRows: this.barFormat['drillRawLeafRows'] === true,
      // Format select: which label spans the two-tier header (measure / column value).
      ...(this.drillHeaderGroupByFrom(this.barFormat) === 'column' ? { headerGroupBy: 'column' as const } : {}),
      // Totals picker: bottom grand-total row and/or the trailing Total block
      // across the pivot column values (both driven by the Grand Total ticks).
      grandTotalRow: drillTotalsAtBottom(this.drillTotalsPlacement),
      ...this.drillRowTotalConfig(this.drillTotalsPlacement, this.barFormat),
    };
  }
  /** A TABLE_MATRIX format value, falling back to the control's CONFIG default
   *  when the format has no value yet (a fresh tile's barFormat is empty at
   *  Generate; an older tile's saved format predates the control). */
  private tmFormatOrDefault(format: Record<string, unknown> | undefined, key: string): unknown {
    const raw = format?.[key];
    if (raw !== undefined && raw !== null && raw !== '') return raw;
    return this.applicableFormatControls('TABLE_MATRIX').find((control) => control.key === key)?.default;
  }
  /** Row-total keys for the tree config / capabilities — on when the Totals
   *  picker includes the Total block. The picker also decides the SIDE
   *  ("Bottom and left" / "Left only" → after the first-level column); the
   *  label comes from the Format popup (`drillRowTotalLabel`, config default
   *  "Total"). */
  private drillRowTotalConfig(
    placement: DrillTotalsPlacement,
    format: Record<string, unknown> | undefined,
  ): { rowTotal?: boolean; rowTotalLabel?: string; rowTotalPosition?: 'left' | 'right' } {
    if (!drillTotalsAtRight(placement)) return {};
    const label = String(this.tmFormatOrDefault(format, 'drillRowTotalLabel') ?? '').trim();
    return {
      rowTotal: true,
      ...(label ? { rowTotalLabel: label } : {}),
      // Only 'left' is baked — absent keeps the legacy trailing block byte-identical.
      ...(drillTotalsBlockPosition(placement) === 'left' ? { rowTotalPosition: 'left' as const } : {}),
    };
  }
  /** Drill-Down "Header grouping" (Format popup `drillHeaderGroupBy`), falling
   *  back to the control's CONFIG default when the format has no value yet —
   *  a fresh tile's barFormat is empty at Generate and an older tile's saved
   *  format predates the control, so without this the config default would
   *  never reach the tree builder. */
  private drillHeaderGroupByFrom(format: Record<string, unknown> | undefined): 'measure' | 'column' | undefined {
    const raw = this.tmFormatOrDefault(format, 'drillHeaderGroupBy');
    // Unresolvable (control missing from the loaded config) → undefined, so
    // nothing explicit is baked and the table's own default applies; never
    // silently pin the legacy 'measure' layout.
    return raw === 'column' || raw === 'measure' ? raw : undefined;
  }
  /** The same config read back from a tile's SAVED capabilities — for the
   *  recompute paths (file upload / input pushes) that rebuild another tile. */
  private drillClientConfigFromCaps(caps: Record<string, unknown>): DrillClientConfig {
    const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
    const placement = normalizeDrillTotalsPlacement(caps['totalsPlacement']);
    return {
      dimensions: list<DrillClientConfig['dimensions'][number]>(caps['dimensions']),
      columnDimensions: list<NonNullable<DrillClientConfig['columnDimensions']>[number]>(caps['columnDimensions']),
      measures: list<DrillClientConfig['measures'][number]>(caps['measures']),
      grandTotalRow: drillTotalsAtBottom(placement),
      computedColumns: list<{ name: string; expression: string }>(caps['computedColumns']),
      columnOrder: list<string>(caps['columnOrder']),
      columnOrderPreset: list<string>(caps['columnOrderPreset']),
      columnValueOrderPreset: list<string>(caps['columnValueOrderPreset']),
      rowTotalColumns: list<string>(caps['rowTotalColumns']),
      rawLeafRows: caps['rawLeafRows'] === true,
      ...(caps['headerGroupBy'] === 'column' ? { headerGroupBy: 'column' as const } : {}),
      ...(caps['rowTotal'] === true
        ? {
            rowTotal: true,
            ...(caps['rowTotalLabel'] ? { rowTotalLabel: String(caps['rowTotalLabel']) } : {}),
            ...(caps['rowTotalPosition'] === 'left' ? { rowTotalPosition: 'left' as const } : {}),
          }
        : {}),
    };
  }
  /** Flat rows → the compute-response-shaped tree envelope that
   *  buildDrillDownWidgetData already consumes (so everything downstream —
   *  editing, save, year filter, visuals — is untouched). */
  private buildDrillClientTreeResponse(rows: Array<Record<string, unknown>>, config: DrillClientConfig = this.drillClientConfig()): Record<string, unknown> {
    const tree = buildDrillDownTreeFromRows(rows, config, (expression, names) => this.formulaService.buildRowEvaluator(expression, names));
    return drillTreeToResponse(tree);
  }
  /** FLAT compute payload for the client-side drill-down: every level, pivot
   *  column and measure as a {dataset, field, displayName} column — the same
   *  cross-dataset contract Normal tables use — plus any raw primary-dataset
   *  column a formula references. Rows come back joined and un-aggregated. */
  private buildDrillClientComputePayload(): Record<string, unknown> {
    const seen = new Set<string>();
    const columns: Array<{ dataset: string; field: string; displayName: string }> = [];
    const push = (dataset: string | undefined, field: string): void => {
      const ds = dataset || this.drillDataset;
      const key = `${ds}|${field}`;
      if (!field || seen.has(key)) return;
      seen.add(key);
      // displayName = field on purpose: the response rows then come back keyed
      // by the raw field, which is what the tree builder groups/aggregates on.
      columns.push({ dataset: ds, field, displayName: field });
    };
    this.drillDownDimensions.forEach((d) => push(d.dataset, d.field));
    this.drillColumnDimensions.forEach((d) => push(d.dataset, d.field));
    this.drillDownMeasures.forEach((m) => push(m.dataset, m.field));
    const expressions = this.drillComputedColumns.map((c) => c.expression).join(' ');
    if (expressions) {
      (this.entityColumnsCache.get(this.drillDataset) || []).forEach((field) => {
        if (field && expressions.includes(field)) push(this.drillDataset, field);
      });
    }
    // Year-scoped measures ("Latest year only") need their year column in the
    // rows so the tree builder can keep just the newest year's rows.
    this.drillDownMeasures.forEach((m) => {
      // Requested from the dataset that OWNS the year column (see resolveYearColumn).
      const yc = this.resolveYearColumn(m);
      if (yc) push(yc.dataset, yc.field);
      // "Average per group" measures need their group column in the rows too.
      push(m.dataset, String(this.groupFieldPayload(m).groupField || ''));
    });
    return {
      componentId: this.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: this.tmType,
      dashboardId: this.dashboardId,
      title: this.title.trim(),
      dataset: this.drillDataset,
      columns,
      aggregation: 'none',
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  // ── Bar & Column builder (BAR) — category + measures + optional series ─────
  // One dataset (drillDataset), one category column, N measures (each with a
  // draw type + axis), and an optional series split. All variant/orientation
  // logic lives in the pure option builder.

  /** Columns of the chosen bar dataset (shared with the drill/hbar pickers). */
  /** Memoized barColumns list — a getter that returned a fresh array every
   *  change-detection cycle made ng-select (appendTo:'body') rebuild its panel
   *  mid-click, so selecting a Measure/Category/Split value silently failed.
   *  Recompute the array ONLY when the dataset columns or the computed columns
   *  actually change → STABLE reference → clicks stick. */
  private barColumnsCache: string[] = [];
  private barColumnsKey = '';
  get barColumns(): string[] {
    // Base dataset columns PLUS any computed (formula) columns, so a computed
    // column can be mapped onto Category / Measure / Split like a real column.
    const base = this.drillDatasetColumns;
    const computed = this.barComputedColumns.map((c) => c.name);
    const key = `${base.join('|')}::${computed.join('|')}`;
    if (key !== this.barColumnsKey) {
      this.barColumnsKey = key;
      this.barColumnsCache = [...base, ...computed.filter((n) => !base.includes(n))];
    }
    return this.barColumnsCache;
  }

  // ── Formula Builder — row-level computed columns (chart-agnostic, config-driven)

  /** The Formula Builder config block (options.formulaBuilder), or {}. All UI text,
   *  templates, validation, and the enabled flag come from here — nothing hardcoded. */
  get formulaBuilderConfig(): FormulaBuilderConfig {
    return ((this.layout.options as Record<string, unknown> | undefined)?.['formulaBuilder'] as FormulaBuilderConfig | undefined) ?? {};
  }
  /** Whether the Formula Builder is shown (config `enabled`, default true). */
  get formulaBuilderEnabled(): boolean {
    return this.formulaBuilderConfig.enabled !== false;
  }
  /** Row-level formula templates (kind: 'column') offered as quick-fill chips —
   *  purely from config; empty when none configured (no dataset-specific defaults). */
  get barFormulaTemplates(): FormulaTemplate[] {
    const list = Array.isArray(this.formulaBuilderConfig.templates) ? this.formulaBuilderConfig.templates : [];
    return list.filter((t) => t.kind === 'column');
  }

  /** Fill the add-column inputs from a template chip. */
  applyBarFormulaTemplate(tpl: FormulaTemplate): void {
    this.newBarColumnName = tpl.defaultName;
    this.newBarColumnExpression = tpl.expressionTemplate;
    this.newBarColumnError = '';
  }

  /** Add a computed column (name + expression), validated against existing
   *  columns. Applies to the preview immediately. */
  addBarComputedColumn(): void {
    const name = this.newBarColumnName.trim();
    const expression = this.newBarColumnExpression.trim();
    this.newBarColumnError = '';
    const cfg = this.formulaBuilderConfig;
    if (!name) { this.newBarColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newBarColumnError = 'Expression is required.'; return; }
    // Config-driven name validation (pattern + message); safe default if absent.
    // Any text but square brackets (the reference delimiters) — "$ Impact",
    // "Price %", "Revenue ($)" are all valid, like measure labels.
    const pattern = cfg.namePattern ? new RegExp(cfg.namePattern) : /^[^[\]]+$/;
    if (!pattern.test(name)) {
      this.newBarColumnError = cfg.nameError || 'Invalid column name.';
      return;
    }
    if (this.drillDatasetColumns.includes(name) || this.barComputedColumns.some((c) => c.name === name)) {
      this.newBarColumnError = `A column named "${name}" already exists.`;
      return;
    }
    // Validate the expression compiles against the current columns.
    try {
      this.formulaService.buildRowEvaluator(expression, this.barColumns);
    } catch (e) {
      this.newBarColumnError = `Invalid expression: ${e instanceof Error ? e.message : 'parse error'}`;
      return;
    }
    this.barComputedColumns = [...this.barComputedColumns, { name, expression }];
    this.newBarColumnName = '';
    this.newBarColumnExpression = '';
    this.onBarChange();
  }

  removeBarComputedColumn(name: string): void {
    this.barComputedColumns = this.barComputedColumns.filter((c) => c.name !== name);
    // Clear any mapping that pointed at the removed column.
    if (this.barCategory === name) this.barCategory = '';
    if (this.barSeries === name) this.barSeries = '';
    this.barMeasures = this.barMeasures.map((m) => (m.field === name ? { ...m, field: '' } : m));
    this.onBarChange();
  }

  /** Apply every computed column to a rows array, returning NEW row objects with
   *  the derived values added. Reuses the shared formula engine. Bad expressions
   *  are skipped (leave the column absent) rather than throwing. */
  private applyBarComputedColumns(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    if (!this.barComputedColumns.length || !rows.length) return rows;
    let working = rows.map((r) => ({ ...r }));
    const known = [...this.drillDatasetColumns];
    for (const computed of this.barComputedColumns) {
      try {
        const evaluator = this.formulaService.buildRowEvaluator(computed.expression, known);
        working = working.map((row) => {
          const value = evaluator(row as Record<string, string>);
          const out = value === null || value === undefined ? '' : value;
          return { ...row, [computed.name]: typeof out === 'number' && !Number.isFinite(out) ? '' : out };
        });
        known.push(computed.name);
      } catch {
        // Skip a broken formula — its column just won't be present.
      }
    }
    return working;
  }

  addBarMeasure(): void {
    this.barMeasures = [
      ...this.barMeasures,
      { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar', axis: 'y1' },
    ];
  }

  removeBarMeasure(measure: BarBuilderMeasure): void {
    this.barMeasures = this.barMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
  }

  moveBarMeasureUp(index: number): void {
    if (index <= 0) return;
    this.reorderBarMeasure(index, index - 1);
  }

  moveBarMeasureDown(index: number): void {
    if (index >= this.barMeasures.length - 1) return;
    this.reorderBarMeasure(index, index + 1);
  }

  private reorderBarMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.barMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.barMeasures = next;
    this.invalidatePreview();
  }

  onBarMeasureChange(): void {
    this.barMeasures = [...this.barMeasures];
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  onBarChange(): void {
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  // ── Line & Area handlers ──────────────────────────────────────────────────
  addLaMeasure(): void {
    this.laMeasures = [...this.laMeasures, { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line', axis: 'y1', symbol: 'circle' }];
  }
  removeLaMeasure(measure: LineAreaBuilderMeasure): void {
    this.laMeasures = this.laMeasures.filter((item) => item.id !== measure.id);
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  moveLaMeasureUp(index: number): void { if (index > 0) this.reorderLaMeasure(index, index - 1); }
  moveLaMeasureDown(index: number): void { if (index < this.laMeasures.length - 1) this.reorderLaMeasure(index, index + 1); }
  private reorderLaMeasure(fromIndex: number, toIndex: number): void {
    const next = [...this.laMeasures];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    this.laMeasures = next;
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaMeasureChange(): void {
    this.laMeasures = [...this.laMeasures];
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  /** Apply a per-measure marker choice immediately. Existing tiles may carry
   *  the old tile-wide symbolSize=0, so selecting a visible shape must also
   *  turn markers on; the user can still set the size back to 0 in Formatting. */
  onLaMeasureSymbolChange(measure: LineAreaBuilderMeasure, symbol: string): void {
    measure.symbol = symbol;
    if (symbol !== 'none' && Number(this.barFormat['symbolSize']) <= 0) {
      this.barFormat = { ...this.barFormat, symbolSize: 6 };
    }
    this.onLaMeasureChange();
  }
  onLaMeasureSymbolSizeChange(measure: LineAreaBuilderMeasure, value: unknown): void {
    const parsed = value === '' || value === null || value === undefined ? 6 : Number(value);
    measure.symbolSize = Number.isFinite(parsed) ? Math.max(0, Math.min(24, parsed)) : 6;
    this.onLaMeasureChange();
  }
  /** Swatch value for a measure's "Line color". An `<input type="color">` has no
   *  empty state, so when the measure has no explicit colour we show the palette
   *  slot the chart is actually drawing with — the picker then reads as the
   *  current colour rather than a misleading black. */
  laMeasureColor(measure: LineAreaBuilderMeasure, index: number): string {
    const explicit = String(measure?.color ?? '').trim();
    if (explicit) return explicit;
    const palette = (this.layout.options?.lineAreaChartDefaults?.['palette'] as string[] | undefined) || [];
    return palette.length ? palette[index % palette.length] : '#5470c6';
  }
  onLineAreaChange(): void {
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  private nextLaMeasureId(): string {
    this.laMeasureIdCounter += 1;
    return `la-measure-${this.laMeasureIdCounter}`;
  }
  onLaMeasureFieldDrop(event: DragEvent, measure?: LineAreaBuilderMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextLaMeasureId(), field: '', label: '', renderAs: 'line' as const, axis: 'y1' as const, symbol: 'circle' };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.laMeasures = [...this.laMeasures, target];
    this.onLaMeasureChange();
  }
  onLaCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laCategory = mapping.field;
    if (!this.laCategoryLabel) this.laCategoryLabel = mapping.field;
    this.onLineAreaChange();
  }
  onLaSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSeries = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryYFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryYField = mapping.field;
    this.onLineAreaChange();
  }
  onLaSecondaryXFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.laSecondaryXField = mapping.field;
    this.onLineAreaChange();
  }
  // ── Reference lines (Line & Area) ──
  addLaReferenceLine(): void {
    this.laRefLineIdCounter += 1;
    this.laReferenceLines = [...this.laReferenceLines, { id: `la-refline-${this.laRefLineIdCounter}`, value: null, label: '', color: '#64748b' }];
  }
  removeLaReferenceLine(id: string): void {
    this.laReferenceLines = this.laReferenceLines.filter((line) => line.id !== id);
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }
  onLaReferenceLineChange(): void {
    this.laReferenceLines = [...this.laReferenceLines];
    this.invalidatePreview();
    this.refreshEditingLineAreaTile();
  }

  /** A Build-form Format control changed (Title/Axis/Legend/Labels/Tooltip/
   *  Formatting/Advanced) — keep the chart `title` in sync with the Title-accordion
   *  control (which writes barFormat['title']), then live-refresh the edited tile. */
  onBarFormatChange(): void {
    // Sync the chart title from the Title-accordion control — but ONLY for layouts
    // that actually surface Title as an accordion (Bar / Waterfall / Table-Matrix
    // Drill-Down). Normal/Editable tables keep the INLINE title field, so a stray
    // empty `barFormat['title']` (seeded from the drilldown-only Title control's
    // default) must not clobber `this.title` and disable Generate/Update.
    const titleFromAccordion = this.isBarLayout || this.isLineAreaLayout || this.isWaterfallLayout || (this.isTableMatrixLayout && this.isTmDrilldown);
    if (titleFromAccordion && typeof this.barFormat['title'] === 'string') this.title = this.barFormat['title'] as string;
    if (this.isWaterfallLayout) {
      this.refreshEditingWaterfallTile();
      return;
    }
    if (this.isTableMatrixLayout) {
      this.foldFormatIntoEditingTableMatrixTile();
      return;
    }
    if (this.isLineAreaLayout) {
      this.refreshEditingLineAreaTile();
      return;
    }
    if (this.isDropdownLayout) {
      this.foldFormatIntoEditingDropdownTile();
      return;
    }
    if (this.isKpiLayout || this.isDrillDownLayout || this.isHorizontalBarLayout) {
      this.foldFormatIntoEditingTile();
      return;
    }
    this.refreshEditingBarTile();
  }

  /** Appearance keys the dropdown's Format accordion/popup may write into the
   *  widget (styled as scoped --dvm-filter-* CSS vars by app-global-dropdown).
   *  showLabel/labelColor/labelFontSize drive the widget's own label above the
   *  control (all display modes: dropdown, buttons, tiles). */
  private static readonly DROPDOWN_STYLE_KEYS = [
    'pillBg', 'pillText', 'pillBorder', 'pillActiveBg', 'pillActiveText', 'pillRadius', 'pillFontSize', 'pillFontFamily',
    'showLabel', 'labelColor', 'labelFontSize',
  ];

  /** Tile-wide font overrides (the shared "Font family" / "Font size" Format
   *  controls) exposed as --dvm-tile-font-* CSS vars on the tile card: HTML
   *  widgets (KPI, inputs, card headers, drill-down) inherit them; canvas
   *  charts receive the same values through their capabilities instead. */
  tileFontFamily(format?: Record<string, unknown>): string | null {
    const family = String(format?.['fontFamily'] ?? '').trim();
    return family || null;
  }
  tileFontSizePx(format?: Record<string, unknown>): string | null {
    const size = Number(format?.['fontSize']);
    return Number.isFinite(size) && size > 0 ? `${size}px` : null;
  }

  /** Copy the style keys from a format object onto a dropdown widgetData
   *  (empty/absent keys are REMOVED so the theme's values apply again). */
  private applyDropdownStyleKeys(format: Record<string, unknown>, widgetData: Record<string, unknown>): void {
    DvmLayoutBuilderComponent.DROPDOWN_STYLE_KEYS.forEach((key) => {
      const value = format[key];
      if (value !== undefined && value !== null && value !== '') widgetData[key] = value;
      else delete widgetData[key];
    });
  }

  /** ONLY the DROPDOWN-applicable format values out of the SHARED barFormat.
   *  barFormat carries leftovers from other layouts edited this session —
   *  folding it wholesale would leak stale keys (a leftover `title` renames
   *  the tile via onTileFormatChange; same class of bug as the table
   *  title-blank issue). */
  private dropdownFormatFromAccordion(): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    this.applicableFormatControls('DROPDOWN').forEach((control) => {
      const value = this.barFormat[control.key];
      if (value !== undefined) format[control.key] = value;
    });
    return format;
  }

  /** DROPDOWN live-refresh while editing: fold the accordion's Appearance
   *  values into the tile and re-apply (same path as the tile Format popup). */
  private foldFormatIntoEditingDropdownTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'DROPDOWN') return;
    tile.format = { ...(tile.format || {}), ...this.dropdownFormatFromAccordion() };
    this.onTileFormatChange(tile);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  /** Generic live-refresh for layouts whose Build-form Format accordion has no
   *  chart-specific rebuild (KPI, Drill-Down): fold barFormat into the editing
   *  tile's format so the header colours apply immediately, and let
   *  onTileFormatChange re-derive any type-specific render data (KPI numbers). */
  private foldFormatIntoEditingTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile) return;
    tile.format = { ...(tile.format || {}), ...this.barFormat };
    this.onTileFormatChange(tile);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  /** Live-refresh the table tile being edited when a Build-form Format control
   *  changes — folds barFormat into the tile's format, then rebuilds its
   *  capabilities from the form state (same path as the tile Format popup). */
  private foldFormatIntoEditingTableMatrixTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'TABLE_MATRIX') return;
    tile.format = { ...(tile.format || {}), ...this.barFormat };
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? tile : t));
    this.refreshEditingTableMatrixTile();
  }

  /** Live-refresh the waterfall tile being edited when a Build-form Format
   *  control changes — folds barFormat into the tile's format and re-derives its
   *  rendered option from the stored bars (same path as the tile Format popup). */
  private refreshEditingWaterfallTile(): void {
    if (!this.editingTileId) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
    const tile = index >= 0 ? this.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'WATERFALL') return;
    const updated: DashboardTile = {
      ...tile,
      format: { ...(tile.format || {}), ...this.barFormat, title: this.title.trim() || undefined },
    };
    this.onTileFormatChange(updated);
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? updated : t));
  }

  /** LIVE PREVIEW — reflect the current bar config on the canvas as it's built:
   *   - Editing an existing tile → rebuild THAT tile in place.
   *   - Otherwise, once enough is mapped (canPreviewBar) → auto-create/update a
   *     DRAFT preview tile so the chart appears BEFORE Generate; it updates on
   *     every change. If the config becomes incomplete, the draft is removed.
   *  Generate later finalizes the draft (see generateConfiguration). */
  private refreshEditingBarTile(): void {
    if (!this.isBarLayout) return;

    // Editing a real (already-generated) tile — refresh it live.
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'BAR') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildBarTile(tile) : t));
      }
      return;
    }

    // Not enough config → drop any existing draft preview tile.
    if (!this.canPreviewBar) {
      this.removePreviewTile();
      return;
    }

    // Create the draft preview tile on first eligibility, else update it.
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildBarTile({ ...base, layoutType: 'BAR' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildBarTile(tile) : t));
    }
  }

  /** Rebuild ONE bar tile's render data from the current builder state (fresh
   *  object refs so the chart's ngOnChanges fires → live re-render). */
  private rebuildBarTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('BAR'), ...this.barFormat, title: this.title.trim() || undefined };
    // SERVER-computed tile: overlay title + format onto the existing widget
    // data instead of rebuilding from raw rows (mirrors rebuildLineAreaTile —
    // a rebuild wiped the server rows on every Title keypress).
    const existing = tile.barData as Record<string, unknown> | null | undefined;
    if (existing && this.isServerComputedClientTile(existing, this.barCategory)) {
      const caps = this.applyBarFormatToCapabilities(
        { ...(existing['capabilities'] as BarColumnCapabilities) },
        format,
      );
      return {
        ...tile,
        title: this.title.trim() || tile.title || 'Untitled',
        format,
        barData: { ...existing, title: this.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      barRows: rows,
      barData: this.buildBarWidgetData(tile.id, format),
    };
  }

  // ── Line & Area live-preview (mirror the BAR trio) ──
  private refreshEditingLineAreaTile(): void {
    if (!this.isLineAreaLayout) return;
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'LINE_AREA') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildLineAreaTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewLineArea) {
      this.removePreviewTile();
      return;
    }
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildLineAreaTile({ ...base, layoutType: 'LINE_AREA' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildLineAreaTile(tile) : t));
    }
  }

  /** Swap a line-area tile onto SERVER-computed rows when the compute response
   *  carries the `data[0].lineArea` envelope (pre-aggregated {department, value}
   *  rows). Nulling `lineAreaRows` matters: it takes the tile OUT of the
   *  client-side cross-filter/dropdown registry, so filters drive it through
   *  the compute API (like waterfall/KPI) instead of filtering rows locally. */
  private applyLineAreaComputeResponse(componentId: string, response: Record<string, unknown> | null): void {
    const rows = extractLineAreaComputeRows(response?.['responseList'])
      ?? extractLineAreaComputeRows(response)
      ?? extractLineAreaFlatRows(response?.['responseList'])
      ?? extractLineAreaFlatRows(response);
    if (!rows?.length || !componentId) return;
    const index = this.dashboardTiles.findIndex((t) => t.componentId === componentId && t.layoutType === 'LINE_AREA');
    if (index < 0) return;
    const tile = this.dashboardTiles[index];
    // A multi-line config fed rows that carry NONE of its measures (e.g. a
    // backend still answering with the single-series {department, value}
    // envelope) keeps its client-side render — applying them would collapse
    // the chart to one line.
    if (!lineAreaRowsFitCapabilities(rows, tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined)) return;
    const data = buildLineAreaComputeWidgetData(response, {
      widgetId: 'dvm-tile-line-area-' + tile.id,
      title: tile.title,
      capabilities: (tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined),
      format: tile.format,
    });
    this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? { ...t, lineAreaRows: null, lineAreaData: data } : t));
    this.reflowCharts();
  }

  /** True when a client-family tile currently renders SERVER-computed rows (the
   *  remapped {department, value} shape): its capabilities.category IS a key of
   *  its staticData rows while the form's configured category is NOT. Rebuilding
   *  such a tile from raw dataset rows wipes the server data (blank chart). */
  private isServerComputedClientTile(data: Record<string, unknown> | null | undefined, formCategory: string): boolean {
    const rows = data?.['staticData'] as Array<Record<string, unknown>> | undefined;
    const caps = data?.['capabilities'] as Record<string, unknown> | undefined;
    const first = Array.isArray(rows) ? rows[0] : undefined;
    if (!first || !caps) return false;
    const capCategory = String(caps['category'] ?? '');
    return !!capCategory && capCategory in first && !!formCategory && !(formCategory in first);
  }

  /** Rebuild ONE line-area tile's render data from the current builder state. */
  private rebuildLineAreaTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('LINE_AREA'), ...this.barFormat, title: this.title.trim() || undefined };
    // SERVER-computed tile: a live refresh (title/format keystrokes) must NOT
    // rebuild from raw dataset rows — that wiped the server rows on every
    // Title-accordion keypress. Overlay title + format onto the EXISTING
    // widget data; structural edits take effect on Update Configuration when
    // the fresh compute response is re-applied.
    const existing = tile.lineAreaData as Record<string, unknown> | null | undefined;
    if (existing && this.isServerComputedClientTile(existing, this.laCategory)) {
      // Axis titles are render-only — reconcile the Chart-Data label inputs
      // live (the fold below then lets Format-accordion titles win over them).
      const existingCaps: LineAreaCapabilities = {
        ...(existing['capabilities'] as LineAreaCapabilities),
        categoryLabel: this.laCategoryLabel || this.laCategory || (existing['capabilities'] as LineAreaCapabilities)?.categoryLabel,
        yAxisLabel: this.laYAxisLabel.trim() || undefined,
        yAxisLabel2: this.laSecondaryYLabel.trim() || undefined,
        showSecondaryYAxis: this.laShowSecondaryYAxis,
        xAxisLabel2: this.laSecondaryXLabel.trim() || undefined,
      };
      // Server-computed tiles keep their response rows during live edits, but
      // marker shape is render-only. Copy the current build-form choice onto
      // the existing measure slots so shape changes appear immediately.
      const currentMeasures = this.buildLineAreaCapabilities().measures;
      if (Array.isArray(existingCaps.measures) && currentMeasures.length) {
        existingCaps.measures = existingCaps.measures.map((measure, index) => ({
          ...measure,
          // Line colour is edited in the build form. Server-computed tiles keep
          // their existing response rows during live edits, so carry this
          // render-only setting onto the existing measure capabilities too.
          color: currentMeasures[index]?.color || undefined,
          ...(currentMeasures[index]?.symbol ? { symbol: currentMeasures[index].symbol } : {}),
          ...(Number.isFinite(Number(currentMeasures[index]?.symbolSize))
            ? { symbolSize: Number(currentMeasures[index]?.symbolSize) }
            : {}),
        }));
      }
      const caps = this.applyLineAreaFormatToCapabilities(existingCaps, format);
      // Secondary-X is the one structural mapping reconciled live: CLEARING it
      // must drop the ghost axis/series immediately (the server rows can't
      // express it anyway), and repositioning it is render-only. Adding one
      // still needs Update Configuration (its column isn't in the server rows).
      if (!this.laSecondaryXField.trim()) {
        delete caps.secondaryXField;
        delete caps.secondaryXValueField;
        delete caps.secondaryXPosition;
      } else {
        caps.secondaryXPosition = this.laSecondaryXPosition;
      }
      return {
        ...tile,
        title: this.title.trim() || tile.title || 'Untitled',
        format,
        lineAreaData: { ...existing, title: this.title.trim() || tile.title, capabilities: caps },
      };
    }
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      lineAreaRows: rows,
      lineAreaData: this.buildLineAreaWidgetData(tile.id, format),
    };
  }

  // ── Scatter & Bubble live-preview (mirror the BAR trio) ──
  onScatterBubbleChange(): void {
    this.invalidatePreview();
    this.refreshEditingScatterBubbleTile();
  }
  onScatterBubbleFormatChange(): void {
    if (typeof this.sbFormat['title'] === 'string') this.title = this.sbFormat['title'] as string;
    this.refreshEditingScatterBubbleTile();
  }
  private refreshEditingScatterBubbleTile(): void {
    if (!this.isScatterBubbleLayout) return;
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((t) => t.id === this.editingTileId);
      const tile = index >= 0 ? this.dashboardTiles[index] : null;
      if (tile && tile.layoutType === 'SCATTER_BUBBLE') {
        this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === index ? this.rebuildScatterBubbleTile(tile) : t));
      }
      return;
    }
    if (!this.canPreviewScatterBubble) {
      this.removePreviewTile();
      return;
    }
    const existingIndex = this.previewTileId ? this.dashboardTiles.findIndex((t) => t.id === this.previewTileId) : -1;
    if (existingIndex < 0) {
      const base = this.newTileBase(this.buildPayload());
      this.previewTileId = base.id;
      const draft = this.rebuildScatterBubbleTile({ ...base, layoutType: 'SCATTER_BUBBLE' });
      this.dashboardTiles = [...this.dashboardTiles, draft];
    } else {
      const tile = this.dashboardTiles[existingIndex];
      this.dashboardTiles = this.dashboardTiles.map((t, i) => (i === existingIndex ? this.rebuildScatterBubbleTile(tile) : t));
    }
  }
  private rebuildScatterBubbleTile(tile: DashboardTile): DashboardTile {
    const format = { ...this.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.title.trim() || undefined };
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return {
      ...tile,
      title: this.title.trim() || tile.title || 'Untitled',
      format,
      scatterBubbleRows: rows,
      scatterBubbleData: this.buildScatterBubbleWidgetData(tile.id, format),
    };
  }

  /** Remove the auto-created draft preview tile (if any). */
  private removePreviewTile(): void {
    if (!this.previewTileId) return;
    const id = this.previewTileId;
    this.previewTileId = null;
    this.dashboardTiles = this.dashboardTiles.filter((t) => t.id !== id);
  }

  /** Drop a column onto the bar category picker. */
  onBarCategoryFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barCategory = mapping.field;
    if (!this.barCategoryLabel) this.barCategoryLabel = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the bar series (split) picker. */
  onBarSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barSeries = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto the hierarchical parent (outer group) picker. */
  onBarParentFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.barCategoryParent = mapping.field;
    this.invalidatePreview();
  }

  // ── Scatter & Bubble field drops (mirror the BAR handlers) ──
  onSbXFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbXField = mapping.field;
    if (!this.sbXLabel) this.sbXLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbYFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbYField = mapping.field;
    if (!this.sbYLabel) this.sbYLabel = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSizeFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbSizeField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbColorFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbColorField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbLabelFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbLabelField = mapping.field;
    this.onScatterBubbleChange();
  }
  onSbSeriesFieldDrop(event: DragEvent): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    this.sbSeries = mapping.field;
    this.onScatterBubbleChange();
  }

  // ── Reference lines (Bar) ──────────────────────────────────────────────────
  addBarReferenceLine(): void {
    this.barRefLineIdCounter += 1;
    this.barReferenceLines = [
      ...this.barReferenceLines,
      { id: `bar-refline-${this.barRefLineIdCounter}`, value: null, label: '', color: '#64748b' },
    ];
  }

  removeBarReferenceLine(id: string): void {
    this.barReferenceLines = this.barReferenceLines.filter((line) => line.id !== id);
    this.invalidatePreview();
  }

  onBarReferenceLineChange(): void {
    this.barReferenceLines = [...this.barReferenceLines];
    this.invalidatePreview();
    this.refreshEditingBarTile();
  }

  /** Drop a column onto a secondary-axis picker. */
  onBarSecondaryAxisFieldDrop(event: DragEvent, which: 'x' | 'y'): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    if (which === 'x') this.barSecondaryXField = mapping.field;
    else this.barSecondaryYField = mapping.field;
    this.invalidatePreview();
  }

  /** Drop a column onto a measure row (or the measures zone → new measure). */
  onBarMeasureFieldDrop(event: DragEvent, measure?: BarBuilderMeasure): void {
    const mapping = this.readFieldDrop(event);
    if (!mapping) return;
    this.useDroppedSingleDataset(mapping.dataset);
    const target = measure || { id: this.nextBarMeasureId(), field: '', label: '', renderAs: 'bar' as const, axis: 'y1' as const };
    target.field = mapping.field;
    if (!target.label) target.label = mapping.field;
    if (!measure) this.barMeasures = [...this.barMeasures, target];
    this.onBarMeasureChange();
  }

  barColumnPlaceholder(): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    return 'Select column';
  }

  private nextBarMeasureId(): string {
    this.barMeasureIdCounter += 1;
    return `bar-measure-${this.barMeasureIdCounter}`;
  }

  drillColumnPlaceholder(kind: 'dimension' | 'measure'): string {
    if (!this.drillDataset) return 'Select a dataset first';
    if (this.isDrillDatasetColumnsLoading) return 'Loading columns...';
    if (this.drillColumnsErrorMessage) return 'Unable to load columns';
    return kind === 'dimension' ? 'Select group-by column' : 'Select value column';
  }

  /** Non-empty once a column load for the current drill dataset has failed,
   *  or succeeded with zero usable columns — see loadColumnsForDataset. */
  get drillColumnsErrorMessage(): string {
    return this.drillDataset ? this.datasetColumnsError[this.drillDataset] || '' : '';
  }

  /** Entity group for the metadata/attribute endpoints — config-driven. */
  private get entityGroupName(): string {
    return this.layout.options?.entityGroup?.trim() || 'NontimeSeriesEntities';
  }

  /** Drop the configured `hiddenColumns` (case-insensitive) and de-dupe, keeping
   *  first-seen order — applied to the attribute names from getEntityAttributes. */
  private filterVisibleColumns(names: string[]): string[] {
    const hidden = new Set((this.layout.options?.hiddenColumns ?? ['uuid', '__rowKey']).map((column) => column.toLowerCase()));
    const seen = new Set<string>();
    const columns: string[] = [];
    (names || []).forEach((name) => {
      const key = String(name || '');
      if (!key || hidden.has(key.toLowerCase()) || seen.has(key)) return;
      seen.add(key);
      columns.push(key);
    });
    return columns;
  }

  // ── Generate configuration + render preview ───────────────────────────────

  /** Enough BAR config is mapped to draw a preview (variant + category + at least
   *  one measure; series-based variants also need a split; comparison needs two
   *  measures). Deliberately does NOT require a title — the preview appears while
   *  building, before Generate. Also needs the dataset's rows to be available. */
  get canPreviewBar(): boolean {
    if (!this.isBarLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    const filledMeasures = this.barMeasures.filter((m) => !!m.field);
    if (!this.barCategory || !filledMeasures.length) return false;
    if (this.isBarComparison) return filledMeasures.length >= 2;
    if (this.barSeriesRequired && !this.barSeries) return false;
    return true;
  }

  /** Enough is mapped to render a live Scatter/Bubble preview: dataset with rows,
   *  X + Y; Bubble also needs Size, Multi Scatter also needs Series. */
  get canPreviewScatterBubble(): boolean {
    if (!this.isScatterBubbleLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    if (!this.sbXField || !this.sbYField) return false;
    if (this.sbVariantUsesSize && !this.sbSizeField) return false;
    if (this.sbVariantUsesSeries && !this.sbSeries) return false;
    return true;
  }

  /** Enough LINE_AREA config is mapped to draw a live preview. */
  get canPreviewLineArea(): boolean {
    if (!this.isLineAreaLayout || !this.drillDataset) return false;
    if (!this.entityRowsCache.get(this.drillDataset)?.length) return false;
    const filledMeasures = this.laMeasures.filter((m) => !!m.field);
    if (!this.laCategory || !filledMeasures.length) return false;
    if (this.laVariantUsesSeries && !this.laSeries && filledMeasures.length < 2) return false;
    return true;
  }

  get canGenerate(): boolean {
    if (this.isGenerating || !this.title.trim()) return false;
    // Input controls don't bind a dataset the way the chart layouts do — they
    // just need at least one target selected. A formula per target is
    // optional, not required: a target can be wired up now and given its
    // formula later (or driven with just the raw value, no formula at all),
    // so Generate/Update must not block on that.
    if (this.isInputControlLayout) {
      return this.selectedTargetCharts.length > 0;
    }
    // File upload is also target-driven and needs no dataset — just a target.
    if (this.isFileUploadLayout) {
      return this.selectedTargetCharts.length > 0;
    }
    // Popup is display-only: no dataset, no targets — it just needs at least
    // one content block with something in it.
    if (this.isPopupLayout) {
      return this.popupBlocks.some((block) => this.popupBlockHasContent(block));
    }
    // A KPI driven purely by a formula (e.g. a computed expression or a
    // cross-chart reference) doesn't require a dataset to be picked — enable
    // Generate on the formula alone, BEFORE the dataset gate below. (A KPI that
    // instead uses Column + Aggregation still needs a dataset, handled below.)
    // Same for a KPI wired up with a [Token] reference row but no formula text
    // yet typed — the reference itself is the intent to drive this KPI from
    // another chart/input's live output, so it shouldn't need a dataset either.
    if (this.isKpiLayout && (this.kpiFormula.trim() || this.targetReferences(this.componentId).length > 0)) return true;
    if (!this.selectedDatasets.length) return false;
    if (this.isDropdownLayout) {
      return !!this.dropdownField && this.selectedTargetCharts.length > 0;
    }
    if (this.isDrillDownLayout) {
      return this.drillDownDimensions.length > 0 && this.drillDownMeasures.length > 0;
    }
    if (this.isTableMatrixLayout) {
      // Drill-Down type needs dimensions+measures; Normal/Editable need only a
      // dataset (already checked above) — columns default to "all" when empty.
      if (this.isTmDrilldown) {
        return this.drillDownDimensions.length > 0 && this.drillDownMeasures.length > 0;
      }
      return true;
    }
    if (this.isHorizontalBarLayout) {
      const hasGroupFormula = this.hbGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
      return !!this.hbGroupByField && (!!this.hbValueFormula.trim() || hasGroupFormula);
    }
    if (this.isKpiLayout) {
      return (!!this.kpiField && !!this.kpiAggregation) || !!this.kpiFormula.trim(); 
    }
    if (this.isBarLayout) {
      const filledMeasures = this.barMeasures.filter((measure) => !!measure.field);
      const hasMeasure = filledMeasures.length > 0 || this.barUsesValueFormula;
      // Series-based variants need a split column; simple/clustered don't.
      const hasSeries = !this.barSeriesRequired || !!this.barSeries;
      // Comparison needs a Primary + a Comparison measure (two).
      if (this.isBarComparison) return !!this.barCategory && filledMeasures.length >= 2;
      return !!this.barCategory && hasMeasure && hasSeries;
    }
    if (this.isLineAreaLayout) {
      const filledMeasures = this.laMeasures.filter((measure) => !!measure.field);
      const hasMeasure = filledMeasures.length > 0;
      const hasSeries = !this.laVariantUsesSeries || !!this.laSeries || filledMeasures.length >= 2;
      return !!this.laCategory && hasMeasure && hasSeries;
    }
    if (this.isScatterBubbleLayout) {
      // X + Y required; Bubble also needs Size, Multi Scatter also needs Series.
      const hasSize = !this.sbVariantUsesSize || !!this.sbSizeField;
      const hasSeries = !this.sbVariantUsesSeries || !!this.sbSeries;
      return !!this.sbXField && !!this.sbYField && hasSize && hasSeries;
    }
    return this.steps.length > 0;
  }

  /** WHY Generate / Update Configuration is disabled — one human-readable item
   *  per unmet requirement, mirroring `canGenerate`'s checks in the same order
   *  and stopping at the same points. Empty = nothing missing. Without this the
   *  button just greys out with no way to tell what still needs filling in. */
  get generateBlockers(): string[] {
    if (this.isGenerating) return [];
    const missing: string[] = [];
    if (!this.title.trim()) missing.push('Title');

    // Target-driven layouts bind no dataset.
    if (this.isInputControlLayout || this.isFileUploadLayout) {
      if (!this.selectedTargetCharts.length) missing.push('At least one target chart');
      return missing;
    }
    if (this.isPopupLayout) {
      if (!this.popupBlocks.some((block) => this.popupBlockHasContent(block))) {
        missing.push('At least one content block with content in it');
      }
      return missing;
    }
    // A formula- or reference-driven KPI needs no dataset.
    if (this.isKpiLayout && (this.kpiFormula.trim() || this.targetReferences(this.componentId).length > 0)) {
      return missing;
    }
    if (!this.selectedDatasets.length) { missing.push('Dataset'); return missing; }

    if (this.isDropdownLayout) {
      if (!this.dropdownField) missing.push('Field');
      if (!this.selectedTargetCharts.length) missing.push('At least one target chart');
      return missing;
    }
    if (this.isDrillDownLayout || (this.isTableMatrixLayout && this.isTmDrilldown)) {
      if (!this.drillDownDimensions.length) missing.push('At least one hierarchy dimension');
      if (!this.drillDownMeasures.length) missing.push('At least one measure');
      return missing;
    }
    // Normal / Editable / Matrix / Pivot tables need only the dataset above —
    // an empty column list means "show every column".
    if (this.isTableMatrixLayout) return missing;

    if (this.isHorizontalBarLayout) {
      if (!this.hbGroupByField) missing.push('Group by column');
      const hasGroupFormula = this.hbGroupFormulas.some((entry) => entry.group.trim() && entry.formula.trim());
      if (!this.hbValueFormula.trim() && !hasGroupFormula) missing.push('Value formula (or one group formula)');
      return missing;
    }
    if (this.isKpiLayout) {
      if (!this.kpiField) missing.push('Column');
      if (!this.kpiAggregation) missing.push('Aggregation');
      if (missing.length) missing.push('or a formula instead');
      return missing;
    }
    if (this.isBarLayout) {
      const filledMeasures = this.barMeasures.filter((measure) => !!measure.field);
      if (!this.barCategory) missing.push('Category');
      if (this.isBarComparison) {
        if (filledMeasures.length < 2) missing.push('Two measures (primary + comparison)');
        return missing;
      }
      if (!filledMeasures.length && !this.barUsesValueFormula) missing.push('At least one measure');
      if (this.barSeriesRequired && !this.barSeries) missing.push('Series (split) column');
      return missing;
    }
    if (this.isLineAreaLayout) {
      const filledMeasures = this.laMeasures.filter((measure) => !!measure.field);
      if (!this.laCategory) missing.push('Category');
      if (!filledMeasures.length) missing.push('At least one measure');
      if (this.laVariantUsesSeries && !this.laSeries && filledMeasures.length < 2) {
        missing.push('Series column (or a second measure)');
      }
      return missing;
    }
    if (this.isScatterBubbleLayout) {
      if (!this.sbXField) missing.push('X axis column');
      if (!this.sbYField) missing.push('Y axis column');
      if (this.sbVariantUsesSize && !this.sbSizeField) missing.push('Size column');
      if (this.sbVariantUsesSeries && !this.sbSeries) missing.push('Series column');
      return missing;
    }
    if (!this.steps.length) missing.push('At least one step');
    return missing;
  }

  /** One-line summary of `generateBlockers` for the button tooltip. */
  get generateBlockerText(): string {
    const missing = this.generateBlockers;
    return missing.length ? `Still needed: ${missing.join(', ')}` : '';
  }

  generateConfiguration(): void {
    if (!this.canGenerate) return;

    // Pre-flight for the drill-down builders (standalone layout AND the Table &
    // Matrix Drill-Down type): an empty or stale field pick (e.g. left over from
    // a dataset switch) would be rejected by the compute API with an opaque
    // "Dataset 'x' has no field 'y'" — fail fast with a fixable message instead.
    if (this.isDrillDownLayout || (this.isTableMatrixLayout && this.isTmDrilldown)) {
      const rows = [...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures];
      // Each level / pivot column / measure may read from its OWN dataset
      // (merged drill-down + pivot) — validate against THAT dataset's columns.
      const stale = rows.filter((row) => {
        const columns = this.entityColumnsCache.get(row.dataset || this.drillDataset);
        return !!columns && !!row.field && !columns.includes(row.field);
      });
      if (stale.length || rows.some((row) => !row.field)) {
        this.generateError = stale.length
          ? `${stale.map((row) => `Dataset "${row.dataset || this.drillDataset}" has no column '${row.field}'`).join('; ')} — re-pick those fields for their dataset.`
          : 'Pick a column for every hierarchy level, pivot column and measure.';
        return;
      }
    }

    this.generateError = '';
    this.isGenerating = true;
    // Bar / Line / Scatter FINALIZE their live draft tile in place instead of
    // going through addTile — stamp the form's Notes onto it first.
    this.syncFormNotesToTile();

    // Give each CHART a stable, type-derived component id (CMP-WATERFALL, …) so
    // the input-control recompute response's `results[].componentId` matches the
    // rendered chart tile directly. Input controls keep their random CMP-<n> id
    // (that id is echoed back as `inputComponentId`).
    //
    // ONLY mint a fresh id when this is a brand-new tile — re-generating an
    // EXISTING tile (editingTileId set) must keep its original componentId.
    // editTile() already restored it onto `this.componentId`; minting a new one
    // here would silently orphan every input control that already targets this
    // tile (their `selectedTargetCharts`/payload targets still point at the OLD
    // id, which would no longer match anything on the dashboard).
    if (!this.editingTileId) {
      const previousComponentId = this.componentId;
      this.componentId = this.componentIdForLayout(this.selectedLayoutType);
      // A chart's OWN formula references (KPI / horizontal bar — see
      // `formulaReferencesEnabledFor`) are keyed by `componentId` at the time
      // the user typed them, which is the id BEFORE this reassignment. Without
      // migrating them here, they'd silently vanish from the UI (the template
      // now looks up the NEW id, which has no entries) and — worse — never
      // reach `buildPayload()` at all, since `referencesForFormula` below also
      // reads off the NEW `this.componentId`.
      if (previousComponentId !== this.componentId && this.targetFormulaReferences[previousComponentId]?.length) {
        const { [previousComponentId]: moved, ...rest } = this.targetFormulaReferences;
        this.targetFormulaReferences = { ...rest, [this.componentId]: moved };
      }
    }

    const payload = this.buildPayload();
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');

    // Persist the component config to the DB in parallel with the compute call
    // below — fired for every layout so the config is saved on every Generate.
    this.saveConfigToDb(payload);

    if (this.isDropdownLayout) {
      // The /compute endpoint's response (`{inputComponentId, results: [...]}`)
      // never carries the field's distinct values at all — it's a target-
      // simulation result, not a row listing — so `dropdownOptionsFromComputeResponse`
      // always came back empty ("No items found"). The dropdown's own values
      // come from `apiEndpoints.filterApi` (`/getData`) scoped to just this
      // field — NOT getEntityRows/dynamic_filter, which pulled every row of
      // the whole dataset over the network just to dedupe one column here.
      const filterApiUrl = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.filterApi, '/getData');
      this.datasetService.getDistinctFieldValues(filterApiUrl, this.drillDataset, this.dropdownField).subscribe({
        next: (rows) => {
          const options = this.distinctDropdownOptions(rows, this.dropdownField);
          this.isGenerating = false;
          const dropdownTile = this.buildDropdownTile(payload, options);
          const updated = this.addTile(dropdownTile);
          this.ui.showSuccess(updated ? 'Dropdown updated on the dashboard.' : 'Dropdown added to the dashboard.');
          // ONE compute call for the whole Generate.
          //
          // This used to fire TWO: a config-registration POST with the fresh
          // form `payload`, and then a second target-push POST from
          // onTileDropdownChange built from `tile.payload` — which, when
          // UPDATING an existing tile, is the tile's PREVIOUS payload. So the
          // backend received the new config and then immediately received the
          // old one, and whichever landed last won.
          //
          // Passing the fresh payload as the target-push BASE collapses both
          // jobs into a single request that carries the new config AND the
          // default value + targets, so the charts still filter on generate
          // (the "target not updating on generate" fix this replaces).
          const persisted = this.dashboardTiles.find((tile) => tile.id === dropdownTile.id) ?? dropdownTile;
          const value = persisted.dropdownControl?.value ?? [];
          this.onTileDropdownChange(persisted, value, payload);
        },
        error: (error: HttpErrorResponse) => {
          this.isGenerating = false;
          this.generateError = error.error?.message || error.message || 'Unable to load dropdown option values.';
          this.ui.showError(this.generateError);
        },
      });
      return;
    }

    // Input controls (slider / input box) are pure UI — they render from the
    // builder inputs alone and don't need the compute response. Add the tile
    // immediately so it always appears; the POST below just registers the
    // config with the backend (its response isn't required to draw the tile).
    if (this.isInputControlLayout) {
      this.isGenerating = false;
      const updated = this.addTile(this.buildInputTile(payload));
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: () => this.ui.showSuccess(updated ? 'Input control updated on the dashboard.' : 'Input control added to the dashboard.'),
        error: () => this.ui.showError('Input control added, but saving its configuration failed.'),
      });
      return;
    }

    // Bar & Column renders CLIENT-SIDE from the dataset rows the builder already
    // fetched (getEntityRows), so the preview is instant and needs no compute
    // response. The POST below just registers the config (structured so a future
    // server-side compute is a drop-in swap: feed its rows to buildBarWidgetData
    // instead of the cached rows). Add the tile immediately.
    if (this.isBarLayout) {
      this.isGenerating = false;
      // If a live draft preview tile already exists, FINALIZE it in place (so we
      // don't add a duplicate) — just refresh it with the final payload/title and
      // promote it to a normal tile. Otherwise add a fresh tile.
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id ? { ...this.rebuildBarTile(t), payload } : t,
        );
        this.previewTileId = null; // promoted → no longer a draft
      } else {
        const fresh = this.buildBarTile(payload);
        // UPDATING a server-computed tile: keep its CURRENT server rows on
        // screen until the fresh compute response lands (mirrors the
        // line-area path — replacing outright blanked the chart).
        const editing = this.editingTileId ? this.dashboardTiles.find((t) => t.id === this.editingTileId) : undefined;
        if (editing?.barData && this.isServerComputedClientTile(editing.barData as Record<string, unknown>, this.barCategory)) {
          fresh.barData = { ...(editing.barData as Record<string, unknown>), title: this.title.trim() || fresh.title };
          fresh.barRows = editing.barRows ?? [];
        }
        this.addTile(fresh);
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          // Formula values exist only in this response; the cached rows lack
          // them. ALSO: a compute backend that answers plain bar configs with
          // SERVER-side pre-aggregated rows (`data[0].bar`, keys
          // {department, value} — same convention as lineArea) must render from
          // those, or the chart shows nothing when the client rows don't line
          // up. Absent envelope + no formula → client-side render as before.
          const serverRows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
          if (this.barUsesValueFormula || serverRows?.length) this.applyBarComputeResponse(response, payload);
          this.ui.showSuccess('Bar chart added to the dashboard.');
        },
        error: () => this.ui.showError('Bar chart added, but saving its configuration failed.'),
      });
      return;
    }

    // Scatter & Bubble also renders CLIENT-SIDE from the cached dataset rows, so
    // it mirrors the Bar path: finalize the draft in place or add a fresh tile.
    if (this.isScatterBubbleLayout) {
      this.isGenerating = false;
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          // componentId is re-minted for a brand-new tile above, so the draft's
          // original one would no longer match the payload it now carries.
          t.id === id ? { ...this.rebuildScatterBubbleTile(t), componentId: this.componentId, payload } : t,
        );
        this.previewTileId = null;
      } else {
        this.addTile(this.buildScatterBubbleTile(payload));
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          // A per-field aggregation (SUM/AVG/…) is computed server-side — the
          // cached raw dataset rows used for the client-side render above don't
          // reflect it, so swap in the response's rows when present.
          this.applyScatterBubbleComputeResponse(payload, response);
          this.ui.showSuccess('Scatter / bubble chart added to the dashboard.');
        },
        error: () => this.ui.showError('Scatter / bubble chart added, but saving its configuration failed.'),
      });
      return;
    }

    // Line & Area also renders CLIENT-SIDE from the cached dataset rows.
    if (this.isLineAreaLayout) {
      this.isGenerating = false;
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id ? { ...this.rebuildLineAreaTile(t), payload } : t,
        );
        this.previewTileId = null;
      } else {
        const fresh = this.buildLineAreaTile(payload);
        // UPDATING a server-computed tile: keep its CURRENT server rows on
        // screen until the fresh compute response lands — the client-side
        // build above has no usable rows for it, so replacing outright
        // blanked the chart on every Update Configuration.
        const editing = this.editingTileId ? this.dashboardTiles.find((t) => t.id === this.editingTileId) : undefined;
        if (editing?.lineAreaData && this.isServerComputedClientTile(editing.lineAreaData as Record<string, unknown>, this.laCategory)) {
          fresh.lineAreaData = { ...(editing.lineAreaData as Record<string, unknown>), title: this.title.trim() || fresh.title };
          fresh.lineAreaRows = editing.lineAreaRows ?? null;
        }
        this.addTile(fresh);
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => {
          // A compute backend can answer the line-area config with SERVER-side
          // pre-aggregated rows (`data[0].lineArea`, keys {department, value}).
          // When present, render FROM the response — the configured dataset
          // columns don't exist on those rows, so the client-side render alone
          // showed an empty chart. Absent → the tile keeps its client rows.
          this.applyLineAreaComputeResponse(String(payload['componentId'] ?? ''), response);
          this.ui.showSuccess('Line / area chart added to the dashboard.');
        },
        error: () => this.ui.showError('Line / area chart added, but saving its configuration failed.'),
      });
      return;
    }

    // File Upload control — pure UI (renders from builder inputs); the actual
    // file is POSTed later when the user picks one. Add the tile at once + POST
    // the descriptor to register the config.
    if (this.isFileUploadLayout) {
      // Pure UI control — at generate time there's NO file to compute, so we do
      // NOT POST to the compute API (it requires a real `value`/file and would
      // 400 with "No value provided"). Just add the uploader tile; the actual
      // file + compute call happens later when the user picks a file.
      this.isGenerating = false;
      const updated = this.addTile(this.buildFileUploadTile(payload));
      this.ui.showSuccess(updated ? 'File upload updated on the dashboard.' : 'File upload added to the dashboard.');
      return;
    }

    // Popup component — renders entirely CLIENT-SIDE (table blocks snapshot
    // their rows from the dataset rows cache), so the tile is added/updated at
    // once and never waits on the network. The full config payload (trigger /
    // behaviour / style / blocks) is still POSTed to the compute API to
    // register the component server-side — mirroring the Scatter/Line pattern:
    // the POST failing only warns, it never blocks the tile.
    if (this.isPopupLayout) {
      this.isGenerating = false;
      // A live draft preview exists (onPopupFormChange) — finalize it in place
      // so Generate doesn't append a duplicate tile.
      let updated = false;
      if (this.previewTileId && this.dashboardTiles.some((t) => t.id === this.previewTileId)) {
        const id = this.previewTileId;
        this.dashboardTiles = this.dashboardTiles.map((t) =>
          t.id === id
            ? { ...t, payload, title: this.title.trim() || t.title, popupControl: { widgetData: this.buildPopupWidgetData() }, format: this.popupCardFormat() }
            : t,
        );
        this.previewTileId = null;
      } else {
        updated = this.addTile(this.buildPopupTile(payload));
      }
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: () => this.ui.showSuccess(updated ? 'Popup updated on the dashboard.' : 'Popup added to the dashboard.'),
        error: () => this.ui.showError('Popup added, but saving its configuration failed.'),
      });
      return;
    }

    // Normal / Editable tables go through the SAME compute API as every other
    // chart. The backend's table contract is dimensions + measures (its flat
    // `columns` shape is rejected server-side: a string array fails to
    // deserialize, column objects 500), so translate the form's column picks
    // into that contract and flatten the tree response back into the flat rows
    // buildTableModel renders. The tile keeps the ORIGINAL form payload
    // (columns/groupBy/aggregation) so save/edit-restore stay unchanged.
    // Merged Drill-Down + Pivot in CLIENT-SIDE mode: fetch the joined flat rows
    // through the cross-dataset flat contract, build the hierarchy / pivot
    // columns / formula columns locally, then hand the result to the SAME tile
    // pipeline the backend tree goes through (buildDrillDownWidgetData).
    if (this.isDrillClientMode) {
      this.http.post<Record<string, unknown>>(url, this.buildDrillClientComputePayload()).subscribe({
        next: (response) => {
          this.isGenerating = false;
          const rows = this.extractTableMatrixRows(response);
          const updated = this.addTileFromResponse(this.buildDrillClientTreeResponse(rows), payload);
          // Keep the tile live-linked to the form (like Normal tables) and cache
          // its source rows so config edits re-shape it without a refetch.
          if (!this.editingTileId) {
            const last = this.dashboardTiles[this.dashboardTiles.length - 1];
            if (last?.layoutType === 'TABLE_MATRIX') this.editingTileId = last.id;
          }
          if (this.editingTileId) this.drillRowsCache.set(this.editingTileId, rows);
          this.ui.showSuccess(updated ? 'Drill-down table updated on the dashboard.' : 'Drill-down table added to the dashboard.');
        },
        error: (error: HttpErrorResponse) => {
          this.isGenerating = false;
          this.generateError = error.error?.message || error.message || 'Unable to generate the drill-down table.';
          this.ui.showError(this.generateError);
        },
      });
      return;
    }

    if (this.isTableMatrixLayout && !this.isTmDrilldown) {
      // Normal / Editable use the FLAT table contract now — the backend returns
      // flat rows in `data[]`, read directly (no tree flatten). extractTableMatrixRows
      // handles the common envelopes (data[], responseList[0].data, bare array).
      this.http.post<Record<string, unknown>>(url, this.buildTableMatrixComputePayload()).subscribe({
        next: (response) => {
          this.isGenerating = false;
          const rows = this.extractTableMatrixRows(response);
          const updated = this.addTileFromResponse({ data: rows }, payload);
          // Keep this table tile "live-linked" to the form so subsequent config
          // edits reflect immediately without needing to re-Generate.
          if (!this.editingTileId) {
            const last = this.dashboardTiles[this.dashboardTiles.length - 1];
            if (last?.layoutType === 'TABLE_MATRIX') this.editingTileId = last.id;
          }
          this.ui.showSuccess(updated ? 'Table updated on the dashboard.' : 'Table added to the dashboard.');
        },
        error: (error: HttpErrorResponse) => {
          this.isGenerating = false;
          this.generateError = error.error?.message || error.message || 'Unable to generate the table.';
          this.ui.showError(this.generateError);
        },
      });
      return;
    }

    // STEP 1 — call the compute API, then append the result as a dashboard tile.
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        this.isGenerating = false;
        const updated = this.addTileFromResponse(response, payload);
        this.ui.showSuccess(updated ? 'Chart updated on the dashboard.' : 'Chart added to the dashboard.');
      },
      error: (error: HttpErrorResponse) => {
        this.isGenerating = false;
        // Horizontal bar keeps a bundled sample fallback for now (per request)
        // so it renders without a reachable compute API; the others surface the
        // error directly.
        if (this.isHorizontalBarLayout) {
          this.http.get<Record<string, unknown>>('/assets/charts-config/horizontal-bar-chart-config.json').subscribe({
            next: (dummy) => {
              const updated = this.addTileFromResponse(dummy, payload);
              this.ui.showSuccess(updated ? 'Analytics engine not reachable - updated with sample bar-chart data.' : 'Analytics engine not reachable - added sample bar-chart data.');
            },
            error: () => {
              this.generateError = 'Unable to generate the configuration.';
              this.ui.showError(this.generateError);
            },
          });
          return;
        }
        this.generateError = error.error?.message || error.message || 'Unable to generate the configuration.';
        this.ui.showError(this.generateError);
      },
    });
  }

  /** Persist the component config to the DB via `saveDbConfigApi`, fired in
   *  parallel with the compute call on every Generate. Non-blocking: the tile
   *  renders from the compute response regardless of this call's outcome, so a
   *  failure here only surfaces a toast — it never aborts the generate flow.
   *  No-op when the endpoint isn't configured. */
  private saveConfigToDb(payload: Record<string, unknown>): void {
    const saveUrl = this.layout.apiEndpoints?.saveDbConfigApi ? resolveSelfServiceApiUrl(this.layout.apiEndpoints.saveDbConfigApi) : '';
    if (!saveUrl) return;
    this.http.post<Record<string, unknown>>(saveUrl, payload).subscribe({
      error: () => this.ui.showError('Chart added, but saving its configuration to the database failed.'),
    });
  }

  /** Build a tile from the compute response for the active (non-input) layout
   *  and append it to the dashboard canvas. */
  private addTileFromResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): boolean {
    const base = this.newTileBase(payload);
    base.format = this.defaultTileFormat(this.selectedLayoutType);
    // Layouts with a Build-form Format accordion fold its values in so Generate
    // honors what was set there (Bar/Waterfall/Table already fold in their own
    // branches below).
    if (this.isKpiLayout || this.isDrillDownLayout || this.isHorizontalBarLayout) {
      base.format = { ...base.format, ...this.barFormat };
    }
    let tile: DashboardTile;
    if (this.isDrillDownLayout) {
      const drillData = this.buildDrillDownWidgetData(response, base.format);
      // Unique per tile so each drill-down instance is independent.
      drillData['widgetId'] = 'dvm-tile-drill-' + base.id;
      tile = { ...base, drillData };
    } else if (this.isTableMatrixLayout) {
      // Fold the Build-form format accordion values into the tile's format (like
      // the Bar/Waterfall paths) so Generate honors what was set in the builder.
      base.format = { ...base.format, ...this.barFormat };
      const tableMatrixData = this.buildTableMatrixWidgetData(response, base.id, base.format);
      tile = { ...base, tableMatrixData };
    } else if (this.isHorizontalBarLayout) {
      tile = { ...base, hbData: this.buildHbWidgetData(response, base.id, base.format) };
    } else if (this.isKpiLayout) {
      tile = { ...base, colSpan: 3, rowSpan: 3, kpiData: this.buildKpiWidgetData(response, base.id, base.format) };
    } else {
      // Fold the Build-form format accordion values into the tile's format (like
      // buildBarTile does) so Generate honors what was set in the builder.
      base.format = { ...base.format, ...this.barFormat, title: this.title.trim() || undefined };
      const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
      const bars = this.applyStepSignsToWaterfallBars(rawBars, payload);
      // Keep the raw bars so the Format popup can re-derive the ECharts option.
      tile = { ...base, waterfallBars: bars, previewOption: this.buildWaterfallPreviewOption(bars, base.format) };
      tile.waterfallData = this.buildWaterfallTileData(tile);
    }
    return this.addTile(tile);
  }

  /** Seed a tile's format object from the configured controls' defaults —
   *  ONLY the controls that actually apply to this layout type (same
   *  `appliesTo` filtering as `formatControlsFor`), so e.g. a KPI-scoped
   *  `numberFormat` default doesn't leak onto Waterfall/HBar tiles when two
   *  controls share the same `key` but target different layout types. */
  private defaultTileFormat(layoutType: string): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    // Table & Matrix gates controls per sub-type (visibleWhenType) — honor that
    // here too, so a control scoped to ONE type (e.g. the drill-down-only `title`
    // accordion control) doesn't seed its default into another type's format.
    const subType = layoutType === 'TABLE_MATRIX' ? this.tmType : '';
    this.applicableFormatControls(layoutType).forEach((control) => {
      if (subType && !this.controlVisibleForSubType(control, subType)) return;
      if (control.default !== undefined) format[control.key] = control.default;
    });
    return format;
  }

  /** Wrap the compute response's single aggregated value as the shared
   *  <app-number-stats-shared>'s `[data]` input. With no apiUrl, getConfig
   *  returns `data.data` unwrapped and NumberStats reads res.data[0][key], so we
   *  inline the computed value under a fixed `value` key. */
  /** KPI card decoration (leading icon, sub-heading caption, trend chip) from
   *  the Format popup. Shared by Generate and the live Format-popup update so
   *  both surfaces pass NumberStatsComponent the same keys. */
  private kpiDecorationData(format?: Record<string, unknown>): Record<string, unknown> {
    return {
      icon: (format?.['icon'] as string) || '',
      iconColor: (format?.['iconColor'] as string) || '',
      iconSize: format?.['iconSize'] ?? '',
      subText: (format?.['subText'] as string) || '',
      deltaText: (format?.['deltaText'] as string) || '',
      deltaDirection: (format?.['deltaDirection'] as string) || 'up',
      deltaColor: (format?.['deltaColor'] as string) || '',
    };
  }

  private buildKpiWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const value = this.extractKpiValue(response);
    return {
      title: this.title.trim() || 'KPI',
      key: 'value',
      widgetId: 'dvm-tile-kpi-' + tileId,
      alignment: (format?.['contentAlignment'] as string) || 'center',
      // '' = inherit the dashboard/theme direction token; only an explicit
      // per-card pick stamps a class that overrides it.
      cardLayout: (format?.['cardLayout'] as string) || '',
      titleFontSize: format?.['titleFontSize'] ?? '',
      valueFontSize: format?.['valueFontSize'] ?? '',
      ...this.kpiDecorationData(format),
      numberFormat: this.toKpiNumberFormat(format),
      numberScale: this.toNumberScale(format?.['numberFormat']),
      decimals: Number(format?.['decimals'] ?? 1),
      prefix: (format?.['prefix'] as string) || '',
      suffix: (format?.['suffix'] as string) || '',
      infoText: (format?.['infoText'] as string) || '',
      infoIconPosition: (format?.['infoIconPosition'] as string) || 'corner',
      // Inline single-row payload — NumberStats reads res.data[0].value.
      data: { data: [{ value }] },
    };
  }

  /** Pull the KPI's single numeric value out of the compute response, trying the
   *  common envelope shapes (responseList[0].data[0], data[0], data, top-level). */
  private extractKpiValue(response: Record<string, unknown> | null): number | string | null {
    if (response == null) return null;
    const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
    const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
    const dataField = fromList ?? (response as { data?: unknown }).data ?? response;
    const row = Array.isArray(dataField) ? dataField[0] : dataField;
    if (row == null) return null;
    if (typeof row === 'number' || typeof row === 'string') return row;
    const record = row as Record<string, unknown>;
    // Prefer an explicit value/result key, else the chosen field, else the first numeric.
    const candidate =
      record['value'] ?? record['result'] ?? record[this.kpiField] ??
      Object.values(record).find((v) => typeof v === 'number');
    return (candidate as number | string | undefined) ?? null;
  }

  /** Build an INPUT_BOX / SLIDER tile from the current builder inputs. The tile
   *  renders the EXISTING shared widget (app-mat-slider / app-input-box); this
   *  just assembles the `[data]` object each of those consumes. See
   *  docs/DVM_LAYOUT_BUILDER_DATA_API.md for the contract. */
  private buildInputTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    // Seed the Format popup (decimals/round-off scale) same as any other
    // formattable tile — without this tile.format stays undefined and the
    // popup never opens (isFormattableTile gates on tile.format truthiness
    // via the template's *ngIf="openFormatTileId === tile.id && tile.format").
    base.format = this.defaultTileFormat(this.selectedLayoutType);
    const dataset = this.selectedDatasets[0] || '';
    // Column list (if the dataset's columns were loaded) powers the widgets'
    // built-in compute/formula panels.
    const computationColumns = (this.entityColumnsCache.get(dataset) || []).map((name) => ({ name }));
    const value = this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : '');
    // `numberFormat` doubles as the display SCALE (K/M/B/%/plain) here — same
    // preset values formatNumber() (number-format.utils.ts) already knows how
    // to render; `decimals` is the round-off precision on that display.
    const numberFormat = (base.format?.['numberFormat'] as string) || 'NONE';
    const decimals = Number(base.format?.['decimals'] ?? 1);

    const widgetData: Record<string, unknown> = this.isSliderLayout
      ? {
          // app-mat-slider self-reads min/max/step via ConfigService.getConfig.
          // With NO apiUrl, getConfig returns `data.data` unwrapped, and the
          // slider reads res.data.{min,max,step,name,rangeStart,rangeEnd}.
          key: this.componentId,
          textCase: 'capital',
          numberFormat,
          decimals,
          data: {
            data: {
              name: this.title.trim() || 'Slider',
              min: this.sliderMin,
              max: this.sliderMax,
              step: this.sliderStep,
              // MatSliderComponent's thumb/input are bound to `rangeEnd` (it's a
              // single-handle slider despite the "range" naming — `rangeStart`
              // is read but never shown), so the configured default value MUST
              // go there, not into rangeStart, or the handle always starts at
              // whatever rangeEnd happens to be (previously always `sliderMax`
              // — the slider looked like it was pinned to its maximum).
              rangeStart: this.inputDefaultValue ?? this.sliderMin,
              rangeEnd: this.inputDefaultValue ?? this.sliderMin,
            },
          },
          computationEntityName: dataset,
          computationColumns,
          // Hide the on-the-fly compute (bolt) button on this tile.
          hideCompute: true,
        }
      : {
          // app-input-box renders directly from `data` (title / placeholder) and
          // drives its own compute panel from computationColumns/entityName.
          title: this.title.trim() || 'Input',
          label: this.title.trim() || 'Input',
          placeholder: 'Enter a value',
          entityName: dataset,
          computationEntityName: dataset,
          computationColumns,
          // Hide the on-the-fly compute (bolt) button on this tile.
          hideCompute: true,
          // The configured default — InputBoxComponent reads this to seed (and
          // later re-sync) its displayed value.
          value,
          numberFormat,
          decimals,
        };

    return {
      ...base,
      // Input controls are compact. No `format`: Slider / Input Box take no
      // card-header formatting (builder or published), by request.
      colSpan: 4,
      rowSpan: 4,
      inputControl: {
        kind: this.isSliderLayout ? 'SLIDER' : 'INPUT_BOX',
        // Display summary only (per-target formulas live in the payload targets).
        formula: this.selectedTargetCharts
          .map((id) => this.targetFormulaSummary(id))
          .filter(Boolean)
          .join(' · '),
        value,
        targets: [...this.selectedTargetCharts],
        widgetData,
        apiConfig: { ...this.buildApiCallConfig },
      },
    };
  }

  /** Build a Bar & Column tile from the current builder inputs, rendering
   *  client-side from the cached dataset rows via the pure option builder. The
   *  tile's `format` starts from the Build-form choices (barFormat) over the
   *  configured defaults, so the tile's Format popup opens pre-filled with what
   *  the user set here. */
  private buildBarTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('BAR'), ...this.barFormat, title: this.title.trim() || undefined };
    // Keep the unfiltered rows (with computed columns) so cross-filter reverses cleanly.
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return { ...base, barRows: rows, barData: this.buildBarWidgetData(base.id, base.format) };
  }

  /** Assemble the BarColumnCapabilities from the current builder state. */
  private buildBarCapabilities(): BarColumnCapabilities {
    const measures = this.barMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({
        field: measure.field,
        label: measure.label || measure.field,
        renderAs: measure.renderAs,
        axis: measure.axis,
      }));
    // The active Secondary axis column (Y when vertical, X when horizontal) is
    // appended as an extra measure bound to the secondary (y2) axis — the option
    // builder resolves y2→x2 for horizontal internally.
    const secondaryField = this.barActiveSecondaryField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({ field: secondaryField, label: secondaryField, renderAs: 'line', axis: 'y2' });
    }
    // Valid reference lines (numeric value set).
    const referenceLines = this.barReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      orientation: this.barOrientation,
      variant: this.barVariant,
      category: this.barCategory,
      // Hierarchical Category Axis — only meaningful when a distinct parent is set.
      categoryParent: this.barCategoryParent && this.barCategoryParent !== this.barCategory ? this.barCategoryParent : undefined,
      categoryLabel: this.barCategoryLabel || this.barCategory,
      // Series split only applies to the series-based variants.
      series: this.barVariantUsesSeries && this.barSeries ? this.barSeries : undefined,
      ...(this.barVariantUsesSeries && this.barSeries && this.parseValueOrder(this.barSeriesOrder).length ? { seriesOrder: this.parseValueOrder(this.barSeriesOrder) } : {}),
      measures,
      // Per-axis label font weights (build form; '' omitted so the tile-wide
      // Format-popup axisFontWeight — or the default — decides that axis).
      ...(this.barXAxisFontWeight ? { xAxisFontWeight: this.barXAxisFontWeight } : {}),
      ...(this.barYAxisFontWeight ? { yAxisFontWeight: this.barYAxisFontWeight } : {}),
      ...(this.barSecondaryAxisFontWeight ? { axis2FontWeight: this.barSecondaryAxisFontWeight } : {}),
      ...(referenceLines.length ? { referenceLines } : {}),
      // Config-driven ECharts styling defaults → the option builder reads these
      // instead of hardcoded constants.
      ...(this.layout.options?.barChartDefaults ? { chartDefaults: this.layout.options.barChartDefaults } : {}),
      // Config-resolved render-dispatch flags (stack/normalize100/comparison) so
      // the option builder needs no variant-name string checks.
      variantFlags: {
        stack: this.barVariantEntry?.stack === true,
        normalize100: this.barVariantEntry?.normalize100 === true,
        comparison: this.barVariantEntry?.comparison === true,
      },
      // Formula-Builder computed columns — carried on the payload so they round-
      // trip on save/publish/edit. The rows passed to the chart already have these
      // applied (see applyBarComputedColumns), so the option builder ignores them.
      ...(this.barComputedColumns.length ? { computedColumns: this.barComputedColumns } : {}),
    } as BarColumnCapabilities;
  }

  /** Fold the tile's Format-popup choices (all accordion sections) into a bar
   *  capabilities object. Shared by initial build + live re-format so the two
   *  paths never drift. Mutates & returns `capabilities`. */
  private applyBarFormatToCapabilities(
    capabilities: BarColumnCapabilities,
    format?: Record<string, unknown>,
  ): BarColumnCapabilities {
    const f = format || {};
    // Formatting section (mirrors the chart-builder's ChartStyle).
    capabilities.showValues = f['showValues'] !== false;
    capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
    // numberScale is now its own control; fall back to deriving it from the
    // numberFormat preset (COMPACT/MILLIONS/…) when not explicitly chosen.
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.labelMode = f['labelMode'] ? String(f['labelMode']) : undefined;
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    // Named palette → resolved colors (from config's palettes registry). Bar
    // coloring is PALETTE-driven; the waterfall-only `positiveColor` must NOT
    // seed `capabilities.colors` here — its green default (#10b981) was winning
    // over the palette (single-color precedence), so a chosen palette never
    // reflected. Leave `colors` unset so the palette always applies.
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    // Axis section — X / Y / Secondary X / Secondary Y titles (mirrors chart-builder).
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
    capabilities.xAxisLabel2 = (f['xAxisLabel2'] as string) || undefined;
    capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || undefined;
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    // Tooltip section (full block).
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
    capabilities.tooltipShowChart = f['tooltipShowChart'] === true;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
    if (f['tooltipBorderRadius'] !== undefined) capabilities.tooltipBorderRadius = Number(f['tooltipBorderRadius']);
    if (f['tooltipFontSize'] !== undefined) capabilities.tooltipFontSize = Number(f['tooltipFontSize']);
    // Tile-wide base font (shared Formatting controls; 0/'' = keep defaults).
    capabilities.fontFamily = f['fontFamily'] ? String(f['fontFamily']) : undefined;
    capabilities.fontSize = Number(f['fontSize']) > 0 ? Number(f['fontSize']) : undefined;
    capabilities.axisFontWeight = f['axisFontWeight'] ? String(f['axisFontWeight']) : undefined;
    // Advanced section.
    if (f['barWidthPercent'] !== undefined) capabilities.barWidthPercent = Number(f['barWidthPercent']);
    capabilities.smoothLines = f['smoothLines'] === true;
    capabilities.showVariance = f['showVariance'] !== false;
    capabilities.varianceUpColor = (f['varianceUpColor'] as string) || undefined;
    capabilities.varianceDownColor = (f['varianceDownColor'] as string) || undefined;
    if (f['barGapPercent'] !== undefined) capabilities.barGapPercent = Number(f['barGapPercent']);
    if (f['barCategoryGapPercent'] !== undefined) capabilities.barCategoryGapPercent = Number(f['barCategoryGapPercent']);
    if (f['barCornerRadius'] !== undefined) capabilities.barCornerRadius = Number(f['barCornerRadius']);
    capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
    capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
    // Blank number inputs come through as '' / null → leave as auto (undefined).
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.valueAxisLog = f['valueAxisLog'] === true;
    capabilities.showStackTotals = f['showStackTotals'] === true;
    capabilities.animation = f['animation'] !== false;
    // Interaction section.
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.zoomAxis = f['zoomAxis'] ? String(f['zoomAxis']) : undefined;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    capabilities.emitSelection = f['emitSelection'] === true;
    return capabilities;
  }

  /** Resolve a palette key (from the config's `palettes` registry) to its color
   *  array; empty when unknown/absent so the option builder uses its default. */
  private resolvePaletteColors(paletteKey?: string): string[] | undefined {
    if (!paletteKey) return undefined;
    const palettes = (this.layout.options as Record<string, unknown> | undefined)?.['palettes'] as
      | Array<{ key?: string; colors?: string[] }>
      | undefined;
    const match = Array.isArray(palettes) ? palettes.find((p) => p.key === paletteKey) : undefined;
    return match?.colors?.length ? match.colors : undefined;
  }

  /** `[data]` input for <app-unified-bar-column-chart>. Carries the capabilities
   *  plus the pre-fetched dataset rows (client-side render) and per-tile format. */
  /** Matched by componentId so a slow response can't land on the wrong tile. */
  private applyBarComputeResponse(response: Record<string, unknown> | null, payload: Record<string, unknown>): void {
    const componentId = String(payload['componentId'] ?? '');
    const tile = this.dashboardTiles.find((candidate) => candidate.componentId === componentId);
    if (!tile) return;
    const rows = extractBarComputeRows(response?.['responseList']) ?? extractBarComputeRows(response);
    if (!rows?.length) {
      this.ui.showError('The value formula returned no data — check the formula and its references.');
      return;
    }
    const widgetData = buildBarComputeWidgetData(response, {
      widgetId: 'dvm-tile-bar-' + tile.id,
      title: this.title.trim(),
      capabilities: this.applyBarFormatToCapabilities(this.buildBarCapabilities(), tile.format) as unknown as Record<string, unknown>,
      payload,
      format: tile.format,
    });
    this.dashboardTiles = this.dashboardTiles.map((candidate) =>
      candidate.id === tile.id
        ? { ...candidate, barRows: rows, barData: widgetData }
        : candidate,
    );
  }

  private buildBarWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    // Apply any Formula-Builder computed columns to the rows so a computed column
    // mapped onto Category/Measure/Split resolves at render time.
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    const capabilities = this.applyBarFormatToCapabilities(this.buildBarCapabilities(), format);
    return {
      widgetId: 'dvm-tile-bar-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  // ── Line & Area builders (mirror the BAR trio) ──
  /** Assemble the LineAreaCapabilities from the current builder state. */
  private buildLineAreaCapabilities(): LineAreaCapabilities {
    const measures: LineAreaMeasure[] = this.laMeasures
      .filter((measure) => measure.field)
      .map((measure) => ({
        field: measure.field,
        ...(measure.dataset ? { dataset: measure.dataset } : {}),
        label: measure.label || measure.field,
        renderAs: measure.renderAs,
        axis: measure.axis,
        // Omit when unset so the chart keeps falling back to the palette.
        ...(String(measure.color ?? '').trim() ? { color: measure.color } : {}),
                      ...(String(measure.symbol ?? '').trim() ? { symbol: measure.symbol } : {}),
        ...(Number.isFinite(Number(measure.symbolSize)) ? { symbolSize: Number(measure.symbolSize) } : {}),
      }));
    const secondaryField = this.laSecondaryYField.trim();
    if (secondaryField && !measures.some((m) => m.field === secondaryField)) {
      measures.push({
        field: secondaryField,
        label: secondaryField,
        renderAs: 'line',
        axis: 'y2',
        ...(this.laSecondaryYDataset || this.drillDataset ? { dataset: this.laSecondaryYDataset || this.drillDataset } : {}),
      });
    }
    const referenceLines = this.laReferenceLines
      .filter((line) => line.value !== null && Number.isFinite(Number(line.value)))
      .map((line) => ({ value: Number(line.value), label: line.label || undefined, color: line.color || undefined }));
    return {
      ...(this.laApiUrl.trim() ? {
        apiOverride: {
          url: this.laApiUrl.trim(),
          method: 'post',
          payload: this.parseLineAreaApiPayload(),
          ...(this.drillDataset ? { dataset: this.drillDataset } : {}),
          datasets: {
            category: this.laCategoryDataset || this.drillDataset,
            ...(this.laSeries ? { series: this.laSeriesDataset || this.drillDataset } : {}),
            measures: measures.map((measure) => ({
              field: measure.field,
              dataset: measure.dataset || (measure.axis === 'y2' ? this.laSecondaryYDataset : this.drillDataset),
            })),
            ...(this.laSecondaryXField ? { secondaryX: this.laSecondaryXDataset || this.drillDataset } : {}),
            ...(this.laSecondaryXField ? { secondaryXValue: this.laSecondaryXValueDataset || this.drillDataset } : {}),
            ...(this.laSecondaryYField ? { secondaryY: this.laSecondaryYDataset || this.drillDataset } : {}),
          },
          ...(this.laApiResponsePath.trim() ? { responsePath: this.laApiResponsePath.trim() } : {}),
        },
      } : {}),
      variant: this.laVariant,
      category: this.laCategory,
      ...(this.laCategoryDataset || this.drillDataset ? { categoryDataset: this.laCategoryDataset || this.drillDataset } : {}),
      categoryParent: this.laCategoryParent && this.laCategoryParent !== this.laCategory ? this.laCategoryParent : undefined,
      categoryLabel: this.laCategoryLabel || this.laCategory,
      series: this.laVariantUsesSeries && this.laSeries ? this.laSeries : undefined,
      ...(this.laVariantUsesSeries && this.laSeries && this.parseValueOrder(this.laSeriesOrder).length ? { seriesOrder: this.parseValueOrder(this.laSeriesOrder) } : {}),
      ...(this.laSeries && (this.laSeriesDataset || this.drillDataset) ? { seriesDataset: this.laSeriesDataset || this.drillDataset } : {}),
      measures,
      // Axis-title overrides typed in the Chart Data accordion (empty = auto).
      ...(this.laYAxisLabel.trim() ? { yAxisLabel: this.laYAxisLabel.trim() } : {}),
      ...(this.laSecondaryYLabel.trim() ? { yAxisLabel2: this.laSecondaryYLabel.trim() } : {}),
      showSecondaryYAxis: this.laShowSecondaryYAxis,
      // Per-axis label font weights (build form; '' omitted so the tile-wide
      // Format-popup axisFontWeight — or the default — decides that axis).
      ...(this.laXAxisFontWeight ? { xAxisFontWeight: this.laXAxisFontWeight } : {}),
      ...(this.laYAxisFontWeight ? { yAxisFontWeight: this.laYAxisFontWeight } : {}),
      ...(this.laSecondaryXFontWeight ? { xAxis2FontWeight: this.laSecondaryXFontWeight } : {}),
      ...(this.laSecondaryYFontWeight ? { yAxis2FontWeight: this.laSecondaryYFontWeight } : {}),
      // Secondary X axis — a second category column with its own value series
      // drawn against a second x-axis (below the primary by default, or on
      // top). Value defaults to the first measure.
      ...(this.laSecondaryXField.trim()
        ? {
            secondaryXField: this.laSecondaryXField.trim(),
            ...(this.laSecondaryXDataset || this.drillDataset ? { secondaryXDataset: this.laSecondaryXDataset || this.drillDataset } : {}),
            secondaryXValueField: (this.laSecondaryXValueField.trim() || this.laMeasures.find((m) => m.field)?.field || ''),
            ...(this.laSecondaryXValueDataset || this.drillDataset ? { secondaryXValueDataset: this.laSecondaryXValueDataset || this.drillDataset } : {}),
            secondaryXPosition: this.laSecondaryXPosition,
            ...(this.laSecondaryXLabel.trim() ? { xAxisLabel2: this.laSecondaryXLabel.trim() } : {}),
          }
        : {}),
      ...(this.laSecondaryYField.trim() && (this.laSecondaryYDataset || this.drillDataset)
        ? { secondaryYDataset: this.laSecondaryYDataset || this.drillDataset }
        : {}),
      ...(referenceLines.length ? { referenceLines } : {}),
      ...(this.layout.options?.lineAreaChartDefaults ? { chartDefaults: this.layout.options.lineAreaChartDefaults } : {}),
      variantFlags: {
        step: this.laVariantEntry?.step === true,
        smooth: this.laVariantEntry?.smooth === true,
        area: this.laVariantEntry?.area === true,
        stack: this.laVariantEntry?.stack === true,
        normalize100: this.laVariantEntry?.normalize100 === true,
      },
    } as LineAreaCapabilities;
  }

  /** Fold the tile's Format-popup choices into a line-area capabilities object. */
  private applyLineAreaFormatToCapabilities(capabilities: LineAreaCapabilities, format?: Record<string, unknown>): LineAreaCapabilities {
    const f = format || {};
    capabilities.showValues = f['showValues'] === true;
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    // Axis titles: the Format-accordion value wins; else keep the Chart-Data
    // override already on the capabilities (buildLineAreaCapabilities); else auto.
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || capabilities.yAxisLabel || undefined;
    capabilities.xAxisLabel2 = (f['xAxisLabel2'] as string) || capabilities.xAxisLabel2 || undefined;
    capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || capabilities.yAxisLabel2 || undefined;
    capabilities.showSecondaryYAxis = f['showSecondaryYAxis'] === undefined
      ? capabilities.showSecondaryYAxis : f['showSecondaryYAxis'] !== false;
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    // Only an explicit toggle choice overrides; absent → undefined so the
    // config-level default (lineAreaChartDefaults.trimLabelPrefix) applies.
    capabilities.trimLabelPrefix = f['trimLabelPrefix'] === undefined ? undefined : f['trimLabelPrefix'] === true;
    // Per-tile "Secondary X values" select: 'table'/'labels' explicitly
    // override; 'default' (or absent) → undefined so the config-level default
    // (lineAreaChartDefaults.secondaryXAsTable) decides.
    const sxDisplay = String(f['secondaryXDisplay'] ?? '');
    capabilities.secondaryXAsTable = sxDisplay === 'table' ? true : sxDisplay === 'labels' ? false : undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    // Tile-wide base font (shared Formatting controls; 0/'' = keep defaults).
    capabilities.fontFamily = f['fontFamily'] ? String(f['fontFamily']) : undefined;
    capabilities.fontSize = Number(f['fontSize']) > 0 ? Number(f['fontSize']) : undefined;
    capabilities.axisFontWeight = f['axisFontWeight'] ? String(f['axisFontWeight']) : undefined;
    if (f['lineWidth'] !== undefined && f['lineWidth'] !== '' && f['lineWidth'] !== null) capabilities.lineWidth = Number(f['lineWidth']);
    if (f['symbolSize'] !== undefined && f['symbolSize'] !== '' && f['symbolSize'] !== null) capabilities.symbolSize = Number(f['symbolSize']);
    if (f['areaOpacity'] !== undefined && f['areaOpacity'] !== '' && f['areaOpacity'] !== null) capabilities.areaOpacity = Number(f['areaOpacity']);
    capabilities.smoothLines = f['smoothLines'] === true;
    capabilities.connectNulls = f['connectNulls'] === true;
    capabilities.showTrendLine = f['showTrendLine'] === true;
    capabilities.forecastPeriods = f['forecastPeriods'] !== undefined && f['forecastPeriods'] !== '' && f['forecastPeriods'] !== null ? Number(f['forecastPeriods']) : undefined;
    capabilities.movingAverageWindow = f['movingAverageWindow'] !== undefined && f['movingAverageWindow'] !== '' && f['movingAverageWindow'] !== null ? Number(f['movingAverageWindow']) : undefined;
    capabilities.analyticsColor = (f['analyticsColor'] as string) || undefined;
    capabilities.sorting = this.buildLineAreaSorting(f) as Array<{ field: string; direction?: string }> | undefined;
    capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
    // Explicit category order (e.g. "QSK60, QST30, QSK50") + alphabetical
    // fallback — same "Value order"/"Sort" pair as the Table & Matrix family.
    const categoryOrder = this.parseValueOrder(f['categoryValueOrder'] as string);
    capabilities.categoryValueOrder = categoryOrder.length ? categoryOrder : undefined;
    capabilities.categoryValueOrderSort = (f['categoryValueOrderSort'] as 'custom' | 'alpha-asc' | 'alpha-desc') || undefined;
    // Category highlight boxes ("Highlight" accordion): comma-separated X
    // categories to draw a dotted box around + outline colour / style / padding.
    const highlightCategories = this.parseValueOrder(f['highlightCategories'] as string);
    capabilities.highlightCategories = highlightCategories.length ? highlightCategories : undefined;
    capabilities.highlightColor = (f['highlightColor'] as string) || undefined;
    capabilities.highlightLineStyle = f['highlightLineStyle'] ? String(f['highlightLineStyle']) : undefined;
    capabilities.highlightPadding = f['highlightPadding'] !== undefined && f['highlightPadding'] !== '' && f['highlightPadding'] !== null ? Number(f['highlightPadding']) : undefined;
    capabilities.highlightLabel = (f['highlightLabel'] as string) || undefined;
    capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.valueAxisLog = f['valueAxisLog'] === true;
    capabilities.animation = f['animation'] !== false;
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    return capabilities;
  }

  /** The compute-API `columns` for a LINE_AREA payload: the category column plus
   *  one aggregated column PER measure (the multi-line contract), each carrying
   *  the raw dataset field + the displayName the response rows are keyed by.
   *  A series split / secondary-X column rides along un-aggregated. */
  private buildLineAreaSorting(format?: Record<string, unknown>): Array<Record<string, unknown>> | undefined {
    const f = format || {};
    const action = String(f['dataAction'] ?? '');
    const field = String(f['dataColumn'] ?? '').trim();
    if (action !== 'orderBy' || !field) return undefined;
    return [{ field, direction: 'ASC' }];
  }

  private buildLineAreaColumns(capabilities: LineAreaCapabilities): Array<Record<string, unknown>> {
    const dataset = capabilities.categoryDataset || this.drillDataset;
    const columns: Array<Record<string, unknown>> = [{
      dataset,
      field: this.laCategory,
      displayName: this.laCategoryLabel || this.laCategory,
    }];
    for (const measure of capabilities.measures || []) {
      if (!measure.field) continue;
      columns.push({
        dataset: measure.dataset || (measure.axis === 'y2' ? capabilities.secondaryYDataset : dataset),
        field: measure.field,
        aggregation: 'SUM',
        displayName: measure.label || measure.field,
      });
    }
    const sortingFields = (capabilities.sorting || []).map((entry) => String(entry.field || '').trim()).filter(Boolean);
    const extras = [
      capabilities.series,
      capabilities.secondaryXField,
      capabilities.secondaryXValueField,
      ...sortingFields,
    ].map((field) => (field || '').trim()).filter(Boolean);
    for (const field of extras) {
      const extraDataset = field === capabilities.series
          ? capabilities.seriesDataset
          : field === capabilities.secondaryXField
            ? capabilities.secondaryXDataset
            : field === capabilities.secondaryXValueField
              ? capabilities.secondaryXValueDataset
              : dataset;
      if (!columns.some((column) => column['field'] === field && column['dataset'] === (extraDataset || dataset))) {
        columns.push({ dataset: extraDataset || dataset, field, displayName: field });
      }
    }
    return columns;
  }

  /** Payload capabilities must reference the RESPONSE row keys — i.e. each
   *  column's displayName — not the raw dataset fields (those live in
   *  `columns`). Keeps the raw category as crossFilterKey so cross-filter can
   *  still address other tiles' raw rows when the category was renamed. */
  /** Scatter & Bubble payload `columns`: one {dataset, field, displayName}
   *  entry per mapped dataset field, in mapping order (X, Y, Size, Color, Label,
   *  Series, tooltip extras), de-duplicated. Only fields that are actually set
   *  are listed — Size is dropped for non-bubble variants, Series for
   *  non-multi ones (buildScatterBubbleCapabilities already blanks those). */
  private buildScatterBubbleColumns(capabilities: ScatterBubbleCapabilities): Array<Record<string, unknown>> {
    const dataset = this.drillDataset;
    const seen = new Set<string>();
    const columns: Array<Record<string, unknown>> = [];
    // X / Y / Size are measures with their own per-field aggregation; the
    // rest (color/label/series/tooltip) are plain dimension lookups.
    const measureFields: Array<[string | undefined, string | undefined]> = [
      [capabilities.xField, capabilities.xFieldAggregation],
      [capabilities.yField, capabilities.yFieldAggregation],
      [capabilities.sizeField, capabilities.sizeFieldAggregation],
    ];
    for (const [raw, aggregation] of measureFields) {
      const field = String(raw || '').trim();
      if (!field || seen.has(field)) continue;
      seen.add(field);
      // Omit `aggregation` entirely for 'NONE' (the default) — sending it would
      // switch the backend to an aggregate query and collapse the raw per-row
      // points scatter/bubble needs unless the user explicitly picked one.
      const agg = aggregation && aggregation !== 'NONE' ? aggregation : undefined;
      columns.push({ dataset, field, displayName: field, ...(agg ? { aggregation: agg } : {}) });
    }
    const dimensionFields = [capabilities.colorField, capabilities.labelField, capabilities.series, ...(capabilities.tooltipColumns || [])];
    for (const raw of dimensionFields) {
      const field = String(raw || '').trim();
      if (!field || seen.has(field)) continue;
      seen.add(field);
      columns.push({ dataset, field, displayName: field });
    }
    return columns;
  }

  private remapLineAreaCapabilitiesToDisplayNames(capabilities: LineAreaCapabilities): LineAreaCapabilities {
    const displayCategory = capabilities.categoryLabel || capabilities.category;
    const remapped: LineAreaCapabilities = {
      ...capabilities,
      category: displayCategory,
      measures: (capabilities.measures || []).map((measure) => ({ ...measure, field: measure.label || measure.field })),
      ...(displayCategory !== capabilities.category
        ? { crossFilterKey: capabilities.crossFilterKey || capabilities.category }
        : {}),
    };
    if (remapped.secondaryXValueField) {
      const measure = (capabilities.measures || []).find((m) => m.field === capabilities.secondaryXValueField);
      if (measure) remapped.secondaryXValueField = measure.label || measure.field;
    }
    delete remapped.sorting;
    return remapped;
  }

  /** Build a Line & Area tile, rendering client-side from cached dataset rows. */
  private buildLineAreaTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('LINE_AREA'), ...this.barFormat, title: this.title.trim() || undefined };
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    return { ...base, lineAreaRows: rows, lineAreaData: this.buildLineAreaWidgetData(base.id, base.format) };
  }

  /** `[data]` input for <app-unified-line-area-chart>. */
  private buildLineAreaWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    const capabilities = this.applyLineAreaFormatToCapabilities(this.buildLineAreaCapabilities(), format);
    return {
      widgetId: 'dvm-tile-line-area-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  private parseLineAreaApiPayload(): unknown {
    const text = this.laApiPayload.trim();
    if (!text) return {};
    try { return JSON.parse(text); } catch { return {}; }
  }

  // ── Scatter & Bubble builders (mirror the BAR trio: capabilities → format →
  //    widgetData → tile) so the format popup, publish and restore reuse them. ──

  /** Build a Scatter/Bubble tile, rendering client-side from cached dataset rows. */
  private buildScatterBubbleTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    base.format = { ...this.defaultTileFormat('SCATTER_BUBBLE'), ...this.sbFormat, title: this.title.trim() || undefined };
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    return { ...base, scatterBubbleRows: rows, scatterBubbleData: this.buildScatterBubbleWidgetData(base.id, base.format) };
  }

  /** `generateConfiguration()`'s scatter/bubble tile is built CLIENT-SIDE from
   *  `entityRowsCache` (a raw, unaggregated sample), so once any X/Y/Size field
   *  carries a query aggregation the initial render is wrong until this swaps
   *  in the backend's actually-aggregated rows — mirrors `applyBarComputeResponse`
   *  / `applyLineAreaComputeResponse` for the other client-rendered chart types. */
  private applyScatterBubbleComputeResponse(payload: Record<string, unknown>, response: Record<string, unknown> | null): void {
    // The compute response arrives either bare or inside a `responseList`
    // envelope, with `data` being the row array or a `{ data: [...] }` wrapper —
    // same contract extractLineAreaFlatRows handles for the multi-column charts.
    const rows = extractLineAreaFlatRows(response?.['responseList']) ?? extractLineAreaFlatRows(response);
    if (!rows?.length) return;
    // Matched by componentId, NOT tile id: updating an existing tile makes
    // addTile() keep the PREVIOUS tile's id, so the freshly built tile's id
    // never appears in dashboardTiles. componentId survives that swap.
    const componentId = String(payload['componentId'] ?? '');
    if (!componentId) return;
    const apply = (t: DashboardTile): DashboardTile => {
      if (t.componentId !== componentId || !t.scatterBubbleData) return t;
      // Rows that don't carry the configured X key would blank the chart —
      // keep the client-side render in that case.
      if (!this.rowsFitClientChart(rows, t.scatterBubbleData, 'xField')) return t;
      return { ...t, scatterBubbleRows: rows, scatterBubbleData: { ...t.scatterBubbleData, staticData: rows } };
    };
    this.dashboardTiles = this.dashboardTiles.map((t) => {
      const updated = apply(t);
      if (updated !== t) return updated;
      // The tile may live inside a group rather than at the top level.
      if (!t.groupMembers?.length) return t;
      const members = t.groupMembers.map(apply);
      return members.some((m, i) => m !== t.groupMembers![i]) ? { ...t, groupMembers: members } : t;
    });
  }

  /** Non-flag keys on a config variant/overlay entry. Every OTHER `true` key is
   *  a render-dispatch flag and passes through to `variantFlags` GENERICALLY —
   *  a new flag invented in JSON reaches the option builder with no code edit
   *  here (the renderer ignores flags it has no drawing logic for). */
  private static readonly SB_NON_FLAG_KEYS = new Set(['label', 'value', 'mode', 'group', 'hint', 'icon', 'usesSize', 'usesSeries']);

  /** Assemble the ScatterBubbleCapabilities from the current builder state. */
  private buildScatterBubbleCapabilities(): ScatterBubbleCapabilities {
    // Render-dispatch flags = base variant ∪ advanced overlay — the pure option
    // builder applies each flag independently, so Quadrant + Bubble etc. compose.
    const base = this.sbVariantEntry;
    const adv = this.sbAdvancedEntry;
    const variantFlags: Record<string, boolean> = {};
    [base, adv].forEach((entry) => {
      if (!entry) return;
      Object.entries(entry as Record<string, unknown>).forEach(([key, val]) => {
        if (val === true && !DvmLayoutBuilderComponent.SB_NON_FLAG_KEYS.has(key)) variantFlags[key] = true;
      });
    });
    return {
      variant: this.sbVariant,
      advanced: this.sbAdvanced || undefined,
      xField: this.sbXField,
      yField: this.sbYField,
      xFieldAggregation: this.sbXAggregation || 'NONE',
      yFieldAggregation: this.sbYAggregation || 'NONE',
      // Size only applies when a size-based variant/overlay is active (Bubble).
      sizeField: this.sbVariantUsesSize && this.sbSizeField ? this.sbSizeField : undefined,
      ...(this.sbVariantUsesSize && this.sbSizeField ? { sizeFieldAggregation: this.sbSizeAggregation || 'NONE' } : {}),
      colorField: this.sbColorField || undefined,
      labelField: this.sbLabelField || undefined,
      // Explicit per-category colors travel only with a Color column and only
      // when the user picked any — unpicked values fall through to the palette.
      ...(this.sbColorField && this.hasSbCategoryColors ? { categoryColors: { ...this.sbCategoryColors } } : {}),
      // Series only applies when a series-based variant/overlay is active
      // (Multi Scatter / Cluster).
      series: this.sbVariantUsesSeries && this.sbSeries ? this.sbSeries : undefined,
      aggregation: (this.sbAggregation || 'none') as ScatterBubbleCapabilities['aggregation'],
      tooltipColumns: this.sbTooltipColumns.length ? [...this.sbTooltipColumns] : undefined,
      xLabel: this.sbXLabel || this.sbXField,
      yLabel: this.sbYLabel || this.sbYField,
      // Per-axis label font weights (build form; '' omitted so the tile-wide
      // Format-popup axisFontWeight — or the default — decides that axis).
      ...(this.sbXAxisFontWeight ? { xAxisFontWeight: this.sbXAxisFontWeight } : {}),
      ...(this.sbYAxisFontWeight ? { yAxisFontWeight: this.sbYAxisFontWeight } : {}),
      // Config-driven ECharts styling defaults.
      ...(this.layout.options?.scatterBubbleChartDefaults
        ? { chartDefaults: this.layout.options.scatterBubbleChartDefaults as ScatterBubbleCapabilities['chartDefaults'] }
        : {}),
      // Config-resolved render-dispatch flags so the builder needs no name checks.
      variantFlags,
    } as ScatterBubbleCapabilities;
  }

  /** Fold the tile's Format-popup choices into a scatter-bubble capabilities
   *  object. Shared by initial build + live re-format. Mutates & returns caps. */
  private applyScatterBubbleFormatToCapabilities(
    capabilities: ScatterBubbleCapabilities,
    format?: Record<string, unknown>,
  ): ScatterBubbleCapabilities {
    const f = format || {};
    // Labels (off by default — point labels clutter a dense scatter).
    capabilities.showValues = f['showValues'] === true;
    capabilities.labelPosition = f['labelPosition'] ? String(f['labelPosition']) : undefined;
    if (f['labelFontSize'] !== undefined && f['labelFontSize'] !== '' && f['labelFontSize'] !== null) capabilities.labelFontSize = Number(f['labelFontSize']);
    capabilities.labelColor = (f['labelColor'] as string) || undefined;
    // Formatting.
    capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
    capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : this.toNumberScale(f['numberFormat']);
    capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
    capabilities.labelMode = f['labelMode'] ? String(f['labelMode']) : undefined;
    if (f['pointOpacity'] !== undefined) capabilities.pointOpacity = Number(f['pointOpacity']);
    // Rich point look (glossy 3D fill / ring border / shadow) — absent keys
    // fall through to chartDefaults in the pure builder.
    capabilities.pointStyle = f['pointStyle'] ? String(f['pointStyle']) : undefined;
    capabilities.pointBorderStyle = f['pointBorderStyle'] ? String(f['pointBorderStyle']) : undefined;
    capabilities.pointBorderColor = (f['pointBorderColor'] as string) || undefined;
    capabilities.pointBorderWidth = f['pointBorderWidth'] !== undefined && f['pointBorderWidth'] !== '' && f['pointBorderWidth'] !== null ? Number(f['pointBorderWidth']) : undefined;
    capabilities.pointShadow = typeof f['pointShadow'] === 'boolean' ? (f['pointShadow'] as boolean) : undefined;
    capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
    capabilities.paletteColors = this.resolvePaletteColors(capabilities.palette);
    capabilities.colors = undefined;
    // Legend.
    capabilities.showLegend = f['showLegend'] !== false;
    capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
    // Axis.
    capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
    capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
    capabilities.showGridLines = f['showGridLines'] !== false;
    // Only a categorical X axis (Bubble Matrix) rotates labels; numeric axes ignore it.
    capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
    // Tooltip.
    capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
    capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
    capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
    capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
    capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
    capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
    capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
    if (f['tooltipBorderRadius'] !== undefined) capabilities.tooltipBorderRadius = Number(f['tooltipBorderRadius']);
    if (f['tooltipFontSize'] !== undefined) capabilities.tooltipFontSize = Number(f['tooltipFontSize']);
    // Tile-wide base font (shared Formatting controls; 0/'' = keep defaults).
    capabilities.fontFamily = f['fontFamily'] ? String(f['fontFamily']) : undefined;
    capabilities.fontSize = Number(f['fontSize']) > 0 ? Number(f['fontSize']) : undefined;
    capabilities.axisFontWeight = f['axisFontWeight'] ? String(f['axisFontWeight']) : undefined;
    // Advanced — symbol sizes, axis bounds, animation.
    if (f['symbolSize'] !== undefined && f['symbolSize'] !== '' && f['symbolSize'] !== null) capabilities.symbolSize = Number(f['symbolSize']);
    if (f['symbolSizeMin'] !== undefined && f['symbolSizeMin'] !== '' && f['symbolSizeMin'] !== null) capabilities.symbolSizeMin = Number(f['symbolSizeMin']);
    if (f['symbolSizeMax'] !== undefined && f['symbolSizeMax'] !== '' && f['symbolSizeMax'] !== null) capabilities.symbolSizeMax = Number(f['symbolSizeMax']);
    capabilities.xAxisMin = f['xAxisMin'] !== undefined && f['xAxisMin'] !== '' && f['xAxisMin'] !== null ? Number(f['xAxisMin']) : undefined;
    capabilities.xAxisMax = f['xAxisMax'] !== undefined && f['xAxisMax'] !== '' && f['xAxisMax'] !== null ? Number(f['xAxisMax']) : undefined;
    capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
    capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
    capabilities.animation = f['animation'] !== false;
    // Interaction.
    capabilities.enableZoom = f['enableZoom'] === true;
    capabilities.showZoomSlider = f['showZoomSlider'] !== false;
    capabilities.highlightOnClick = f['highlightOnClick'] === true;
    capabilities.crossFilter = f['crossFilter'] === true;
    capabilities.emitSelection = f['emitSelection'] === true;
    // Overlay tuning groups (Quadrant / Regression / Correlation / Cluster) —
    // inert unless the matching variantFlag is active, so mapping them
    // unconditionally is safe.
    capabilities.showQuadrantLabels = f['showQuadrantLabels'] !== false;
    capabilities.quadrantLabels = [f['quadrantLabelTR'], f['quadrantLabelTL'], f['quadrantLabelBL'], f['quadrantLabelBR']]
      .map((v) => (v ? String(v) : ''));
    capabilities.quadrantLineColor = (f['quadrantLineColor'] as string) || undefined;
    capabilities.regressionType = f['regressionType'] ? String(f['regressionType']) : undefined;
    capabilities.regressionLineColor = (f['regressionLineColor'] as string) || undefined;
    capabilities.showEquation = f['showEquation'] !== false;
    capabilities.showConfidenceBand = f['showConfidenceBand'] !== false;
    if (f['confidenceBandOpacity'] !== undefined && f['confidenceBandOpacity'] !== '' && f['confidenceBandOpacity'] !== null) capabilities.confidenceBandOpacity = Number(f['confidenceBandOpacity']);
    capabilities.correlationMethod = f['correlationMethod'] ? String(f['correlationMethod']) : undefined;
    capabilities.showCoefficient = f['showCoefficient'] !== false;
    capabilities.correlationGradientStart = (f['correlationGradientStart'] as string) || undefined;
    capabilities.correlationGradientEnd = (f['correlationGradientEnd'] as string) || undefined;
    if (f['clusterCount'] !== undefined && f['clusterCount'] !== '' && f['clusterCount'] !== null) capabilities.clusterCount = Number(f['clusterCount']);
    capabilities.showClusterHulls = f['showClusterHulls'] !== false;
    if (f['clusterHullOpacity'] !== undefined && f['clusterHullOpacity'] !== '' && f['clusterHullOpacity'] !== null) capabilities.clusterHullOpacity = Number(f['clusterHullOpacity']);
    return capabilities;
  }

  /** `[data]` input for <app-unified-scatter-bubble-chart>. */
  private buildScatterBubbleWidgetData(tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const rows = this.applyBarComputedColumns(this.entityRowsCache.get(this.drillDataset) || []);
    const capabilities = this.applyScatterBubbleFormatToCapabilities(this.buildScatterBubbleCapabilities(), format);
    return {
      widgetId: 'dvm-tile-sb-' + tileId,
      title: this.title.trim(),
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }

  private distinctDropdownOptions(rows: Array<Record<string, unknown>>, field: string): string[] {
    return this.uniqueDropdownOptions((rows || []).map((row) => row?.[field]));
  }

  private dropdownOptionsFromComputeResponse(response: Record<string, unknown> | null, field: string): string[] {
    const root = response ?? {};
    const result = root['result'] as Record<string, unknown> | undefined;
    const dataRecord = root['data'] as Record<string, unknown> | undefined;
    const optionArrays = [
      root['optionsList'],
      root['options'],
      root['values'],
      result?.['optionsList'],
      result?.['options'],
      dataRecord?.['optionsList'],
      dataRecord?.['options'],
    ];
    const explicit = optionArrays.find(Array.isArray) as unknown[] | undefined;
    if (explicit) return this.uniqueDropdownOptions(explicit);

    const rows =
      this.dropdownRowsFromComputeResponse(root['data']) ??
      this.dropdownRowsFromComputeResponse(result?.['data']) ??
      this.dropdownRowsFromComputeResponse(root['responseList']);
    return rows ? this.distinctDropdownOptions(rows, field) : [];
  }

  private dropdownRowsFromComputeResponse(source: unknown): Array<Record<string, unknown>> | null {
    if (!Array.isArray(source)) return null;
    if (!source.length) return [];
    if (source.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      const rows = source as Array<Record<string, unknown>>;
      const nested = rows.map((item) => item['data']).find(Array.isArray) as Array<Record<string, unknown>> | undefined;
      return nested ?? rows;
    }
    return null;
  }

  private uniqueDropdownOptions(values: unknown[]): string[] {
    return [...new Set(
      values
        .filter((value) => value !== undefined && value !== null && String(value).trim() !== '')
        .map((value) => String(value)),
    )];
  }

  private normalizeDropdownOptionOrderMode(value: unknown): 'response' | 'asc' | 'desc' | 'custom' {
    const mode = String(value ?? 'response').toLowerCase();
    return mode === 'asc' || mode === 'desc' || mode === 'custom' ? mode : 'response';
  }

  private orderDropdownOptions(
    options: string[],
    mode = this.dropdownOptionOrderMode,
    customOrder = this.dropdownCustomOptionOrder,
  ): string[] {
    const unique = this.uniqueDropdownOptions(options);
    if (mode === 'asc' || mode === 'desc') {
      const sorted = [...unique].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      return mode === 'desc' ? sorted.reverse() : sorted;
    }
    if (mode === 'custom') {
      const requested = this.uniqueDropdownOptions(String(customOrder || '').split(',').map((value) => value.trim()));
      if (!requested.length) return unique;
      const byLower = new Map(unique.map((value) => [value.toLowerCase(), value]));
      const ordered = requested
        .map((value) => byLower.get(value.toLowerCase()))
        .filter((value): value is string => !!value);
      const picked = new Set(ordered.map((value) => value.toLowerCase()));
      return [...ordered, ...unique.filter((value) => !picked.has(value.toLowerCase()))];
    }
    return unique;
  }

  private buildDropdownTile(payload: Record<string, unknown>, options: string[]): DashboardTile {
    const base = this.newTileBase(payload);
    const orderedOptions = this.orderDropdownOptions(options);
    const defaultValues = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter((value) => orderedOptions.includes(value))
      : [];
    const value = this.dropdownIsMultiSelect ? defaultValues : defaultValues.slice(0, 1);
    // Default footprint: grid rows are 40px, so rowSpan 1 rendered header-only
    // ("compressed"). 3 fits label + select (also the resize clamp's minimum);
    // button/tile mode gets more room for the wrapping option pills.
    const isButtonMode = this.dropdownDisplayMode === 'buttons' || this.dropdownDisplayMode === 'tiles';
    // Appearance values from the Build-form Format accordion (pill colors /
    // radius / font size) — kept as tile.format for the popup AND baked into
    // widgetData so the widget styles itself (published spreads widgetData).
    const dropdownFormat = this.dropdownFormatFromAccordion();
    const widgetData: Record<string, unknown> = {
      name: this.dropdownDisplayLabel.trim() || this.title.trim() || this.dropdownField,
      key: this.dropdownField,
      isMultiSelect: this.dropdownIsMultiSelect,
      showAllOption: this.dropdownShowAllOption,
      autoSelectFirst: this.dropdownAutoSelectFirst,
      displayMode: this.dropdownDisplayMode || 'dropdown',
      optionGroups: this.sanitizedDropdownGroups(),
      optionOrderMode: this.dropdownOptionOrderMode,
      customOptionOrder: this.dropdownCustomOptionOrder.trim(),
      textCase: 'capital',
      optionsList: orderedOptions,
    };
    this.applyDropdownStyleKeys(dropdownFormat, widgetData);
    return {
      ...base,
      payload: { ...payload, value },
      colSpan: 4,
      rowSpan: isButtonMode ? 5 : 3,
      format: dropdownFormat,
      dropdownControl: {
        field: this.dropdownField,
        options: orderedOptions,
        value,
        targets: [...this.selectedTargetCharts],
        widgetData,
        apiConfig: { ...this.buildApiCallConfig },
      },
    };
  }

  private buildFileUploadTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    const buttonLabel = this.fileUploadButtonLabel.trim() || 'Upload file';
    const accept = this.fileUploadAccept.trim() || '.xlsx,.xls,.csv';
    return {
      ...base,
      // Base footprint for the upload tile. No `format`: File Upload takes no
      // card-header formatting (builder or published), by request.
      colSpan: 4,
      rowSpan: 4,
      fileUploadControl: {
        targets: [...this.selectedTargetCharts],
        buttonLabel,
        accept,
        apiConfig: { ...this.buildApiCallConfig },
        widgetData: {
          title: this.title.trim() || 'File Upload',
          buttonLabel,
          accept,
          // Targets echoed here so the published widget can drive them without
          // digging into the payload.
          targets: this.selectedTargetCharts.map((componentId) => ({
            componentId,
            label: this.targetLabelForId(componentId),
          })),
        },
      },
    };
  }

  private buildPopupTile(payload: Record<string, unknown>): DashboardTile {
    const base = this.newTileBase(payload);
    const mode = this.popupTriggerMode || 'button';
    return {
      ...base,
      // Compact footprints per trigger mode — an icon/button needs almost no
      // room (just the trigger itself); only the tile mode's icon+label+hint
      // stack needs the taller footprint.
      colSpan: mode === 'icon' ? 2 : 3,
      rowSpan: mode === 'tile' ? 4 : 2,
      popupControl: { widgetData: this.buildPopupWidgetData() },
      // Tile CARD header colors — same keys/flow as every other chart tile
      // (builder chrome header + published card header read these off format).
      format: this.popupCardFormat(),
    };
  }

  /** The popup tile's `[data]` — trigger + behaviour + style keys and the
   *  content blocks (table rows snapshotted from the client rows cache). */
  private buildPopupWidgetData(): Record<string, unknown> {
    return {
      title: this.title.trim() || 'Popup',
      triggerMode: this.popupTriggerMode || 'button',
      triggerIcon: this.popupTriggerIcon,
      triggerLabel: this.popupTriggerLabel.trim(),
      triggerHint: this.popupTriggerHint.trim(),
      popupSize: this.popupSize || 'medium',
      popupPosition: this.popupPosition || 'center',
      closeLabel: this.popupCloseLabel.trim(),
      dismissOnBackdrop: this.popupDismissOnBackdrop !== false,
      triggerBg: this.popupTriggerBg,
      triggerText: this.popupTriggerText,
      ...(this.popupTriggerRadius != null ? { triggerRadius: this.popupTriggerRadius } : {}),
      ...(this.popupFontSize && this.popupFontSize > 0 ? { fontSize: this.popupFontSize } : {}),
      fontFamily: this.popupFontFamily,
      headerBg: this.popupHeaderBg,
      headerText: this.popupHeaderText,
      bodyBg: this.popupBodyBg,
      bodyText: this.popupBodyText,
      buttonBg: this.popupButtonBg,
      buttonText: this.popupButtonText,
      ...(this.popupButtonRadius != null ? { buttonRadius: this.popupButtonRadius } : {}),
      ...(this.popupRadius != null ? { popupRadius: this.popupRadius } : {}),
      blocks: this.popupBlocks
        .filter((block) => this.popupBlockHasContent(block))
        .map((block) => this.popupBlockToWidgetBlock(block)),
    };
  }

  /** Form block → render block. Table blocks SNAPSHOT their rows here (picked
   *  columns only, capped at maxRows) so builder preview AND published view
   *  render without any backend call. */
  private popupBlockToWidgetBlock(block: PopupBlockForm): DvmPopupBlock {
    if (block.type === 'table') {
      const allRows = this.entityRowsCache.get(block.dataset) || [];
      const hidden = new Set(((this.layout.options as Record<string, unknown> | undefined)?.['hiddenColumns'] as string[] | undefined) ?? []);
      const columns = block.columns.length
        ? block.columns
        : (this.entityColumnsCache.get(block.dataset) || []).filter((column) => !hidden.has(column));
      const max = block.maxRows && block.maxRows > 0 ? block.maxRows : 50;
      const rows = allRows.slice(0, max).map((row) => {
        const out: Record<string, unknown> = {};
        columns.forEach((column) => { out[column] = row[column]; });
        return out;
      });
      return { type: 'table', dataset: block.dataset, columns, maxRows: max, rows };
    }
    if (block.type === 'keyValue') {
      return { type: 'keyValue', pairs: block.pairs.filter((pair) => pair.label.trim() || pair.value.trim()) };
    }
    if (block.type === 'apiTable') {
      // Config only — rows are FETCHED by the popup component when it opens.
      return {
        type: 'apiTable',
        url: block.url.trim(),
        method: block.method || 'GET',
        body: block.body.trim(),
        rowsPath: block.rowsPath.trim(),
        groupsPath: block.groupsPath.trim(),
        groupRowsField: block.groupRowsField.trim(),
        metaFields: block.metaFields.filter((meta) => meta.field.trim()).map((meta) => ({ field: meta.field.trim(), label: meta.label.trim() })),
        apiColumns: block.apiColumns.filter((column) => column.field.trim()).map((column) => ({ field: column.field.trim(), label: column.label.trim(), color: column.color })),
        ...(block.maxRows != null && block.maxRows > 0 ? { maxRows: block.maxRows } : {}),
        refreshOnOpen: !!block.refreshOnOpen,
      };
    }
    if (block.type === 'buttons') {
      const buttons: DvmPopupButton[] = block.buttons
        .filter((button) => button.label.trim())
        .map((button) => ({
          label: button.label.trim(),
          action: (button.action === 'link' || button.action === 'navigate' ? button.action : 'close'),
          url: button.url.trim(),
          targetDashboard: button.targetDashboard,
          style: (button.style === 'secondary' || button.style === 'link' ? button.style : 'primary'),
        }));
      return { type: 'buttons', buttons, align: block.align };
    }
    if (block.type === 'divider') return { type: 'divider' };
    return { type: block.type, text: block.text, level: block.level, align: block.align };
  }

  /** Rows for `dataset` just landed — re-snapshot every popup tile whose table
   *  block(s) read from it (they may have been generated while the fetch was
   *  still in flight and snapshotted zero rows). */
  private refreshPopupTilesForDataset(dataset: string): void {
    if (!dataset) return;
    let changed = false;
    const refresh = (tile: DashboardTile): DashboardTile => {
      if (tile.layoutType === 'GROUP' && tile.groupMembers) {
        return { ...tile, groupMembers: tile.groupMembers.map(refresh) };
      }
      if (!tile.popupControl) return tile;
      const blocks = (tile.popupControl.widgetData['blocks'] as DvmPopupBlock[] | undefined) ?? [];
      if (!blocks.some((block) => block.type === 'table' && block.dataset === dataset && !(block.rows?.length))) return tile;
      const rowsAll = this.entityRowsCache.get(dataset) || [];
      const nextBlocks = blocks.map((block) => {
        if (block.type !== 'table' || block.dataset !== dataset || block.rows?.length) return block;
        const columns = block.columns?.length ? block.columns : (rowsAll[0] ? Object.keys(rowsAll[0]) : []);
        const max = block.maxRows && block.maxRows > 0 ? block.maxRows : 50;
        const rows = rowsAll.slice(0, max).map((row) => {
          const out: Record<string, unknown> = {};
          columns.forEach((column) => { out[column] = row[column]; });
          return out;
        });
        return { ...block, columns, rows };
      });
      changed = true;
      return { ...tile, popupControl: { widgetData: { ...tile.popupControl.widgetData, blocks: nextBlocks } } };
    };
    const next = this.dashboardTiles.map(refresh);
    if (changed) this.dashboardTiles = next;
  }

  /** Shared skeleton for a new tile: id, title, and a default grid footprint
   *  (half-width, 8 row-units). The grid flows tiles in array order — no
   *  positions to compute and no overlap. */
  private newTileBase(payload: Record<string, unknown>): DashboardTile {
    return {
      id: this.nextTileId(),
      componentId: this.componentId,
      layoutType: this.selectedLayoutType,
      title: this.title.trim() || 'Untitled',
      colSpan: 6,
      rowSpan: 8,
      payload,
    };
  }

  /** Every REAL tile (chart/input), flattening GROUP members out to look like
   *  top-level tiles — target pickers, type/step resolution, and recompute
   *  matching all need to find a grouped chart exactly like a standalone one.
   *  The GROUP wrapper itself is never a valid target, so it's excluded, not
   *  flattened alongside its members. */
  private allTilesFlat(): DashboardTile[] {
    const out: DashboardTile[] = [];
    this.dashboardTiles.forEach((t) => {
      if (t.layoutType === 'GROUP') { if (t.groupMembers) out.push(...t.groupMembers); return; }
      out.push(t);
    });
    return out;
  }

  /** Where a tile id lives if it's nested inside a GROUP's members (not a
   *  top-level dashboardTiles entry). */
  private findGroupMemberLocation(id: string): { group: DashboardTile; index: number } | null {
    for (const t of this.dashboardTiles) {
      if (t.layoutType !== 'GROUP' || !t.groupMembers) continue;
      const index = t.groupMembers.findIndex((m) => m.id === id);
      if (index >= 0) return { group: t, index };
    }
    return null;
  }

  /** Find a tile by id anywhere — top-level OR nested inside a group. Editing,
   *  format, interactions, and recompute all need to reach a grouped chart the
   *  same way they reach a standalone one. */
  private findTileAnywhere(id: string): DashboardTile | undefined {
    const top = this.dashboardTiles.find((t) => t.id === id);
    if (top) return top;
    return this.findGroupMemberLocation(id)?.group.groupMembers?.find((m) => m.id === id);
  }

  /** Replace a tile by id wherever it lives (top-level array, or nested inside
   *  a group's members) and bump `dashboardTiles` to a new reference so the
   *  template re-renders — the single write-back path for anything that edits
   *  a tile in place (format popup, interactions popup, table capabilities). */
  private updateTileInPlace(next: DashboardTile): void {
    const topIndex = this.dashboardTiles.findIndex((t) => t.id === next.id);
    if (topIndex >= 0) {
      const updated = [...this.dashboardTiles];
      updated[topIndex] = next;
      this.dashboardTiles = updated;
      return;
    }
    const loc = this.findGroupMemberLocation(next.id);
    if (!loc) return;
    const members = [...(loc.group.groupMembers || [])];
    members[loc.index] = next;
    loc.group.groupMembers = members;
    this.dashboardTiles = [...this.dashboardTiles];
  }

  private addTile(tile: DashboardTile): boolean {
    // Every generated chart/table tile carries the form's Notes.
    if (DvmLayoutBuilderComponent.NOTES_LAYOUT_TYPES.has(tile.layoutType)) {
      tile = { ...tile, tileNotes: this.formNotes() };
    }
    if (this.editingTileId) {
      const index = this.dashboardTiles.findIndex((item) => item.id === this.editingTileId);
      if (index >= 0) {
        const previous = this.dashboardTiles[index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const next = [...this.dashboardTiles];
        next[index] = replacement;
        this.dashboardTiles = next;
        this.editingTileId = replacement.id;
        // Reapply the tile's existing visual choices to the fresh API response.
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      // Not a top-level tile — check whether it's a GROUP member being edited
      // in place (grouping doesn't take away a chart's own Edit/Format popup;
      // "Update Configuration" here must land back inside its group, not get
      // appended as a brand-new top-level tile).
      const loc = this.findGroupMemberLocation(this.editingTileId);
      if (loc) {
        const previous = loc.group.groupMembers![loc.index];
        const replacement: DashboardTile = {
          ...tile,
          id: previous.id,
          colSpan: previous.colSpan,
          rowSpan: previous.rowSpan,
          x: previous.x,
          y: previous.y,
          customSize: previous.customSize,
          format: previous.format ?? tile.format,
        };
        const members = [...loc.group.groupMembers!];
        members[loc.index] = replacement;
        loc.group.groupMembers = members;
        this.dashboardTiles = [...this.dashboardTiles];
        this.editingTileId = replacement.id;
        if (replacement.format) this.onTileFormatChange(replacement);
        return true;
      }
      this.editingTileId = null;
    }
    this.dashboardTiles = [...this.dashboardTiles, tile];
    return false;
  }

  /** Extract the horizontal-bar rows from the response and wrap them as the
   *  shared <app-horizontal-bar-chart>'s `data` input. */
  private buildHbWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    // The rows live at responseList[0].data[0].horizontalBar (sample envelope)
    // OR data[0].horizontalBar (flat compute response) — try both.
    const rows =
      this.extractHorizontalBarRows(response?.['responseList']) ??
      this.extractHorizontalBarRows(response) ??
      [];
    return {
      staticData: rows,
      categoryKey: 'department',
      valueKey: 'value',
      widgetId: 'dvm-tile-hbar-' + tileId,
      title: this.title.trim(),
      height: '100%',
      // Show full (wrapped) category names, center the bar value labels.
      fullYAxisLabels: true,
      yAxisLabelWidth: 150,
      valueLabelPosition: 'inside',
      numberFormat: this.toKpiNumberFormat(format),
      numberScale: this.toNumberScale(format?.['numberFormat']),
      decimals: Number(format?.['decimals'] ?? 1),
      yAxisName: this.hbGroupByLabel || '',
      xAxisName: this.hbValueLabel || 'Value',
      showXAxis: true,
    };
  }

  // ── Dashboard tile lifecycle + free drag/resize ───────────────────────────

  /** Keeps each tile's chart component instance stable across format-popup
   *  edits — without this, replacing the edited tile's object reference in
   *  `dashboardTiles` (onTileFormatChange) makes NgFor's default identity
   *  tracking tear down and recreate that tile's chart on every keystroke. */
  trackTileById(_index: number, tile: DashboardTile): string {
    return tile.id;
  }

  private nextTileId(): string {
    this.tileIdCounter += 1;
    return `tile-${this.tileIdCounter}`;
  }

  private restoreTileId(value: unknown): string {
    const id = String(value ?? '').trim();
    if (!id) return this.nextTileId();
    this.rememberTileId(id);
    return id;
  }

  private rememberTileId(id: string): void {
    const match = /^tile-(\d+)$/.exec(id);
    if (!match) return;
    this.tileIdCounter = Math.max(this.tileIdCounter, Number(match[1]) || 0);
  }

  private readPublishedLayoutItems(layout: Record<string, unknown> | null): Array<Record<string, unknown>> {
    const savedLayout = this.cloneRecord(layout);
    if (!savedLayout) return [];

    const layoutItems = Array.isArray(savedLayout['items']) ? savedLayout['items'] as Array<Record<string, unknown>> : [];
    const breakpoints = savedLayout['breakpoints'] as Record<string, unknown> | undefined;
    const breakpointItems = Array.isArray(breakpoints?.['xl']) ? breakpoints['xl'] as Array<Record<string, unknown>> : [];
    const rawItems = breakpointItems.length ? breakpointItems : layoutItems;
    if (!rawItems.length) return [];

    const componentIdByLayoutId = new Map<string, string>();
    layoutItems.forEach((item) => {
      const id = String(item['id'] ?? '').trim();
      const componentId = String(item['componentId'] ?? '').trim();
      if (id && componentId) componentIdByLayoutId.set(id, componentId);
    });

    return rawItems.map((item) => {
      const id = String(item['id'] ?? '').trim();
      return { ...item, componentId: item['componentId'] ?? componentIdByLayoutId.get(id) ?? '' };
    });
  }

  private applyPublishedLayoutToTiles(tiles: DashboardTile[], layout: Record<string, unknown> | null): DashboardTile[] {
    const rawItems = this.readPublishedLayoutItems(layout);
    if (!rawItems.length) return tiles;

    const tileById = new Map(tiles.map((tile) => [tile.id, tile]));
    const tileByComponentId = new Map(tiles.map((tile) => [tile.componentId, tile]));
    const seen = new Set<string>();
    const ordered: Array<{ order: number; index: number; tile: DashboardTile }> = [];

    rawItems.forEach((item, index) => {
      const id = String(item['id'] ?? '').trim();
      const componentId = String(item['componentId'] ?? '').trim();
      const tile = (id ? tileById.get(id) : undefined) ?? (componentId ? tileByComponentId.get(componentId) : undefined);
      if (!tile || seen.has(tile.id)) return;
      seen.add(tile.id);

      const colSpan = this.toFiniteNumber(item['colSpan']);
      const rowSpan = this.toFiniteNumber(item['rowSpan']);
      const x = this.toFiniteNumber(item['x']);
      const y = this.toFiniteNumber(item['y']);
      const order = this.toFiniteNumber(item['order']);
      // Same minimums as the interactive corner-resize (2 cols / 3 rows): a
      // saved layout with smaller spans would render tiles too small to show
      // their chart/KPI at all.
      const nextColSpan = colSpan === null ? tile.colSpan : this.clamp(Math.round(colSpan), 2, DvmLayoutBuilderComponent.DASH_COLS);
      ordered.push({
        order: order ?? index,
        index,
        tile: {
          ...tile,
          colSpan: nextColSpan,
          rowSpan: rowSpan === null ? tile.rowSpan : Math.max(3, Math.round(rowSpan)),
          x: x === null ? tile.x : this.clamp(Math.round(x), 0, DvmLayoutBuilderComponent.DASH_COLS - nextColSpan),
          y: y === null ? tile.y : Math.max(0, Math.round(y)),
          customSize: item['customSize'] === true || tile.customSize === true,
        },
      });
    });

    ordered.sort((a, b) => a.order - b.order || a.index - b.index);
    return [
      ...ordered.map((item) => item.tile),
      ...tiles.filter((tile) => !seen.has(tile.id)),
    ];
  }



  /** The cross button on a tile — confirms with the user first (this deletes
   *  the tile's persisted `component_definitions` row, not just its spot on the
   *  canvas), then calls `deleteComponentApi` before removing it locally. */
  async removeTile(tile: DashboardTile): Promise<void> {
    const result = await firstValueFrom(this.confirmationDialog.open({
      title: 'Delete chart',
      message: `Do you want to delete "${tile.title || 'this chart'}" permanently? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    }));
    const confirmed = typeof result === 'boolean' ? result : !!result?.confirmed;
    if (!confirmed) return;

    const deleteUrl = (this.layout.apiEndpoints?.deleteComponentApi || '').trim();
    if (deleteUrl && tile.componentId) {
      try {
        const response = await firstValueFrom(this.datasetService.deleteEntityRow({
          entityName: (this.layout.apiEndpoints?.deleteComponentEntityName || 'component_definitions').trim(),
          keyColumn: (this.layout.apiEndpoints?.deleteComponentKeyColumn || 'component_id').trim(),
          keyValue: tile.componentId,
        }));
        if (response?.status && response.status !== 'SUCCESS') {
          this.ui.showError(response.message || 'The server rejected the delete — the chart was not removed.');
          return;
        }
      } catch (error) {
        this.ui.showError(error instanceof Error ? error.message : 'Unable to delete the chart — please try again.');
        return;
      }
    }

    this.dashboardTiles = this.dashboardTiles.filter((item) => item.id !== tile.id);
    if (this.editingTileId === tile.id) this.editingTileId = null;
    if (deleteUrl && tile.componentId) this.ui.showSuccess('Chart deleted.');
  }

  // ── Grouping: club several existing tiles into one titled card ─────────────
  toggleGroupSelectMode(): void {
    this.groupSelectMode = !this.groupSelectMode;
    if (!this.groupSelectMode) this.selectedTileIds = new Set();
  }

  isTileSelected(tile: DashboardTile): boolean {
    return this.selectedTileIds.has(tile.id);
  }

  toggleTileSelected(tile: DashboardTile): void {
    if (tile.layoutType === 'GROUP') return; // v1: no grouping a group
    const next = new Set(this.selectedTileIds);
    if (next.has(tile.id)) next.delete(tile.id); else next.add(tile.id);
    this.selectedTileIds = next;
  }

  get canGroupSelectedTiles(): boolean {
    return this.selectedTileIds.size >= 2;
  }

  /** Club the selected tiles into one card: they leave the flat dashboard grid
   *  and become `groupMembers`, stacked inside the new GROUP tile that takes
   *  their place (at the first selected tile's position). Each member keeps
   *  its own render data untouched — grouping only changes WHERE it renders,
   *  not what it renders. */
  groupSelectedTiles(): void {
    const ids = this.selectedTileIds;
    if (ids.size < 2) return;
    const selected = this.dashboardTiles.filter((t) => ids.has(t.id));
    if (selected.length < 2) return;

    const innerCols = this.clamp(Math.max(...selected.map((t) => t.colSpan)), 2, DvmLayoutBuilderComponent.DASH_COLS);
    const groupTile: DashboardTile = {
      id: `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      componentId: this.createComponentId('GRP'),
      layoutType: 'GROUP',
      title: 'Grouped card',
      groupTitle: 'Grouped card',
      colSpan: innerCols,
      rowSpan: selected.reduce((sum, t) => sum + t.rowSpan, 0),
      // Seed the card design controls (border / backgrounds / transparency) from
      // their configured defaults — the Format popup only renders for a tile that
      // HAS a format object.
      format: this.defaultTileFormat('GROUP'),
      payload: {},
      groupMembers: selected.map((t) => ({ ...t })),
    };

    let inserted = false;
    const next: DashboardTile[] = [];
    this.dashboardTiles.forEach((t) => {
      if (!ids.has(t.id)) { next.push(t); return; }
      if (!inserted) { next.push(groupTile); inserted = true; }
    });
    this.dashboardTiles = next;
    this.selectedTileIds = new Set();
    this.groupSelectMode = false;
    if (this.editingTileId && ids.has(this.editingTileId)) this.editingTileId = null;
    this.ui.showSuccess(`Grouped ${selected.length} charts into "${groupTile.title}".`);
  }

  /** Reverse of groupSelectedTiles: the group's members go back to being
   *  independent top-level tiles, in its place, in their original order. */
  ungroupTile(tile: DashboardTile): void {
    if (tile.layoutType !== 'GROUP' || !tile.groupMembers?.length) return;
    const index = this.dashboardTiles.findIndex((t) => t.id === tile.id);
    if (index === -1) return;
    this.dashboardTiles = [
      ...this.dashboardTiles.slice(0, index),
      ...tile.groupMembers,
      ...this.dashboardTiles.slice(index + 1),
    ];
  }

  removeGroupMember(group: DashboardTile, member: DashboardTile): void {
    if (!group.groupMembers) return;
    group.groupMembers = group.groupMembers.filter((m) => m.id !== member.id);
  }

  /** Move a chart within its group card. `groupMembers` order IS the layout —
   *  buildGroupCardWidget walks it in sequence to lay the children out, and the
   *  published card renders them in that same order — so swapping entries is
   *  the whole operation. Stacked: top-to-bottom; 2-column: left-to-right then
   *  wrap, hence "earlier / later" rather than "up / down".
   *  The swap keeps the SAME member objects, so *ngFor moves the existing views
   *  instead of rebuilding them and the charts don't re-initialise. */
  moveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    const members = group.groupMembers;
    if (!members?.length) return;
    const from = members.findIndex((m) => m.id === member.id);
    const to = from + direction;
    if (from === -1 || to < 0 || to >= members.length) return;
    const next = [...members];
    [next[from], next[to]] = [next[to], next[from]];
    group.groupMembers = next;
  }

  /** Card design (Format popup, appliesTo:["GROUP"]) as inline styles: border,
   *  background, corner radius, or fully transparent. Returned as a style map so
   *  the same values can be published verbatim and re-applied by the published
   *  view — see DvmPublishedDashboardComponent.groupCardStyles. */
  groupCardStyles(tile: DashboardTile): Record<string, string> {
    if (tile?.layoutType !== 'GROUP') return {};
    return DvmLayoutBuilderComponent.groupCardStyleMap(tile.format);
  }

  /** Shared translation of a group card's `format` into CSS declarations. Static
   *  so the publish path can reuse it without a component instance. */
  static groupCardStyleMap(format: Record<string, unknown> | undefined): Record<string, string> {
    const styles: Record<string, string> = {};
    if (!format) return styles;
    const transparent = format['cardTransparent'] === true || format['cardTransparent'] === 'true';
    const border = String(format['cardBorderColor'] ?? '').trim();
    const width = Number(format['cardBorderWidth']);
    const background = String(format['cardBackground'] ?? '').trim();
    const radius = Number(format['cardCornerRadius']);

    if (transparent) {
      // Transparent means exactly that: no fill, no border, no shadow — the
      // members float on the dashboard background.
      styles['background'] = 'transparent';
      styles['border-color'] = 'transparent';
      styles['box-shadow'] = 'none';
    } else {
      if (background) styles['background'] = background;
      if (border) styles['border-color'] = border;
    }
    if (Number.isFinite(width)) styles['border-width'] = `${Math.max(0, width)}px`;
    if (Number.isFinite(radius)) styles['border-radius'] = `${Math.max(0, radius)}px`;
    return styles;
  }

  canMoveGroupMember(group: DashboardTile, member: DashboardTile, direction: -1 | 1): boolean {
    const members = group.groupMembers;
    if (!members?.length) return false;
    const index = members.findIndex((m) => m.id === member.id);
    if (index === -1) return false;
    const target = index + direction;
    return target >= 0 && target < members.length;
  }

  /** Cycle a group card's inner layout between 1 (stacked) and 2 side-by-side
   *  columns. Members keep whatever they were rendering — this only changes
   *  how many sit per row inside the card. */
  toggleGroupColumns(group: DashboardTile): void {
    group.groupColumns = (group.groupColumns || 1) >= 2 ? 1 : 2;
  }

  /** Load a generated tile's saved request back into the appropriate builder. */
  editTile(tile: DashboardTile, event: MouseEvent): void {
    event.stopPropagation();
    // Defer the form-fill one tick so the many state fields this sets (incl.
    // tmNavEnabled) don't mutate mid change-detection — that raced the checkbox
    // binding and threw NG0100 (ExpressionChangedAfterItHasBeenChecked), which
    // made the restored Navigation toggle read back as unchecked. Matches the
    // Back-to-Builder auto-load path, which already defers via setTimeout(0).
    setTimeout(() => this.loadTileIntoForm(tile), 0);
  }

  /** Load a tile's config into the left build form (autofill). Used by the tile
   *  pencil (editTile) AND on "Back to Builder" so the form is pre-filled with the
   *  restored chart without needing a manual click. */
  loadTileIntoForm(tile: DashboardTile): void {
    // Loading a real tile for editing — discard any in-progress draft preview.
    this.removePreviewTile();
    const payload = tile.payload || {};
    this.editingTileId = tile.id;
    this.selectedLayoutType = tile.layoutType;
    this.componentId = tile.componentId;
    this.relationHops = this.activeRelationHops ? copyRelationHops(payload['bde_relation']) : [];
    this.title = String(payload['title'] ?? tile.title ?? '');
    this.generateError = '';
    this.stepsPanelCollapsed = false;
    this.openFormatTileId = null;
    // Notes accordion (every chart/table type) — restored up front.
    this.restoreNotesFromTile(tile);

    const dataset = String(payload['dataset'] ?? '');
    const datasets = Array.isArray(payload['datasets'])
      ? (payload['datasets'] as unknown[]).map(String)
      : dataset ? [dataset] : [];
    // Older published payloads may not carry the dataset at top level (e.g.
    // margin-estimator dashboards published before `dataset` was emitted) —
    // recover it from layout-specific payload fields before giving up.
    if (!datasets.length) {
      const filters = Array.isArray(payload['filters']) ? (payload['filters'] as Array<Record<string, unknown>>) : [];
      filters.forEach((filter) => {
        const filterDataset = String(filter?.['dataset'] ?? '');
        if (filterDataset && !datasets.includes(filterDataset)) datasets.push(filterDataset);
      });
      const steps = Array.isArray(payload['steps']) ? (payload['steps'] as Array<Record<string, unknown>>) : [];
      steps.forEach((step) => {
        const stepDataset = String(step?.['dataset'] ?? '');
        if (stepDataset && !datasets.includes(stepDataset)) datasets.push(stepDataset);
      });
    }
    // Apply only when the payload actually names dataset(s): a restored tile
    // WITHOUT dataset info must not wipe the current selection — clearing it
    // disabled every dataset-driven picker ("Select a dataset first") and made
    // Back-to-Builder autofill look broken for those tiles.
    if (datasets.length) this.onSelectedDatasetsChange(datasets);

    // Restore this component's interaction targets into the form (and RESET
    // them when the payload has none, so a previous edit's selection can't
    // leak in). The input/dropdown branches below rebuild the same field
    // together with their per-target formulas — same ids, so harmless.
    const payloadTargets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.selectedTargetCharts = payloadTargets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    // The Chart-interactions accordion: show it open when this tile already has
    // targets wired, keep it tucked away otherwise.
    this.interactionsSectionOpen = this.selectedTargetCharts.length > 0;
    // Restore the custom "API Call" config (if this control has one) into the
    // build form so re-editing shows what's already configured.
    // Spread over the DEFAULTS, not on its own: a config saved before `mode` /
    // `targets` existed has neither key, and binding the new controls straight
    // to `undefined` leaves the mode select blank and the target picker
    // unusable. The defaults fill exactly the keys a stored copy predates.
    this.buildApiCallConfig = {
      ...this.defaultApiCallConfig(),
      ...(tile.dropdownControl?.apiConfig
        ?? tile.inputControl?.apiConfig
        ?? tile.fileUploadControl?.apiConfig
        ?? {}),
    };

    if (tile.layoutType === 'WATERFALL') {
      const savedSteps = Array.isArray(payload['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
      this.steps = savedSteps.map((step) => ({
        id: this.nextStepId(),
        stepType: String(step['stepType'] ?? 'CHANGE') as BridgeStepType,
        label: String(step['label'] ?? ''),
        dataset: String(step['dataset'] ?? ''),
        field: String(step['field'] ?? ''),
        aggregation: String(step['aggregation'] ?? ''),
        sign: String(step['sign'] ?? 'AUTO') as BridgeSign,
        formula: String(step['formula'] ?? ''),
        formulaMode: !!step['formula'] || String(step['stepType']) === 'TOTAL',
      }));
      this.steps.forEach((step) => { if (step.dataset) this.loadColumnsForDataset(step.dataset); });
      // Restore the Build-form format accordions (Title/Axis/Legend/…) from the
      // tile's format so they come back filled — mirrors the BAR branch below.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'DRILL_DOWN_TABLE') {
      const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
      const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
      this.drillDownDimensions = dimensions.map((item) => ({
        id: this.nextDrillDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
        ...this.restoreValueOrder(item),
        editable: item['editable'] === true,
        ...this.restoreDropdownEditor(item),
      }));
      this.drillDownMeasures = measures.map((item) => ({
        id: this.nextDrillMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
        aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
        editable: item['editable'] === true,
        ...this.restoreDropdownEditor(item),
        ...this.restoreEditableLevels(item),
        ...(typeof item['groupField'] === 'string' && item['groupField'].trim() ? { groupField: item['groupField'].trim() } : {}),
      }));
    } else if (tile.layoutType === 'TABLE_MATRIX') {
      // Restore the table type + its per-type inputs. `subType` was persisted in
      // the payload by buildPayload; fall back to the render data's capabilities.
      const caps = ((tile.tableMatrixData as Record<string, unknown>)?.['capabilities'] as Record<string, unknown>) || {};
      this.tmType = String(payload['subType'] ?? caps['tableType'] ?? 'normal');
      // A tile saved with the RETIRED 'pivot' type (merged into the Drill-Down
      // Table) is migrated into a client-mode Drill-Down on restore, so its
      // config lands in the supported type instead of a "(legacy)" picker entry.
      const migratedPivot = this.legacyPivotToDrilldown(payload, caps);
      this.tmMigrationNotice = '';
      if (migratedPivot) {
        this.tmType = 'drilldown';
        // this.tmMigrationNotice = 'This table was saved as the retired "Pivot Table" type and has been converted to a Drill-Down Table (row fields → levels, column fields → pivot column dimensions, values → measures). Click Update Configuration to regenerate it.';
      }
      // Restore the Build-form format accordions from the tile's format so they
      // come back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
      // Drill-Down surfaces Title via a 'Title' format accordion (bound to
      // barFormat['title']) — seed it from the restored title so it isn't blank.
      if (this.isTmDrilldown && this.barFormat['title'] == null) this.barFormat['title'] = this.title;
      if (migratedPivot) {
        this.drillDownDimensions = migratedPivot.dimensions;
        this.drillColumnDimensions = migratedPivot.columnDimensions;
        this.drillDownMeasures = migratedPivot.measures;
        this.drillComputedColumns = [];
        this.drillColumnOrder = [];
        this.drillDataMode = 'client';
        this.drillTotalsPlacement = 'both';
      } else if (this.isTmDrilldown) {
        const dimensions = Array.isArray(payload['dimensions']) ? payload['dimensions'] as Array<Record<string, unknown>> : [];
        const measures = Array.isArray(payload['measures']) ? payload['measures'] as Array<Record<string, unknown>> : [];
        const toDimension = (item: Record<string, unknown>): DrillDownDimension => ({
          id: this.nextDrillDimensionId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
          ...this.restoreValueOrder(item),
          editable: item['editable'] === true,
          ...this.restoreDropdownEditor(item),
          dataset: String(item['dataset'] ?? ''),
        });
        this.drillDownDimensions = dimensions.map(toDimension);
        // Year-column helper measures (drillYearHelperMeasures) are payload
        // plumbing, not user measures — they don't come back into the form.
        this.drillDownMeasures = measures.filter((item) => item['yearHelper'] !== true).map((item) => ({
          id: this.nextDrillMeasureId(), field: String(item['field'] ?? ''), label: String(item['label'] ?? ''),
          aggregation: String(item['aggregation'] ?? 'SUM'), rollUpStrategy: String(item['rollUpStrategy'] ?? 'SUM'), total: !!item['total'],
          editable: item['editable'] === true,
          ...this.restoreDropdownEditor(item),
          ...this.restoreEditableLevels(item),
          ...(typeof item['groupField'] === 'string' && item['groupField'].trim() ? { groupField: item['groupField'].trim() } : {}),
          dataset: String(item['dataset'] ?? ''),
          ...(item['pivot'] === false ? { pivot: false as const } : {}),
          ...(item['kind'] === 'text' ? { kind: 'text' as const } : {}),
          ...(item['hidden'] === true ? { hidden: true } : {}),
          // Restore the SAVED values only — the year-column fallback runs at
          // payload time (resolveYearColumn), never gets stamped onto the form.
          ...(item['yearScope'] === 'latest' ? {
            yearScope: 'latest' as const,
            ...(typeof item['yearField'] === 'string' && item['yearField'].trim() ? { yearField: item['yearField'].trim() } : {}),
            ...(item['showYearColumn'] === true ? { showYearColumn: true } : {}),
          } : {}),
          ...this.rowTotalHeaderPayload({ rowTotalHeader: typeof item['rowTotalHeader'] === 'string' ? item['rowTotalHeader'] : undefined }),
        }));
        // Merged drill-down + pivot extras — payload first, saved capabilities
        // as the fallback (published layouts round-trip the capabilities).
        const savedColumnDims = (Array.isArray(payload['columnDimensions']) ? payload['columnDimensions'] : (Array.isArray(caps['columnDimensions']) ? caps['columnDimensions'] : [])) as Array<Record<string, unknown>>;
        this.drillColumnDimensions = savedColumnDims.map(toDimension);
        const savedComputed = (Array.isArray(payload['computedColumns']) ? payload['computedColumns'] : (Array.isArray(caps['computedColumns']) ? caps['computedColumns'] : [])) as Array<Record<string, unknown>>;
        this.drillComputedColumns = savedComputed
          .map((c) => ({ name: String(c['name'] ?? ''), expression: String(c['expression'] ?? ''), ...(c['after'] ? { after: String(c['after']) } : {}) }))
          .filter((c) => c.name && c.expression);
        const savedColumnOrder = (Array.isArray(payload['columnOrder']) ? payload['columnOrder'] : (Array.isArray(caps['columnOrder']) ? caps['columnOrder'] : [])) as unknown[];
        this.drillColumnOrder = savedColumnOrder.map((k) => String(k ?? '').trim()).filter(Boolean);
        // Tiles saved before the merge carry no data mode → keep the backend
        // tree they were built with.
        this.drillDataMode = (payload['dataMode'] ?? caps['drillDataMode']) === 'client' ? 'client' : 'server';
        // Totals placement (absent on tiles saved before the picker → both).
        this.drillTotalsPlacement = normalizeDrillTotalsPlacement(payload['totalsPlacement'] ?? caps['totalsPlacement']);
        // Warm every non-primary dataset a level / measure reads from so its
        // picker shows the selection instead of an empty dropdown.
        new Set([...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures].map((row) => row.dataset).filter((ds): ds is string => !!ds))
          .forEach((ds) => this.loadColumnsForDataset(ds));
      } else {
        this.restoreTmColumns(payload['columns'], payload['initialFilters'] ?? caps['initialFilters']);
        this.restoreTmToggleView(payload['toggleView'] ?? caps['toggleView']);
        this.tmGroupBy = String(payload['groupBy'] ?? caps['groupBy'] ?? '');
        this.tmAggregation = String(payload['aggregation'] ?? caps['aggregation'] ?? 'none');
        // New shape types restore from the saved capabilities (authoritative for
        // per-type config, which the flat compute payload doesn't carry).
        this.tmRowField = String(caps['rowField'] ?? '');
        this.tmColumnField = String(caps['columnField'] ?? '');
        this.tmValueField = String(caps['valueField'] ?? '');
        // Pivot multi-measure: prefer the saved list; older single-measure
        // tiles seed the list from valueField so the form comes back filled.
        this.tmValueFields = Array.isArray(caps['valueFields']) && (caps['valueFields'] as unknown[]).length
          ? (caps['valueFields'] as unknown[]).map(String)
          : (this.tmValueField ? [this.tmValueField] : []);
        this.tmRowFields = Array.isArray(caps['rowFields']) ? (caps['rowFields'] as unknown[]).map(String) : [];
        this.tmColumnFields2 = Array.isArray(caps['columnFields']) ? (caps['columnFields'] as unknown[]).map(String) : [];
        this.tmPivotColumnOrderText = this.valueOrderToText(caps['pivotColumnValueOrder']);
        this.tmPivotColumnOrderSort = (caps['pivotColumnValueOrderSort'] as typeof this.tmPivotColumnOrderSort) || '';
        this.tmGroupByFields = Array.isArray(caps['groupByFields']) ? (caps['groupByFields'] as unknown[]).map(String) : [];
        this.tmGroupsExpanded = caps['groupsExpanded'] !== false;
        this.tmTreeIdField = String(caps['treeIdField'] ?? '');
        this.tmTreeParentField = String(caps['treeParentField'] ?? '');
        this.tmRoleField = String(caps['roleField'] ?? '');
        this.tmIndentField = String(caps['indentField'] ?? '');
        // Restore navigation from the payload, falling back to the tile's saved
        // capabilities (published layouts spread capabilities into the widget but
        // may not round-trip the builder payload's `navigation`, so read both).
        const nav = ((payload['navigation'] as Record<string, unknown>)
          || (caps['navigation'] as Record<string, unknown>)
          || {}) as Record<string, unknown>;
        this.tmNavEnabled = nav['enabled'] === true;
        this.tmNavFilterKey = String(nav['filterKey'] ?? '');
        this.tmNavColumns = Array.isArray(nav['navigableColumns']) ? (nav['navigableColumns'] as unknown[]).map(String) : [];
        this.tmNavTargetDashboard = String(nav['targetDashboard'] ?? '');
        this.tmNavTargetComponents = Array.isArray(nav['targetComponents']) ? (nav['targetComponents'] as unknown[]).map(String) : [];
        this.tmNavButtonLabel = String(nav['buttonLabel'] ?? 'Navigate');
        this.tmNavAccordionOpen = this.tmNavEnabled;
        // If the saved target dashboard isn't in the current dropdown list yet
        // (the API list loads async), ensure it appears so the ng-select can
        // render it as selected instead of showing blank — otherwise a
        // re-Generate would silently drop it.
        if (this.tmNavTargetDashboard) this.ensureNavTargetInList(this.tmNavTargetDashboard);
      }
    } else if (tile.layoutType === 'HORIZONTAL_BAR_CHART') {
      const groupBy = (payload['groupBy'] as Record<string, unknown>) || {};
      const value = (payload['value'] as Record<string, unknown>) || {};
      this.hbGroupByField = String(groupBy['field'] ?? '');
      this.hbGroupByLabel = String(groupBy['label'] ?? '');
      this.hbValueLabel = String(value['label'] ?? 'Value');
      this.hbValueFormula = String(value['formula'] ?? '');
      const groupFormulas = (value['groupFormulas'] as Record<string, unknown>) || {};
      this.hbGroupFormulas = Object.keys(groupFormulas).map((group) => ({ group, formula: String(groupFormulas[group] ?? '') }));
      this.rehydrateOwnFormulaReferences(this.componentId, value['references']);
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'KPI') {
      this.kpiField = String(payload['field'] ?? '');
      this.kpiAggregation = String(payload['aggregation'] ?? 'SUM');
      // Restore the formula + its typed [Token] references (kaveri-lite-dev
      // parity — a bad merge had moved these into the SCATTER_BUBBLE branch,
      // so editing a KPI blanked its formula and Update then SAVED it blank).
      this.kpiFormula = String(payload['formula'] ?? '');
      this.rehydrateOwnFormulaReferences(this.componentId, payload['references']);
      // Restore the Build-form Format accordion from the tile's format so it
      // comes back filled — mirrors the BAR / WATERFALL branches.
      this.barFormat = { ...(tile.format || {}) };
    } else if (tile.layoutType === 'BAR') {
      // Rehydrate the whole Bar build form from the tile's capabilities. Prefer
      // the payload (builder-saved), fall back to barData.capabilities (which is
      // the copy that survives publish → so "Back to Builder" re-fills too).
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.barData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      this.barOrientation = (String(caps['orientation'] ?? 'vertical') as BarColumnOrientation);
      this.barVariant = (String(caps['variant'] ?? 'simple') as BarColumnVariant);
      this.barCategory = String(caps['category'] ?? '');
      this.barCategoryParent = String(caps['categoryParent'] ?? '');
      this.barCategoryLabel = String(caps['categoryLabel'] ?? '');
      // Per-axis weights only ever come from the build form, so no format-wins guard.
      this.barXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
      this.barYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
      this.barSecondaryAxisFontWeight = String(caps['axis2FontWeight'] ?? '');
      this.barSeries = String(caps['series'] ?? '');
      this.barSeriesOrder = this.valueOrderToText(caps['seriesOrder']);
      const barValue = (payload['value'] as Record<string, unknown> | undefined) ?? {};
      this.barValueLabel = String(barValue['label'] ?? '');
      const savedBarFormula = String(barValue['formula'] ?? '');
      // A bare SUM(col) is our auto-generated fallback, not user input — don't
      // restore it, or it outranks the measure on the next save.
      this.barValueFormula = /^SUM\([^()]*\)$/i.test(savedBarFormula.trim()) ? '' : savedBarFormula;
      const barSavedGroupFormulas = (barValue['groupFormulas'] as Record<string, unknown>) || {};
      this.barGroupFormulas = Object.keys(barSavedGroupFormulas)
        .map((group) => ({ group, formula: String(barSavedGroupFormulas[group] ?? '') }));
      this.rehydrateOwnFormulaReferences(this.componentId, barValue['references']);
      const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
      this.barMeasures = savedMeasures.length
        ? savedMeasures.map((m) => ({
            id: this.nextBarMeasureId(),
            field: String(m['field'] ?? ''),
            label: String(m['label'] ?? ''),
            renderAs: (String(m['renderAs'] ?? 'bar') as 'bar' | 'line' | 'area'),
            axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
          }))
        : [];
      // Split a dual-axis measure back onto the Secondary picker so it round-trips.
      this.barSecondaryXField = '';
      this.barSecondaryYField = '';
      const secondaryMeasure = this.barMeasures.find((m) => m.axis === 'y2');
      if (secondaryMeasure) {
        if (this.barOrientation === 'horizontal') this.barSecondaryXField = secondaryMeasure.field;
        else this.barSecondaryYField = secondaryMeasure.field;
      }
      const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
      this.barReferenceLines = savedRefLines.map((line) => {
        this.barRefLineIdCounter += 1;
        return {
          id: `bar-refline-${this.barRefLineIdCounter}`,
          value: line['value'] == null ? null : Number(line['value']),
          label: String(line['label'] ?? ''),
          color: String(line['color'] ?? '#64748b'),
        };
      });
      // Restore the Formula-Builder computed columns.
      const savedComputed = Array.isArray(caps['computedColumns']) ? caps['computedColumns'] as Array<Record<string, unknown>> : [];
      this.barComputedColumns = savedComputed
        .map((c) => ({ name: String(c['name'] ?? ''), expression: String(c['expression'] ?? '') }))
        .filter((c) => c.name && c.expression);
      // Restore the Format-form values (Formatting/Tooltip/Interaction/Advanced)
      // from the tile's format so the accordions come back filled.
      this.barFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'LINE_AREA') {
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.lineAreaData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      // The multi-line payload keys its capabilities by each column's
      // displayName (the response row keys) — the RAW dataset fields live in
      // payload.columns. Map them back so the form's pickers show real columns.
      const savedColumns = Array.isArray(payload['columns']) ? payload['columns'] as Array<Record<string, unknown>> : [];
      const rawFieldByDisplayName = new Map(savedColumns
        .map((column) => [String(column['displayName'] ?? column['field'] ?? ''), String(column['field'] ?? '')] as const)
        .filter(([displayName, field]) => displayName && field));
      const toRawField = (name: string) => rawFieldByDisplayName.get(name) || name;
      this.laVariant = (String(caps['variant'] ?? 'line') as LineAreaVariant);
      this.laCategory = toRawField(String(caps['category'] ?? ''));
      this.laCategoryDataset = String(caps['categoryDataset'] ?? '');
      this.laCategoryParent = String(caps['categoryParent'] ?? '');
      this.laCategoryLabel = String(caps['categoryLabel'] ?? '');
      // Rehydrate the Chart-Data axis-title inputs — but only when the value
      // did NOT come from the Format accordion (format wins on re-generate, so
      // mirroring it here would duplicate it into the form field).
      const laSavedFormat = (tile.format || {}) as Record<string, unknown>;
      this.laYAxisLabel = laSavedFormat['yAxisLabel'] ? '' : String(caps['yAxisLabel'] ?? '');
      this.laSecondaryYLabel = laSavedFormat['yAxisLabel2'] ? '' : String(caps['yAxisLabel2'] ?? '');
      this.laShowSecondaryYAxis = caps['showSecondaryYAxis'] !== false;
      this.laSecondaryXLabel = laSavedFormat['xAxisLabel2'] ? '' : String(caps['xAxisLabel2'] ?? '');
      // Per-axis weights only ever come from the build form, so no format-wins guard.
      this.laXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
      this.laYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
      this.laSecondaryXFontWeight = String(caps['xAxis2FontWeight'] ?? '');
      this.laSecondaryYFontWeight = String(caps['yAxis2FontWeight'] ?? '');
      this.laSeries = String(caps['series'] ?? '');
      this.laSeriesOrder = this.valueOrderToText(caps['seriesOrder']);
      this.laSeriesDataset = String(caps['seriesDataset'] ?? '');
      const savedApi = (caps['apiOverride'] ?? payload['apiOverride']) as Record<string, unknown> | undefined;
      this.laApiUrl = String(savedApi?.['url'] ?? '');
      this.laApiPayload = savedApi?.['payload'] === undefined ? '' : JSON.stringify(savedApi['payload'], null, 2);
      this.laApiResponsePath = String(savedApi?.['responsePath'] ?? '');
      const savedMeasures = Array.isArray(caps['measures']) ? caps['measures'] as Array<Record<string, unknown>> : [];
      this.laMeasures = savedMeasures.length
        ? savedMeasures.map((m) => ({
            id: this.nextLaMeasureId(),
            field: toRawField(String(m['field'] ?? '')),
            dataset: String(m['dataset'] ?? ''),
            label: String(m['label'] ?? ''),
            renderAs: (String(m['renderAs'] ?? 'line') as 'line' | 'area'),
            axis: (String(m['axis'] ?? 'y1') as 'y1' | 'y2'),
            color: String(m['color'] ?? ''),
            symbol: String(m['symbol'] ?? 'circle'),
            symbolSize: m['symbolSize'] === undefined || m['symbolSize'] === '' ? 6 : Number(m['symbolSize']),
          }))
        : [];
      // Split a dual-axis measure back onto the Secondary-Y picker so it round-trips.
      this.laSecondaryYField = '';
      const secondaryMeasure = this.laMeasures.find((m) => m.axis === 'y2');
      if (secondaryMeasure) this.laSecondaryYField = secondaryMeasure.field;
      this.laSecondaryYDataset = String(caps['secondaryYDataset'] ?? secondaryMeasure?.dataset ?? '');
      this.laSecondaryXField = String(caps['secondaryXField'] ?? '');
      this.laSecondaryXDataset = String(caps['secondaryXDataset'] ?? '');
      this.laSecondaryXValueField = toRawField(String(caps['secondaryXValueField'] ?? ''));
      this.laSecondaryXValueDataset = String(caps['secondaryXValueDataset'] ?? '');
      this.laSecondaryXPosition = caps['secondaryXPosition'] === 'top' ? 'top' : 'bottom';
      const savedRefLines = Array.isArray(caps['referenceLines']) ? caps['referenceLines'] as Array<Record<string, unknown>> : [];
      this.laReferenceLines = savedRefLines.map((line) => {
        this.laRefLineIdCounter += 1;
        return {
          id: `la-refline-${this.laRefLineIdCounter}`,
          value: line['value'] == null ? null : Number(line['value']),
          label: String(line['label'] ?? ''),
          color: String(line['color'] ?? '#64748b'),
        };
      });
      this.barFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'SCATTER_BUBBLE') {
      // Rehydrate the Scatter/Bubble build form from the tile's capabilities.
      const caps = (payload['capabilities'] as Record<string, unknown> | undefined)
        ?? ((tile.scatterBubbleData?.['capabilities'] as Record<string, unknown> | undefined))
        ?? {};
      const savedVariant = String(caps['variant'] ?? 'scatter');
      const isBaseVariant = this.scatterBubbleVariants.some(
        (v) => v.value === savedVariant && !this.sbVariantIsAdvanced(v),
      );
      if (isBaseVariant) {
        this.sbVariant = savedVariant as ScatterBubbleVariant;
        this.sbAdvanced = String(caps['advanced'] ?? '');
      } else {
        // Legacy tile: an advanced pick was saved AS the variant — split it into
        // base 'scatter' + overlay so the form (and re-save) use the new model.
        this.sbVariant = 'scatter';
        this.sbAdvanced = savedVariant;
      }
      this.sbMode = this.sbModeForVariant(this.sbVariant);
      // Surface a saved overlay: reopen the Advanced accordion; else collapsed.
      this.sbAdvancedAccordionOpen = !!this.sbAdvanced;
      this.sbXField = String(caps['xField'] ?? '');
      this.sbYField = String(caps['yField'] ?? '');
      this.sbSizeField = String(caps['sizeField'] ?? '');
      this.sbXAggregation = String(caps['xFieldAggregation'] ?? 'NONE');
      this.sbYAggregation = String(caps['yFieldAggregation'] ?? 'NONE');
      this.sbSizeAggregation = String(caps['sizeFieldAggregation'] ?? 'NONE');
      this.sbColorField = String(caps['colorField'] ?? '');
      this.sbLabelField = String(caps['labelField'] ?? '');
      const savedCategoryColors = caps['categoryColors'];
      this.sbCategoryColors = savedCategoryColors && typeof savedCategoryColors === 'object'
        ? Object.fromEntries(
            Object.entries(savedCategoryColors as Record<string, unknown>)
              .filter(([, v]) => typeof v === 'string' && !!v)
              .map(([k, v]) => [k, String(v)]),
          )
        : {};
      this.sbSeries = String(caps['series'] ?? '');
      this.sbAggregation = String(caps['aggregation'] ?? 'none');
      this.sbTooltipColumns = Array.isArray(caps['tooltipColumns']) ? (caps['tooltipColumns'] as string[]).map(String) : [];
      this.sbXLabel = String(caps['xLabel'] ?? '');
      this.sbYLabel = String(caps['yLabel'] ?? '');
      // Per-axis weights only ever come from the build form, so no format-wins guard.
      this.sbXAxisFontWeight = String(caps['xAxisFontWeight'] ?? '');
      this.sbYAxisFontWeight = String(caps['yAxisFontWeight'] ?? '');
      this.sbFormat = { ...(tile.format || {}) };
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'DROPDOWN') {
      this.dropdownField = String(payload['field'] ?? '');
      this.dropdownDisplayLabel = String(payload['displayLabel'] ?? '');
      this.dropdownIsMultiSelect = !!payload['isMultiSelect'];
      this.dropdownShowAllOption = payload['showAllOption'] === true;
      this.dropdownAutoSelectFirst = (payload['autoSelectFirst'] ?? tile.dropdownControl?.widgetData?.['autoSelectFirst']) === true;
      this.dropdownDisplayMode = String(payload['displayMode'] ?? 'dropdown');
      const savedGroups = Array.isArray(payload['optionGroups'])
        ? payload['optionGroups']
        : tile.dropdownControl?.widgetData?.['optionGroups'];
      this.dropdownGroups = Array.isArray(savedGroups)
        ? (savedGroups as Array<Record<string, unknown>>).map((group) => ({
            label: String(group?.['label'] ?? ''),
            displayLabel: String(group?.['displayLabel'] ?? ''),
            children: Array.isArray(group?.['children']) ? (group['children'] as unknown[]).map(String) : [],
            showAll: group?.['showAll'] !== false,
          }))
        : [];
      this.dropdownOptionOrderMode = this.normalizeDropdownOptionOrderMode(payload['optionOrderMode'] ?? tile.dropdownControl?.widgetData?.['optionOrderMode']);
      this.dropdownCustomOptionOrder = String(payload['customOptionOrder'] ?? tile.dropdownControl?.widgetData?.['customOptionOrder'] ?? '');
      this.dropdownAdvancedAccordionOpen = this.dropdownOptionOrderMode !== 'response';
      const rawDefault = payload['defaultValue'];
      this.dropdownDefaultValue = Array.isArray(rawDefault) ? rawDefault.map(String).join(', ') : String(rawDefault ?? '');
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
      this.rehydrateOwnFormulaReferences(this.componentId, payload['references']);
      // Seed ONLY the dropdown-applicable keys of the SHARED barFormat from the
      // saved tile format so the Formatting accordion shows the tile's values
      // (wholesale copy would clobber other layouts' working values). Payload
      // `capabilities` is the fallback for tiles saved without tile.format.
      const savedTileFormat = {
        ...((payload['capabilities'] as Record<string, unknown>) || {}),
        ...((tile.format || {}) as Record<string, unknown>),
      };
      this.applicableFormatControls('DROPDOWN').forEach((control) => {
        if (savedTileFormat[control.key] !== undefined) this.barFormat[control.key] = savedTileFormat[control.key];
      });
      // Reopened tile: fetch the dataset's columns + rows like the other
      // dataset-driven layouts do — the Option field picker needs the columns
      // and the Submenu-group pickers list distinct values from the ROWS
      // cache (dropdownFieldOptions), which nothing else fills on reopen.
      if (this.drillDataset) this.loadColumnsForDataset(this.drillDataset);
    } else if (tile.layoutType === 'FILE_UPLOAD') {
      this.fileUploadButtonLabel = String(payload['buttonLabel'] ?? 'Upload file');
      this.fileUploadAccept = String(payload['accept'] ?? '.xlsx,.xls,.csv');
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    } else if (tile.layoutType === 'POPUP') {
      // Prefer the payload's form-shaped config; fall back to the widgetData
      // (a tile restored from a published layout may predate the payload keys).
      const widgetData = (tile.popupControl?.widgetData as Record<string, unknown>) || {};
      const trigger = (payload['trigger'] as Record<string, unknown>) || {};
      const behaviour = (payload['behaviour'] as Record<string, unknown>) || {};
      // Formatting: flat `format` (the shape other charts send) — the nested
      // `style` key is the legacy fallback for tiles saved before the rename.
      const style = {
        ...((payload['style'] as Record<string, unknown>) || {}),
        ...((payload['format'] as Record<string, unknown>) || {}),
      };
      this.popupTriggerMode = String(trigger['mode'] ?? widgetData['triggerMode'] ?? 'button');
      this.popupTriggerIcon = String(trigger['icon'] ?? widgetData['triggerIcon'] ?? 'info');
      this.popupTriggerLabel = String(trigger['label'] ?? widgetData['triggerLabel'] ?? '');
      this.popupTriggerHint = String(trigger['hint'] ?? widgetData['triggerHint'] ?? '');
      this.popupSize = String(behaviour['size'] ?? widgetData['popupSize'] ?? 'medium');
      this.popupPosition = String(behaviour['position'] ?? widgetData['popupPosition'] ?? 'center');
      this.popupCloseLabel = String(behaviour['closeLabel'] ?? widgetData['closeLabel'] ?? 'Close');
      this.popupDismissOnBackdrop = (behaviour['dismissOnBackdrop'] ?? widgetData['dismissOnBackdrop']) !== false;
      this.popupTriggerBg = String(style['triggerBg'] ?? widgetData['triggerBg'] ?? '');
      this.popupTriggerText = String(style['triggerText'] ?? widgetData['triggerText'] ?? '');
      const radius = Number(style['triggerRadius'] ?? widgetData['triggerRadius']);
      this.popupTriggerRadius = Number.isFinite(radius) ? radius : null;
      const fontSize = Number(style['fontSize'] ?? widgetData['fontSize']);
      this.popupFontSize = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : null;
      this.popupFontFamily = String(style['fontFamily'] ?? widgetData['fontFamily'] ?? '');
      this.popupHeaderBg = String(style['headerBg'] ?? widgetData['headerBg'] ?? '');
      this.popupHeaderText = String(style['headerText'] ?? widgetData['headerText'] ?? '');
      const tileFormat = (tile.format || {}) as Record<string, unknown>;
      this.popupCardHeaderBg = String(style['cardHeaderBackground'] ?? style['cardHeaderBg'] ?? tileFormat['cardHeaderBackground'] ?? '');
      this.popupCardHeaderText = String(style['cardHeaderColor'] ?? style['cardHeaderText'] ?? tileFormat['cardHeaderColor'] ?? '');
      this.popupBodyBg = String(style['bodyBg'] ?? widgetData['bodyBg'] ?? '');
      this.popupBodyText = String(style['bodyText'] ?? widgetData['bodyText'] ?? '');
      this.popupButtonBg = String(style['buttonBg'] ?? widgetData['buttonBg'] ?? '');
      this.popupButtonText = String(style['buttonText'] ?? widgetData['buttonText'] ?? '');
      const buttonRadius = Number(style['buttonRadius'] ?? widgetData['buttonRadius']);
      this.popupButtonRadius = Number.isFinite(buttonRadius) ? buttonRadius : null;
      const popupRadius = Number(style['popupRadius'] ?? widgetData['popupRadius']);
      this.popupRadius = Number.isFinite(popupRadius) ? popupRadius : null;
      this.popupFormatAccordionOpen = !!(this.popupTriggerBg || this.popupTriggerText || this.popupTriggerRadius != null || this.popupFontSize || this.popupFontFamily || this.popupHeaderBg || this.popupHeaderText || this.popupCardHeaderBg || this.popupCardHeaderText
        || this.popupBodyBg || this.popupBodyText || this.popupButtonBg || this.popupButtonText || this.popupButtonRadius != null || this.popupRadius != null);
      const savedBlocks = Array.isArray(payload['blocks'])
        ? (payload['blocks'] as Array<Record<string, unknown>>)
        : Array.isArray(widgetData['blocks']) ? (widgetData['blocks'] as Array<Record<string, unknown>>) : [];
      this.popupBlocks = savedBlocks.map((block) => {
        const fresh = this.newPopupBlock((String(block['type'] ?? 'text') as PopupBlockForm['type']));
        fresh.text = String(block['text'] ?? '');
        fresh.level = String(block['level'] ?? 'h2');
        fresh.align = String(block['align'] ?? 'left');
        fresh.dataset = String(block['dataset'] ?? '');
        fresh.columns = Array.isArray(block['columns']) ? (block['columns'] as unknown[]).map(String) : [];
        const max = Number(block['maxRows']);
        fresh.maxRows = Number.isFinite(max) && max > 0 ? max : null;
        // apiTable config.
        fresh.url = String(block['url'] ?? '');
        fresh.method = String(block['method'] ?? 'GET');
        fresh.body = String(block['body'] ?? '');
        fresh.rowsPath = String(block['rowsPath'] ?? '');
        fresh.groupsPath = String(block['groupsPath'] ?? '');
        fresh.groupRowsField = String(block['groupRowsField'] ?? '');
        fresh.refreshOnOpen = block['refreshOnOpen'] === true;
        const metaFields = Array.isArray(block['metaFields']) ? (block['metaFields'] as Array<Record<string, unknown>>) : [];
        if (metaFields.length) fresh.metaFields = metaFields.map((meta) => ({ field: String(meta['field'] ?? ''), label: String(meta['label'] ?? '') }));
        const apiColumns = Array.isArray(block['apiColumns']) ? (block['apiColumns'] as Array<Record<string, unknown>>) : [];
        if (apiColumns.length) fresh.apiColumns = apiColumns.map((column) => ({ field: String(column['field'] ?? ''), label: String(column['label'] ?? ''), color: String(column['color'] ?? '') }));
        const pairs = Array.isArray(block['pairs']) ? (block['pairs'] as Array<Record<string, unknown>>) : [];
        if (pairs.length) fresh.pairs = pairs.map((pair) => ({ label: String(pair['label'] ?? ''), value: String(pair['value'] ?? '') }));
        const buttons = Array.isArray(block['buttons']) ? (block['buttons'] as Array<Record<string, unknown>>) : [];
        if (buttons.length) {
          fresh.buttons = buttons.map((button) => ({
            label: String(button['label'] ?? ''),
            action: String(button['action'] ?? 'close'),
            url: String(button['url'] ?? ''),
            targetDashboard: String(button['targetDashboard'] ?? ''),
            style: String(button['style'] ?? 'primary'),
          }));
        }
        return fresh;
      });
      // Table blocks need their dataset's columns (picker) + rows (snapshot).
      this.popupBlocks.forEach((block) => { if (block.dataset) this.loadColumnsForDataset(block.dataset); });
    } else if (tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'SLIDER') {
      this.inputDefaultValue = payload['defaultValue'] == null ? null : Number(payload['defaultValue']);
      const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
      this.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
      // Rebuild per-target formulas from each target's `formulas` array (one
      // entry per bar for a waterfall target, one entry overall otherwise).
      // Falls back to the legacy single top-level `formula` string (older
      // dashboards saved before the `formulas` array existed).
      const legacyFormula = String(payload['formula'] ?? '');
      this.targetFormulas = {};
      this.targetStepFormulas = {};
      this.targetFormulaReferences = {};
      targets.forEach((target) => {
        const id = String(target['componentId'] ?? '');
        if (!id) return;
        const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
        if (entries) {
          const withStep = entries.filter((entry) => !!entry['step']);
          if (withStep.length) {
            const perStep: Record<string, string> = {};
            withStep.forEach((entry) => { perStep[String(entry['step'])] = String(entry['formula'] ?? ''); });
            this.targetStepFormulas[id] = perStep;
          } else {
            this.targetFormulas[id] = String(entries[0]?.['formula'] ?? '');
          }
          // Rehydrate every entry's `references` map back into typed-in rows —
          // one row per token, scoped back to whichever bar/step it came from
          // (undefined for a non-waterfall target's single formula entry).
          const referenceRows: TargetFormulaReferenceEntry[] = [];
          entries.forEach((entry) => {
            const references = entry['references'] as Record<string, Record<string, unknown>> | undefined;
            if (!references || typeof references !== 'object') return;
            const entryStep = entry['step'] ? String(entry['step']) : undefined;
            Object.keys(references).forEach((token) => {
              const ref = references[token] || {};
              referenceRows.push({
                token,
                inputTitle: String(ref['inputTitle'] ?? ''),
                targetLabel: String(ref['targetLabel'] ?? ''),
                rowLabel: String(ref['rowLabel'] ?? ''),
                step: entryStep,
              });
            });
          });
          if (referenceRows.length) this.targetFormulaReferences[id] = referenceRows;
        } else {
          // Legacy shapes: a per-target `formula` string, a `stepFormulas` map,
          // or (oldest) just the input's single top-level `formula`.
          const legacyStepFormulas = target['stepFormulas'] as Record<string, string> | undefined;
          if (legacyStepFormulas && typeof legacyStepFormulas === 'object') {
            this.targetStepFormulas[id] = { ...legacyStepFormulas };
          } else {
            this.targetFormulas[id] = String(target['formula'] ?? legacyFormula);
          }
        }
      });
      const range = (payload['range'] as Record<string, unknown>) || {};
      this.sliderMin = Number(range['min'] ?? 0);
      this.sliderMax = Number(range['max'] ?? 100);
      this.sliderStep = Number(range['step'] ?? 1);
    }

    this.onLayoutTypeChange();
  }

  /** Reset the builder form for the next chart (keeps datasets + layout type so
   *  several charts can be built from the same data). */
  /** Clear the ENTIRE dashboard — remove every tile, reset the name and the
   *  active saved-dashboard link (so the next Save creates a fresh record), and
   *  reset the chart form. Confirms first when there are unsaved tiles to lose. */
  startNewDashboard(): void {
    if (
      this.dashboardTiles.length &&
      !window.confirm('Start a new dashboard? This clears all charts and the dashboard name.')
    ) {
      return;
    }
    this.dashboardTiles = [];
    this.dashboardName = '';
    this.activeSavedDashboardId = '';
    this.restoredDashboardId = '';
    this.restoredPublishedLayout = null;
    this.openFormatTileId = null;
    this.previewEditLayout = false;
    this.startNewChart();
    this.ui.showSuccess('Started a new dashboard.');
  }

  /** Open the Landing Page template builder — landing pages are authored in
   *  their own template-driven editor but publish through the same
   *  dashboard_layouts pipeline, so the result lists in the dashboards menu. */
  openLandingPageBuilder(): void {
    void this.router.navigate(['/dvm-landing-builder']);
  }

  startNewChart(): void {
    // Discard any un-generated draft preview tile (after Generate this is already
    // null, so it only removes a still-in-progress preview).
    this.removePreviewTile();
    this.editingTileId = null;
    this.title = '';
    this.componentId = this.createComponentId();
    this.steps = [];
    this.interactionsSectionOpen = false;
    this.drillDownDimensions = [];
    this.drillDownMeasures = [];
    this.tmType = 'normal';
    this.tmMigrationNotice = '';
    this.tmColumns = [];
    this.tmToggleEnabled = false;
    this.tmToggleLabel1 = '';
    this.tmToggleLabel2 = '';
    this.tmToggleDataset = '';
    this.tmToggleColumns = [];
    this.tmGroupBy = '';
    this.tmAggregation = 'none';
    this.tmRowField = '';
    this.tmColumnField = '';
    this.tmValueField = '';
    this.tmValueFields = [];
    this.tmRowFields = [];
    this.tmColumnFields2 = [];
    this.tmPivotColumnOrderText = '';
    this.tmPivotColumnOrderSort = '';
    this.tmGroupByFields = [];
    this.tmGroupsExpanded = true;
    this.tmTreeIdField = '';
    this.tmTreeParentField = '';
    this.tmRoleField = '';
    this.tmIndentField = '';
    this.tableMatrixWidgetData = null;
    this.drillDataMode = 'client';
    this.drillTotalsPlacement = 'both';
    this.drillColumnDimensions = [];
    this.drillComputedColumns = [];
    this.drillColumnOrder = [];
    this.newDrillColumnName = '';
    this.newDrillColumnExpression = '';
    this.newDrillColumnAfter = '';
    this.newDrillColumnError = '';
    this.tmNavEnabled = false;
    this.tmNavFilterKey = '';
    this.tmNavColumns = [];
    this.tmNavTargetDashboard = '';
    this.tmNavTargetComponents = [];
    this.tmNavButtonLabel = 'Navigate';
    this.tmNavAccordionOpen = false;
    this.resetTmNotes();
    this.navPinnedTarget = null;
    this.navDashboardsMemo = null;
    // Popup reset.
    this.popupTriggerMode = 'button';
    this.popupTriggerIcon = 'info';
    this.popupTriggerLabel = '';
    this.popupTriggerHint = '';
    this.popupSize = 'medium';
    this.popupPosition = 'center';
    this.popupCloseLabel = 'Close';
    this.popupDismissOnBackdrop = true;
    this.popupTriggerBg = '';
    this.popupTriggerText = '';
    this.popupTriggerRadius = null;
    this.popupFontSize = null;
    this.popupFontFamily = '';
    this.popupHeaderBg = '';
    this.popupHeaderText = '';
    this.popupCardHeaderBg = '';
    this.popupCardHeaderText = '';
    this.popupBodyBg = '';
    this.popupBodyText = '';
    this.popupButtonBg = '';
    this.popupButtonText = '';
    this.popupButtonRadius = null;
    this.popupRadius = null;
    this.popupFormatAccordionOpen = false;
    this.popupBlocks = [];
    this.hbGroupByField = '';
    this.hbGroupByLabel = '';
    this.hbValueLabel = 'Value';
    this.hbValueFormula = '';
    this.hbGroupFormulas = [];
    this.kpiField = '';
    this.kpiAggregation = 'SUM';
    this.barCategory = '';
    this.barCategoryParent = '';
    this.barCategoryLabel = '';
    this.barSeries = '';
    this.barSeriesOrder = '';
    this.barValueFormula = '';
    this.barValueLabel = '';
    this.barGroupFormulas = [];
    this.barSecondaryXField = '';
    this.barSecondaryYField = '';
    this.barXAxisFontWeight = '';
    this.barYAxisFontWeight = '';
    this.barSecondaryAxisFontWeight = '';
    this.barMeasures = [];
    this.barReferenceLines = [];
    this.barComputedColumns = [];
    this.newBarColumnName = '';
    // Line & Area reset (reuses barFormat, already cleared above).
    this.laVariant = 'line';
    this.laCategory = '';
    this.laCategoryDataset = '';
    this.laCategoryParent = '';
    this.laCategoryLabel = '';
    this.laYAxisLabel = '';
    this.laSecondaryYLabel = '';
    this.laSecondaryXLabel = '';
    this.laXAxisFontWeight = '';
    this.laYAxisFontWeight = '';
    this.laSecondaryXFontWeight = '';
    this.laSecondaryYFontWeight = '';
      this.laSeries = '';
      this.laSeriesOrder = '';
      this.laSeriesDataset = '';
      this.laApiUrl = '';
      this.laApiPayload = '';
      this.laApiResponsePath = '';
    this.laSecondaryYField = '';
    this.laSecondaryYDataset = '';
    this.laSecondaryXField = '';
    this.laSecondaryXDataset = '';
    this.laSecondaryXValueField = '';
    this.laSecondaryXValueDataset = '';
    this.laSecondaryXPosition = 'bottom';
    this.laMeasures = [];
    this.laReferenceLines = [];
    this.newBarColumnExpression = '';
    this.newBarColumnError = '';
    this.barFormat = {};
    // Scatter & Bubble reset — defaults come from config: first mode entry,
    // then that side's first base variant.
    this.sbMode = this.sbDefaultMode;
    this.sbAdvanced = '';
    this.sbAdvancedAccordionOpen = false;
    this.sbVariant = (this.sbVariantItems[0]?.value ?? 'scatter') as ScatterBubbleVariant;
    this.sbXField = '';
    this.sbYField = '';
    this.sbSizeField = '';
    this.sbXAggregation = 'NONE';
    this.sbYAggregation = 'NONE';
    this.sbSizeAggregation = 'NONE';
    this.sbColorField = '';
    this.sbLabelField = '';
    this.sbCategoryColors = {};
    this.sbSeries = '';
    this.sbAggregation = 'none';
    this.sbTooltipColumns = [];
    this.sbXLabel = '';
    this.sbYLabel = '';
    this.sbXAxisFontWeight = '';
    this.sbYAxisFontWeight = '';
    this.sbFormat = {};
    if (this.isBarLayout) this.addBarMeasure();
    if (this.isLineAreaLayout) this.addLaMeasure();
    if (this.isBarLayout || this.isWaterfallLayout || this.isLineAreaLayout) this.seedBarFormatDefaults();
    if (this.isScatterBubbleLayout) this.seedScatterBubbleFormatDefaults();
    this.kpiFormula = '';
    this.dropdownField = '';
    this.dropdownDisplayLabel = '';
    this.dropdownIsMultiSelect = false;
    this.dropdownShowAllOption = false;
    this.dropdownAutoSelectFirst = false;
    this.dropdownDefaultValue = '';
    this.dropdownDisplayMode = 'dropdown';
    this.dropdownGroups = [];
    this.dropdownOptionOrderMode = 'response';
    this.dropdownCustomOptionOrder = '';
    this.dropdownAdvancedAccordionOpen = false;
    this.fileUploadButtonLabel = 'Upload file';
    this.fileUploadAccept = '.xlsx,.xls,.csv';
    this.inputFormula = '';
    this.clearInputTargetState();
    this.inputDefaultValue = null;
    this.generateError = '';
  }

  // ── Reorder (HTML5 drag between tiles) ────────────────────────────────────

  onTileReorderStart(index: number, event: DragEvent): void {
    this.tileDragIndex = index;
    event.dataTransfer?.setData(TILE_DRAG_TYPE, String(index));
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onTileReorderOver(event: DragEvent): void {
    if (this.tileDragIndex === null) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onTileReorderDrop(targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    const from = this.tileDragIndex;
    this.tileDragIndex = null;
    if (from === null || from === targetIndex) return;
    const next = [...this.dashboardTiles];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    this.dashboardTiles = next;
    this.reflowCharts();
  }

  onTileReorderEnd(): void {
    this.tileDragIndex = null;
  }

  // ── Corner resize (grid-snapped) ──────────────────────────────────────────

  startTileResize(tile: DashboardTile, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeTileInteraction = {
      tile,
      startX: event.clientX,
      startY: event.clientY,
      startColSpan: tile.colSpan,
      startRowSpan: tile.rowSpan,
    };
  }

  toggleDashboardFullscreen(): void {
    this.isDashboardFullscreen = !this.isDashboardFullscreen;
    // Leaving preview always drops back to the clean (non-edit) view.
    if (!this.isDashboardFullscreen) this.previewEditLayout = false;
    this.reflowCharts();
  }

  // ── Save / publish (same stores the dashboard builder uses) ─

  isPublishing = false;

  get canSaveDashboard(): boolean {
    return !this.isSavingDashboard && !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get canPublish(): boolean {
    // Only needs at least one chart — the name falls back to "Untitled dashboard"
    // (the header field is empty until typed; its text is just a placeholder).
    return !this.isPublishing && this.dashboardTiles.length > 0;
  }

  get publishButtonLabel(): string {
    if (this.isPublishing) return this.isExistingPublishedDashboard ? 'Updating…' : 'Publishing…';
    return this.isExistingPublishedDashboard ? 'Update' : 'Publish';
  }

  async saveDashboard(): Promise<void> {
    if (!this.canSaveDashboard) {
      this.ui.showError('Add at least one chart before saving the dashboard.');
      return;
    }

    this.isSavingDashboard = true;
    try {
      await this.saveDashboardInternal('draft');
    } finally {
      this.isSavingDashboard = false;
    }
  }

  private async saveDashboardInternal(status: 'draft' | 'published'): Promise<SavedSelfServiceDashboard | null> {
    const now = new Date().toISOString();
    const dashboardName = this.dashboardName.trim() || 'Untitled dashboard';
    const config = this.buildDashboardLayoutJson(status);
    const savedDashboards = this.persistence.consolidateSavedDashboards(
      readJson<SavedSelfServiceDashboard[]>(DvmLayoutBuilderComponent.SAVED_DASHBOARDS_STORAGE_KEY, []),
    );
    const normalizedName = this.normalizeDashboardName(dashboardName);
    const existing = savedDashboards.find((dashboard) =>
      dashboard.id === this.activeSavedDashboardId
      || (this.restoredDashboardId && dashboard.config?.['dashboardId'] === this.restoredDashboardId)
      || this.normalizeDashboardName(dashboard.name) === normalizedName,
    );

    // Resolved once, up front, so both the server-side existing-dashboard
    // lookup below AND the actual publish call use the SAME email.
    let emailId = '';
    try {
      if (await this.keycloak.isLoggedIn()) {
        emailId = (await this.keycloak.loadUserProfile()).email || '';
      }
    } catch {
      // Not logged in / Keycloak unavailable — publish anonymously.
    }

    // Neither the route restore NOR the local cache told us this dashboard
    // already has a backend row (e.g. reopening the builder fresh after time
    // has passed, on another browser, or once localStorage has been cleared —
    // `activeSavedDashboardId`/`restoredDashboardId` are empty and the local
    // `savedDashboards` cache no longer has a matching entry). Without this,
    // the code below would mint a BRAND NEW dashboard_id from the name and
    // search `dynamic_filter` for that instead of the dashboard's real,
    // already-published id — which never matches, so publish silently inserts
    // a duplicate row even though the dashboard is plainly sitting in that
    // same dynamic_filter response under its real id. Ask the backend by name
    // (same dynamic_filter query the sidenav/list already use) before falling
    // back to minting anything.
    let resolvedDashboardId = this.restoredDashboardId || (existing?.config?.['dashboardId'] as string | undefined) || '';
    if (!resolvedDashboardId && emailId) {
      try {
        const owned = await firstValueFrom(this.dashboardService.fetchOwnedDashboardsFromApi(emailId));
        const match = owned.find((o) =>
          this.normalizeDashboardName(String(o.layoutJson?.['dashboard-name'] ?? o.layoutJson?.['name'] ?? '')) === normalizedName,
        );
        // `OwnedDashboardLayout.name` IS the backend's `dashboard_id` (see
        // fetchOwnedDashboardsFromApi) — not the display name.
        if (match) resolvedDashboardId = match.name;
      } catch {
        // Best-effort — fall through to minting a dashboardId as before.
      }
    }
    if (resolvedDashboardId) this.restoredDashboardId = resolvedDashboardId;
    this.isExistingPublishedDashboard = !!existing || !!resolvedDashboardId;

    // The publicly-facing dashboard_id/URL slug used to be JUST the slugified
    // name — two dashboards named the same collided (same backend record, same
    // URL). Append a short suffix derived from this dashboard's OWN stable
    // internal id (reused across saves via `existing.id`, so the suffix never
    // changes once assigned) so same-named dashboards get distinct identities.
    const stableId = existing?.id || this.createId();
    // Reuse the slug this dashboard was already published under whenever we
    // have one (route restore, local cache, or the backend name lookup above)
    // — recomputing from name+stableId here would mint a different slug,
    // silently forking a new backend row instead of updating the one being edited.
    const dashboardId = resolvedDashboardId || this.buildDashboardId(dashboardName, stableId);
    (config as Record<string, unknown>)['dashboardId'] = dashboardId;

    const savedDashboard: SavedSelfServiceDashboard = {
      id: stableId,
      name: dashboardName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      items: [],
      layout: (config['layout'] as SavedSelfServiceDashboard['layout']) ?? { columns: DvmLayoutBuilderComponent.DASH_COLS },
      status,
      publishedAt: status === 'published' ? now : existing?.publishedAt,
      config,
    };

    try {
      // Save-dashboard-draft API call intentionally disabled — only publishApi
      // is called now. `savedDashboard` is already the full record; use it
      // directly instead of round-tripping through that endpoint.
      const persistedDashboard = savedDashboard;

      // Only publishApi persists the dashboard now — lands it in the shared
      // `dashboard_layouts` store, matching how DvmPublishedDashboardComponent.
      // saveLayout() republishes on every edit.
      //
      // This call used to be swallowed (catch → console.warn only), so on any
      // backend failure (expired token, network blip, CORS, 500…) the UI still
      // showed "Dashboard saved" even though nothing actually landed on the
      // server that time — the dashboard only looked saved because the LOCAL
      // cache below still got updated. Track success and surface a real error
      // instead of a false-positive toast.
      let publishSucceeded = false;
      try {
        const updatedExisting = await this.publishLayoutJsonToApi(config, emailId, dashboardId);
        this.isExistingPublishedDashboard = this.isExistingPublishedDashboard || updatedExisting;
        publishSucceeded = true;
      } catch (publishError) {
        console.warn('[dvm-layout-builder] saveDashboardInternal: publishApi sync failed:', publishError);
      }

      const nextDashboards = existing
        ? [
            persistedDashboard,
            ...savedDashboards.filter((dashboard) => (
              dashboard.id !== existing.id
              && this.normalizeDashboardName(dashboard.name) !== normalizedName
            )),
          ]
        : [persistedDashboard, ...savedDashboards];

      // The backend call above is the durable save — it already succeeded once we
      // get here. persistSavedDashboards only refreshes the local list cache (used
      // to populate this builder's saved-dashboard picker without a round trip);
      // on a full browser storage quota it can fail even though the dashboard is
      // safely stored server-side. Treat that as a soft/local-only miss, not a
      // save failure — surfacing "Unable to save... too large for browser
      // storage" here was misleading users into thinking the save itself failed.
      const cached = this.persistence.persistSavedDashboards(nextDashboards, { silent: true });
      this.activeSavedDashboardId = persistedDashboard.id;
      if (publishSucceeded) {
        this.ui.showSuccess(status === 'published' ? 'Dashboard saved and ready to publish.' : existing ? 'Dashboard updated.' : 'Dashboard saved.');
      } else {
        // The server call failed — this dashboard is NOT durably saved, even
        // though it's cached locally in this browser. Say so explicitly
        // instead of a misleading success toast.
        this.ui.showError('Saved locally, but the server could not be reached — this dashboard is not saved yet. Try again once you\'re back online.');
      }
      if (!cached) {
        console.warn('[dvm-layout-builder] Dashboard saved to the server, but the local saved-dashboards cache is full and was not updated.');
      }
      return persistedDashboard;
    } catch (error) {
      this.ui.showError(error instanceof Error ? error.message : 'Unable to save the dashboard.');
      return null;
    }
  }

  private normalizeDashboardName(name: string): string {
    return String(name || '').trim().toLowerCase();
  }

  /** Name → URL slug, byte-for-byte identical to the sidenav's `nameToSlug` and
   *  the dashboard service's `slugify`, so the redirect target matches the menu
   *  link and `getPublishedLayoutBySlug` resolves the same record. */
  private dashboardSlug(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** Unique id for a saved dashboard record (mirrors the dashboard builder's
   *  createId — used when persisting a brand-new dashboard). */
  private createId(): string {
    return `dvm-dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /** The public dashboard_id / URL slug: the name's slug plus a short suffix
   *  from this dashboard's stable internal `id` (reused across saves), so two
   *  dashboards sharing a name no longer collide into the same backend record
   *  / published URL. `stableId` looks like `dvm-dashboard-<ts>-<rand>` — the
   *  trailing random segment (after the last '-') IS the uniqueness we want. */
  private buildDashboardId(name: string, stableId: string): string {
    const namePart = this.dashboardSlug(name) || 'untitled-dashboard';
    const suffix = stableId.split('-').pop() || stableId;
    return `${namePart}-${suffix}`;
  }

  /** Best-effort human-readable detail from a failed HTTP call — status +
   *  whatever message the backend body/status text carried — so publish
   *  failures point at the actual cause instead of a generic string. */
  private extractHttpErrorDetail(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const bodyMessage =
        (typeof error.error === 'string' && error.error) ||
        error.error?.message ||
        error.error?.detail ||
        error.error?.error ||
        '';
      return [error.status ? `${error.status}` : '', bodyMessage || error.statusText || error.message]
        .filter(Boolean)
        .join(' ');
    }
    return error instanceof Error ? error.message : '';
  }

  /** Publish the whole dashboard so it lands in the shared `dashboard_layouts`
   *  store and appears in the left-side saved-dashboards menu — mirrors
   *  DashboardBuilderComponent.publishDashboard(). Uses the config's `publishApi`
   *  when set, else the shared SelfServiceDashboardService insert. */
  async publishDashboard(): Promise<void> {
    if (!this.canPublish) {
      this.ui.showError('Add at least one chart and name the dashboard before publishing.');
      return;
    }
    this.isPublishing = true;
    try {
      // saveDashboardInternal already syncs to publishApi itself (see there) —
      // no need to publish a second time here.
      const savedDashboard = await this.saveDashboardInternal('published');
      if (!savedDashboard) return;

      const layoutJson = savedDashboard.config;
      // saveDashboardInternal computed the real (unique, suffixed) dashboard_id
      // and stamped it onto config — read it back instead of recomputing from
      // the raw name, so the redirect slug matches what was actually published.
      const dashboardId = String(layoutJson['dashboardId'] ?? this.dashboardName.trim() ?? 'Untitled dashboard');
      // The sidenav listens for this to refresh the saved-dashboards list. Carry
      // the just-published dashboard's id + name in the event so the sidebar can
      // add it OPTIMISTICALLY — the API re-query it also runs can race the
      // backend's indexing of the brand-new row (see the cache-warm note below),
      // which would otherwise leave the new dashboard missing until a reload.
      window.dispatchEvent(new CustomEvent('dvm:dashboard-published', {
        detail: { dashboardId, name: String(layoutJson['dashboard-name'] ?? layoutJson['name'] ?? this.dashboardName.trim() ?? dashboardId) },
      }));
      this.ui.showSuccess('Dashboard published — opening it now.');
      // Warm the dynamic-dashboard cache with the config we just published so the
      // published view paints it INSTANTLY on redirect — otherwise it depended on
      // the API/sidenav having indexed the brand-new record, which isn't
      // guaranteed the moment after insert (that gap showed an empty dashboard).
      const slug = this.dashboardSlug(dashboardId);
      writeDynamicDashboardCache(slug, layoutJson);
      // Hand the just-written layout DIRECTLY to the published view for the one
      // load that follows this redirect. dashboard_layouts/dynamic_filter is
      // eventually consistent — for a moment after the write it still serves the
      // PREVIOUS version, which is why a publish used to need a manual refresh
      // before the change appeared. Synchronous (unlike the deferred cache write
      // above, which can still be sitting in the idle queue when the published
      // view reads) and one-shot, so every later load reads the API as normal.
      markDashboardJustPublished(slug, layoutJson);
      // Redirect to the published dashboard view (the API-backed dynamic route,
      // resolved by name-slug — the same URL the sidenav's menu item points at).
      this.router.navigate(['/dvm-published', slug]);
    } catch (error) {
      // Surface the real backend error instead of a blanket message — publishUrl
      // (when configured) is posted to directly, with no catchError swallow, so
      // whatever the server actually returned (404/400/500 + body) is what threw.
      console.error('[dvm-layout-builder] publishDashboard failed:', error);
      const detail = this.extractHttpErrorDetail(error);
      this.ui.showError(detail ? `Unable to publish the dashboard: ${detail}` : 'Unable to publish the dashboard.');
    } finally {
      this.isPublishing = false;
    }
  }

  /** POST a dashboard's layout_json to `apiEndpoints.publishApi` (or the shared
   *  SelfServiceDashboardService insert when that's not configured) — used by
   *  saveDashboardInternal so EVERY save (draft or published) syncs to the
   *  shared `dashboard_layouts` store, not just an explicit Publish click.
   *  Returns true when an existing owned row was found (and thus `update`d)
   *  instead of inserted — callers use this to drive the Publish/Update label. */
  private async publishLayoutJsonToApi(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string): Promise<boolean> {
    // This email already owns a `dashboard_layouts` row for this dashboard →
    // update that row instead of inserting a duplicate one (see
    // SelfServiceDashboardService.findOwnedDashboardLayoutRecord — publish was
    // always `insert`, so re-publishing kept appending new rows for the same
    // dashboard).
    const existingRecord = emailId
      ? await firstValueFrom(this.dashboardService.findOwnedDashboardLayoutRecord(dashboardId, emailId))
      : null;
    // .trim() so a whitespace-only config value (e.g. `"publishApi": " "`, as
    // shipped in dvm-layout-builder.json) is treated as unconfigured — a bare
    // space is truthy in JS and was wrongly routing every publish through the
    // direct-http.post branch below, which (unlike the shared service call)
    // has no catchError and threw on any failure instead of degrading safely.
    const configuredPublishApi = (this.layout.apiEndpoints?.publishApi || '').trim();
    const publishUrl = configuredPublishApi ? resolveSelfServiceApiUrl(configuredPublishApi) : '';
    const publish$ = publishUrl
      ? this.http.post(publishUrl, this.buildPublishEnvelope(layoutJson, emailId, dashboardId, existingRecord?.uuid ?? null))
      : this.dashboardService.publishLayoutToApi(layoutJson, emailId, dashboardId);
    await firstValueFrom(publish$);
    return !!existingRecord;
  }

  /** Insert envelope matching SelfServiceDashboardService.publishLayoutToApi, for
   *  when a custom `publishApi` URL is configured. `uuid` present → this
   *  dashboard already has an owned row, so the operation becomes `update`. */
  private buildPublishEnvelope(layoutJson: Record<string, unknown>, emailId: string, dashboardId: string, uuid: string | null = null): Record<string, unknown> {
    return {
      operation: uuid ? 'update' : 'insert',
      requestData: {
        email_id: emailId,
        role: 'User',
        
        layout_json: layoutJson,
        dashboard_id: dashboardId,
        ...(uuid ? { uuid } : {}),
      },
      responseList: [],
      entity: 'dashboard_layouts',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [],
      version: '1.0',
    };
  }

  /** Build the published `layout_json` from the dashboard tiles — the same
   *  envelope shape the dashboard builder emits (dashboard-name / layout /
   *  status / pageContent rows of widgets), so the saved-dashboards list and
   *  published view can consume it. */
  private buildDashboardLayoutJson(status: 'draft' | 'published' = 'published'): Record<string, unknown> {
    const columns = DvmLayoutBuilderComponent.DASH_COLS;
    // Map each DVM layout type to the renderer type whose component consumes the
    // SAME `data` shape we build for the preview tile — so the published view
    // renders identically. DRILL_DOWN_TABLE → 'drill-down' (DrillDownComponent,
    // the pre-built headers/staticData shape), NOT 'generic-drill-down' (which
    // self-fetches + builds its own tree from raw rows).
    const typeMap: Record<string, string> = {
      WATERFALL: 'waterfall-chart',
      DRILL_DOWN_TABLE: 'drill-down',
      HORIZONTAL_BAR_CHART: 'horizontal-bar-chart',
      KPI: 'kpi-card',
      BAR: 'bar-column',
      SCATTER_BUBBLE: 'scatter-bubble',
      LINE_AREA: 'line-area',
      SLIDER: 'slider',
      INPUT_BOX: 'input-box',
      DROPDOWN: 'dropdown',
      TABLE_MATRIX: 'table-matrix',
      FILE_UPLOAD: 'file-upload',
      POPUP: 'popup',
    };

    const layoutItems = this.buildPublishedLayoutItems();
    const layoutById = new Map(layoutItems.map((item) => [item.id, item]));

    // Pack tiles into rows that sum to <= columns (same as the builder's
    // buildPageContent), so the published grid mirrors the preview layout.
    const pageContent: Record<string, unknown>[] = [];
    let buffer: Record<string, unknown>[] = [];
    let used = 0;
    this.dashboardTiles.forEach((tile, index) => {
      // Skip the un-generated draft preview tile — it's not a real saved chart
      // until the user clicks Generate (which promotes it).
      if (tile.id === this.previewTileId) return;
      const tileLayout = layoutById.get(tile.id) ?? this.layoutItemForTile(tile, index);
      const span = Math.min(tileLayout.colSpan, columns);
      const widget = tile.layoutType === 'GROUP' && tile.groupMembers?.length
        ? this.buildGroupCardWidget(tile, tileLayout, typeMap)
        : this.buildTileWidget(tile, typeMap, tileLayout.rowSpan, tileLayout.x, tileLayout.y, tileLayout.order, tileLayout.customSize, span);
      if (used + span > columns && buffer.length) {
        pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });
        buffer = [];
        used = 0;
      }
      buffer.push(widget);
      used += span;
    });
    if (buffer.length) pageContent.push({ id: `row${pageContent.length + 1}`, columns: buffer });

    const name = this.dashboardName.trim() || 'Untitled dashboard';
    // Return a JSON-safe clone: the drill-down tree nodes carry a runtime
    // `parent` back-reference (set by DrillDownComponent), which makes a circular
    // structure that breaks JSON.stringify on both the publish POST and the
    // localStorage save. toSerializable strips those refs / cuts cycles.
    const now = new Date().toISOString();
    return toSerializable({
      'dashboard-name': name,
      name,
      // Marks this dashboard as built by the DVM layout builder so the published
      // view's "Back to Builder" returns HERE (not the old dashboard builder).
      builder: 'dvm-layout',
      screenFloating: true,
      enableEdit: true,
      layout: this.buildPublishedLayoutSnapshot(layoutItems),
      status,
      // WHEN this version was written. Republishing can APPEND a new
      // dashboard_layouts row (publishLayoutToApi only UPDATEs when it can find
      // an existing uuid), and the read side gets every historical row back for
      // the same dashboard_id. SelfServiceDashboardService.pickNewest() resolves
      // that by timestamp — with none present every row scored equal and it fell
      // back to "last row the backend happened to return", so a stale version
      // could win and a freshly published change would not appear.
      updatedAt: now,
      ...(status === 'published' ? { publishedAt: now } : {}),
      pageContent,
    });
  }

  /** Build one tile's published widget object — extracted from the per-tile
   *  loop above so GROUP members (below) can build theirs the exact same way. */
  private buildTileWidget(
    tile: DashboardTile,
    typeMap: Record<string, string>,
    rowSpan: number,
    x: number,
    y: number,
    order: number,
    customSize: boolean,
    span: number,
  ): Record<string, unknown> {
    // ChartRendererService assigns the WHOLE widget object to the rendered
    // component's `data` input, and each component reads its fields
    // (headers/staticData/key/…) directly off `data`. So the render payload
    // must be SPREAD at the top level of the widget — NOT nested under a
    // `data` key (that was why the published view showed "no data").
    const renderData: Record<string, unknown> =
      (tile.drillData as Record<string, unknown>) ??
      this.publishableTableMatrixData(tile.tableMatrixData as Record<string, unknown> | undefined) ??
      (tile.hbData as Record<string, unknown>) ??
      (tile.kpiData as Record<string, unknown>) ??
      (tile.barData as Record<string, unknown>) ??
      (tile.lineAreaData as Record<string, unknown>) ??
      (tile.scatterBubbleData as Record<string, unknown>) ??
      (tile.inputControl?.widgetData as Record<string, unknown>) ??
      (tile.dropdownControl?.widgetData as Record<string, unknown>) ??
      (tile.fileUploadControl?.widgetData as Record<string, unknown>) ??
      (tile.popupControl?.widgetData as Record<string, unknown>) ??
      // Waterfall: publish the raw BARS (not just the pre-built option). The
      // option's number formatters are FUNCTIONS, which JSON.stringify drops on
      // publish — so the published view rebuilds the option from bars + format
      // at render time (functions restored). `option` kept as a fallback.
      (tile.waterfallBars
        ? { waterfallBars: tile.waterfallBars, option: tile.previewOption }
        : (tile.previewOption ? { option: tile.previewOption } : {}));
    const isInputControl = tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX' || tile.layoutType === 'DROPDOWN' || tile.layoutType === 'FILE_UPLOAD';
    return {
      ...renderData,
      type: typeMap[tile.layoutType] || 'bar-chart',
      title: tile.title,
      widgetId: tile.id,
      componentId: tile.componentId,
      // DashboardComponent derives a widget's width from Bootstrap-style
      // column classes. Keep layout.colSpan as structured metadata as well,
      // but emit the class so the published footprint matches the builder.
      class: `col-${span}`,
      layout: { colSpan: span, rowSpan, x, y, order, customSize },
      // The published grid sizes each tile from a pixel `height` (it ignores
      // `rowSpan`) and, with NO height, falls back to an oversized default —
      // which made small controls (KPI / slider / input box) render huge. Emit
      // a height proportional to the builder's rowSpan so the published tile
      // mirrors the footprint the user laid out here.
      height: Math.max(rowSpan, 1) * DvmLayoutBuilderComponent.DASH_ROW_PX,
      payload: tile.payload,
      // Persist the per-tile Format popup selections so the published view can
      // preserve them on recompute (rebuilds the option from `format`) and so
      // re-opening in the builder restores them — without this the waterfall's
      // formatting was lost after an input change / on edit.
      ...(tile.format ? { format: tile.format } : {}),
      // Notes drawn under the card, outside it (every chart/table type).
      ...(this.tileNotesOf(tile) ? { tileNotes: tile.tileNotes } : {}),
      // Cross-filter config: the column this tile publishes + which tiles react.
      // Persist the EFFECTIVE values (explicit override, else the same defaults
      // the builder itself uses — category column + every reactable tile), NOT
      // the raw tile fields. Otherwise a tile that cross-filters in the builder
      // by default published NOTHING (raw filterKey/listenerTileIds were unset),
      // so the published view lost the wiring. Emitted for client-side charts
      // AND recompute participants (waterfall).
      ...(this.isCrossFilterTile(tile) || this.isRecomputeCrossFilterTile(tile)
        ? {
            filterKey: this.effectiveTileFilterKey(tile),
            listenerTileIds: this.effectiveListenerTileIds(tile),
          }
        : {}),
      // Waterfall interaction flags travel on the WIDGET (the published view
      // hands rec.widget straight to <app-waterfall-chart>, which reads
      // data.crossFilter/highlightOnClick) + the compute URL it needs to re-run
      // the bridge with a cross-filter (layout_json omits apiEndpoints).
      ...(this.isRecomputeCrossFilterTile(tile)
        ? {
            crossFilter: tile.format?.['crossFilter'] === true,
            highlightOnClick: tile.format?.['highlightOnClick'] === true,
            recomputeUrl: this.layout.apiEndpoints?.generateConfigApi?.trim() || '/components/compute',
          }
        : {}),
      // Input controls drive a live recompute in the published view. The
      // published layout_json omits `apiEndpoints`, so persist the compute URL
      // onto the control's widget so the published dashboard can re-post here
      // on change (it can't otherwise resolve generateConfigApi).
      ...(isInputControl
        ? {
            recomputeUrl: this.layout.apiEndpoints?.generateConfigApi?.trim() || '/components/compute',
            // The configured custom "API Call" (URL/method/body template) —
            // published-view's onInputWidgetChange reads this to fire the
            // configured call instead of the default recompute when enabled.
            apiConfig: tile.dropdownControl?.apiConfig
              ?? tile.inputControl?.apiConfig
              ?? tile.fileUploadControl?.apiConfig,
          }
        : {}),
    };
  }

  /** A GROUP tile publishes as ONE 'group-card' widget whose `children` are the
   *  member tiles' own widgets (built via buildTileWidget, same as any other
   *  tile) — the published view renders one card and lays children out inside
   *  it. Children's x/y/order are relative to the CARD's own inner grid, not
   *  the outer dashboard grid — they stack top-to-bottom, full card width. */
  private buildGroupCardWidget(
    tile: DashboardTile,
    tileLayout: PublishedLayoutSnapshotItem,
    typeMap: Record<string, string>,
  ): Record<string, unknown> {
    const members = tile.groupMembers || [];
    let cursorY = 0;
    const children = members.map((member, memberIndex) => {
      const memberSpan = this.clamp(member.colSpan, 1, tileLayout.colSpan);
      const child = this.buildTileWidget(member, typeMap, member.rowSpan, 0, cursorY, memberIndex, false, memberSpan);
      cursorY += member.rowSpan;
      return child;
    });
    return {
      type: 'group-card',
      title: tile.groupTitle || tile.title || 'Grouped card',
      widgetId: tile.id,
      componentId: tile.componentId,
      // The card's own design (border / backgrounds / radius / transparency)
      // travels with the published widget exactly like a chart's format, so the
      // published view can re-apply it and it survives save/reload.
      ...(tile.format ? { format: tile.format } : {}),
      class: `col-${tileLayout.colSpan}`,
      layout: { colSpan: tileLayout.colSpan, rowSpan: tileLayout.rowSpan, x: tileLayout.x, y: tileLayout.y, order: tileLayout.order, customSize: tileLayout.customSize },
      height: Math.max(tileLayout.rowSpan, 1) * DvmLayoutBuilderComponent.DASH_ROW_PX,
      innerColumns: tileLayout.colSpan,
      groupColumns: tile.groupColumns || 1,
      children,
    };
  }

  private buildPublishedLayoutSnapshot(items = this.buildPublishedLayoutItems()): Record<string, unknown> {
    const existing = this.cloneRecord(this.restoredPublishedLayout) ?? {};
    return {
      ...existing,
      columns: Number(existing['columns'] ?? DvmLayoutBuilderComponent.DASH_COLS),
      items,
      breakpoints: {
        ...((existing['breakpoints'] as Record<string, unknown> | undefined) ?? {}),
        xl: items.map(({ id, colSpan, rowSpan, x, y, order, customSize }) => ({ id, colSpan, rowSpan, x, y, order, customSize })),
      },
    };
  }

  private buildPublishedLayoutItems(): PublishedLayoutSnapshotItem[] {
    const restoredItems = this.readPublishedLayoutItems(this.restoredPublishedLayout);
    const restoredById = new Map(restoredItems.map((item) => [String(item['id'] ?? '').trim(), item]));
    const restoredByComponentId = new Map(restoredItems.map((item) => [String(item['componentId'] ?? '').trim(), item]));
    const seeds = this.dashboardTiles.map((tile, index) => {
      const restored = restoredById.get(tile.id) ?? restoredByComponentId.get(tile.componentId);
      return this.layoutItemForTile(tile, index, restored);
    });
    return this.placeMissingLayoutItems(seeds);
  }

  /** Build one tile's layout-snapshot entry.
   *
   *  EXISTING component (has a `restored` entry from the published layout we got
   *  via the dynamic_filter API): keep its saved layout VERBATIM — same
   *  x/y/colSpan/rowSpan/order/customSize. The builder must never re-derive or
   *  re-flow a tile that already has a published position, or it would reset the
   *  arrangement the user set in the published view.
   *
   *  NEW component (no `restored` entry — just added in the builder): emit it
   *  UNPLACED (x/y = -1) so placeMissingLayoutItems appends it below everything
   *  else. This is the ONLY case where the builder decides a position. */
  private layoutItemForTile(tile: DashboardTile, order: number, restored?: Record<string, unknown>): PublishedLayoutSnapshotItem {
    if (restored) {
      const colSpan = this.clamp(Math.round(this.toFiniteNumber(restored['colSpan']) ?? this.toFiniteNumber(tile.colSpan) ?? 6), 1, DvmLayoutBuilderComponent.DASH_COLS);
      const restoredX = this.toFiniteNumber(restored['x']);
      const restoredY = this.toFiniteNumber(restored['y']);
      return {
        id: tile.id,
        componentId: tile.componentId,
        colSpan,
        rowSpan: Math.max(1, Math.round(this.toFiniteNumber(restored['rowSpan']) ?? this.toFiniteNumber(tile.rowSpan) ?? 8)),
        // Keep the published position exactly; only fall back to append (-1) if
        // the saved entry genuinely had no position (legacy published layout).
        x: restoredX === null ? -1 : Math.max(0, Math.round(restoredX)),
        y: restoredY === null ? -1 : Math.max(0, Math.round(restoredY)),
        order: this.toFiniteNumber(restored['order']) ?? order,
        customSize: restored['customSize'] === true,
      };
    }
    // Brand-new tile: leave it unplaced so it's appended after existing ones.
    return {
      id: tile.id,
      componentId: tile.componentId,
      colSpan: this.clamp(Math.round(this.toFiniteNumber(tile.colSpan) ?? 6), 1, DvmLayoutBuilderComponent.DASH_COLS),
      rowSpan: Math.max(1, Math.round(this.toFiniteNumber(tile.rowSpan) ?? 8)),
      x: -1,
      y: -1,
      order,
      customSize: tile.customSize === true,
    };
  }

  private placeMissingLayoutItems(items: PublishedLayoutSnapshotItem[]): PublishedLayoutSnapshotItem[] {
    let cursorX = 0;
    let cursorY = items.reduce((bottom, item) => item.x >= 0 && item.y >= 0 ? Math.max(bottom, item.y + item.rowSpan) : bottom, 0);
    let rowHeight = 0;

    return items.map((item) => {
      if (item.x >= 0 && item.y >= 0) return item;
      if (cursorX + item.colSpan > DvmLayoutBuilderComponent.DASH_COLS) {
        cursorX = 0;
        cursorY += rowHeight;
        rowHeight = 0;
      }
      const placed = { ...item, x: cursorX, y: cursorY };
      cursorX += item.colSpan;
      rowHeight = Math.max(rowHeight, item.rowSpan);
      return placed;
    });
  }

  private toFiniteNumber(value: unknown): number | null {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }


  private cloneRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return toSerializable(value as Record<string, unknown>);
  }

  /** The reused app-mat-slider emits { start, end } on change — take the end as
   *  the control's value and re-post the tile's config so its target charts
   *  recompute, then live-update every returned chart tile with its new data. */
  onTileSliderChange(tile: DashboardTile, range: { start: number; end: number }): void {
    this.pushInputValueAndRecompute(tile, range.end);
  }

  /** The reused app-input-box emits its value on change — same recompute path. */
  onTileInputValueChange(tile: DashboardTile, value: number | string): void {
    this.pushInputValueAndRecompute(tile, value);
  }

  /** A Table & Matrix tile fired navigation — route to the target published
   *  dashboard, passing the clicked value as query params so that dashboard
   *  filters its charts on load. Works from the builder preview too (the target
   *  is a published dashboard, independent of this builder session). */
  onTileNavigate(_tile: DashboardTile, event: { targetDashboard: string; targetComponents: string[]; filterKey: string; filterValue: string }): void {
    if (!event?.targetDashboard) return;
    this.router.navigate(['/dvm-published', event.targetDashboard], {
      queryParams: {
        filterKey: event.filterKey,
        filterValue: event.filterValue,
        ...(event.targetComponents?.length ? { filterTargets: event.targetComponents.join(',') } : {}),
      },
    });
  }

  /** Save all clicked on a table tile in the BUILDER preview with no save
   *  endpoint configured (the component POSTs directly when one is set, and the
   *  published view has its own default-endpoint handler). The builder is a
   *  design surface — don't write preview edits to the backend; confirm the
   *  capture and point at the published dashboard for real saving. */
  onTileTableSave(tile: DashboardTile, event: TableMatrixSaveEvent): void {
    const count = event?.rows?.length || 0;
    // Drill-down cell edits: fold the saved changes into the tile's STORED
    // tree (drilldownData.staticData). lib-drill-down edits only its own clone,
    // so without this the publish payload would carry the pre-edit snapshot and
    // the published dashboard would open with the original values.
    const drill = (tile.tableMatrixData as Record<string, unknown> | null | undefined)?.['drilldownData'] as Record<string, unknown> | undefined;
    if (drill && applyDrillDownSaveToWidgetData(drill, event)) {
      this.ui.showSuccess(`${count} change${count === 1 ? '' : 's'} saved into this tile's data — Publish carries them to the published dashboard. (Update Configuration re-fetches from the backend and overwrites them.)`);
      return;
    }
    this.ui.showSuccess(`${count} change${count === 1 ? '' : 's'} captured (builder preview only) — saving to the backend happens on the published dashboard.`);
  }

  /** A Normal/Editable table tile requested a refresh — re-fetch its dataset rows
   *  and re-render (same getEntityRows path the initial generate uses). */
  onTileTableRefresh(tile: DashboardTile): void {
    if (!tile.tableMatrixData) return;
    const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
    const dataset = String(caps['dataset'] || tile.payload?.['dataset'] || '');
    if (!dataset) return;
    this.datasetService.getEntityRows(dataset).subscribe({
      next: (rows) => {
        tile.tableMatrixData = { ...(tile.tableMatrixData as Record<string, unknown>), staticData: rows };
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
      },
      error: () => { /* keep current rows on failure */ },
    });
  }

  /** The table preview emitted a new drag-reordered column order — persist it
   *  into the tile's capabilities.columns so the order survives Generate/publish
   *  (the child's runtime `columnOrder` alone doesn't round-trip). When columns
   *  were "all" (empty config), materialize them from the new order first. */
  onTileColumnReorder(tile: DashboardTile, order: string[]): void {
    if (!tile.tableMatrixData || !Array.isArray(order) || !order.length) return;
    const data = tile.tableMatrixData as Record<string, unknown>;
    const caps = { ...((data['capabilities'] as Record<string, unknown>) || {}) };
    const existing = (Array.isArray(caps['columns']) ? caps['columns'] : []) as Array<Record<string, unknown>>;
    const byField = new Map(existing.map((c) => [String(c['field']), c]));
    // Reorder existing column configs by the new key order; any key without an
    // existing config (columns were inferred/"all") becomes a bare {field}.
    const reordered = order.map((field) => byField.get(field) || { field });
    caps['columns'] = reordered;
    tile.tableMatrixData = { ...data, capabilities: caps };
    // Keep the tile's PERSISTED request payload in the same order too — otherwise
    // a re-Generate/Update (which rebuilds from payload.columns) would drop back
    // to the old column order and lose the reorder. Only reorder the keys that
    // were already in the payload (preserve "all"/empty-means-all semantics).
    const payload = tile.payload as Record<string, unknown> | undefined;
    if (payload && Array.isArray(payload['columns']) && (payload['columns'] as unknown[]).length) {
      // Entries are {dataset, field, displayName} objects now (legacy payloads
      // may still hold plain strings) — reorder by field key either way.
      const entries = payload['columns'] as unknown[];
      const fieldOf = (entry: unknown): string =>
        entry && typeof entry === 'object' ? String((entry as Record<string, unknown>)['field'] ?? '') : String(entry ?? '');
      const byField = new Map(entries.map((entry) => [fieldOf(entry), entry]));
      payload['columns'] = order.filter((field) => byField.has(field)).map((field) => byField.get(field));
    }
    // Mirror into the build form when THIS tile is the one being edited, so a
    // subsequent Generate/Update keeps the reordered order.
    if (this.editingTileId === tile.id && this.tmColumns.length) {
      const byField = new Map(this.tmColumns.map((column) => [column.field, column]));
      const reordered = order.map((field) => byField.get(field)).filter((c): c is TmColumnDef => !!c);
      // Keep any configured rows the runtime order didn't mention (e.g. a row
      // whose field is still blank) at the end rather than dropping them.
      const mentioned = new Set(reordered.map((column) => column.id));
      this.tmColumns = [...reordered, ...this.tmColumns.filter((column) => !mentioned.has(column.id))];
    }
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? { ...tile } : t));
  }

  /**
   * A dropdown tile's value changed → push it to the target charts.
   *
   * `basePayload` is the GENERATE-time form payload. When given it replaces
   * `tile.payload` as the request's base (so the fresh config goes out, not the
   * tile's previous one) and the POST fires even with no targets, because that
   * same request is what registers the component config. Interactive changes
   * omit it and keep using the tile's own payload.
   */
  onTileDropdownChange(tile: DashboardTile, value: string | string[], basePayload?: Record<string, unknown>): void {
    if (!tile.dropdownControl) return;
    const selected = Array.isArray(value) ? value.map(String) : value == null || value === '' ? [] : [String(value)];
    tile.dropdownControl.value = selected;
    // The client-side chart family (bar / line-area / scatter — the cross-filter
    // registry tiles) filters LOCALLY from its cached original rows: the compute
    // API has no reliable recompute for those types, so they'd either never
    // update or get clobbered with unfiltered rows. Track this dropdown's
    // selection (several dropdowns compose) and re-apply.
    const dropdownField = tile.dropdownControl.field ?? '';
    if (selected.length && dropdownField) {
      this.activeDropdownFilters.set(String(tile.componentId ?? ''), { field: dropdownField, values: selected });
    } else {
      this.activeDropdownFilters.delete(String(tile.componentId ?? ''));
    }
    this.applyDropdownFiltersToClientTiles();
    // Registry tiles whose rows carry the dropdown's field are fully handled
    // client-side — keep them OUT of the server target list so a compute
    // response can't overwrite the local filter with unfiltered rows.
    const clientHandledIds = new Set(
      this.dashboardTiles
        .filter((item) => this.isCrossFilterTile(item) && !!dropdownField && this.tileClientRows(item).some((row) => dropdownField in row))
        .map((item) => String(item.componentId ?? '')),
    );
    // Targets = what the build form's "Target chart(s) / input(s)" picker chose.
    //
    // This used to IGNORE the picker and send every chart tile on the dashboard,
    // on the reasoning that a dropdown is a global filter. But the form does
    // offer a target picker, so picking one chart and having two arrive in the
    // payload is simply wrong — the explicit choice has to win.
    //
    // Nothing picked still means "all charts" (the global-filter default), which
    // keeps dropdowns built before the picker existed behaving as they did.
    const configuredTargetIds = new Set(
      (tile.dropdownControl?.targets ?? []).map((id) => String(id ?? '')).filter(Boolean),
    );
    const chartTargets = this.dashboardTiles
      .filter((item) => item.componentId !== tile.componentId
        && !clientHandledIds.has(String(item.componentId ?? ''))
        // Explicit picker choice wins outright, whatever the target's type
        // (a chart OR another dropdown/input) — an explicitly configured
        // target being silently dropped for being "not a chart" is the bug
        // this replaces. No picker selection falls back to the legacy
        // global-filter default of every chart tile.
        && (configuredTargetIds.size
          ? configuredTargetIds.has(String(item.componentId ?? ''))
          : this.isChartTile(item)))
      .map((item) => ({ componentId: item.componentId, label: item.title, field: tile.dropdownControl?.field ?? '' }));
    const payload: Record<string, unknown> = {
      // Generate passes the fresh form payload; an interactive change continues
      // from the tile's own. See the doc comment — mixing these up is what sent
      // a stale config on the second of two calls.
      ...(basePayload ?? tile.payload),
      value: selected,
      filters: this.activeDropdownFilterPayload(),
      targets: chartTargets,
      otherInputs: this.otherActiveInputValues(tile.componentId),
    };
    if (!selected.length) {
      delete payload['value'];
    }
    tile.payload = payload;
    const targetIds = chartTargets.map((target) => String(target.componentId ?? '')).filter(Boolean);
    // No targets: an interactive change has nothing to push, but a GENERATE must
    // still POST — that request is the config registration. A configured custom
    // "API Call" must ALSO still fire: it is the author's own endpoint, a side
    // effect that does not depend on target charts existing (same reasoning as
    // the published view's onInputWidgetChange guard).
    const hasCustomApiCall = !!tile.dropdownControl?.apiConfig?.enabled
      && !!tile.dropdownControl.apiConfig.url?.trim();
    if (!targetIds.length && !basePayload && !hasCustomApiCall) return;
    const hasTargets = targetIds.length > 0;
    if (hasTargets) {
      this.isRecomputing = true;
      this.recomputingComponentIds = new Set(targetIds);
    }
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    this.resolveInputComputeCall(tile.dropdownControl?.apiConfig, url, payload, {
      // Every payload key is available as a token — see the published view's
      // matching call. Explicit keys last so the live ones win.
      ...payload,
      value: selected,
      componentId: tile.componentId,
      field: dropdownField,
    }).subscribe({
      next: (response) => {
        if (hasTargets) this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        // Generate-time failure means the config wasn't saved — surface it,
        // rather than only logging as an interactive push does.
        if (basePayload) this.ui.showError('Dropdown added, but saving its configuration failed.');
        console.warn('[dvm-layout-builder] dropdown value push failed:', error);
      },
    });
  }

  /** Default "API Call" config for a freshly created input-control tile —
   *  disabled, with a starter body template showing the available tokens. */
  private defaultApiCallConfig(): InputApiCallConfig {
    return {
      enabled: false,
      url: '',
      method: 'POST',
      bodyTemplate: '{\n  "value": "{value}",\n  "componentId": "{componentId}"\n}',
      // `replace` keeps the original behaviour for every config saved so far.
      mode: 'replace',
      targets: [],
    };
  }

  /** The custom "API Call" block for an INPUT control's compute payload. The
   *  compute / update-configuration request is what persists a control's
   *  definition, so without this the configured URL/method/body template was
   *  lost the moment the dashboard was reopened — the build form came back
   *  empty and the control silently fell back to the default recompute.
   *  Omitted when nothing was ever configured (unchecked AND no URL), so
   *  untouched controls keep sending exactly the payload they always did. */
  private apiCallConfigPayload(): Record<string, unknown> {
    const config = this.buildApiCallConfig;
    if (!config?.enabled && !config?.url?.trim()) return {};
    return { apiConfig: this.sanitizeApiCallConfig(config) };
  }

  /** Wire shape for an InputApiCallConfig — trimmed URL, normalized method. */
  private sanitizeApiCallConfig(config: InputApiCallConfig): Record<string, unknown> {
    return {
      enabled: !!config.enabled,
      url: config.url?.trim() ?? '',
      method: config.method || 'POST',
      bodyTemplate: config.bodyTemplate ?? '',
      mode: config.mode === 'parallel' ? 'parallel' : 'replace',
      targets: (config.targets ?? []).map(String).filter(Boolean),
    };
  }

  /** Rebuild a full InputApiCallConfig from a persisted blob (the saved compute
   *  payload, or the published widget), filling any key a stored copy predates
   *  from the defaults. Undefined when the control has no API call configured. */
  private restoreApiCallConfig(raw: unknown): InputApiCallConfig | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const source = raw as Record<string, unknown>;
    const fallback = this.defaultApiCallConfig();
    const method = String(source['method'] ?? fallback.method).toUpperCase();
    return {
      enabled: source['enabled'] === true,
      url: String(source['url'] ?? ''),
      method: (['GET', 'POST', 'PUT', 'PATCH'].includes(method)
        ? method
        : fallback.method) as InputApiCallConfig['method'],
      bodyTemplate: String(source['bodyTemplate'] ?? fallback.bodyTemplate),
      // Absent on configs saved before parallel mode existed → 'replace'.
      mode: source['mode'] === 'parallel' ? 'parallel' : 'replace',
      targets: Array.isArray(source['targets'])
        ? (source['targets'] as unknown[]).map(String).filter(Boolean)
        : [],
    };
  }

  /** Chooses between a configured custom "API Call" (URL/method/body
   *  template) and the built-in generate/recompute POST — shared by every
   *  input-control value-change handler (dropdown, slider/input-box, file
   *  upload). When `apiConfig.enabled`, ONLY the custom call fires; its
   *  response is still expected in the same
   *  `{ results: [{ componentId, result }] }` shape applyRecomputeResults
   *  reads, so no separate response-mapping config is needed. */
  private resolveInputComputeCall(
    apiConfig: InputApiCallConfig | undefined,
    fallbackUrl: string,
    fallbackPayload: Record<string, unknown>,
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    if (!apiConfig?.enabled || !apiConfig.url?.trim()) {
      return this.http.post<Record<string, unknown>>(fallbackUrl, fallbackPayload);
    }
    const custom$ = this.customApiCall$(apiConfig, tokens);
    if (apiConfig.mode !== 'parallel') return custom$;

    // PARALLEL mode: the custom endpoint owns `apiConfig.targets`; every other
    // target stays on the built-in compute call. Both requests go out at once
    // (forkJoin) and their `results` arrays are merged, so one target can be
    // served by a microservice while the rest come back from compute as usual.
    const customIds = new Set((apiConfig.targets ?? []).map(String).filter(Boolean));
    const computeTargets = this.targetsExcluding(fallbackPayload, customIds);
    // Nothing left for compute — the custom call already answers for everything.
    if (!computeTargets.length) return custom$;
    const compute$ = this.http.post<Record<string, unknown>>(fallbackUrl, { ...fallbackPayload, targets: computeTargets });
    return forkJoin([custom$, compute$]).pipe(
      map(([customResponse, computeResponse]) => this.mergeComputeResponses(customResponse, computeResponse, customIds)),
    );
  }

  /** The configured custom request on its own (no compute fallback). */
  private customApiCall$(
    apiConfig: { url?: string; method?: string; bodyTemplate?: string },
    tokens: Record<string, unknown>,
  ): Observable<Record<string, unknown>> {
    const method = (apiConfig.method || 'POST').toUpperCase();
    if (method === 'GET') {
      // A GET has no body, and the config UI hides the body editor for it — so
      // previously the control's value never left the browser and the endpoint
      // was called with nothing. Carry the same tokens the body template would
      // have received as query params instead (arrays repeat the key).
      return this.http.get<Record<string, unknown>>(apiConfig.url!, { params: this.buildApiCallParams(tokens) });
    }
    const body = this.buildApiCallBody(apiConfig.bodyTemplate, tokens);
    if (method === 'PUT') return this.http.put<Record<string, unknown>>(apiConfig.url!, body);
    if (method === 'PATCH') return this.http.patch<Record<string, unknown>>(apiConfig.url!, body);
    return this.http.post<Record<string, unknown>>(apiConfig.url!, body);
  }

  /** A compute payload's `targets` minus the ids the custom call already owns. */
  private targetsExcluding(payload: Record<string, unknown>, excluded: Set<string>): Array<Record<string, unknown>> {
    const targets = Array.isArray(payload?.['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [];
    return targets.filter((target) => !excluded.has(String(target?.['componentId'] ?? '')));
  }

  /** Merge a parallel pair of `{ results: [...] }` responses. The custom call is
   *  authoritative for the ids it owns, so any compute entry for one of those is
   *  dropped rather than racing it. Every other key comes from the compute
   *  response (it is the canonical shape) with the custom one layered under it. */
  private mergeComputeResponses(
    customResponse: Record<string, unknown> | null,
    computeResponse: Record<string, unknown> | null,
    customIds: Set<string>,
  ): Record<string, unknown> {
    const entriesOf = (response: Record<string, unknown> | null): Array<Record<string, unknown>> =>
      Array.isArray(response?.['results']) ? (response!['results'] as Array<Record<string, unknown>>) : [];
    const customEntries = entriesOf(customResponse);
    const ownedByCustom = new Set(
      customEntries.map((entry) => String(entry?.['componentId'] ?? '')).filter(Boolean),
    );
    const computeEntries = entriesOf(computeResponse).filter((entry) => {
      const id = String(entry?.['componentId'] ?? '');
      return !ownedByCustom.has(id) && !customIds.has(id);
    });
    return {
      ...(customResponse ?? {}),
      ...(computeResponse ?? {}),
      results: [...customEntries, ...computeEntries],
    };
  }

  /** Fills `{token}` placeholders in a JSON body template.
   *
   *  - `"{value}"` (the token IS the whole JSON string) → the token's native
   *    JSON value, so an array stays an array and a number stays a number.
   *  - `["{value}"]` (bracket-wrapped, array-valued token) → that same array,
   *    NOT a nested one.
   *  - `{value}` embedded in a longer string → stringified (objects as JSON).
   *
   *  Unknown tokens are left as literal text; if that makes the result invalid
   *  JSON the tokens object is sent instead of an empty body, with a warning. */
  private buildApiCallBody(bodyTemplate: string | undefined, tokens: Record<string, unknown>): unknown {
    const template = String(bodyTemplate ?? '').trim() || '{}';
    const filled = template
      // `["{value}"]` — a one-element array whose only item is an ARRAY-valued
      // token — collapses to that array rather than nesting it ([["Sany"]]).
      // A multi-select dropdown's value is already a list, and wrapping the
      // token in brackets is the natural thing to write when the example
      // payload shows `"value": ["Sany"]`, so both spellings must work.
      .replace(/\[\s*"\{(\w+)\}"\s*\]/g, (match, key) =>
        (key in tokens && Array.isArray(tokens[key]) ? JSON.stringify(tokens[key]) : match))
      .replace(/"\{(\w+)\}"/g, (match, key) => (key in tokens ? JSON.stringify(tokens[key]) : match))
      .replace(/\{(\w+)\}/g, (match, key) => {
        if (!(key in tokens)) return match;
        const token = tokens[key];
        // Embedded in a longer string: objects/arrays stringify as JSON (not
        // "[object Object]"), scalars as themselves.
        return token !== null && typeof token === 'object' ? JSON.stringify(token) : String(token ?? '');
      });
    try {
      return JSON.parse(filled);
    } catch {
      // Returning `{}` here silently dropped the whole payload — the endpoint
      // got an empty object and no indication why. A template is invalid JSON
      // mainly when it carries a token this control doesn't supply (it stays as
      // a literal `{foo}`), so fall back to sending the tokens themselves
      // rather than nothing, and say so.
      console.warn('[dvm] custom API body template is not valid JSON after token substitution; sending tokens instead:', filled);
      return { ...tokens };
    }
  }

  /** Query params for a GET custom API call: every token that has a value, with
   *  arrays repeated as multiple entries of the same key. */
  private buildApiCallParams(tokens: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    Object.entries(tokens).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value.forEach((entry) => { params = params.append(key, String(entry)); });
      } else {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  /** A tile that renders a chart (not an input control) — the set a dropdown
   *  filter recomputes when its value changes. */
  private isChartTile(tile: DashboardTile): boolean {
    return tile.layoutType !== 'SLIDER' && tile.layoutType !== 'INPUT_BOX' && tile.layoutType !== 'DROPDOWN' && tile.layoutType !== 'FILE_UPLOAD' && tile.layoutType !== 'POPUP';
  }

  // ── File upload control (preview) — POST the file, re-render targets ────────
  private uploadingTileIds = new Set<string>();
  private tileUploadMessages: Record<string, string> = {};
  isTileUploading(tile: DashboardTile): boolean { return this.uploadingTileIds.has(tile.id); }
  tileUploadMessage(tile: DashboardTile): string { return this.tileUploadMessages[tile.id] || ''; }

  /** A file was picked on a FILE_UPLOAD tile → base64-encode it into a JSON
   *  payload and POST to the compute API (the compute API is JSON-only — a
   *  multipart FormData gives 415), tagged with the tile's targets. Then apply
   *  the per-target results to the matching tiles (same machinery a dropdown
   *  value push uses). */
  onTileFilePicked(tile: DashboardTile, file: File): void {
    if (!tile.fileUploadControl || !file) return;
    const targetIds = (tile.fileUploadControl.targets || []).filter(Boolean);
    this.uploadingTileIds = new Set(this.uploadingTileIds).add(tile.id);
    this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploading ${file.name}…` };
    if (targetIds.length) this.recomputingComponentIds = new Set(targetIds);

    this.fileToBase64(file).then((base64) => {
      // JSON payload (Content-Type application/json) — file as base64 so the
      // JSON-only compute API accepts it.
      const payload: Record<string, unknown> = {
        componentId: tile.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        fileName: file.name,
        fileType: file.type,
        fileContent: base64,
        // The compute API requires every INPUT to carry a `value` (same field
        // dropdowns/sliders send). A file-upload's value IS the file — send the
        // base64 there too so the backend's "No value provided" check passes.
        value: base64,
        defaultValue: base64,
        targets: targetIds.map((componentId) => ({ componentId, label: this.targetLabelForId(componentId) })),
      };
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute');
      this.resolveInputComputeCall(tile.fileUploadControl?.apiConfig, url, payload, {
        value: base64,
        componentId: tile.componentId,
        fileName: file.name,
        fileType: file.type,
      }).subscribe({
        next: (response) => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          if (targetIds.length) this.applyRecomputeResults(response, targetIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Uploaded ${file.name}` };
        },
        error: () => {
          this.uploadingTileIds.delete(tile.id);
          this.uploadingTileIds = new Set(this.uploadingTileIds);
          this.recomputingComponentIds = new Set();
          this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Upload failed for ${file.name}` };
        },
      });
    }).catch(() => {
      this.uploadingTileIds.delete(tile.id);
      this.uploadingTileIds = new Set(this.uploadingTileIds);
      this.recomputingComponentIds = new Set();
      this.tileUploadMessages = { ...this.tileUploadMessages, [tile.id]: `Could not read ${file.name}` };
    });
  }

  /** Read a File as a base64 string (no data-URL prefix — just the base64). */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        // Strip the `data:<mime>;base64,` prefix so only the base64 payload goes.
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /** POST the input control's payload with its new `value` and apply the
   *  per-chart `results` from the response to the matching dashboard tiles.
   *  Non-blocking: the control keeps its value locally regardless. */
  /** componentIds of the tiles a recompute currently in flight will update —
   *  drives `isTileRecomputing()` so only THOSE tiles show a loading overlay
   *  instead of the whole preview canvas. */
  private recomputingComponentIds = new Set<string>();

  /** True when this tile is one of the targets of a recompute currently in
   *  flight — used by the template to show a per-tile loading overlay. */
  isTileRecomputing(tile: DashboardTile): boolean {
    const id = String(tile?.componentId ?? '');
    return !!id && this.recomputingComponentIds.has(id);
  }

  /** Every OTHER input control tile currently on the dashboard, with its live
   *  value — bundled into every single-input recompute call so a target/KPI
   *  formula referencing another input's [Token] resolves against what that
   *  input is showing RIGHT NOW, not a value the backend may have cached from
   *  an earlier call. Mirrors dvm-published-dashboard's otherActiveInputValues. */
  private otherActiveInputValues(excludeComponentId: string): Array<{ componentId: string; title: string; value: unknown }> {
    return this.dashboardTiles
      .filter((item) => !this.isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP' && item.componentId !== excludeComponentId)
      .map((item) => ({
        componentId: item.componentId,
        title: item.title || '',
        value: (item.payload as Record<string, unknown> | undefined)?.['value'] ?? item.inputControl?.value ?? null,
      }))
      .filter((entry) => !!entry.componentId && this.hasMeaningfulInputValue(entry.value));
  }

  private hasMeaningfulInputValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== ''
      && !(Array.isArray(value) && value.length === 0);
  }

  /** SSA $ resolves Price Inflation %/Cost Inflation %'s own cross-referenced
   *  values (DN Price, Total Cost, ...) from whatever the backend last cached
   *  for THEM - but every input tile's compute call is independent, so moving
   *  SSA $ alone never refreshes that cache unless Price Inflation %/Cost
   *  Inflation % have ALSO been (re-)submitted since. Replaying their own
   *  compute call first - with whatever value they currently show, even their
   *  default/zero - keeps every cross-reference SSA $ depends on fresh.
   *  Mirrors dvm-published-dashboard's sideEffectInputsFor. */
  private sideEffectInputsFor(tile: DashboardTile): DashboardTile[] {
    const title = String(tile?.title ?? '').trim().toLowerCase();
    if (title !== 'ssa $') return [];
    const wanted = new Set(['price inflation %', 'cost inflation %']);
    return this.dashboardTiles.filter((item) => !this.isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP'
      && item.componentId !== tile.componentId
      && wanted.has(String(item.title ?? '').trim().toLowerCase()));
  }

  private pushInputValueAndRecompute(tile: DashboardTile, value: number | string): void {
    if (!tile.inputControl) return;
    tile.inputControl.value = value;
    const payload = { ...tile.payload, value, otherInputs: this.otherActiveInputValues(tile.componentId) };
    tile.payload = payload;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    // Only THIS input's own targets may be updated from its recompute response —
    // the compute API can echo back entries for other charts too, and those
    // must be left alone even if they carry real-looking data.
    const payloadTargets = (payload as Record<string, unknown>)['targets'];
    const targetIds = (Array.isArray(payloadTargets) ? (payloadTargets as Array<Record<string, unknown>>) : [])
      .map((t) => String(t?.['componentId'] ?? ''))
      .filter(Boolean);

    const sideEffectTiles = this.sideEffectInputsFor(tile);
    this.isRecomputing = true;
    this.recomputingComponentIds = new Set([...targetIds, ...sideEffectTiles.map((t) => t.componentId)]);

    const runMainCompute = () => this.resolveInputComputeCall(tile.inputControl?.apiConfig, url, payload, {
      value,
      componentId: tile.componentId,
    }).subscribe({
      next: (response) => {
        this.applyRecomputeResults(response, targetIds);
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
      },
      error: (error: HttpErrorResponse) => {
        this.isRecomputing = false;
        this.recomputingComponentIds = new Set();
        console.warn('[dvm-layout-builder] input value push failed:', error);
      },
    });

    if (!sideEffectTiles.length) {
      runMainCompute();
      return;
    }

    // Replay Price Inflation %/Cost Inflation %'s own compute call FIRST -
    // with whatever value they currently show, even default/zero - so their
    // cross-referenced values are freshly cached before SSA $'s own call
    // resolves against them.
    const sideEffectRequests = sideEffectTiles.map((sideTile) => {
      const sidePayload = { ...sideTile.payload, otherInputs: this.otherActiveInputValues(sideTile.componentId) };
      return this.http.post<Record<string, unknown>>(url, sidePayload).pipe(
        map((response) => ({ sideTile, response, error: null as unknown })),
        catchError((error) => of({ sideTile, response: null, error })),
      );
    });

    forkJoin(sideEffectRequests).subscribe((results) => {
      results.forEach(({ sideTile, response, error }) => {
        if (response) {
          const sideTargets = (Array.isArray((sideTile.payload as Record<string, unknown>)?.['targets'])
            ? ((sideTile.payload as Record<string, unknown>)['targets'] as Array<Record<string, unknown>>)
            : []).map((t) => String(t?.['componentId'] ?? '')).filter(Boolean);
          this.applyRecomputeResults(response, sideTargets);
        } else if (error) {
          console.warn('[dvm-layout-builder] SSA $ side-effect recompute for', sideTile.componentId, 'failed:', error);
        }
      });
      runMainCompute();
    });
  }

  /** Apply an input-control recompute response `{ inputComponentId, results:[…] }`
   *  to the dashboard tiles: each result is matched to a tile by `componentId`
   *  (charts carry a fixed, type-derived id — see componentIdForLayout) and its
   *  chart data is rebuilt in place. */
  private applyRecomputeResults(response: Record<string, unknown> | null, targetIds: string[] = []): void {
    const results = (response?.['results'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(results) || !results.length) return;
    const allowedIds = new Set(targetIds);
    let changed = false;
    results.forEach((entry) => {
      // Only apply entries that actually carry data — the compute API returns
      // placeholder entries for untouched target charts, and applying an empty
      // one would blank/load that chart.
      if (!recomputeEntryHasData(entry)) return;
      const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
      const entryComponentId = String(entry?.['componentId'] ?? result?.['componentId'] ?? '').trim();
      const chartType = resolveRecomputeChartType(entry);
      const tile = this.findRecomputeTargetTile(entryComponentId, chartType, allowedIds);
      if (tile && this.applyResultToTile(tile, entry)) changed = true;
    });
    if (changed) {
      // New array ref so the *ngFor tiles re-evaluate their inputs, then nudge
      // ECharts widgets to re-fit.
      this.dashboardTiles = [...this.dashboardTiles];
      this.reflowCharts();
    }
  }

  private findRecomputeTargetTile(entryComponentId: string, chartType: string, allowedIds: Set<string>): DashboardTile | undefined {
    const allTiles = this.allTilesFlat();
    if (entryComponentId && (!allowedIds.size || allowedIds.has(entryComponentId))) {
      const direct = allTiles.find((tile) => tile.componentId === entryComponentId);
      if (direct) return direct;
    }

    if (!chartType) return undefined;
    const candidates = allowedIds.size
      ? allTiles.filter((tile) => allowedIds.has(String(tile.componentId ?? '')))
      : allTiles;
    // Type fallback only when unambiguous — previously returned the FIRST
    // same-type tile, so a dropdown/input driving two same-type charts (or a
    // stale target id) silently drove the WRONG tile. Refuse to guess when
    // more than one candidate matches. (Mirrors findRecomputeTargetWidget in
    // the published view.)
    const sameType = candidates.filter((tile) => tile.layoutType === chartType);
    return sameType.length === 1 ? sameType[0] : undefined;
  }

  /** Rebuild ONE tile's chart data from a recompute result entry
   *  `{ componentId, label, result: { chartType, data, … } }`, reusing the same
   *  builders the initial generate uses. Returns true if the tile was updated. */
  private applyResultToTile(tile: DashboardTile, entry: Record<string, unknown>): boolean {
    const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
    if (tile.layoutType === 'DROPDOWN' && resolveRecomputeChartType(entry) === 'INPUT') {
      // A dropdown target's INPUT result is a cascading refresh of its
      // SELECTABLE OPTIONS (`result.data: [{value}, ...]`), not a scalar
      // value — treating it as a value wrote the raw object array into
      // `dropdownControl.value` (rendering as "[object Object]") and never
      // touched `options`, the field the template actually renders choices
      // from, so the dropdown never visibly updated.
      if (!tile.dropdownControl) return false;
      const rawOptions = Array.isArray(result['data']) ? (result['data'] as unknown[]) : [];
      const options = rawOptions
        .map((item) => (item && typeof item === 'object' ? String((item as Record<string, unknown>)['value'] ?? '') : String(item ?? '')))
        .filter(Boolean);
      // Drop any previously selected value no longer offered by the
      // refreshed option set, same as a native cascading filter.
      const value = tile.dropdownControl.value.filter((v) => options.includes(v));
      tile.payload = { ...tile.payload, value };
      tile.dropdownControl = { ...tile.dropdownControl, options, value };
      return true;
    }
    // A target that is itself an input control (slider/input box) comes back
    // as `componentType: "INPUT"` — it just needs its live value pushed in,
    // not rebuilt like a chart's data.
    if ((tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX') && resolveRecomputeChartType(entry) === 'INPUT') {
      const value = this.extractKpiValue(result);
      if (value === null || !tile.inputControl) return false;
      tile.payload = { ...tile.payload, value };
      // The rendered widget reads from `inputControl.widgetData`, NOT
      // `tile.payload`/`inputControl.value` — those must get a NEW widgetData
      // reference too, or app-mat-slider/app-input-box's [data] input never
      // sees a change and the displayed value never updates.
      const isSlider = tile.layoutType === 'SLIDER';
      const widgetData = tile.inputControl.widgetData || {};
      tile.inputControl = {
        ...tile.inputControl,
        value,
        widgetData: isSlider
          ? { ...widgetData, data: { ...(widgetData['data'] as Record<string, unknown> | undefined), data: { ...((widgetData['data'] as Record<string, unknown> | undefined)?.['data'] as Record<string, unknown> | undefined), rangeStart: value, rangeEnd: value } } }
          : { ...widgetData, value },
      };
      return true;
    }
    const chartType = String(result?.['chartType'] ?? tile.layoutType ?? '');
    const format = (tile.format as Record<string, unknown>) || this.defaultTileFormat(tile.layoutType);
    switch (chartType) {
      case 'WATERFALL': {
        const rawBars = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: WaterfallBar[] })?.data || [])) as WaterfallBar[];
        const bars = this.applyStepSignsToWaterfallBars(rawBars, tile.payload);
        tile.waterfallBars = bars;
        tile.previewOption = this.buildWaterfallPreviewOption(bars, format);
        tile.waterfallData = this.buildWaterfallTileData(tile);
        return true;
      }
      case 'DRILL_DOWN_TABLE': {
        const drillData = this.buildDrillDownWidgetData(result, format);
        drillData['widgetId'] = 'dvm-tile-drill-' + tile.id;
        tile.drillData = drillData;
        return true;
      }
      case 'HORIZONTAL_BAR_CHART': {
        const hbData = this.buildHbWidgetData(result, tile.id, tile.format);
        // Keep the tile's own title (the builders read the ACTIVE builder state).
        hbData['title'] = tile.title;
        tile.hbData = hbData;
        return true;
      }
      case 'KPI': {
        tile.kpiData = this.buildKpiWidgetData(result, tile.id, tile.format);
        return true;
      }
      case 'BAR': {
        // Server-computed envelope (`data[0].bar`, pre-aggregated
        // {department, value} rows) → rebuild the widget with remapped
        // capabilities, mirroring the LINE_AREA case below.
        const envRows = extractBarComputeRows(result['responseList']) ?? extractBarComputeRows(result);
        if (envRows?.length && tile.barData) {
          tile.barData = buildBarComputeWidgetData(result, {
            widgetId: 'dvm-tile-bar-' + tile.id,
            title: tile.title,
            capabilities: (tile.barData['capabilities'] as Record<string, unknown> | undefined) ?? {},
            payload: (tile.payload as Record<string, unknown>) ?? {},
            format: tile.format,
          });
          tile.barRows = envRows;
          return true;
        }
        // A recompute returns fresh rows — keep the tile's existing capabilities,
        // just swap in the new rows so the client-side render updates.
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.barData && this.rowsFitClientChart(rows, tile.barData, 'category')) {
          tile.barData = { ...tile.barData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'LINE_AREA': {
        // Server-computed envelope (`data[0].lineArea`) → rebuild the widget
        // data with remapped capabilities (rows are pre-aggregated
        // {department, value}, not the configured dataset columns).
        const envRows = extractLineAreaComputeRows(result['responseList']) ?? extractLineAreaComputeRows(result);
        if (envRows?.length && tile.lineAreaData
          && lineAreaRowsFitCapabilities(envRows, tile.lineAreaData['capabilities'] as Record<string, unknown> | undefined)) {
          tile.lineAreaData = buildLineAreaComputeWidgetData(result, {
            widgetId: 'dvm-tile-line-area-' + tile.id,
            title: tile.title,
            capabilities: (tile.lineAreaData['capabilities'] as Record<string, unknown> | undefined),
            format: tile.format,
          });
          tile.lineAreaRows = null; // server-computed → not client-filterable
          return true;
        }
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.lineAreaData && this.rowsFitClientChart(rows, tile.lineAreaData, 'category')) {
          tile.lineAreaData = { ...tile.lineAreaData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'SCATTER_BUBBLE': {
        const rows = (Array.isArray(result['data'])
          ? result['data']
          : ((result['data'] as { data?: unknown })?.data ?? [])) as Array<Record<string, unknown>>;
        if (tile.scatterBubbleData && this.rowsFitClientChart(rows, tile.scatterBubbleData, 'xField')) {
          tile.scatterBubbleData = { ...tile.scatterBubbleData, staticData: Array.isArray(rows) ? rows : [] };
          return true;
        }
        return false;
      }
      case 'TABLE_MATRIX': {
        // A file-upload (or other input) drove this table target — swap in the
        // response rows while PRESERVING the table's existing capabilities so it
        // re-renders with the new data. Drill-Down tables carry a tree instead.
        const rows = this.extractTableMatrixRows(result);
        const existing = (tile.tableMatrixData as Record<string, unknown>) || {};
        const caps = existing['capabilities'] as Record<string, unknown> | undefined;
        const isDrill = !!caps && (caps['tableType'] === 'drilldown' || (caps['typeFlags'] as Record<string, unknown> | undefined)?.['drilldown'] === true);
        if (isDrill) {
          // Client-mode drill-down: the result is FLAT rows → rebuild the tree /
          // pivot / formulas from the tile's saved config first.
          const source = caps['drillDataMode'] === 'client'
            ? this.buildDrillClientTreeResponse(rows, this.drillClientConfigFromCaps(caps))
            : result;
          const drilldownData = this.buildDrillDownWidgetData(source, format);
          drilldownData['widgetId'] = 'dvm-tile-table-matrix-' + tile.id + '-drill';
          tile.tableMatrixData = { ...existing, drilldownData };
        } else {
          tile.tableMatrixData = { ...existing, staticData: rows };
        }
        return true;
      }
      default:
        return false;
    }
  }

  /** A recompute row set fits a CLIENT-SIDE chart only when it carries the field
   *  the chart renders its axis from (capabilities[fieldKey]). The compute API
   *  can answer a dropdown/input push with rows in a DIFFERENT shape (e.g.
   *  {department, value}) — swapping those into staticData blanked the chart,
   *  since none of its configured columns exist on them. Empty rows pass (a
   *  legitimately-empty filter result must still render). */
  private rowsFitClientChart(
    rows: Array<Record<string, unknown>>,
    data: Record<string, unknown>,
    fieldKey: 'category' | 'xField',
  ): boolean {
    if (!Array.isArray(rows) || !rows.length) return true;
    const field = String((data['capabilities'] as Record<string, unknown> | undefined)?.[fieldKey] ?? '');
    return !field || rows.some((row) => field in row);
  }

  /** Pull the `horizontalBar` array out of either envelope shape. Accepts the
   *  raw response, or its `responseList` array, and digs through `data[0]`. */
  private extractHorizontalBarRows(source: unknown): Array<Record<string, unknown>> | null {
    const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
    const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
    const bar = dataArr?.[0]?.['horizontalBar'];
    return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
  }

  /** Builds the shared DrillDownComponent's `data` input from an ALREADY-fetched
   *  compute response. The tree/header/editable-cell construction lives in the
   *  SHARED buildDrillDownWidgetData (self-service-recompute.utils) so the
   *  published dashboard's recompute path produces exactly what the builder
   *  bakes at publish time — this wrapper only layers on the builder-only
   *  extras (title, visual rules, export/print toggles, year filter).
   *
   *  Columns are derived from the RESPONSE, not from the builder inputs — the
   *  response's actual depth + measure keys are what must render. The configured
   *  dimensions/measures only contribute their Editable flags (levels whose
   *  labels become text inputs; measures whose leaf cells become inputs). */
  private buildDrillDownWidgetData(response: Record<string, unknown> | null, format?: Record<string, unknown>): Record<string, unknown> {
    const shared = buildDrillDownWidgetData(response, {
      widgetId: 'dvm-layout-builder-drilldown-preview',
      fallbackHeaderName: this.drillDownDimensions[0]?.label,
      format,
      dimensions: this.drillDownDimensions,
      measures: this.drillDownMeasures,
      // Merged drill-down + pivot: formula columns evaluated per tree node (a
      // client-built tree already carries them; a backend tree gets them here).
      computedColumns: this.drillComputedColumns,
      formulaEvaluator: (expression, names) => this.formulaService.buildRowEvaluator(expression, names),
    });
    const measureKeys = ((shared['headers'] as Array<{ prop: string; expandabile?: boolean }>) || [])
      .filter((header) => !header.expandabile)
      .map((header) => header.prop);
    const fmt = format || {};
    return {
      ...shared,
      title: this.title.trim(),
      // Level → dataset field, so an editable-label rename reports the field
      // (always present here — the builder knows every level's field).
      levelFields: shared['levelFields'] ?? {},
      // ── Visual + reporting capabilities (config-driven; DrillDownComponent
      // reads these off `data`). Per-column visual rules travel in `visualRules`
      // (built from the Format popup / defaults). ──
      // `=== true` like its siblings — with `!== false` this forwarded `true` for
      // every tile, so the component's own default could never take effect.
      enableExpandCollapseAll: fmt['enableExpandCollapseAll'] === true,
      enableExport: fmt['enableExport'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      enableSavedViews: fmt['enableSavedViews'] === true,
      showColumnDividers: fmt['showColumnDividers'] === true,
      // Editable measures: whether an edit re-sums parents + the grand total
      // client-side (default on; `!== false` so tiles saved before this
      // control existed keep the recalculating behaviour).
      recalcTotalsOnEdit: fmt['drillEditRecalcTotals'] !== false,
      // Latest-N-years column filter ("Filters" accordion — shared with the
      // whole table family). Keys land on the widget data only when the toggle
      // is ON so older tiles stay byte-identical; the component evaluates the
      // window at RENDER time, so published tiles roll into the new year
      // automatically.
      ...this.yearFilterConfig(fmt),
      visualRules: this.buildDrillDownVisualRules(measureKeys, fmt),
      // Level-2 text colour grouping ("Colour level 2 by"). Keys land on the
      // widget data only when a mode is chosen so older tiles stay
      // byte-identical; the component resolves the groups at render time.
      ...this.level2ColorConfig(fmt),
    };
  }

  /** Drill-Down level-2 colour grouping from a tile's format — empty while
   *  the mode is none/unset. */
  private level2ColorConfig(fmt: Record<string, unknown>): Record<string, unknown> {
    const mode = String(fmt['drillLevel2ColorBy'] || '').trim();
    return !mode || mode === 'none' ? {} : { level2ColorBy: mode };
  }

  /** Latest-N-years column filter ("Filters" accordion, whole table family):
   *  the resolved count/base from a tile's format, or undefined while the
   *  toggle is off. */
  private yearFilterCountFrom(fmt: Record<string, unknown>): number | undefined {
    if (fmt['yearFilter'] !== true) return undefined;
    const n = Number(fmt['yearFilterCount']);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 3;
  }
  private yearFilterBaseFrom(fmt: Record<string, unknown>): 'data' | 'current' | undefined {
    if (fmt['yearFilter'] !== true) return undefined;
    // Default 'data' — "latest N" means the newest years PRESENT in the data
    // (fiscal/planning data carries future years: 2024–2028 with count 3 must
    // show 2026/2027/2028). 'current' opts into anchoring at the system year,
    // hiding future years.
    return fmt['yearFilterBase'] === 'current' ? 'current' : 'data';
  }
  /** Explicit year COLUMN for the row-level filter (blank = auto-detect). */
  private yearFilterFieldFrom(fmt: Record<string, unknown>): string | undefined {
    if (fmt['yearFilter'] !== true) return undefined;
    return String(fmt['yearFilterField'] ?? '').trim() || undefined;
  }
  /** The same pair as a spreadable object — empty while the toggle is off, so
   *  tiles that never enabled it stay byte-identical. */
  private yearFilterConfig(fmt: Record<string, unknown>): Record<string, unknown> {
    const count = this.yearFilterCountFrom(fmt);
    return count === undefined ? {} : { yearFilterCount: count, yearFilterBase: this.yearFilterBaseFrom(fmt) };
  }

  /** Assemble per-column visual rules (data bar / heat map / conditional / …)
   *  for the drill-down from the Format popup. A single "Apply to all measures"
   *  visual can be chosen (`drillVisualKind`), or left as plain numbers. All
   *  config-driven — no hardcoded columns. */
  private buildDrillDownVisualRules(measureKeys: string[], fmt: Record<string, unknown>): Record<string, unknown> {
    const kind = String(fmt['drillVisualKind'] || '').trim();
    if (!kind || kind === 'none') return {};
    const color = String(fmt['drillVisualColor'] || '#3b82f6');
    const rules: Record<string, unknown> = {};
    measureKeys.forEach((key) => { rules[key] = { kind, color }; });
    return rules;
  }

  // ── Table & Matrix widget data ─────────────────────────────────────────────

  /** Build the `[data]` object for <app-unified-table-matrix-chart>. ONE family,
   *  three types: Drill-Down reuses the drill-down tree widget under
   *  `drilldownData` (the component passes it to <lib-drill-down>); Normal /
   *  Editable get flat rows under `staticData` for the client-side buildTableModel.
   *  All styling/behaviour lives in `capabilities` (config-driven). */
  private buildTableMatrixWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    const capabilities = this.buildTableMatrixCapabilities(format);
    const widgetId = 'dvm-tile-table-matrix-' + tileId;
    if (this.isTmDrilldown) {
      const drilldownData = this.buildDrillDownWidgetData(response, format);
      drilldownData['widgetId'] = widgetId + '-drill';
      const toggleView = this.buildTmToggleView();
      return {
        widgetId,
        title: this.title.trim(),
        type: 'table-matrix',
        capabilities,
        drilldownData,
        ...(toggleView ? { toggleView } : {}),
      };
    }
    // Normal / Editable — flat rows from the compute response envelope.
    const rows = this.extractTableMatrixRows(response);
    const toggleView = this.buildTmToggleView();
    return {
      widgetId,
      title: this.title.trim(),
      type: 'table-matrix',
      capabilities,
      staticData: rows,
      ...(toggleView ? { toggleView } : {}),
    };
  }

  /** Assemble the config-driven `capabilities` object for the table-matrix tile
   *  from the active table type + the tile's format selections + config defaults.
   *  Every value is config-sourced (nothing baked in). */
  private buildTableMatrixCapabilities(format?: Record<string, unknown>): TableMatrixCapabilities {
    const fmt = format || {};
    const options = (this.layout.options as Record<string, unknown> | undefined) || {};
    const chartDefaults = (options['tableMatrixChartDefaults'] as Record<string, unknown>) || {};
    const typeFlags = this.tmFlagsFor(this.tmType);

    const caps: TableMatrixCapabilities = {
      tableType: this.tmType,
      typeFlags,
      chartDefaults: chartDefaults as TableMatrixCapabilities['chartDefaults'],
      // ── Formatting (from the tile Format popup, config-declared controls) ──
      showValues: fmt['showValues'] !== false,
      numberFormat: fmt['numberFormat'] as string | undefined,
      decimals: fmt['decimals'] != null ? Number(fmt['decimals']) : undefined,
      density: (fmt['density'] as string) || 'comfortable',
      striped: fmt['striped'] !== false,
      headerBackground: fmt['headerBackground'] as string | undefined,
      headerColor: fmt['headerColor'] as string | undefined,
      // ── Columns / interaction ──
      stickyHeader: fmt['stickyHeader'] !== false,
      stickyFooter: fmt['stickyFooter'] === true,
      freezeFirstColumn: fmt['freezeFirstColumn'] === true,
      freezeColumnCount: fmt['freezeColumnCount'] != null ? Number(fmt['freezeColumnCount']) : undefined,
      freezeRowCount: fmt['freezeRowCount'] != null ? Number(fmt['freezeRowCount']) : undefined,
      enableColumnReorder: fmt['enableColumnReorder'] === true,
      enableColumnResize: fmt['enableColumnResize'] === true,
      virtualScroll: fmt['virtualScroll'] === true,
      pageSize: fmt['pageSize'] != null ? Number(fmt['pageSize']) : undefined,
      enableSearch: fmt['enableSearch'] !== false,
      enableColumnSearch: fmt['enableColumnSearch'] === true,
      enableAdvancedFilter: fmt['enableAdvancedFilter'] === true,
      enableRefresh: fmt['enableRefresh'] === true,
      enableSavePrefs: fmt['enableSavePrefs'] === true,
      enableExcelExport: fmt['enableExcelExport'] === true,
      enablePdfExport: fmt['enablePdfExport'] === true,
      enablePrint: fmt['enablePrint'] === true,
      rowSelection: (fmt['rowSelection'] as string) || 'single',
      crossFilter: fmt['crossFilter'] === true,
      enableExport: fmt['enableExport'] !== false,
      // Stable default row order (Format popup → Interaction accordion).
      defaultSort: (fmt['defaultSort'] as string) || undefined,
      defaultSortDir: fmt['defaultSortDir'] === 'desc' ? 'desc' : undefined,
      // Explicit row order for the "Default sort" column (e.g. "K, Q, G" on
      // Engine Model) + an alphabetical fallback for whatever's unlisted —
      // same "Value order"/"Sort" pair used by Drill-Down/Pivot.
      rowValueOrder: (() => { const o = this.parseValueOrder(fmt['rowValueOrder'] as string); return o.length ? o : undefined; })(),
      rowValueOrderSort: (fmt['rowValueOrderSort'] as 'custom' | 'alpha-asc' | 'alpha-desc') || undefined,
      // ── Latest-N-years column filter (Filters accordion, ALL table types;
      // the drill-down additionally gets it on drilldownData for lib-drill-down).
      yearFilterCount: this.yearFilterCountFrom(fmt),
      yearFilterBase: this.yearFilterBaseFrom(fmt),
      yearFilterField: this.yearFilterFieldFrom(fmt),
      // ── Totals (Normal) ──
      showSubtotals: fmt['showSubtotals'] === true,
      showGrandTotal: fmt['showGrandTotal'] === true,
      // ── Editable (Application Table) — client-side CRUD + change-tracking ──
      // Row-level CRUD is EXCLUSIVE to the Editable type: a Normal table with
      // per-column cell editing (caps.cellEditing) gets inputs + Save/Cancel +
      // undo, but must never grow Add/Insert/Delete/Duplicate row buttons.
      editMode: (fmt['editMode'] as 'cell' | 'row') || 'cell',
      enableRowAdd: this.isTmEditable && fmt['enableRowAdd'] !== false,
      enableRowInsert: this.isTmEditable && fmt['enableRowInsert'] !== false,
      enableRowDelete: this.isTmEditable && fmt['enableRowDelete'] !== false,
      enableSoftDelete: this.isTmEditable && fmt['enableSoftDelete'] === true,
      enableRowDuplicate: this.isTmEditable && fmt['enableRowDuplicate'] !== false,
      enableCopyPaste: this.isTmEditable && fmt['enableCopyPaste'] === true,
      enableUndoRedo: fmt['enableUndoRedo'] !== false,
      showRowState: fmt['showRowState'] !== false,
      enableValidation: fmt['enableValidation'] !== false,
      enableRowSave: fmt['enableRowSave'] === true,
      enableBulkEdit: fmt['enableBulkEdit'] === true,
      enableAutoSave: fmt['enableAutoSave'] === true,
      autoSaveDebounceMs: fmt['autoSaveDebounceMs'] != null ? Number(fmt['autoSaveDebounceMs']) : undefined,
      saveOnRowExit: fmt['saveOnRowExit'] === true,
      // ── Backend CRUD (generic config-driven contract) ──
      saveUrl: (fmt['saveUrl'] as string) || undefined,
      bulkSaveUrl: (fmt['bulkSaveUrl'] as string) || undefined,
      createUrl: (fmt['createUrl'] as string) || undefined,
      updateUrl: (fmt['updateUrl'] as string) || undefined,
      deleteUrl: (fmt['deleteUrl'] as string) || undefined,
      enableOptimisticLock: fmt['enableOptimisticLock'] === true,
      versionField: (fmt['versionField'] as string) || undefined,
      transactional: fmt['transactional'] === true,
      payloadTemplates: (fmt['payloadTemplates'] as TableMatrixCapabilities['payloadTemplates']) || undefined,
      // ── Workflow ──
      enableWorkflow: fmt['enableWorkflow'] === true,
      workflowUrl: (fmt['workflowUrl'] as string) || undefined,
      enableComments: fmt['enableComments'] === true,
      enableAttachments: fmt['enableAttachments'] === true,
      enableAuditTrail: fmt['enableAuditTrail'] === true,
      // ── File upload (file → compute API → table re-renders) ──
      enableUpload: fmt['enableUpload'] === true,
      uploadButtonLabel: (fmt['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (fmt['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (fmt['uploadUrl'] as string) || undefined,
      // Context so the upload knows which table + where to POST (works in the
      // published view too, where these are baked into the widget capabilities).
      componentId: this.componentId,
      dataset: this.drillDataset || undefined,
      computeUrl: resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/components/compute'),
    };

    // ── Navigation (cell → target dashboard) — from the builder form ──
    // Written whenever navigation is ENABLED (target dashboard optional) so the
    // component shows the button + clickable cells immediately; the button stays
    // disabled until a target is picked (component's canNavigate/navHasTarget).
    if (this.tmNavEnabled) {
      caps.navigation = {
        enabled: true,
        filterKey: this.tmNavFilterKey || undefined,
        navigableColumns: this.tmNavColumns.length ? [...this.tmNavColumns] : undefined,
        targetDashboard: this.tmNavTargetDashboard || undefined,
        targetComponents: this.tmNavTargetComponents.length ? [...this.tmNavTargetComponents] : [],
        buttonLabel: this.tmNavButtonLabel || 'Navigate',
      };
    }

    // Normal / Editable: pick the chosen columns (empty = all); each column
    // carries its own editable flag + input control, so a table can mix
    // editable and read-only columns. A NORMAL table with at least one column
    // marked editable turns on `cellEditing` (inputs + Save/Cancel, no row
    // CRUD). Group-by + aggregation apply to Normal only.
    if (!this.isTmDrilldown) {
      const cols = this.tmColumnEntries();
      if (cols.length) {
        caps.columns = this.tmCapabilityColumns(cols);
      }
      if (this.isTmNormal && cols.some((column) => column.editable === true && column.control)) {
        caps.cellEditing = true;
      }
      if (this.isTmNormal) {
        if (this.tmGroupBy) caps.groupBy = this.tmGroupBy;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'none';
      }
      // ── New shape types map their own per-type config ──
      if (this.isTmMatrix) {
        caps.rowField = this.tmRowField || undefined;
        caps.columnField = this.tmColumnField || undefined;
        caps.valueField = this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
      }
      if (this.isTmPivot) {
        caps.rowFields = this.tmRowFields.length ? [...this.tmRowFields] : undefined;
        caps.columnFields = this.tmColumnFields2.length ? [...this.tmColumnFields2] : undefined;
        // Multi-measure pivot: every picked measure fans out per column value.
        // valueField mirrors the first pick for backward compatibility.
        caps.valueFields = this.tmValueFields.length ? [...this.tmValueFields] : undefined;
        caps.valueField = this.tmValueFields[0] || this.tmValueField || undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
        const pivotColOrder = this.parseValueOrder(this.tmPivotColumnOrderText);
        caps.pivotColumnValueOrder = pivotColOrder.length ? pivotColOrder : undefined;
        caps.pivotColumnValueOrderSort = this.tmPivotColumnOrderSort || undefined;
      }
      if (this.isTmGrouped) {
        caps.groupByFields = this.tmGroupByFields.length ? [...this.tmGroupByFields] : undefined;
        caps.aggregation = (this.tmAggregation as TableMatrixCapabilities['aggregation']) || 'sum';
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmTreegrid) {
        caps.treeIdField = this.tmTreeIdField || undefined;
        caps.treeParentField = this.tmTreeParentField || undefined;
        caps.groupsExpanded = this.tmGroupsExpanded;
      }
      if (this.isTmFinancial) {
        caps.roleField = this.tmRoleField || undefined;
        caps.indentField = this.tmIndentField || undefined;
      }
    } else {
      // Drill-Down type: surface the dimensions/measures on capabilities too
      // (the tree render uses drilldownData, but keep the model for save/debug).
      caps.dimensions = this.drillDownDimensions.map((d) => ({
        field: d.field,
        label: d.label,
        editable: !!d.editable,
        ...this.dropdownEditorPayload(d),
        ...(d.dataset ? { dataset: d.dataset } : {}),
        ...this.valueOrderPayload(d),
      }));
      caps.measures = [
        ...this.drillDownMeasures.map((m) => ({
          field: m.field,
          label: m.label,
          aggregation: m.aggregation,
          rollUpStrategy: m.rollUpStrategy,
          total: !!m.total,
          editable: !!m.editable,
          ...this.dropdownEditorPayload(m),
          ...this.editableLevelsPayload(m),
          ...(m.dataset ? { dataset: m.dataset } : {}),
          ...(m.pivot === false ? { pivot: false } : {}),
          ...(m.kind === 'text' ? { kind: 'text' as const } : {}),
          ...(m.format === 'percent' ? { format: 'percent' as const } : {}),
          ...(m.percentScale === 'fraction' ? { percentScale: 'fraction' as const } : {}),
          ...(m.hideNegative ? { hideNegative: true } : {}),
          ...(typeof m.decimals === 'number' ? { decimals: m.decimals } : {}),
          ...(m.hidden ? { hidden: true } : {}),
          ...this.rowTotalHeaderPayload(m),
          ...this.yearScopePayload(m),
          ...this.groupFieldPayload(m),
        })),
        // Hidden year-column helpers so a recompute's flat rows carry the year.
        ...this.drillYearHelperMeasures(),
      ];
      // Merged Drill-Down + Pivot config, kept on the capabilities so the
      // recompute paths (published inputs / file upload) can rebuild the tree
      // from flat rows without the builder form. Only when in use — legacy
      // tiles stay byte-identical.
      if (this.drillDataMode === 'client') {
        caps.drillDataMode = 'client';
        if (this.drillColumnDimensions.length) {
          caps.columnDimensions = this.drillColumnDimensions.map((d) => ({
            field: d.field,
            ...(d.label ? { label: d.label } : {}),
            ...(d.dataset ? { dataset: d.dataset } : {}),
            ...this.valueOrderPayload(d),
          }));
        }
      }
      caps.computedColumns = this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) }));
      if (this.drillColumnOrder.length) caps.columnOrder = [...this.drillColumnOrder];
      // The design preset rides the tile so the published view (which has no
      // config access) applies the same default order on every recompute.
      if (this.drillColumnOrderPreset.length) caps.columnOrderPreset = [...this.drillColumnOrderPreset];
      if (this.drillPivotValueOrderPreset.length) caps.columnValueOrderPreset = [...this.drillPivotValueOrderPreset];
      if (this.drillRowTotalColumnsPreset.length) caps.rowTotalColumns = [...this.drillRowTotalColumnsPreset];
      if (fmt['drillRawLeafRows'] === true) caps.rawLeafRows = true;
      // Column-value-major header rides the tile so the published recompute
      // rebuilds the same "Mining | Rail | Marine" band layout.
      // Always baked (both values): the table applies it at render time, so
      // the layout flips live on a format change and survives a re-publish
      // without a regenerate.
      const headerGroupBy = this.drillHeaderGroupByFrom(fmt);
      if (headerGroupBy) caps.headerGroupBy = headerGroupBy;
      // Totals placement (+ the row-total keys it implies) ride the tile so the
      // published recompute rebuilds the same totals and the table knows
      // whether to show the bottom total row.
      caps.totalsPlacement = this.drillTotalsPlacement;
      Object.assign(caps, this.drillRowTotalConfig(this.drillTotalsPlacement, fmt));
    }
    return caps;
  }

  /** Merge only the Format-popup styling/behaviour keys onto an EXISTING
   *  capabilities object — leaves columns/dimensions/measures/tableType intact.
   *  Used by onTileFormatChange so a format edit never disturbs the tile's data
   *  shape. Every key mirrors buildTableMatrixCapabilities' format reads. */
  private applyTableMatrixFormatToCapabilities(
    caps: Record<string, unknown>,
    format: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      ...caps,
      showValues: format['showValues'] !== false,
      numberFormat: format['numberFormat'],
      decimals: format['decimals'] != null ? Number(format['decimals']) : caps['decimals'],
      density: (format['density'] as string) || 'comfortable',
      striped: format['striped'] !== false,
      headerBackground: format['headerBackground'],
      headerColor: format['headerColor'],
      // Tile-wide base font (shared Formatting controls; 0/'' = keep defaults).
      fontFamily: (format['fontFamily'] as string) || undefined,
      fontSize: Number(format['fontSize']) > 0 ? Number(format['fontSize']) : caps['fontSize'],
      stickyHeader: format['stickyHeader'] !== false,
      stickyFooter: format['stickyFooter'] === true,
      freezeFirstColumn: format['freezeFirstColumn'] === true,
      freezeColumnCount: format['freezeColumnCount'] != null ? Number(format['freezeColumnCount']) : caps['freezeColumnCount'],
      freezeRowCount: format['freezeRowCount'] != null ? Number(format['freezeRowCount']) : caps['freezeRowCount'],
      enableColumnReorder: format['enableColumnReorder'] === true,
      enableColumnResize: format['enableColumnResize'] === true,
      virtualScroll: format['virtualScroll'] === true,
      pageSize: format['pageSize'] != null ? Number(format['pageSize']) : caps['pageSize'],
      enableSearch: format['enableSearch'] !== false,
      enableColumnSearch: format['enableColumnSearch'] === true,
      enableAdvancedFilter: format['enableAdvancedFilter'] === true,
      enableRefresh: format['enableRefresh'] === true,
      enableSavePrefs: format['enableSavePrefs'] === true,
      enableExcelExport: format['enableExcelExport'] === true,
      enablePdfExport: format['enablePdfExport'] === true,
      enablePrint: format['enablePrint'] === true,
      rowSelection: (format['rowSelection'] as string) || 'single',
      crossFilter: format['crossFilter'] === true,
      enableExport: format['enableExport'] !== false,
      // Stable default row order — mirrors buildTableMatrixCapabilities.
      defaultSort: (format['defaultSort'] as string) || undefined,
      defaultSortDir: format['defaultSortDir'] === 'desc' ? 'desc' : undefined,
      rowValueOrder: (() => { const o = this.parseValueOrder(format['rowValueOrder'] as string); return o.length ? o : undefined; })(),
      rowValueOrderSort: (format['rowValueOrderSort'] as 'custom' | 'alpha-asc' | 'alpha-desc') || undefined,
      // Latest-N-years column filter — mirrors buildTableMatrixCapabilities
      // (undefined turns the filter off when the toggle is unchecked).
      yearFilterCount: this.yearFilterCountFrom(format),
      yearFilterBase: this.yearFilterBaseFrom(format),
      yearFilterField: this.yearFilterFieldFrom(format),
      showSubtotals: format['showSubtotals'] === true,
      showGrandTotal: format['showGrandTotal'] === true,
      groupsExpanded: format['groupsExpanded'] !== false,
      rawLeafRows: format['drillRawLeafRows'] === true,
      headerGroupBy: this.drillHeaderGroupByFrom(format),
      // Row-total label follows the Format popup live; the block itself (rowTotal /
      // totalsPlacement) is build-form state preserved via ...caps.
      ...(caps['rowTotal'] === true && String(format['drillRowTotalLabel'] ?? '').trim()
        ? { rowTotalLabel: String(format['drillRowTotalLabel']).trim() }
        : {}),
      // File upload knobs (componentId/dataset/computeUrl are preserved via ...caps).
      enableUpload: format['enableUpload'] === true,
      uploadButtonLabel: (format['uploadButtonLabel'] as string) || 'Upload',
      uploadAccept: (format['uploadAccept'] as string) || '.xlsx,.xls,.csv',
      uploadUrl: (format['uploadUrl'] as string) || caps['uploadUrl'],
      // ── Backend CRUD + per-endpoint request bodies ──
      // These live in the Format popup's CRUD group, so a tile edit MUST carry
      // them through or the change silently does nothing. Each falls back to the
      // existing capability so a value set on the builder form (where `format`
      // may not hold the key at all) is never blanked out by a format edit.
      saveUrl: (format['saveUrl'] as string) || caps['saveUrl'],
      bulkSaveUrl: (format['bulkSaveUrl'] as string) || caps['bulkSaveUrl'],
      createUrl: (format['createUrl'] as string) || caps['createUrl'],
      updateUrl: (format['updateUrl'] as string) || caps['updateUrl'],
      deleteUrl: (format['deleteUrl'] as string) || caps['deleteUrl'],
      versionField: (format['versionField'] as string) || caps['versionField'],
      enableOptimisticLock: format['enableOptimisticLock'] != null ? format['enableOptimisticLock'] === true : caps['enableOptimisticLock'],
      transactional: format['transactional'] != null ? format['transactional'] === true : caps['transactional'],
      payloadTemplates: format['payloadTemplates'] ?? caps['payloadTemplates'],
    };
  }

  /** The compute payload for a Normal/Editable table. The backend only computes
   *  tables from non-empty `dimensions` + `measures` arrays (tree response) —
   *  so: numeric columns become measures (with the form's aggregation), the
   *  rest become dimension levels in pick order. Group-by mode collapses the
   *  dimensions to just the group column; raw-rows ('none') mode keeps every
   *  non-numeric column as a level so the tree's leaf paths ARE the table rows.
   *  Labels are the raw field names so the flattened row keys line up with
   *  `capabilities.columns`. */
  private buildTableMatrixComputePayload(): Record<string, unknown> {
    // Normal / Editable now use the FLAT table contract: send the chosen columns
    // as a plain list + tableType so the backend returns flat rows directly (no
    // dimensions/measures tree). Only Drill-Down keeps the hierarchy contract.
    if (!this.isTmDrilldown) {
      return this.buildFlatTableComputePayload();
    }
    return this.buildTreeTableComputePayload();
  }

  /** FLAT payload for a Normal/Editable table: the selected columns (empty = all)
   *  sent as `{dataset, field, displayName}` objects with `tableType`/`subType`
   *  so the backend can join columns across datasets and returns flat rows in
   *  `data[]`. No dimensions/measures. */
  private buildFlatTableComputePayload(): Record<string, unknown> {
    // Matrix/Pivot configure FIELDS (row/column/value pickers), not column
    // cards — synthesize their columns from those fields so the backend's
    // component validation ("non-empty 'columns' array …") passes and the
    // flat response carries exactly the fields the client-side pivot needs.
    const picked = this.tmColumnEntries();
    const columns = picked.length ? picked : this.tmShapeFieldColumns();
    // Send the selected columns (empty = all) + everything the table payload
    // carried before (aggregation, chart targets, and the `format` styling blob)
    // so the backend receives the full Normal-table definition + formatting.
    return {
      componentId: this.componentId,
      componentType: 'TABLE',
      tableType: 'NORMAL',
      subType: this.tmType,
      dashboardId: this.dashboardId,
      title: this.title.trim(),
      dataset: this.drillDataset,
      columns,
      ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
      aggregation: this.tmAggregation,
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** TREE payload (dimensions + measures) for the Drill-Down table type. */
  private buildTreeTableComputePayload(): Record<string, unknown> {
    const selected = this.tmColumnFields.filter(Boolean);
    const columns = selected.length ? selected : [...(this.entityColumnsCache.get(this.drillDataset) || [])];
    const rows = this.entityRowsCache.get(this.drillDataset) || [];
    const isNumeric = (field: string): boolean => {
      const sample = rows.find((row) => row[field] != null && row[field] !== '');
      return typeof sample?.[field] === 'number';
    };
    const aggregationMap: Record<string, string> = { sum: 'SUM', avg: 'AVG', count: 'COUNT' };
    const aggregation = aggregationMap[this.tmAggregation] || 'SUM';

    const numericFields = columns.filter(isNumeric);
    let dimensionFields = this.tmGroupBy && this.tmAggregation !== 'none'
      ? [this.tmGroupBy]
      : columns.filter((field) => !numericFields.includes(field));
    let measureFields = numericFields.filter((field) => !dimensionFields.includes(field));
    if (!dimensionFields.length && measureFields.length) {
      dimensionFields = [measureFields[0]];
      measureFields = measureFields.slice(1);
    }
    const measures = measureFields.length
      ? measureFields.map((field) => ({ field, label: field, aggregation, rollUpStrategy: 'SUM', total: false }))
      : [{ field: dimensionFields[dimensionFields.length - 1], label: dimensionFields[dimensionFields.length - 1], aggregation: 'COUNT', rollUpStrategy: 'SUM', total: false }];

    return {
      componentId: this.componentId,
      componentType: 'TABLE',
      tableType: 'TABLE_MATRIX',
      subType: this.tmType,
      dashboardId: this.dashboardId,
      title: this.title.trim(),
      dataset: this.drillDataset,
      dimensions: dimensionFields.map((field, index) => ({ level: index + 1, field, label: field })),
      measures,
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** Flatten the compute API's dimension tree into flat table rows — one row
   *  per leaf path, dimension values keyed by their `levelLabel` (= raw field
   *  name, see buildTableMatrixComputePayload) plus the leaf's measures.
   *  Dimension values win key collisions (the no-numeric-column COUNT fallback
   *  reuses a dimension field as its measure). */
  private flattenComputeTreeToRows(response: Record<string, unknown> | null): Array<Record<string, unknown>> {
    const tree = (response?.['data'] ?? response?.['Data'] ?? []) as Array<Record<string, unknown>>;
    if (!Array.isArray(tree)) return [];
    const out: Array<Record<string, unknown>> = [];
    const walk = (nodes: Array<Record<string, unknown>>, path: Record<string, unknown>): void => {
      for (const node of nodes || []) {
        const level = { ...path, [String(node['levelLabel'] ?? 'level')]: node['label'] };
        const children = node['children'] as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(children) && children.length) {
          walk(children, level);
        } else {
          out.push({ ...((node['measures'] as Record<string, unknown>) || {}), ...level });
        }
      }
    };
    walk(tree, {});
    return out;
  }

  /** Pull flat rows out of the compute response, trying the common envelope
   *  shapes (responseList[0].data, data, top-level array). */
  private extractTableMatrixRows(response: Record<string, unknown> | null): Array<Record<string, unknown>> {
    if (response == null) return [];
    if (Array.isArray(response)) return response as Array<Record<string, unknown>>;
    const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
    const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
    const dataField = fromList ?? (response as { data?: unknown }).data ?? (response as { Data?: unknown }).Data;
    if (Array.isArray(dataField)) return dataField as Array<Record<string, unknown>>;
    // A drill-tree response for a Normal table → flatten to its leaf rows.
    return [];
  }

  /** Client-mode drill-down being edited: rebuild its tree from the cached
   *  source rows so label / aggregation / value-type / split / total / formula
   *  changes show immediately. Skipped (→ needs Update Configuration) when a
   *  level / measure now points at a column the cached rows don't carry. */
  private reshapeEditingDrillTile(): void {
    if (!this.editingTileId || !this.isDrillClientMode) return;
    const rows = this.drillRowsCache.get(this.editingTileId);
    // Only Generate / Update fill the cache — a tile edited from a loaded or
    // published dashboard has none, so a label / formula rename silently did
    // nothing until Update Configuration. Fetch its rows once, then reshape.
    if (!rows) { this.fetchRowsForEditingDrillTile(); return; }
    const tile = this.findTileAnywhere(this.editingTileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const norm = (s: string): string => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const keys = new Set<string>();
    rows.slice(0, 200).forEach((row) => Object.keys(row || {}).forEach((k) => keys.add(norm(k))));
    const present = (row: { field: string; label?: string }): boolean =>
      !row.field || keys.has(norm(row.field)) || (!!row.label && keys.has(norm(row.label)));
    const all = [...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures];
    if (!all.every(present)) return;
    tile.tableMatrixData = this.buildTableMatrixWidgetData(this.buildDrillClientTreeResponse(rows), tile.id, tile.format);
    this.updateTileInPlace({ ...tile });
  }

  /** Fetch the flat source rows of the client-mode drill-down tile being
   *  edited (the same compute call Generate makes) into drillRowsCache, then
   *  apply the edits made meanwhile. Skipped while a request is in flight or
   *  a level / pivot column / measure has no (or a stale) field pick — the
   *  compute API would reject that; Update Configuration reports it. */
  private fetchRowsForEditingDrillTile(): void {
    const tileId = this.editingTileId;
    if (!tileId || this.drillRowsFetching.has(tileId)) return;
    const tile = this.findTileAnywhere(tileId);
    if (!tile || tile.layoutType !== 'TABLE_MATRIX' || !tile.tableMatrixData) return;
    const picks = [...this.drillDownDimensions, ...this.drillColumnDimensions, ...this.drillDownMeasures];
    const stale = (row: { field: string; dataset?: string }): boolean => {
      const columns = this.entityColumnsCache.get(row.dataset || this.drillDataset);
      return !!columns && !columns.includes(row.field);
    };
    if (!picks.length || picks.some((row) => !row.field || stale(row))) return;
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    this.drillRowsFetching.add(tileId);
    this.http.post<Record<string, unknown>>(url, this.buildDrillClientComputePayload()).subscribe({
      next: (response) => {
        this.drillRowsFetching.delete(tileId);
        const rows = this.extractTableMatrixRows(response);
        if (!rows.length) return;
        this.drillRowsCache.set(tileId, rows);
        if (this.editingTileId === tileId) this.reshapeEditingDrillTile();
      },
      error: () => this.drillRowsFetching.delete(tileId),
    });
  }

  private invalidatePreview(): void {
    // Client-mode drill-down tiles DO refresh live (from their cached rows).
    this.reshapeEditingDrillTile();
    // Steps/levels changed since the last generate — the current preview no
    // longer reflects the working config. Leave it rendered (still useful
    // context) but the user must click Generate again to refresh it.
  }

  /** `{ targets: [...] }` for a CHART's payload — the component(s) this chart
   *  interacts with, selected in the build form or the tile's Interactions
   *  popup. Empty selection → no key at all (keeps older payloads unchanged). */
  private chartTargetsPayload(): Record<string, unknown> {
    if (!this.selectedTargetCharts.length) return {};
    return {
      targets: this.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.targetLabelForId(componentId),
      })),
    };
  }
// PAYLOAD BUILDER FOR DVM LAYOUT 
  /** Card-header colours from the ACTIVE Build-form format state — included in
   *  the compute payload (Generate/Update Configuration) so the backend gets
   *  them alongside the chart definition (live-verified: /components/compute
   *  accepts and ignores the extra `format` key). The published view still
   *  reads them from layout_json — this is an additional, informational copy. */
  private headerFormatPayload(): Record<string, unknown> {
    const source = this.isScatterBubbleLayout ? this.sbFormat : this.barFormat;
    if (!source || !Object.keys(source).length) return {};
    return { format: { ...source } };
  }

  private buildPayload(): Record<string, unknown> {
    const payload = this.buildComponentPayload();
    if (this.activeRelationHops) {
      // An empty array explicitly removes previously saved relations on Update.
      payload['bde_relation'] = copyRelationHops(this.relationHops);
    }
    return payload;
  }

  private buildComponentPayload(): Record<string, unknown> {
    if (this.isInputControlLayout) {
      return this.buildInputControlPayload();
    }

    if (this.isDrillDownLayout) {
      return {
        componentId: this.componentId,
        componentType: 'TABLE',
        tableType: this.selectedLayoutType,
        dashboardId: this.dashboardId,
        title: this.title.trim(),
        dataset: this.drillDataset,
        dimensions: this.drillDownDimensions.map((dimension, index) => ({
          level: index + 1,
          field: dimension.field,
          label: dimension.label,
          ...this.valueOrderPayload(dimension),
          // Only when set — existing tiles' payloads stay byte-identical.
          ...(dimension.editable ? { editable: true } : {}),
          ...this.dropdownEditorPayload(dimension),
        })),
        measures: this.drillDownMeasures.map((measure) => ({
          field: measure.field,
          label: measure.label,
          aggregation: measure.aggregation,
          rollUpStrategy: measure.rollUpStrategy,
          total: !!measure.total,
          // Only when set — existing tiles' payloads stay byte-identical.
          ...(measure.editable ? { editable: true } : {}),
          ...this.dropdownEditorPayload(measure),
          ...this.editableLevelsPayload(measure),
        })),
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isTableMatrixLayout) {
      // ONE unified family, THREE table types. Drill-Down sends the same
      // dimensions/measures payload as DRILL_DOWN_TABLE (identical compute
      // contract → tree response). Normal/Editable send the chosen columns as
      // flat group-by dimensions so the compute API returns flat rows the
      // client-side buildTableModel can render.
      const base = {
        componentId: this.componentId,
        componentType: 'TABLE',
        // Normal/Editable are the FLAT table contract → tableType 'NORMAL' (matches
        // the compute payload); Drill-Down keeps the hierarchy contract 'TABLE_MATRIX'.
        tableType: this.isTmDrilldown ? 'TABLE_MATRIX' : 'NORMAL',
        subType: this.tmType,
        dashboardId: this.dashboardId,
        title: this.title.trim(),
        dataset: this.drillDataset,
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
      if (this.isTmDrilldown) {
        const toggleView = this.buildTmToggleView();
        return {
          ...base,
          ...(toggleView ? { toggleView } : {}),
          dimensions: this.drillDownDimensions.map((dimension, index) => ({
            level: index + 1,
            field: dimension.field,
            label: dimension.label,
            ...this.valueOrderPayload(dimension),
            // Only when set — existing tiles' payloads stay byte-identical.
            ...(dimension.editable ? { editable: true } : {}),
            ...this.dropdownEditorPayload(dimension),
            // Merged drill-down + pivot: a level from a NON-primary dataset.
            ...(dimension.dataset ? { dataset: dimension.dataset } : {}),
          })),
          measures: [
            ...this.drillDownMeasures.map((measure) => ({
              field: measure.field,
              label: measure.label,
              aggregation: measure.aggregation,
              rollUpStrategy: measure.rollUpStrategy,
              total: !!measure.total,
              // Only when set — existing tiles' payloads stay byte-identical.
              ...(measure.editable ? { editable: true } : {}),
              ...this.dropdownEditorPayload(measure),
              ...this.editableLevelsPayload(measure),
              ...(measure.dataset ? { dataset: measure.dataset } : {}),
              ...(measure.pivot === false ? { pivot: false } : {}),
              ...(measure.kind === 'text' ? { kind: 'text' } : {}),
              ...(measure.format === 'percent' ? { format: 'percent' } : {}),
              ...(measure.percentScale === 'fraction' ? { percentScale: 'fraction' } : {}),
              ...(measure.hideNegative ? { hideNegative: true } : {}),
              ...(typeof measure.decimals === 'number' ? { decimals: measure.decimals } : {}),
              ...(measure.hidden ? { hidden: true } : {}),
              ...this.rowTotalHeaderPayload(measure),
              ...this.yearScopePayload(measure),
              ...this.groupFieldPayload(measure),
            })),
            // Year-scoped measures: the year column as a hidden helper measure, so
            // the published view's recompute (which re-sends THIS payload) gets it
            // back in the flat rows. Empty unless a scope is on.
            ...this.drillYearHelperMeasures(),
          ],
          // Merged drill-down + pivot extras — only when in use, so tiles that
          // never touched them (and every legacy tile) keep the same payload.
          // Totals placement (bottom row / right block / both / none).
          totalsPlacement: this.drillTotalsPlacement,
          ...(this.drillDataMode === 'client'
            ? {
                dataMode: 'client',
                ...(this.drillColumnDimensions.length
                  ? {
                      columnDimensions: this.drillColumnDimensions.map((dimension) => ({
                        field: dimension.field,
                        ...(dimension.label ? { label: dimension.label } : {}),
                        ...(dimension.dataset ? { dataset: dimension.dataset } : {}),
                        ...this.valueOrderPayload(dimension),
                      })),
                    }
                  : {}),
              }
            : {}),
          computedColumns: this.drillComputedColumns.map((c) => ({ name: c.name, expression: c.expression, ...(c.after ? { after: c.after } : {}) })),
          ...(this.drillColumnOrder.length ? { columnOrder: [...this.drillColumnOrder] } : {}),
          ...(this.drillColumnOrderPreset.length ? { columnOrderPreset: [...this.drillColumnOrderPreset] } : {}),
          ...(this.drillPivotValueOrderPreset.length ? { columnValueOrderPreset: [...this.drillPivotValueOrderPreset] } : {}),
          ...(this.drillRowTotalColumnsPreset.length ? { rowTotalColumns: [...this.drillRowTotalColumnsPreset] } : {}),
        };
      }
      // Normal / Editable — flat table. Columns are {dataset, field, displayName}
      // objects (cross-dataset capable); an empty list still means "all columns
      // of the primary dataset".
      // Matrix/Pivot configure FIELDS (row/column/value pickers), not column
      // cards — synthesize their payload columns from those fields so the
      // backend's component validation ("non-empty 'columns' array …") passes
      // and the flat NORMAL contract returns exactly the fields the
      // client-side pivot needs.
      const columns = this.tmColumnEntries().length ? this.tmColumnEntries() : this.tmShapeFieldColumns();
      const toggleView = this.buildTmToggleView();
      return {
        ...base,
        columns,
        ...(toggleView ? { toggleView } : {}),
        ...(this.tmGroupBy ? { groupBy: this.tmGroupBy } : {}),
        aggregation: this.tmAggregation,
        ...(this.tmNavEnabled
          ? {
              navigation: {
                enabled: true,
                filterKey: this.tmNavFilterKey,
                navigableColumns: this.tmNavColumns,
                targetDashboard: this.tmNavTargetDashboard,
                targetComponents: this.tmNavTargetComponents,
                buttonLabel: this.tmNavButtonLabel,
              },
            }
          : {}),
      };
    }

    if (this.isHorizontalBarLayout) {
      const hbFormula = this.hbValueFormula.trim();
      // Same [Token] → other-input reference mechanism an input control's
      // target formula uses, keyed by THIS chart's own componentId instead of
      // a target id — lets the bar's value formula reference another
      // chart/input's live output directly, not only when wired up as a target.
      const hbReferences = this.referencesForFormula(this.componentId, hbFormula);
      // Per-group overrides: a group missing here falls back to `hbFormula`.
      const groupFormulaEntries = this.hbGroupFormulas
        .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
        .filter((entry) => entry.group && entry.formula);
      const groupFormulas = groupFormulaEntries.length
        ? Object.fromEntries(groupFormulaEntries.map((entry) => [entry.group, entry.formula]))
        : undefined;
      // Auto-scope the chart to exactly the groups that have their own formula -
      // otherwise a group with neither a group-formula nor a default `hbFormula`
      // (e.g. a "Routing Cost" row the spec doesn't cover) would error out.
      // Skipped when `hbFormula` is also set, since that's a deliberate
      // "everyone else falls back to this" default covering the rest.
      const hbFilters = groupFormulaEntries.length && !hbFormula
        ? [{ dataset: this.drillDataset, field: this.hbGroupByField, operator: 'IN', value: groupFormulaEntries.map((entry) => entry.group) }]
        : undefined;
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType,
        title: this.title.trim(),
        dataset: this.drillDataset,
        groupBy: {
          field: this.hbGroupByField,
          label: this.hbGroupByLabel || this.hbGroupByField,
        },
        value: {
          label: this.hbValueLabel || 'Value',
          ...(hbFormula ? { formula: hbFormula } : {}),
          ...(groupFormulas ? { groupFormulas } : {}),
          ...(hbReferences ? { references: hbReferences } : {}),
        },
        ...this.chartTargetsPayload(),
        ...(hbFilters ? { filters: hbFilters } : {}),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isKpiLayout) {
      const kpiFormula = this.kpiFormula.trim();
      // Same [Token] → other-input reference mechanism an input control's
      // target formula uses, keyed by THIS KPI's own componentId instead of a
      // target id — lets a KPI's formula reference another chart/input's live
      // output directly, not only when the KPI is wired up as an input's target.
      const kpiReferences = this.referencesForFormula(this.componentId, kpiFormula);
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'KPI'
        title: this.title.trim(),
        dataset: this.drillDataset,
        // A KPI card is a single column aggregated to one value — or, when the
        // user gave a formula, a computed value in place of that (backend
        // prioritizes `formula` over `field`/`aggregation` when both are sent).
        field: this.kpiField,
        aggregation: this.kpiAggregation,
        ...(kpiFormula ? { formula: kpiFormula } : {}),
        ...(kpiReferences ? { references: kpiReferences } : {}),
        ...this.chartTargetsPayload(),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isFileUploadLayout) {
      // A file-upload control: componentType INPUT (drives targets), inputType
      // FILE_UPLOAD. The actual FILE is sent as multipart FormData at upload time
      // (published view / preview), NOT in this JSON descriptor — this just
      // registers the control + its targets.
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'FILE_UPLOAD',
        title: this.title.trim(),
        buttonLabel: this.fileUploadButtonLabel.trim() || 'Upload file',
        accept: this.fileUploadAccept.trim() || '.xlsx,.xls,.csv',
        // The compute API requires every INPUT to carry a `value`/`defaultValue`
        // (see "No value provided" error). At GENERATE time no file is picked
        // yet, so register the control with an empty value — the real file is
        // sent as `value` later when the user actually uploads.
        value: '',
        defaultValue: '',
        targets: this.selectedTargetCharts.map((componentId) => ({
          componentId,
          label: this.targetLabelForId(componentId),
        })),
        ...this.apiCallConfigPayload(),
      };
    }

    if (this.isPopupLayout) {
      // Display-only descriptor — NEVER POSTed to the compute API (Generate
      // skips the call entirely, see generateConfiguration). It exists so the
      // published layout_json / saved dashboard carries the FORM config and
      // loadTileIntoForm can restore the popup for editing. Table-block ROWS
      // are not here (they ride the widgetData snapshot); only the config is.
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: 'POPUP',
        title: this.title.trim(),
        trigger: {
          mode: this.popupTriggerMode || 'button',
          icon: this.popupTriggerIcon,
          label: this.popupTriggerLabel.trim(),
          hint: this.popupTriggerHint.trim(),
        },
        behaviour: {
          size: this.popupSize || 'medium',
          position: this.popupPosition || 'center',
          closeLabel: this.popupCloseLabel.trim(),
          dismissOnBackdrop: this.popupDismissOnBackdrop !== false,
        },
        // Formatting — the SAME wire shape every other chart sends (see
        // headerFormatPayload): one flat top-level `format` object. Shared
        // concepts keep the shared key names (cardHeaderBackground /
        // cardHeaderColor / fontSize / fontFamily); popup-specific keys ride
        // alongside. Only keys the user actually set are sent.
        ...this.popupFormatPayload(),
        // Each block sends ONLY its own type's fields (a text block has no
        // pairs/dataset/buttons); empty pair/button rows are dropped too.
        blocks: this.popupBlocks.map((block) => ({
          type: block.type,
          ...(block.type === 'heading' || block.type === 'text' ? { text: block.text, align: block.align } : {}),
          ...(block.type === 'heading' ? { level: block.level } : {}),
          ...(block.type === 'keyValue'
            ? { pairs: block.pairs.filter((pair) => pair.label.trim() || pair.value.trim()) }
            : {}),
          ...(block.type === 'table'
            ? { dataset: block.dataset, columns: block.columns, ...(block.maxRows != null ? { maxRows: block.maxRows } : {}) }
            : {}),
          ...(block.type === 'apiTable'
            ? {
                url: block.url.trim(),
                method: block.method || 'GET',
                ...(block.body.trim() ? { body: block.body.trim() } : {}),
                ...(block.rowsPath.trim() ? { rowsPath: block.rowsPath.trim() } : {}),
                ...(block.groupsPath.trim() ? { groupsPath: block.groupsPath.trim() } : {}),
                ...(block.groupRowsField.trim() ? { groupRowsField: block.groupRowsField.trim() } : {}),
                metaFields: block.metaFields.filter((meta) => meta.field.trim()),
                apiColumns: block.apiColumns.filter((column) => column.field.trim()),
                ...(block.maxRows != null && block.maxRows > 0 ? { maxRows: block.maxRows } : {}),
                refreshOnOpen: !!block.refreshOnOpen,
              }
            : {}),
          ...(block.type === 'buttons'
            ? { align: block.align, buttons: block.buttons.filter((button) => button.label.trim()) }
            : {}),
        })),
      };
    }

    if (this.isBarLayout) {
      //  BAR computes via the `groupBy` + `value`
      // shape — category → groupBy, first measure → SUM formula. The full
      // `capabilities` object still rides along for the client-side render and
      // Back-to-Builder form restore (the backend ignores it; live-verified
      // 200 with real grouped data).
      const firstBarMeasure = this.barMeasures.find((measure) => !!measure.field);
      const barFormula = this.barValueFormula.trim();
      const barReferences = this.referencesForFormula(this.componentId, barFormula);
      const barGroupFormulaEntries = this.barGroupFormulas
        .map((entry) => ({ group: entry.group.trim(), formula: entry.formula.trim() }))
        .filter((entry) => entry.group && entry.formula);
      const barGroupFormulas = barGroupFormulaEntries.length
        ? Object.fromEntries(barGroupFormulaEntries.map((entry) => [entry.group, entry.formula]))
        : undefined;
      // Scope to the listed categories — without a shared fallback the others
      // have nothing to compute and error the request.
      const barFilters = barGroupFormulaEntries.length && !barFormula
        ? [{ dataset: this.drillDataset, field: this.barCategory, operator: 'IN', value: barGroupFormulaEntries.map((entry) => entry.group) }]
        : undefined;
      const barValueFallback = firstBarMeasure?.field ? `SUM(${firstBarMeasure.field})` : '';
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'BAR'
        title: this.title.trim(),
        dataset: this.drillDataset,
        groupBy: {
          field: this.barCategory,
          label: this.barCategoryLabel || this.barCategory,
        },
        value: {
          label: this.barValueLabel.trim() || firstBarMeasure?.label || firstBarMeasure?.field || 'Value',
          formula: barFormula || (barGroupFormulas ? '' : barValueFallback),
          ...(barGroupFormulas ? { groupFormulas: barGroupFormulas } : {}),
          ...(barReferences ? { references: barReferences } : {}),
        },
        capabilities: this.buildBarCapabilities() as unknown as Record<string, unknown>,
        ...(barFilters ? { filters: barFilters } : {}),
        ...this.headerFormatPayload(),
      };
    }

    if (this.isLineAreaLayout) {
      // Multi-measure FLAT contract: the category + EVERY measure ride
      // `columns` ({dataset, field, aggregation, displayName}) so the backend
      // computes ALL series — the old groupBy + single `value` formula only
      // carried the first measure, so a multi-line chart always came back as
      // one line. The capabilities reference the RESPONSE row keys (each
      // column's displayName); the raw dataset fields live only in `columns`
      // and rehydrate maps them back onto the form.
      const capabilities = this.applyLineAreaFormatToCapabilities(this.buildLineAreaCapabilities(), this.barFormat);
      const sorting = capabilities.sorting?.length ? capabilities.sorting : undefined;
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'LINE_AREA'
        title: this.title.trim(),
        dataset: this.drillDataset,
        columns: this.buildLineAreaColumns(capabilities),
        ...(sorting ? { sorting } : {}),
        capabilities: this.remapLineAreaCapabilitiesToDisplayNames(capabilities) as unknown as Record<string, unknown>,
        format: { ...this.defaultTileFormat('LINE_AREA'), ...this.barFormat, title: this.title.trim() || undefined },
      };
    }

    if (this.isScatterBubbleLayout) {
      // FLAT-column contract (same as Line & Area / Normal tables): EVERY
      // dataset field the chart maps — X, Y, Size, Color, Label, Series, tooltip
      // extras — rides `columns` as {dataset, field, displayName} so the backend
      // knows which fields to fetch/return. displayName = field on purpose:
      // response rows then come back keyed by the raw field names the
      // capabilities already reference, so no remap is needed on either side.
      // The full capabilities (chartDefaults / variantFlags / format-derived
      // keys) still ride along for the client-side render + Back-to-Builder
      // restore, exactly as BAR does.
      const capabilities = this.buildScatterBubbleCapabilities();
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'CHART',
        chartType: this.selectedLayoutType, // 'SCATTER_BUBBLE'
        title: this.title.trim(),
        dataset: this.drillDataset,
        columns: this.buildScatterBubbleColumns(capabilities),
        capabilities: capabilities as unknown as Record<string, unknown>,
        ...this.headerFormatPayload(),
      };
    }

    if (this.isDropdownLayout) {
      const defaultValue = this.dropdownDefaultValue.trim()
        ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter(Boolean)
        : [];
      return {
        componentId: this.componentId,
        dashboardId: this.dashboardId,
        componentType: 'INPUT',
        inputType: 'DROPDOWN',
        title: this.title.trim(),
        dataset: this.drillDataset,
        field: this.dropdownField,
        displayLabel: this.dropdownDisplayLabel.trim() || this.title.trim(),
        isMultiSelect: this.dropdownIsMultiSelect,
        showAllOption: this.dropdownShowAllOption,
        autoSelectFirst: this.dropdownAutoSelectFirst,
        displayMode: this.dropdownDisplayMode || 'dropdown',
        optionGroups: this.sanitizedDropdownGroups(),
        optionOrderMode: this.dropdownOptionOrderMode,
        customOptionOrder: this.dropdownCustomOptionOrder.trim(),
        // Formatting rides the payload the same way the chart families send
        // theirs (BAR/LINE_AREA fold format into `capabilities`) — so a backend
        // that stores the component definition keeps the styling too.
        capabilities: this.dropdownFormatFromAccordion(),
        defaultValue: this.dropdownIsMultiSelect ? defaultValue : (defaultValue[0] ?? ''),
        value: this.dropdownIsMultiSelect ? defaultValue : (defaultValue.length ? [defaultValue[0]] : []),
        targets: this.selectedTargetCharts.map((componentId) => ({
          componentId,
          label: this.targetLabelForId(componentId),
          field: this.dropdownField,
        })),
        ...this.apiCallConfigPayload(),
      };
    }

    return {
      componentId: this.componentId,
      dashboardId: this.dashboardId,
      componentType: this.componentType,
      chartType: this.selectedLayoutType,
      title: this.title.trim(),
      datasets: [...this.selectedDatasets],
      steps: this.steps.map((step) => this.sanitizeStepForPayload(step)),
      ...this.chartTargetsPayload(),
      ...this.headerFormatPayload(),
    };
  }

  /** Compute-API payload for an input control (INPUT_BOX / SLIDER). The control
   *  captures a title, a formula/measure expression, and the set of dashboard
   *  charts it drives; each `target` carries the target chart's component id +
   *  label. SLIDER additionally sends its numeric bounds under `range`. */
  private buildInputControlPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      componentId: this.componentId,
      dashboardId: this.dashboardId,
      componentType: 'INPUT',
      inputType: this.selectedLayoutType, // 'INPUT_BOX' | 'SLIDER'
      title: this.title.trim(),
      // A formula may reference a dataset's fields — carry the chosen dataset
      // when one was picked (optional for input controls).
      dataset: this.selectedDatasets[0] || '',
      defaultValue: this.inputDefaultValue,
      // Seed value the tile starts with (the configured default). Once the tile
      // is on the canvas, its live value is pushed via onTileInputValueChange.
      value: this.inputDefaultValue ?? (this.isSliderLayout ? this.sliderMin : null),
      // Every target carries the SAME `formulas` array shape, fed by this
      // control's value — a non-waterfall target sends one entry (no `step`),
      // a WATERFALL target sends one entry PER bridge step/bar (Start, Change,
      // Total are each independently calculated). One key, always an array —
      // no separate "formula" vs "stepFormulas" for the backend to branch on.
      targets: this.selectedTargetCharts.map((value) => ({
        componentId: value,
        label: this.targetLabelForId(value),
        formulas: this.targetFormulasForPayload(value),
      })),
      // Persist the per-control custom "API Call" so reopening restores it.
      ...this.apiCallConfigPayload(),
    };

    if (this.isSliderLayout) {
      payload['range'] = {
        min: this.sliderMin,
        max: this.sliderMax,
        step: this.sliderStep,
      };
    }

    return payload;
  }

  private sanitizeStepForPayload(step: BridgeStep): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {
      label: step.label,
      stepType: step.stepType,
    };

    if (step.stepType === 'TOTAL' || ((step.stepType === 'CHANGE' || step.stepType === 'START') && step.formulaMode)) {
      sanitized['formula'] = step.formula;
    } else {
      sanitized['dataset'] = step.dataset;
      sanitized['field'] = step.field;
      sanitized['aggregation'] = step.aggregation;
    }

    // "AUTO" is a UI-only helper meaning "let the database handle sign logic" -
    // strip it from the payload rather than sending a placeholder sign value.
    // Formula-mode CHANGE steps still need this because the formula only computes
    // the value; the direction controls how that value impacts the bridge.
    if (step.stepType === 'CHANGE' && step.sign && step.sign !== 'AUTO') {
      sanitized['sign'] = step.sign;
    }

    return sanitized;
  }

  /**
   * Attaches each step's configured "Direction" to its bar as `colorSign` —
   * a display-only hint the color-picker (see `colorFor` in
   * buildWaterfallOption) can use instead of the bar's own delta sign.
   * Deliberately does NOT touch delta/bottom/top: those are the backend's
   * own computed bridge values, and "Direction" must only ever choose a
   * color, never recompute the numbers or bridge position it's painted on.
   */
  private applyStepSignsToWaterfallBars(bars: WaterfallBar[], payload?: Record<string, unknown>): WaterfallBar[] {
    if (!Array.isArray(bars) || !bars.length) return [];
    const steps = Array.isArray(payload?.['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
    if (!steps.length) return bars;

    const normalize = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
    const stepsByLabel = new Map<string, Record<string, unknown>>();
    steps.forEach((step) => {
      const key = normalize(step['label']);
      if (key) stepsByLabel.set(key, step);
    });

    return bars.map((bar, index) => {
      const matchingStep = stepsByLabel.get(normalize(bar.label)) ?? steps[index];
      const sign = String(matchingStep?.['sign'] ?? 'AUTO').toUpperCase();
      return sign === 'AUTO' ? bar : { ...bar, colorSign: sign };
    });
  }

  private buildWaterfallPreviewOption(bars: WaterfallBar[], format?: Record<string, unknown>): echarts.EChartsOption {
    // DUAL-BUILDER rule: the builder preview, the published dashboard AND the
    // waterfall chart component all render through the ONE shared builder in
    // self-service-recompute.utils, so format handling (colors, legend, axes,
    // tooltip, number format) can never drift between preview and published.
    return buildWaterfallOption(bars, format) as echarts.EChartsOption;
  }

  private toAxisLabelRotation(value: unknown, fallback: number): number {
    const rotation = Number(value ?? fallback);
    return Number.isFinite(rotation) ? rotation : fallback;
  }

  private withWaterfallXAxisLabelRotation(option: echarts.EChartsOption | null | undefined, format?: Record<string, unknown>): echarts.EChartsOption | null | undefined {
    if (!option || typeof option !== 'object') return option;
    const rotate = this.toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
    const patchAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown> | undefined) || {}),
      axisLabel: {
        ...(((axis as Record<string, unknown> | undefined)?.['axisLabel'] as Record<string, unknown> | undefined) || {}),
        rotate,
      },
    });
    const next = { ...(option as Record<string, unknown>) } as echarts.EChartsOption;
    next.xAxis = Array.isArray(option.xAxis)
      ? option.xAxis.map(patchAxis) as echarts.EChartsOption['xAxis']
      : patchAxis(option.xAxis) as echarts.EChartsOption['xAxis'];
    return next;
  }
  private toKpiNumberFormat(format?: Record<string, unknown>): string {
    const selected = String(format?.['numberFormat'] ?? 'NONE');
    if (selected === 'THOUSANDS') return 'thousands';
    if (selected === 'PERCENT') return 'percent';
    return 'plain';
  }

  private toNumberScale(value: unknown): string {
    switch (String(value ?? 'NONE')) {
      case 'COMPACT': return 'compact';
      case 'SCALE_THOUSANDS': return 'thousands';
      case 'MILLIONS': return 'millions';
      case 'BILLIONS': return 'billions';
      default: return 'none';
    }
  }

  // ── Per-tile Format popup (chart tiles only) ──────────────────────────────

  /** Every tile is formattable EXCEPT Slider / Input Box — those two take no
   *  card-header formatting (by request), in the builder or published view.
   *  Everything else (charts + dropdown + file upload) gets the Format popup. */
  isFormattableTile(tile: DashboardTile): boolean {
    // Every tile gets a Format popup EXCEPT File-upload (no useful formatting
    // for it) — Slider/Input Box get one (decimals + number-scale on the
    // displayed value), Dropdown gets its Appearance controls (pill/tile
    // colors, radius, font size), and any new chart type (BAR/SCATTER_BUBBLE/…)
    // is formattable automatically without needing to be listed here.
    // GROUP is included, but its popup holds only the card design controls
    // (appliesTo:["GROUP"] — border / backgrounds / transparency), never the
    // per-chart ones its members already carry. See applicableFormatControls.
    return (
      !!tile &&
      tile.layoutType !== 'FILE_UPLOAD' &&
      // POPUP styles its trigger via its own build-form "Trigger style"
      // fields, so the generic Format popup would only duplicate/conflict.
      tile.layoutType !== 'POPUP'
    );
  }

  /** Tiles whose CARD header paints the cardHeaderBackground/cardHeaderColor
   *  format colors — every formattable tile PLUS the popup (which has no
   *  Format popup, but sets the same keys from its build-form Formatting
   *  accordion). */
  tileTakesHeaderFormat(tile: DashboardTile): boolean {
    return this.isFormattableTile(tile) || tile?.layoutType === 'POPUP';
  }

  /** Format controls that apply to this tile (enabled + matching appliesTo,
   *  deduped by key — chart-specific wins over shared). Table & Matrix tiles are
   *  further gated per sub-type via `visibleWhenType`. */
  formatControlsFor(tile: DashboardTile): FormatControl[] {
    const subType = this.tileTableSubType(tile);
    return this.applicableFormatControls(tile.layoutType)
      .filter((control) => this.controlVisibleForSubType(control, subType));
  }

  /** A TABLE_MATRIX tile's sub-type (normal/editable/drilldown), '' otherwise. */
  private tileTableSubType(tile: DashboardTile): string {
    if (tile.layoutType !== 'TABLE_MATRIX') return '';
    const caps = ((tile.tableMatrixData as Record<string, unknown> | undefined)?.['capabilities'] as Record<string, unknown>) || {};
    return String(caps['tableType'] ?? '');
  }

  /** Preferred accordion section order in the Format popup. Any group a control
   *  names that isn't listed here is appended after these, in first-seen order. */
  /** Default Format accordion section order — fallback when config omits `formatGroupOrder`. */
  private static readonly FORMAT_GROUP_ORDER = ['Title', 'Axis', 'Tooltip', 'Formatting', 'Interaction', 'Advanced'];

  /** Config-driven Format accordion section order (falls back to the default). */
  private get formatGroupOrder(): string[] {
    return this.layout.options?.formatGroupOrder?.length
      ? this.layout.options.formatGroupOrder
      : DvmLayoutBuilderComponent.FORMAT_GROUP_ORDER;
  }

  /** The set of Format-popup accordion sections open for a tile (keyed by tile id
   *  → set of open group names). First group opens by default. */
  private openFormatGroups = new Map<string, Set<string>>();

  /** Group this tile's applicable controls into ordered accordion sections. A
   *  control with no `group` falls into the first configured section (so a flat,
   *  ungrouped config still renders as one open section).
   *  MEMOIZED per tile — the template calls this every CD cycle, and a fresh
   *  array of fresh group objects makes the popup's *ngFor tear down and
   *  recreate every control (incl. appendTo:'body' ng-selects, which re-trigger
   *  CD on rebuild → freeze); see [[project_ngselect_appendtobody_memoize]]. */
  private tileFormatGroupsCache = new Map<string, { key: string; groups: FormatControlGroup[] }>();
  formatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    // formatControls loads async with the layout config — key on its size so
    // the cache refills once the real control list arrives. Table tiles also key
    // on their sub-type so a regenerated tile's popup regroups its controls.
    const key = `${tile.layoutType}::${this.formatControls.length}::${this.tileTableSubType(tile)}`;
    const cached = this.tileFormatGroupsCache.get(tile.id);
    if (cached && cached.key === key) return cached.groups;
    const groups = this.buildFormatGroupsFor(tile);
    this.tileFormatGroupsCache.set(tile.id, { key, groups });
    return groups;
  }

  private buildFormatGroupsFor(tile: DashboardTile): FormatControlGroup[] {
    const controls = this.formatControlsFor(tile);
    const byGroup = new Map<string, FormatControl[]>();
    const seen: string[] = [];
    controls.forEach((control) => {
      const group = control.group || 'Format';
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        seen.push(group);
      }
      byGroup.get(group)!.push(control);
    });
    const order = this.formatGroupOrder;
    seen.sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? order.length + seen.indexOf(a) : ia) - (ib === -1 ? order.length + seen.indexOf(b) : ib);
    });
    return seen.map((name) => ({ name, controls: byGroup.get(name)! }));
  }

  /** Whether a Format-popup accordion section is expanded (first one defaults open). */
  isFormatGroupOpen(tile: DashboardTile, group: string, index: number): boolean {
    const set = this.openFormatGroups.get(tile.id);
    if (!set) return index === 0;
    return set.has(group);
  }

  /** Toggle a Format-popup accordion section. */
  toggleFormatGroup(tile: DashboardTile, group: string, index: number): void {
    let set = this.openFormatGroups.get(tile.id);
    if (!set) {
      // Seed with the default-open first section so toggling behaves predictably.
      set = new Set<string>();
      const groups = this.formatGroupsFor(tile);
      if (groups[0]) set.add(groups[0].name);
      this.openFormatGroups.set(tile.id, set);
    }
    if (set.has(group)) set.delete(group);
    else set.add(group);
  }

  toggleTileFormat(tile: DashboardTile, event?: MouseEvent): void {
    if (this.openFormatTileId === tile.id) {
      this.openFormatTileId = null;
      return;
    }
    // The popup template renders only for a tile that HAS a format object, so a
    // tile predating its controls (a GROUP card grouped before the card-design
    // controls existed, or restored from an older published layout) would open
    // nothing at all. Seed it from the configured defaults on first open.
    if (!tile.format) tile.format = this.defaultTileFormat(tile.layoutType);
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.openFormatTileId = tile.id;
  }

  // ── Per-tile Interactions popup (self-contained; does NOT touch the left
  //    builder form / sidebar). Lets an INPUT control's targets be (re)wired
  //    after other components exist. State is independent of the create-form
  //    fields so opening it never loads the tile into the sidebar. ──────────
  /** The input-control tile whose Interactions popup is open. */
  interactionTile: DashboardTile | null = null;
  /** componentIds this control drives, edited in the popup. */
  interactionTargets: string[] = [];
  /** Non-waterfall target → its single formula. */
  interactionFormulas: Record<string, string> = {};
  /** Waterfall target → { bar/step label → formula }. */
  interactionStepFormulas: Record<string, Record<string, string>> = {};
  /** Target → typed reference rows (token → other-input mapping). */
  interactionReferences: Record<string, TargetFormulaReferenceEntry[]> = {};

  /** Every tile gets the Interactions popup: INPUT controls wire targets WITH
   *  per-target formulas/references; CHART tiles wire plain targets — the
   *  component(s) the chart interacts with (besides filterKey/filterListeners). */
  canEditInteractions(tile: DashboardTile): boolean {
    // Interactions are per-chart (targets & references). A GROUP card is a
    // container — its members each have their own button.
    return !!tile && tile.layoutType !== 'GROUP';
  }

  /** True when the open popup belongs to an input control — only those get the
   *  per-target formula + reference editors; charts just pick their targets. */
  get interactionTileIsInput(): boolean {
    const layoutType = this.interactionTile?.layoutType;
    return layoutType === 'SLIDER' || layoutType === 'INPUT_BOX' || layoutType === 'DROPDOWN';
  }

  /** Open/close the popup. Seeds its OWN state from the tile's persisted
   *  payload — the left builder form is left completely untouched. */
  toggleTileInteractions(tile: DashboardTile, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.openInteractionTileId === tile.id) {
      this.closeTileInteractions();
      return;
    }
    this.openFormatTileId = null;
    this.formatPopupAlign = this.resolveFormatPopupAlign(event);
    this.seedInteractionState(tile);
    this.interactionTargetOptionsList = this.buildInteractionTargetOptions(tile);
    this.interactionTile = tile;
    this.openInteractionTileId = tile.id;
  }

  closeTileInteractions(): void {
    this.openInteractionTileId = null;
    this.interactionTile = null;
    this.interactionTargets = [];
    this.interactionFormulas = {};
    this.interactionStepFormulas = {};
    this.interactionReferences = {};
    this.interactionTargetOptionsList = [];
  }

  /** Every OTHER tile on the dashboard, as target options for the popup.
   *  Computed ONCE when the popup opens and held in a STABLE array — binding a
   *  method call as [items] hands ng-select a fresh array every change-detection
   *  cycle, which rebuilds its option list mid-click and swallows the selection
   *  (same bug the memoized `targetCharts` getter fixes for the builder form).
   *  Tiles can't change while the modal overlay is open, so once is enough. */
  interactionTargetOptionsList: LabelValueOption[] = [];

  private buildInteractionTargetOptions(tile: DashboardTile): LabelValueOption[] {
    const selfId = String(tile.componentId ?? '');
    const seen = new Set<string>();
    const options: LabelValueOption[] = [];
    this.dashboardTiles.forEach((item) => {
      const value = String(item.componentId ?? '');
      if (!value || value === selfId || seen.has(value) || item.layoutType === 'POPUP') return;
      seen.add(value);
      const isInput = item.layoutType === 'SLIDER' || item.layoutType === 'INPUT_BOX' || item.layoutType === 'DROPDOWN' || item.layoutType === 'FILE_UPLOAD';
      options.push({ label: (item.title || item.layoutType) + (isInput ? ' (Input)' : ''), value });
    });
    return options;
  }

  interactionStepFormula(id: string, step: string): string {
    return this.interactionStepFormulas[id]?.[step] ?? '';
  }
  setInteractionStepFormula(id: string, step: string, value: string): void {
    const perStep = { ...(this.interactionStepFormulas[id] ?? {}) };
    perStep[step] = value ?? '';
    this.interactionStepFormulas = { ...this.interactionStepFormulas, [id]: perStep };
  }
  interactionRefs(id: string, step?: string): TargetFormulaReferenceEntry[] {
    return (this.interactionReferences[id] || []).filter((entry) => (step ? entry.step === step : !entry.step));
  }
  addInteractionRef(id: string, step?: string): void {
    const rows = [...(this.interactionReferences[id] || []), { token: '', inputTitle: '', targetLabel: '', rowLabel: '', step }];
    this.interactionReferences = { ...this.interactionReferences, [id]: rows };
  }
  removeInteractionRef(id: string, entry: TargetFormulaReferenceEntry): void {
    const rows = (this.interactionReferences[id] || []).filter((item) => item !== entry);
    this.interactionReferences = { ...this.interactionReferences, [id]: rows };
  }

  /** Load the popup's state from a tile's persisted `payload.targets`. */
  private seedInteractionState(tile: DashboardTile): void {
    this.interactionTargets = [];
    this.interactionFormulas = {};
    this.interactionStepFormulas = {};
    this.interactionReferences = {};
    const targets = Array.isArray(tile.payload?.['targets']) ? tile.payload!['targets'] as Array<Record<string, unknown>> : [];
    targets.forEach((target) => {
      const id = String(target?.['componentId'] ?? '').trim();
      if (!id || this.interactionTargets.includes(id)) return;
      this.interactionTargets.push(id);
      const entries = Array.isArray(target['formulas']) ? target['formulas'] as Array<Record<string, unknown>> : null;
      if (entries) {
        const withStep = entries.filter((e) => !!e['step']);
        if (withStep.length) {
          const perStep: Record<string, string> = {};
          withStep.forEach((e) => { perStep[String(e['step'])] = String(e['formula'] ?? ''); });
          this.interactionStepFormulas[id] = perStep;
        } else {
          this.interactionFormulas[id] = String(entries[0]?.['formula'] ?? '');
        }
        const rows: TargetFormulaReferenceEntry[] = [];
        entries.forEach((e) => {
          const refs = e['references'] as Record<string, Record<string, unknown>> | undefined;
          if (!refs || typeof refs !== 'object') return;
          const entryStep = e['step'] ? String(e['step']) : undefined;
          Object.keys(refs).forEach((token) => {
            const r = refs[token] || {};
            rows.push({ token, inputTitle: String(r['inputTitle'] ?? ''), targetLabel: String(r['targetLabel'] ?? ''), rowLabel: String(r['rowLabel'] ?? ''), step: entryStep });
          });
        });
        if (rows.length) this.interactionReferences[id] = rows;
      } else {
        this.interactionFormulas[id] = String(target['formula'] ?? '');
      }
    });
  }

  /** Build one target's `formulas[]` payload entry (matches buildInputControlPayload). */
  private interactionFormulasForPayload(id: string): Array<Record<string, unknown>> {
    const refsFor = (step?: string): Record<string, unknown> | undefined => {
      const entries = (this.interactionReferences[id] || []).filter((e) => (step ? e.step === step : !e.step));
      const map: Record<string, unknown> = {};
      entries.forEach((e) => {
        const token = e.token.trim();
        if (!token) return;
        map[token] = { inputTitle: e.inputTitle.trim(), targetLabel: e.targetLabel.trim(), rowLabel: e.rowLabel.trim() };
      });
      return Object.keys(map).length ? map : undefined;
    };
    if (this.isWaterfallTarget(id)) {
      return this.targetSteps(id)
        .map((step) => {
          const formula = this.interactionStepFormula(id, step.label).trim();
          const references = refsFor(step.label);
          return { step: step.label, formula, ...(references ? { references } : {}) };
        })
        .filter((entry) => !!entry['formula']);
    }
    const formula = (this.interactionFormulas[id] || '').trim();
    // Same pass-through default as targetFormulasForPayload: an input-control
    // target with no formula gets `value`, so it receives the driving value
    // instead of being silently skipped by the recompute.
    if (!formula) return this.isInputControlTargetId(id) ? [{ formula: 'value' }] : [];
    const references = refsFor();
    return [{ formula, ...(references ? { references } : {}) }];
  }

  /** Persist the wired targets onto the tile and recompute them — WITHOUT going
   *  through the left form (so the sidebar stays as-is). */
  saveTileInteractions(): void {
    const tile = this.interactionTile;
    if (!tile) return;
    const isInput = this.interactionTileIsInput;
    // Charts persist plain targets (who they interact with); only input
    // controls carry per-target formulas.
    const targets = this.interactionTargets.map((id) => ({
      componentId: id,
      label: this.targetLabelForId(id),
      ...(isInput ? { formulas: this.interactionFormulasForPayload(id) } : {}),
    }));
    const payload = { ...tile.payload, targets };
    tile.payload = payload;
    if (tile.inputControl) tile.inputControl.targets = [...this.interactionTargets];
    if (tile.dropdownControl) tile.dropdownControl.targets = [...this.interactionTargets];
    this.updateTileInPlace({ ...tile });

    // Recompute the targets so the preview reflects the new wiring immediately
    // (same POST + apply path the live input/dropdown change uses). Only input
    // controls push values into their targets — a chart's target wiring is
    // metadata for its click-interactions, nothing to recompute yet.
    const targetIds = isInput ? targets.map((t) => t.componentId).filter(Boolean) : [];
    if (targetIds.length) {
      const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
      this.isRecomputing = true;
      this.recomputingComponentIds = new Set(targetIds);
      this.http.post<Record<string, unknown>>(url, payload).subscribe({
        next: (response) => { this.applyRecomputeResults(response, targetIds); this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
        error: () => { this.isRecomputing = false; this.recomputingComponentIds = new Set(); },
      });
    }
    this.ui.showSuccess('Interactions updated.');
    this.closeTileInteractions();
  }

  cancelTileInteractions(): void {
    this.closeTileInteractions();
  }

  private resolveFormatPopupAlign(event?: MouseEvent): 'left' | 'right' {
    const trigger = event?.currentTarget as HTMLElement | null;
    const canvas = this.dashboardCanvasRef?.nativeElement;
    if (!trigger || !canvas) return 'right';
    const triggerRect = trigger.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const popupWidth = 280;
    const gutter = 12;
    return triggerRect.right - popupWidth < canvasRect.left + gutter ? 'left' : 'right';
  }

  private buildWaterfallTileData(tile: DashboardTile): Record<string, unknown> {
    return {
      type: 'waterfall-chart',
      title: tile.title,
      widgetId: `dvm-tile-waterfall-${tile.id}`,
      height: '100%',
      hideCardHeader: true,
      waterfallBars: tile.waterfallBars,
      option: tile.previewOption,
      format: tile.format,
      // Interaction accordion → the chart component's click handler. The column
      // actually published is re-resolved by onTileCrossFilter from the tile's
      // CURRENT filterKey, so a stale copy here is harmless.
      crossFilter: tile.format?.['crossFilter'] === true,
      highlightOnClick: tile.format?.['highlightOnClick'] === true,
      filterKey: tile.filterKey || undefined,
    };
  }
  /** Re-derive the tile's rendered chart from its stored source data + format. */
  onTileFormatChange(tile: DashboardTile): void {
    const format = tile.format || {};
    if (format['title']) tile.title = format['title'] as string;
    if (tile.layoutType === 'WATERFALL') {
      tile.previewOption = tile.waterfallBars
        ? this.buildWaterfallPreviewOption(tile.waterfallBars, format)
        : this.withWaterfallXAxisLabelRotation(tile.previewOption, format) as echarts.EChartsOption | undefined;
      tile.waterfallData = this.buildWaterfallTileData(tile);
    } else if (tile.layoutType === 'HORIZONTAL_BAR_CHART' && tile.hbData) {
      // The shared bar widget reads a few of these directly; remount so it picks
      // up the new values (title / label visibility / bar color).
      tile.hbData = {
        ...tile.hbData,
        title: (format['title'] as string) || tile.title,
        valueLabelPosition: format['showValues'] === false ? 'none' : 'inside',
        color: format['positiveColor'] || (tile.hbData['color'] as string),
        barColor: format['positiveColor'] || (tile.hbData['barColor'] as string),
        numberFormat: this.toKpiNumberFormat(format),
        numberScale: this.toNumberScale(format['numberFormat']),
        decimals: Number(format['decimals'] ?? 1),
      };
    } else if (tile.layoutType === 'KPI' && tile.kpiData) {
      tile.kpiData = {
        ...tile.kpiData,
        title: (format['title'] as string) || tile.title,
        numberFormat: this.toKpiNumberFormat(format),
        numberScale: this.toNumberScale(format['numberFormat']),
        decimals: Number(format['decimals'] ?? 1),
        prefix: (format['prefix'] as string) || '',
        suffix: (format['suffix'] as string) || '',
        infoText: (format['infoText'] as string) || '',
        infoIconPosition: (format['infoIconPosition'] as string) || 'corner',
        alignment: (format['contentAlignment'] as string) || 'center',
        cardLayout: (format['cardLayout'] as string) || '',
        titleFontSize: format['titleFontSize'] ?? '',
        valueFontSize: format['valueFontSize'] ?? '',
        ...this.kpiDecorationData(format),
      };
    } else if (tile.layoutType === 'DRILL_DOWN_TABLE' && tile.drillData) {
      // Round-off precision on the measure cells + Total row — DrillDownComponent
      // reads `decimals` straight off its data input, so a remount re-renders.
      tile.drillData = {
        ...tile.drillData,
        decimals: Number(format['decimals'] ?? 0),
      };
    } else if (tile.layoutType === 'DROPDOWN' && tile.dropdownControl) {
      // Appearance keys ride the widgetData (the published entry spreads it),
      // so builder preview and published view style identically with no extra
      // publish wiring. app-global-dropdown maps them onto its --dvm-filter-*
      // CSS vars scoped to that one widget. New refs so ngOnChanges fires.
      const widgetData: Record<string, unknown> = { ...(tile.dropdownControl.widgetData || {}) };
      this.applyDropdownStyleKeys(format, widgetData);
      tile.dropdownControl = { ...tile.dropdownControl, widgetData };
      // Keep the payload's `capabilities` in sync so a re-save/publish after a
      // popup edit sends the CURRENT formatting to the backend too.
      const capabilities: Record<string, unknown> = {};
      this.applyDropdownStyleKeys(format, capabilities);
      tile.payload = { ...tile.payload, capabilities };
    } else if (tile.layoutType === 'BAR' && tile.barData) {
      // Fold ALL format sections (Title/Axis/Tooltip/Formatting/Advanced) into the
      // tile's stored capabilities + remount so the chart re-renders. Structural
      // fields (category/measures/series/orientation) are preserved.
      const capabilities = this.applyBarFormatToCapabilities(
        { ...(tile.barData['capabilities'] as unknown as BarColumnCapabilities) },
        format,
      );
      tile.barData = {
        ...tile.barData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if (tile.layoutType === 'LINE_AREA' && tile.lineAreaData) {
      const capabilities = this.applyLineAreaFormatToCapabilities(
        { ...(tile.lineAreaData['capabilities'] as unknown as LineAreaCapabilities) },
        format,
      );
      tile.lineAreaData = {
        ...tile.lineAreaData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
      tile.payload = {
        ...tile.payload,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if (tile.layoutType === 'SCATTER_BUBBLE' && tile.scatterBubbleData) {
      const capabilities = this.applyScatterBubbleFormatToCapabilities(
        { ...(tile.scatterBubbleData['capabilities'] as unknown as ScatterBubbleCapabilities) },
        format,
      );
      tile.scatterBubbleData = {
        ...tile.scatterBubbleData,
        title: (format['title'] as string) || tile.title,
        capabilities: capabilities as unknown as Record<string, unknown>,
      };
    } else if ((tile.layoutType === 'SLIDER' || tile.layoutType === 'INPUT_BOX') && tile.inputControl) {
      const numberFormat = (format['numberFormat'] as string) || 'NONE';
      const decimals = Number(format['decimals'] ?? 1);
      tile.inputControl = {
        ...tile.inputControl,
        widgetData: { ...tile.inputControl.widgetData, numberFormat, decimals },
      };
    } else if (tile.layoutType === 'TABLE_MATRIX' && tile.tableMatrixData) {
      // Merge ONLY the styling/behaviour keys from the (changed) format into the
      // tile's existing capabilities — preserving its own columns / dimensions /
      // measures / tableType (which don't change in the Format popup). This keeps
      // the format edit independent of whatever the builder form currently holds.
      const caps = (tile.tableMatrixData['capabilities'] as Record<string, unknown>) || {};
      const styled = this.applyTableMatrixFormatToCapabilities(caps, format);
      tile.tableMatrixData = {
        ...tile.tableMatrixData,
        title: (format['title'] as string) || tile.title,
        capabilities: styled,
      };
    }
    this.updateTileInPlace({ ...tile });
    this.reflowCharts();
  }

  // ── Cross-filter (bar tile click → filter the OTHER tiles) ─────────────────

  /** A bar tile emitted a cross-filter click. Set/clear the active filter and
   *  re-render the OTHER bar tiles from their rows filtered by column = value.
   *  Clicking the same value again (value === null) clears it. */
  onTileCrossFilter(sourceTile: DashboardTile, event: { column: string; value: string | null; values?: string[] }): void {
    // The published column is the tile's configured filterKey (override → category),
    // falling back to whatever the chart emitted.
    const column = this.effectiveTileFilterKey(sourceTile) || event.column;
    // Multi-select: the chart emits the FULL current selection in `values`. Empty
    // selection → clear the filter. (Older single-value events fall back to value.)
    const values = Array.isArray(event.values)
      ? event.values
      : (event.value ? [event.value] : []);
    if (!values.length) {
      this.activeCrossFilter = null;
    } else {
      this.activeCrossFilter = { column, values, sourceTileId: sourceTile.id };
    }
    // Notify the backend which value was clicked and where the filter applied.
    // Best-effort: filtering stays client-side (instant), so a failed/absent
    // response never blocks the UI. Skipped when the filter is cleared (no values).
    this.postCrossFilterEvent(sourceTile, column, values);
    this.applyCrossFilterToTiles();
  }

  /** Best-effort POST to /{entity}/dynamic_filter describing a cross-filter click:
   *  the column published, the clicked value(s), the source tile, and the tiles
   *  that react. Fire-and-forget — the UI never waits and errors are swallowed
   *  (client-side filtering already happened). Dataset comes from the source
   *  tile's own payload, so it's config-free and generic. */
  private postCrossFilterEvent(sourceTile: DashboardTile, column: string, values: string[]): void {
    if (!values.length) return; // filter cleared — nothing to report
    const dataset = this.tileDatasets(sourceTile)[0] ?? '';
    if (!dataset) return; // no dataset to scope the call to
    const url = buildSelfServiceApiUrl(`/${encodeURIComponent(dataset)}/dynamic_filter`);
    const body = {
      uuid: null,
      // The cross-filter selection: column, clicked value(s), source tile, and the
      // tiles that react. `filters` is the shape a filter-aware backend consumes.
      crossFilter: {
        column,
        values,
        sourceTileId: sourceTile.id,
        listenerTileIds: this.effectiveListenerTileIds(sourceTile),
      },
      filters: [{ column, operator: 'IN', values }],
    };
    this.http.post(url, body).pipe(catchError(() => of(null))).subscribe();
  }

  /** Clear the active cross-filter (chip × button) → restore all tiles' rows. */
  clearCrossFilter(): void {
    if (!this.activeCrossFilter) return;
    this.activeCrossFilter = null;
    this.applyCrossFilterToTiles();
  }

  // ── Generic client-side cross-filter accessors (registry-driven) ──────────

  /** The cross-filter spec for a tile's layoutType, or null when the tile isn't
   *  a client-side cross-filter chart (waterfall/kpi/drill/input don't render
   *  from client rows). Adding a chart type to the registry makes it participate. */
  private crossFilterSpec(tile: DashboardTile): ClientChartCrossFilterSpec | null {
    return CLIENT_CHART_CROSS_FILTER[tile.layoutType] ?? null;
  }

  /** True when a tile can publish/react to cross-filters (is in the registry AND
   *  has its render data + original rows populated). */
  isCrossFilterTile(tile: DashboardTile): boolean {
    const spec = this.crossFilterSpec(tile);
    return !!spec && !!tile[spec.dataField] && Array.isArray(tile[spec.rowsField]);
  }

  /** Tiles whose funnel (listener editor) opens: registry tiles PLUS the
   *  waterfall, which both PUBLISHES (clicked step label) and REACTS via server
   *  recompute (see recomputeWaterfallTile). */
  canConfigureCrossFilter(tile: DashboardTile): boolean {
    return this.isCrossFilterTile(tile) || (tile.layoutType === 'WATERFALL' && !!tile.waterfallData);
  }

  /** A tile's ORIGINAL unfiltered rows (registry-resolved), or []. */
  private tileClientRows(tile: DashboardTile): Array<Record<string, unknown>> {
    const spec = this.crossFilterSpec(tile);
    const rows = spec ? tile[spec.rowsField] : undefined;
    return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
  }

  /** A tile's widget `[data]` object (registry-resolved), or null. */
  private tileClientData(tile: DashboardTile): Record<string, unknown> | null {
    const spec = this.crossFilterSpec(tile);
    const data = spec ? tile[spec.dataField] : undefined;
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  }

  /** Return a copy of `tile` whose rendered rows (its data's `staticData`) are
   *  replaced with `rows`, using the registry field for this chart type. */
  private withTileClientRows(tile: DashboardTile, rows: Array<Record<string, unknown>>): DashboardTile {
    const spec = this.crossFilterSpec(tile);
    const data = this.tileClientData(tile);
    if (!spec || !data) return tile;
    return { ...tile, [spec.dataField]: { ...data, staticData: rows } } as DashboardTile;
  }

  // ── Cross-filter apply (generic across every registered client chart) ──────

  /** Re-render the tiles that REACT to the active cross-filter from their original
   *  rows filtered by column IN values (or restore all rows when cleared). Only
   *  the source tile's configured LISTENERS react; the source keeps its own data.
   *  Works for ANY registered client-side chart (BAR today, Pie/Line/… later).
   *  RECOMPUTE listeners (WATERFALL) can't filter client-side — they re-run their
   *  compute payload with the filter attached (see recomputeWaterfallTile). */
  private applyCrossFilterToTiles(): void {
    const filter = this.activeCrossFilter;
    const sourceTile = filter ? this.dashboardTiles.find((t) => t.id === filter.sourceTileId) : undefined;
    const listeners = sourceTile ? new Set(this.effectiveListenerTileIds(sourceTile)) : new Set<string>();
    const filterValues = filter ? new Set(filter.values) : null;
    this.dashboardTiles = this.dashboardTiles.map((tile) => {
      // Only registered client-side chart tiles with kept rows participate.
      if (!this.isCrossFilterTile(tile)) return tile;
      if (filter && tile.id === filter.sourceTileId) return tile;
      const rows = this.tileClientRows(tile);
      // A tile reacts only if it's in the source's listener set AND actually has
      // the filter column. Non-listeners always show their full rows.
      const reacts = !!filter && listeners.has(tile.id) && rows.some((row) => filter.column in row);
      const nextRows = reacts && filterValues
        ? rows.filter((row) => filterValues.has(String(row[filter!.column] ?? '')))
        : rows;
      return this.withTileClientRows(tile, nextRows);
    });
    // Server-recompute listeners (waterfall bridges): aggregated charts can only
    // react by recomputing on the filtered data. Fire per-tile recomputes for
    // listening waterfalls; restore every other/cleared waterfall to its
    // unfiltered bars (instant, cached — no API call).
    this.dashboardTiles.forEach((tile) => {
      if (!this.isRecomputeCrossFilterTile(tile)) return;
      if (filter && tile.id === filter.sourceTileId) return;
      if (filter && listeners.has(tile.id)) this.recomputeWaterfallTile(tile, filter);
      else this.restoreWaterfallTile(tile);
    });
    this.reflowCharts();
  }

  // ── Dropdown → client-side chart filtering (preview) ───────────────────────

  /** Active dropdown selections, keyed by the dropdown tile's componentId, so
   *  SEVERAL dropdowns compose: each re-apply starts from every registry tile's
   *  ORIGINAL rows and applies all active filters in turn. */
  private activeDropdownFilters = new Map<string, { field: string; values: string[] }>();

  private activeDropdownFilterPayload(): Array<{ column: string; operator: 'IN'; values: string[] }> {
    return [...this.activeDropdownFilters.values()]
      .filter((filter) => filter.field && filter.values.length)
      .map((filter) => ({ column: filter.field, operator: 'IN' as const, values: [...filter.values] }));
  }

  /** Filter the client-side chart family (the cross-filter registry tiles —
   *  bar / line-area / scatter) locally from their cached original rows. A tile
   *  ignores filters whose field its rows don't carry; with no active filters
   *  every registry tile is restored to its full rows. */
  private applyDropdownFiltersToClientTiles(): void {
    // Drop filters whose source dropdown tile no longer exists.
    const liveIds = new Set(this.dashboardTiles.map((t) => String(t.componentId ?? '')));
    [...this.activeDropdownFilters.keys()].forEach((key) => { if (!liveIds.has(key)) this.activeDropdownFilters.delete(key); });
    const filters = [...this.activeDropdownFilters.values()].filter((f) => f.field && f.values.length);
    this.dashboardTiles = this.dashboardTiles.map((tile) => {
      if (!this.isCrossFilterTile(tile)) return tile;
      const original = this.tileClientRows(tile);
      let rows = original;
      filters.forEach(({ field, values }) => {
        if (!original.some((row) => field in row)) return;
        const wanted = new Set(values.map(String));
        rows = rows.filter((row) => wanted.has(String(row[field] ?? '')));
      });
      return this.withTileClientRows(tile, rows);
    });
    this.reflowCharts();
  }

  /** WATERFALL tiles react to cross-filters via SERVER recompute (they render
   *  aggregated bars, not client rows) — needs the compute payload to re-run. */
  private isRecomputeCrossFilterTile(tile: DashboardTile): boolean {
    return tile.layoutType === 'WATERFALL' && !!tile.waterfallBars && !!tile.payload;
  }

  /** Monotonic per-tile sequence so a slow recompute response can never clobber
   *  a newer filter state (or a restore) that happened while it was in flight. */
  private waterfallFilterSeq = new Map<string, number>();

  /** Re-run a waterfall tile's own compute payload with the active cross-filter
   *  attached (`filters: [{column, operator:'IN', values}]`) and redraw from the
   *  response. Best-effort by design: if the backend ignores/errs on `filters`,
   *  the tile simply keeps showing its current bars. */
  private recomputeWaterfallTile(tile: DashboardTile, filter: { column: string; values: string[] }): void {
    const seq = (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1;
    this.waterfallFilterSeq.set(tile.id, seq);
    const url = resolveSelfServiceApiUrl(this.layout.apiEndpoints?.generateConfigApi, '/api/dvm/self-service/waterfall/generate');
    const payload = {
      ...tile.payload,
      filters: [{ column: filter.column, operator: 'IN', values: filter.values }],
    };
    this.http.post<Record<string, unknown>>(url, payload).subscribe({
      next: (response) => {
        if (this.waterfallFilterSeq.get(tile.id) !== seq) return; // stale response
        const rawBars = (Array.isArray(response) ? response : (response as { data?: WaterfallBar[] })?.data || []) as WaterfallBar[];
        if (!rawBars.length) return; // nothing usable — keep the current view
        const bars = this.applyStepSignsToWaterfallBars(rawBars, tile.payload);
        const current = this.dashboardTiles.find((t) => t.id === tile.id);
        if (!current) return;
        const updated: DashboardTile = {
          ...current,
          // Keep the original unfiltered bars ONCE so clearing restores instantly.
          waterfallBarsAll: current.waterfallBarsAll ?? current.waterfallBars,
          waterfallBars: bars,
        };
        updated.previewOption = this.buildWaterfallPreviewOption(bars, updated.format);
        updated.waterfallData = this.buildWaterfallTileData(updated);
        this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? updated : t));
        this.reflowCharts();
      },
      // Best-effort: a failed recompute leaves the tile's current bars untouched.
      error: () => undefined,
    });
  }

  /** Put a waterfall tile back on its unfiltered bars (kept when the first
   *  filtered recompute landed). Also invalidates any in-flight recompute. */
  private restoreWaterfallTile(tile: DashboardTile): void {
    this.waterfallFilterSeq.set(tile.id, (this.waterfallFilterSeq.get(tile.id) ?? 0) + 1);
    if (!tile.waterfallBarsAll) return; // never showed filtered bars
    const restored: DashboardTile = { ...tile, waterfallBars: tile.waterfallBarsAll, waterfallBarsAll: undefined };
    restored.previewOption = this.buildWaterfallPreviewOption(restored.waterfallBars!, restored.format);
    restored.waterfallData = this.buildWaterfallTileData(restored);
    this.dashboardTiles = this.dashboardTiles.map((t) => (t.id === tile.id ? restored : t));
  }

  // ── Cross-filter listener editor (per-tile filterKey + who reacts) ─────────

  /** Every OTHER cross-filter-capable tile that could react to `sourceTile`:
   *  registered client-side charts (filter their cached rows) PLUS recompute
   *  listeners (waterfall — re-runs its compute payload with the filter). KPI/
   *  drill/input tiles still never appear: they have neither rows nor recompute. */
  reactableTilesFor(sourceTile: DashboardTile): DashboardTile[] {
    return this.dashboardTiles.filter(
      (t) =>
        t.id !== sourceTile.id &&
        t.id !== this.previewTileId &&
        (this.isCrossFilterTile(t) || this.isRecomputeCrossFilterTile(t)),
    );
  }

  /** The listener tile ids that actually react: the explicit list when set, else
   *  the default of EVERY other cross-filter-capable tile (so a freshly-added
   *  tile cross-filters out of the box until the user narrows it). */
  private effectiveListenerTileIds(sourceTile: DashboardTile): string[] {
    if (Array.isArray(sourceTile.listenerTileIds)) return sourceTile.listenerTileIds;
    return this.reactableTilesFor(sourceTile).map((t) => t.id);
  }

  /** The filter column a tile publishes: its explicit override, else its category
   *  column (read generically from the tile's widget capabilities) so a click
   *  cross-filters by default. */
  effectiveTileFilterKey(tile: DashboardTile): string {
    if (tile.filterKey && tile.filterKey.trim()) return tile.filterKey.trim();
    const data = this.tileClientData(tile);
    const caps = data?.['capabilities'] as { category?: string } | undefined;
    return caps?.category || '';
  }

  /** The dataset(s) a tile reads — single `dataset` (bar/drill/kpi) or the
   *  multi-select `datasets` (waterfall bridge). */
  private tileDatasets(tile: DashboardTile): string[] {
    const single = String(tile.payload?.['dataset'] ?? '').trim();
    const multi = Array.isArray(tile.payload?.['datasets'])
      ? (tile.payload['datasets'] as unknown[]).map(String).filter(Boolean)
      : [];
    return multi.length ? multi : single ? [single] : [];
  }

  /** Columns a tile can publish a filter on — the union of its dataset(s)' columns.
   *  Memoized per tile — same appendTo:'body' identity-churn hazard as
   *  barColumns/columnsForRow: a fresh array every CD cycle made the listener
   *  editor's filter-key ng-select rebuild its panel mid-click, so selecting a
   *  value silently failed. Stable reference ⇒ clicks stick. */
  private tileFilterColumnsMemo = new Map<
    string,
    { key: string; sources: (string[] | undefined)[]; result: string[] }
  >();
  tileFilterColumns(tile: DashboardTile): string[] {
    const datasets = this.tileDatasets(tile);
    const key = datasets.join('|');
    const sources = datasets.map((dataset) => this.entityColumnsCache.get(dataset));
    const cached = this.tileFilterColumnsMemo.get(tile.id);
    if (cached && cached.key === key && cached.sources.every((source, i) => source === sources[i])) {
      return cached.result;
    }
    const seen = new Set<string>();
    const columns: string[] = [];
    sources.forEach((source) => {
      (source || []).forEach((column) => {
        if (!seen.has(column)) {
          seen.add(column);
          columns.push(column);
        }
      });
    });
    this.tileFilterColumnsMemo.set(tile.id, { key, sources, result: columns });
    return columns;
  }

  /** Open/close the listener editor for a tile (only one open at a time). */
  toggleListenerEditor(tile: DashboardTile, event?: Event): void {
    event?.stopPropagation();
    this.expandedListenerTileId = this.expandedListenerTileId === tile.id ? null : tile.id;
    if (this.expandedListenerTileId === tile.id) {
      // Ensure the tile's dataset columns are loaded for the filter-key dropdown.
      this.tileDatasets(tile).forEach((dataset) => this.loadColumnsForDataset(dataset));
    }
  }

  /** Set/clear the column a tile publishes as its cross-filter key. */
  setTileFilterKey(tile: DashboardTile, value: string): void {
    const trimmed = (value || '').trim();
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === tile.id ? { ...t, filterKey: trimmed || undefined } : t,
    );
    // A key change invalidates any active filter this tile published.
    if (this.activeCrossFilter?.sourceTileId === tile.id) this.clearCrossFilter();
  }

  /** Is `candidateTileId` currently a listener of `sourceTile`? */
  isTileListener(sourceTile: DashboardTile, candidateTileId: string): boolean {
    return this.effectiveListenerTileIds(sourceTile).includes(candidateTileId);
  }

  /** Toggle a candidate tile in/out of the source tile's listener set. */
  toggleTileListener(sourceTile: DashboardTile, candidateTileId: string): void {
    const current = this.effectiveListenerTileIds(sourceTile);
    const next = current.includes(candidateTileId)
      ? current.filter((id) => id !== candidateTileId)
      : [...current, candidateTileId];
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, listenerTileIds: next } : t,
    );
    // Re-apply so the change is visible immediately if a filter is active.
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.applyCrossFilterToTiles();
  }

  /** Reset a tile's filter config back to defaults (publishes category, all react). */
  resetTileListeners(sourceTile: DashboardTile): void {
    this.dashboardTiles = this.dashboardTiles.map((t) =>
      t.id === sourceTile.id ? { ...t, filterKey: undefined, listenerTileIds: undefined } : t,
    );
    if (this.activeCrossFilter?.sourceTileId === sourceTile.id) this.clearCrossFilter();
  }

  private nextStepId(): string {
    this.stepIdCounter += 1;
    return `bridge-step-${this.stepIdCounter}`;
  }

  private nextDrillDimensionId(): string {
    this.drillDimensionIdCounter += 1;
    return `drill-dimension-${this.drillDimensionIdCounter}`;
  }

  private nextDrillMeasureId(): string {
    this.drillMeasureIdCounter += 1;
    return `drill-measure-${this.drillMeasureIdCounter}`;
  }

  private tmColumnIdCounter = 0;

  private nextTmColumnId(): string {
    this.tmColumnIdCounter += 1;
    return `tm-column-${this.tmColumnIdCounter}`;
  }

  private componentIdCounter = 0;

  /** Unique component id. Optional prefix keeps it human-readable (e.g.
   *  `CMP-KPI-3-482`); counter guarantees uniqueness within a session and the
   *  random suffix avoids collisions with ids restored from a saved dashboard. */
  private createComponentId(prefix = 'CMP'): string {
    this.componentIdCounter += 1;
    return `${prefix}-${this.componentIdCounter}-${Math.floor(100 + Math.random() * 900)}`;
  }

  /** UNIQUE component id per chart (type prefix for readability). Every chart —
   *  including multiple of the same type (e.g. several KPIs) — gets its own id,
   *  so they ALL appear as distinct entries in an input control's target list,
   *  and applyRecomputeResults can match each recompute result back to the ONE
   *  tile it targets (by this id, not just by chart type). */
  private componentIdForLayout(layoutType: string): string {
    const prefix: Record<string, string> = {
      WATERFALL: 'CMP-WATERFALL',
      DRILL_DOWN_TABLE: 'CMP-DRILLDOWN',
      HORIZONTAL_BAR_CHART: 'CMP-HBAR',
      KPI: 'CMP-KPI',
      BAR: 'CMP-BAR',
      SCATTER_BUBBLE: 'CMP-SCATTER',
      LINE_AREA: 'CMP-LINE',
      TABLE_MATRIX: 'CMP-TMATRIX',
      DROPDOWN: 'CMP-DROPDOWN',
      FILE_UPLOAD: 'CMP-FILEUPLOAD',
      POPUP: 'CMP-POPUP',
    };
    return this.createComponentId(prefix[layoutType] || 'CMP');
  }
}






